import "core-js/modules/es.string.trim-start.js";
import React__default, { createElement, useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { format } from 'd3-format';
import { first } from 'lodash-es';
import moment from 'moment-timezone';
import classNames from 'classnames';
import { ResponsiveContainer, AreaChart, CartesianGrid, XAxis, YAxis, Area, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import mergeRefs from 'react-merge-refs';
import { usePopper } from 'react-popper';
import { useSpring, animated } from '@react-spring/web';
import { createPortal } from 'react-dom';
import { useMeasure } from '@react-hookz/web';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { Link } from 'react-router-dom';
var _DefaultMetricUnitNum;
/**
 *
 * @param val
 * @param numberOfDecimals
 */
var decimalFormatter = function decimalFormatter(val, numberOfDecimals) {
  if (numberOfDecimals === void 0) {
    numberOfDecimals = 0;
  }
  return format(",." + numberOfDecimals + "f")(Number(val));
};
var MetricUnit;
(function (MetricUnit) {
  MetricUnit["none"] = "NONE";
  MetricUnit["percent"] = "PERCENT";
  MetricUnit["percent_whole"] = "PERCENT_WHOLE";
  MetricUnit["preConvertedPercent"] = "PRECONVERTEDPERCENT";
  MetricUnit["percent_preformatted"] = "PERCENT_PREFORMATTED";
  // Only show dollar value
  MetricUnit["currency_whole"] = "CURRENCY_WHOLE";
  MetricUnit["currency"] = "CURRENCY";
  MetricUnit["currency_low"] = "CURRENCY_LOW";
  MetricUnit["currency_auto"] = "CURRENCY_AUTO";
  MetricUnit["number"] = "NUMBER";
  MetricUnit["number_low"] = "NUMBER_LOW";
  MetricUnit["number_whole"] = "NUMBER_WHOLE";
  MetricUnit["number_auto"] = "NUMBER_AUTO";
  // Web Specific
  MetricUnit["text"] = "TEXT";
  MetricUnit["multiplier"] = "MULTIPLIER";
})(MetricUnit || (MetricUnit = {}));
var DefaultMetricUnitNumDecimals = (_DefaultMetricUnitNum = {}, _DefaultMetricUnitNum[MetricUnit.none] = 0, _DefaultMetricUnitNum[MetricUnit.percent] = 2, _DefaultMetricUnitNum[MetricUnit.percent_whole] = 0, _DefaultMetricUnitNum[MetricUnit.preConvertedPercent] = 2, _DefaultMetricUnitNum[MetricUnit.percent_preformatted] = 2, _DefaultMetricUnitNum[MetricUnit.currency_whole] = 0, _DefaultMetricUnitNum[MetricUnit.currency] = 2, _DefaultMetricUnitNum[MetricUnit.currency_auto] = 2, _DefaultMetricUnitNum[MetricUnit.currency_low] = 2, _DefaultMetricUnitNum[MetricUnit.number] = 2, _DefaultMetricUnitNum[MetricUnit.number_whole] = 0, _DefaultMetricUnitNum[MetricUnit.number_auto] = 2, _DefaultMetricUnitNum[MetricUnit.number_low] = 2, _DefaultMetricUnitNum[MetricUnit.text] = 0, _DefaultMetricUnitNum[MetricUnit.multiplier] = 2, _DefaultMetricUnitNum);
var castMetricToNumber = function castMetricToNumber(value) {
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value === "string") {
    return parseFloat(value);
  }
  return value;
};
var castPercentMetricToNumber = function castPercentMetricToNumber(value) {
  return castMetricToNumber(value) * 0.01;
};
var CurrencyMetric = function CurrencyMetric(props) {
  var _props$allowNull = props.allowNull,
    allowNull = _props$allowNull === void 0 ? false : _props$allowNull,
    className = props.className,
    formatCurrency = props.formatCurrency;
  var display = React__default.useMemo(function () {
    var value = castMetricToNumber(props.value || null);
    if (value === null || value === undefined) {
      if (allowNull) {
        return "--";
      }
      return "";
    }
    return formatCurrency(value);
  }, [props.value, allowNull]);
  return React__default.createElement("span", {
    className: className
  }, display);
};
CurrencyMetric.defaultProps = {
  className: ""
};
var MultiplierMetric = function MultiplierMetric(props) {
  var className = props.className,
    numDecimals = props.numDecimals,
    allowNull = props.allowNull;
  var display = React__default.useMemo(function () {
    var value = castMetricToNumber(props.value);
    if (value === null || value === undefined) {
      if (allowNull) {
        return "--";
      }
      return "";
    }
    return decimalFormatter(value, numDecimals) + "x";
  }, [allowNull, numDecimals, props.value]);
  return React__default.createElement("span", {
    className: className
  }, display);
};
MultiplierMetric.defaultProps = {
  className: "",
  numDecimals: DefaultMetricUnitNumDecimals[MetricUnit.multiplier]
};
var NumberMetric = function NumberMetric(props) {
  var numDecimals = props.numDecimals,
    allowNull = props.allowNull,
    className = props.className;
  var display = React__default.useMemo(function () {
    var value = castMetricToNumber(props.value);
    if (value === null || value === undefined) {
      if (allowNull) {
        return "--";
      }
      return "";
    }
    return decimalFormatter(value, numDecimals);
  }, [props.value, allowNull, numDecimals]);
  return React__default.createElement("span", {
    className: className
  }, display);
};
NumberMetric.defaultProps = {
  className: "",
  numDecimals: DefaultMetricUnitNumDecimals[MetricUnit.number]
};
var PercentageMetric = function PercentageMetric(props) {
  var numDecimals = props.numDecimals,
    allowNull = props.allowNull,
    className = props.className,
    _props$isAlreadyPerce = props.isAlreadyPercent,
    isAlreadyPercent = _props$isAlreadyPerce === void 0 ? false : _props$isAlreadyPerce;
  var display = React__default.useMemo(function () {
    var value = isAlreadyPercent ? castPercentMetricToNumber(props.value) : castMetricToNumber(props.value);
    if (value === null || value === undefined) {
      if (allowNull) {
        return "--";
      }
      return "";
    }
    return decimalFormatter(value * 100, numDecimals) + "%";
  }, [props.value, numDecimals, allowNull]);
  return React__default.createElement("span", {
    className: className
  }, display);
};
PercentageMetric.defaultProps = {
  numDecimals: DefaultMetricUnitNumDecimals[MetricUnit.percent]
};
var ThemeContextMode;
(function (ThemeContextMode) {
  ThemeContextMode["neutral"] = "neutral";
  ThemeContextMode["light"] = "light";
  ThemeContextMode["dark"] = "dark";
})(ThemeContextMode || (ThemeContextMode = {}));
var ChartAxis;
(function (ChartAxis) {
  ChartAxis["default"] = "DEFAULT";
  ChartAxis["smallValue"] = "SMALL_VALUE";
})(ChartAxis || (ChartAxis = {}));
var LineGraphAxis;
(function (LineGraphAxis) {
  LineGraphAxis["percent"] = "PERCENT";
  LineGraphAxis["percent_low"] = "PERCENT_LOW";
  LineGraphAxis["percent_preformatted"] = "PERCENT_PREFORMATTED";
  LineGraphAxis["currency"] = "CURRENCY";
  LineGraphAxis["currency_low"] = "CURRENCY_LOW";
  LineGraphAxis["currency_auto"] = "CURRENCY_AUTO";
  LineGraphAxis["number"] = "NUMBER";
  LineGraphAxis["number_low"] = "NUMBER_LOW";
  LineGraphAxis["number_auto"] = "NUMBER_AUTO"; // number axis based on the range of values of metric
})(LineGraphAxis || (LineGraphAxis = {}));
var UIMetricCardColor;
(function (UIMetricCardColor) {
  UIMetricCardColor["indigo"] = "bg-indigo-1 group-hover:bg-indigo-2-dark group-active:bg-indigo-3-dark dark:group-hover:bg-indigo-2 dark:group-active:bg-indigo-3";
  UIMetricCardColor["teal"] = "bg-teal-1 group-hover:bg-teal-2-dark group-active:bg-teal-3-dark dark:group-hover:bg-teal-2 dark:group-active:bg-teal-3";
  UIMetricCardColor["orange"] = "bg-orange-1 group-hover:bg-orange-2-dark group-active:bg-orange-3-dark dark:group-hover:bg-orange-2 dark:group-active:bg-orange-3";
  UIMetricCardColor["yellow"] = "bg-yellow-1 group-hover:bg-yellow-2-dark group-active:bg-yellow-3-dark dark:group-hover:bg-yellow-2 dark:group-active:bg-yellow-3";
  UIMetricCardColor["pink"] = "bg-pink-1 group-hover:bg-pink-2-dark group-active:bg-pink-3-dark dark:group-hover:bg-pink-2 dark:group-active:bg-pink-3";
  UIMetricCardColor["gray3light"] = "bg-gray-3 group-hover:bg-gray-2 group-active:bg-gray-1 dark:bg-gray-3-dark dark:group-hover:bg-gray-1 dark:group-active:bg-gray-2";
  UIMetricCardColor["gray3dark"] = "bg-gray-3-dark group-hover:bg-gray-2-dark group-active:bg-gray-1 dark:bg-gray-3-light dark:group-hover:bg-gray-1 dark:group-active:bg-gray-2-light";
})(UIMetricCardColor || (UIMetricCardColor = {}));
// LineGraph strings to add to source keys to indicate separate lines on the graph with the same keys
var LineGraphSuffix;
(function (LineGraphSuffix) {
  LineGraphSuffix["COMPLETE"] = "-Complete";
  LineGraphSuffix["OTHER"] = "-Other";
  LineGraphSuffix["OTHER_COMPLETE"] = "-Other-Complete";
})(LineGraphSuffix || (LineGraphSuffix = {}));
var UILineGraphDot = function UILineGraphDot(props) {
  var cx = props.cx,
    cy = props.cy,
    color = props.color,
    themeContextMode = props.themeContextMode;
  var offset = 5;
  //stroke should be gray-5-dark when in dark mode
  var strokeColor = themeContextMode === ThemeContextMode.dark ? "#111827" : "#FFF";
  return React__default.createElement("svg", {
    width: offset * 4,
    height: offset * 4,
    x: cx - offset - 2,
    y: cy - offset - 2
  }, React__default.createElement("g", {
    fill: "none",
    fillRule: "evenodd",
    transform: "translate(2 2)"
  }, React__default.createElement("circle", {
    cx: offset,
    cy: offset,
    r: offset - 1,
    fill: color,
    fillRule: "nonzero",
    stroke: strokeColor,
    strokeWidth: "2"
  })));
};
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  _setPrototypeOf(subClass, superClass);
}
function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf(o, p);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

/******************************************* Color  ******************************************/
var ThemeAwareUIColor;
(function (ThemeAwareUIColor) {
  // Purple
  ThemeAwareUIColor["purple2"] = "purple-2";
  ThemeAwareUIColor["purple3"] = "purple-3";
  // Indigo
  ThemeAwareUIColor["indigo2"] = "indigo-2";
  ThemeAwareUIColor["indigo3"] = "indigo-3";
  ThemeAwareUIColor["indigo4"] = "indigo-4";
  // Pink
  ThemeAwareUIColor["pink2"] = "pink-2";
  ThemeAwareUIColor["pink3"] = "pink-3";
  // Blue
  ThemeAwareUIColor["blue2"] = "blue-2";
  ThemeAwareUIColor["blue3"] = "blue-3";
  // Green
  ThemeAwareUIColor["green2"] = "green-2";
  ThemeAwareUIColor["green3"] = "green-3";
  // Teal
  ThemeAwareUIColor["teal2"] = "teal-2";
  ThemeAwareUIColor["teal3"] = "teal-3";
  // Red
  ThemeAwareUIColor["red2"] = "red-2";
  ThemeAwareUIColor["red3"] = "red-3";
  // Orange
  ThemeAwareUIColor["orange2"] = "orange-2";
  ThemeAwareUIColor["orange3"] = "orange-3";
  // Yellow
  ThemeAwareUIColor["yellow2"] = "yellow-2";
  ThemeAwareUIColor["yellow3"] = "yellow-3";
  // Gray
  ThemeAwareUIColor["gray2"] = "gray-2";
  ThemeAwareUIColor["gray3"] = "gray-3";
  ThemeAwareUIColor["gray4"] = "gray-4";
  ThemeAwareUIColor["gray5"] = "gray-5";
  ThemeAwareUIColor["gray6"] = "gray-6";
})(ThemeAwareUIColor || (ThemeAwareUIColor = {}));
var StaticUIColor;
(function (StaticUIColor) {
  StaticUIColor["white"] = "white";
  StaticUIColor["black"] = "black";
  // Purple
  StaticUIColor["purple1"] = "purple-1";
  StaticUIColor["purpleLight2"] = "purple-2-light";
  StaticUIColor["purpleLight3"] = "purple-3-light";
  StaticUIColor["purpleDark2"] = "purple-2-dark";
  StaticUIColor["purpleDark3"] = "purple-3-dark";
  // Indigo
  StaticUIColor["indigo1"] = "indigo-1";
  StaticUIColor["indigoLight2"] = "indigo-2-light";
  StaticUIColor["indigoLight3"] = "indigo-3-light";
  StaticUIColor["indigoLight4"] = "indigo-4-light";
  StaticUIColor["indigoDark2"] = "indigo-2-dark";
  StaticUIColor["indigoDark3"] = "indigo-3-dark";
  StaticUIColor["indigoDark4"] = "indigo-4-dark";
  // Pink
  StaticUIColor["pink1"] = "pink-1";
  StaticUIColor["pinkLight2"] = "pink-2-light";
  StaticUIColor["pinkLight3"] = "pink-3-light";
  StaticUIColor["pinkDark2"] = "pink-2-dark";
  StaticUIColor["pinkDark3"] = "pink-3-dark";
  // Blue
  StaticUIColor["blue1"] = "blue-1";
  StaticUIColor["blueLight2"] = "blue-2-light";
  StaticUIColor["blueLight3"] = "blue-3-light";
  StaticUIColor["blueLight4"] = "blue-2-dark";
  StaticUIColor["blueLight5"] = "blue-3-dark";
  // Green
  StaticUIColor["green1"] = "green-1";
  StaticUIColor["greenLight2"] = "green-2-light";
  StaticUIColor["greenLight3"] = "green-3-light";
  StaticUIColor["greenDark2"] = "green-2-dark";
  StaticUIColor["greenDark3"] = "green-3-dark";
  // Teal
  StaticUIColor["teal1"] = "teal-1";
  StaticUIColor["tealLight2"] = "teal-2-light";
  StaticUIColor["tealLight3"] = "teal-3-light";
  StaticUIColor["tealDark2"] = "teal-2-dark";
  StaticUIColor["tealDark3"] = "teal-3-dark";
  // Red
  StaticUIColor["red1"] = "red-1";
  StaticUIColor["redLight2"] = "red-2-light";
  StaticUIColor["redLight3"] = "red-3-light";
  StaticUIColor["redDark2"] = "red-2-dark";
  StaticUIColor["redDark3"] = "red-3-dark";
  // Orange
  StaticUIColor["orange1"] = "orange-1";
  StaticUIColor["orangeLight2"] = "orange-2-light";
  StaticUIColor["orangeLight3"] = "orange-3-light";
  StaticUIColor["orangeDark2"] = "orange-2-dark";
  StaticUIColor["orangeDark3"] = "orange-3-dark";
  // Yellow
  StaticUIColor["yellow1"] = "yellow-1";
  StaticUIColor["yellowLight2"] = "yellow-2-light";
  StaticUIColor["yellowLight3"] = "yellow-3-light";
  StaticUIColor["yellowDark2"] = "yellow-2-dark";
  StaticUIColor["yellowDark3"] = "yellow-3-dark";
  // Gray
  StaticUIColor["gray1"] = "gray-1";
  StaticUIColor["grayLight2"] = "gray-2-light";
  StaticUIColor["grayLight3"] = "gray-3-light";
  StaticUIColor["grayLight4"] = "gray-4-light";
  StaticUIColor["grayLight5"] = "gray-5-light";
  StaticUIColor["grayLight6"] = "gray-6-light";
  StaticUIColor["grayDark2"] = "gray-2-dark";
  StaticUIColor["grayDark3"] = "gray-3-dark";
  StaticUIColor["grayDark4"] = "gray-4-dark";
  StaticUIColor["grayDark5"] = "gray-5-dark";
  StaticUIColor["grayDark6"] = "gray-6-dark";
  // White Label
  StaticUIColor["primaryColor"] = "primary-color";
  StaticUIColor["primaryColorHover"] = "primary-color-hover";
  StaticUIColor["primaryColorActive"] = "primary-color-active";
  StaticUIColor["secondaryColor"] = "secondary-color";
  StaticUIColor["secondaryColorHover"] = "secondary-color-hover";
  StaticUIColor["secondaryColorActive"] = "secdonary-color-active";
  StaticUIColor["accentColor"] = "accent-color";
  StaticUIColor["primaryTextColor"] = "primary-text-color";
  StaticUIColor["secondaryTextColor"] = "secondary-text-color";
  StaticUIColor["backgroundColor"] = "background-color";
  StaticUIColor["containerColor"] = "containerColor";
  StaticUIColor["sideBarColor"] = "sidebarColor";
  StaticUIColor["sideBarItemColorHover"] = "sidebar-item-color-hover";
  StaticUIColor["sideBarItemColorActive"] = "sidebar-item-color-active";
  StaticUIColor["sideBarItemColorActiveHover"] = "sidebar-item-color-active-hover";
  // Extra
  StaticUIColor["transparent"] = "transparent";
  StaticUIColor["inherit"] = "";
})(StaticUIColor || (StaticUIColor = {}));
/**
 * Enum representing our design system color palette
 * matching hex code can be found in tailwind.config.js
 * https://www.figma.com/file/iNedOoN7sChzGjoJgZjQWF/Perpetua-Master
 */
var UIColor = /*#__PURE__*/Object.freeze( /*#__PURE__*/_extends({}, ThemeAwareUIColor, StaticUIColor));
var _path;
function _extends$1() {
  _extends$1 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1.apply(this, arguments);
}
var SvgSb = function SvgSb(props) {
  return /*#__PURE__*/createElement("svg", _extends$1({
    width: 16,
    height: 16,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path || (_path = /*#__PURE__*/createElement("path", {
    d: "M6.052 6.122a1.641 1.641 0 0 0-.638-.484 1.707 1.707 0 0 0-.748-.187 2.34 2.34 0 0 0-.385.033.92.92 0 0 0-.34.132.828.828 0 0 0-.265.242.644.644 0 0 0-.099.374c0 .132.026.242.077.33.059.088.14.165.242.23.11.067.238.13.385.188.147.051.312.106.495.165.264.088.54.187.825.297.286.103.546.242.781.418a2.2 2.2 0 0 1 .583.66c.154.257.231.58.231.968 0 .447-.084.836-.253 1.166-.161.323-.381.59-.66.803-.279.213-.598.37-.957.473-.36.103-.73.154-1.11.154a4.665 4.665 0 0 1-1.618-.286 3.481 3.481 0 0 1-1.298-.836l1.232-1.254c.19.235.44.433.748.594.315.154.627.23.935.23.14 0 .275-.014.407-.043a.957.957 0 0 0 .341-.143.7.7 0 0 0 .242-.264.831.831 0 0 0 .088-.396.627.627 0 0 0-.11-.374.997.997 0 0 0-.319-.275 2.04 2.04 0 0 0-.506-.231 14.51 14.51 0 0 0-.682-.231 6.949 6.949 0 0 1-.737-.286 2.663 2.663 0 0 1-.638-.418 2.175 2.175 0 0 1-.451-.627c-.11-.25-.165-.55-.165-.902 0-.433.088-.803.264-1.111.176-.308.407-.561.693-.76.286-.197.609-.34.968-.428a3.996 3.996 0 0 1 2.431.099c.462.161.865.4 1.21.715L6.052 6.122ZM14.51 9.62c0 .425-.085.785-.254 1.078a2.024 2.024 0 0 1-.67.693 3.013 3.013 0 0 1-.958.385c-.352.073-.715.11-1.089.11H8.437V4.098h3.102c.3 0 .609.033.924.099.315.059.601.165.858.319.257.147.466.348.627.605.161.257.242.583.242.979 0 .433-.117.792-.352 1.078-.235.279-.543.48-.924.605v.022c.22.037.425.106.616.209.198.095.367.22.506.374.147.154.26.334.341.539.088.205.132.436.132.693Zm-2.135-3.267c0-.264-.103-.455-.308-.572-.198-.125-.495-.187-.89-.187h-.925V7.2h1.012c.352 0 .623-.07.814-.21.198-.146.297-.358.297-.637Zm.253 3.09c0-.322-.12-.545-.363-.67-.242-.125-.565-.187-.968-.187h-1.045v1.782h1.056c.147 0 .297-.011.451-.033.161-.03.304-.077.43-.143a.83.83 0 0 0 .318-.275.855.855 0 0 0 .121-.473Z"
  })));
};
var _path$1;
function _extends$2() {
  _extends$2 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2.apply(this, arguments);
}
var SvgSd = function SvgSd(props) {
  return /*#__PURE__*/createElement("svg", _extends$2({
    width: 16,
    height: 10,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1 || (_path$1 = /*#__PURE__*/createElement("path", {
    d: "M5.552 3.122a1.641 1.641 0 0 0-.638-.484 1.707 1.707 0 0 0-.748-.187 2.34 2.34 0 0 0-.385.033.92.92 0 0 0-.341.132.828.828 0 0 0-.264.242.644.644 0 0 0-.099.374c0 .132.026.242.077.33.059.088.14.165.242.23.11.067.238.13.385.188.147.051.312.106.495.165.264.088.539.187.825.297.286.103.546.242.781.418a2.2 2.2 0 0 1 .583.66c.154.257.231.58.231.968 0 .447-.084.836-.253 1.166-.161.323-.381.59-.66.803-.279.213-.598.37-.957.473-.36.103-.73.154-1.111.154a4.666 4.666 0 0 1-1.617-.286A3.48 3.48 0 0 1 .8 7.962l1.232-1.254c.19.235.44.433.748.594.315.154.627.23.935.23.14 0 .275-.014.407-.043a.957.957 0 0 0 .341-.143.7.7 0 0 0 .242-.264.831.831 0 0 0 .088-.396.627.627 0 0 0-.11-.374.997.997 0 0 0-.319-.275 2.04 2.04 0 0 0-.506-.231c-.198-.073-.425-.15-.682-.231a6.948 6.948 0 0 1-.737-.286 2.663 2.663 0 0 1-.638-.418 2.175 2.175 0 0 1-.451-.627c-.11-.25-.165-.55-.165-.902 0-.433.088-.803.264-1.111.176-.308.407-.561.693-.76.286-.197.609-.34.968-.428a3.996 3.996 0 0 1 2.431.099c.462.161.865.4 1.21.715L5.552 3.122ZM15.208 4.97c0 .682-.128 1.272-.385 1.77-.25.492-.583.9-1.001 1.222a4.25 4.25 0 0 1-1.397.704c-.52.147-1.049.22-1.584.22H7.937V1.098h2.816c.55 0 1.093.066 1.628.198a3.958 3.958 0 0 1 1.43.638c.418.293.755.69 1.012 1.188.257.499.385 1.115.385 1.848Zm-1.958 0c0-.44-.073-.803-.22-1.09a1.726 1.726 0 0 0-.572-.692 2.182 2.182 0 0 0-.814-.374c-.3-.073-.612-.11-.935-.11h-.935v4.554h.891c.337 0 .66-.037.968-.11.315-.08.59-.21.825-.385.242-.176.433-.41.572-.704.147-.293.22-.656.22-1.09Z",
    fill: "#9CA3AF"
  })));
};
var _path$2;
function _extends$3() {
  _extends$3 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$3.apply(this, arguments);
}
var SvgSp = function SvgSp(props) {
  return /*#__PURE__*/createElement("svg", _extends$3({
    width: 16,
    height: 16,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2 || (_path$2 = /*#__PURE__*/createElement("path", {
    d: "M6.252 6.122a1.641 1.641 0 0 0-.638-.484 1.707 1.707 0 0 0-.748-.187 2.34 2.34 0 0 0-.385.033.92.92 0 0 0-.341.132.828.828 0 0 0-.264.242.644.644 0 0 0-.099.374c0 .132.026.242.077.33.059.088.14.165.242.23.11.067.238.13.385.188.147.051.312.106.495.165.264.088.539.187.825.297.286.103.546.242.781.418a2.2 2.2 0 0 1 .583.66c.154.257.231.58.231.968 0 .447-.084.836-.253 1.166-.161.323-.381.59-.66.803-.279.213-.598.37-.957.473-.36.103-.73.154-1.111.154a4.665 4.665 0 0 1-1.617-.286 3.481 3.481 0 0 1-1.298-.836l1.232-1.254c.19.235.44.433.748.594.315.154.627.23.935.23.14 0 .275-.014.407-.043a.957.957 0 0 0 .341-.143.7.7 0 0 0 .242-.264.831.831 0 0 0 .088-.396.627.627 0 0 0-.11-.374.997.997 0 0 0-.319-.275 2.04 2.04 0 0 0-.506-.231 14.51 14.51 0 0 0-.682-.231 6.949 6.949 0 0 1-.737-.286 2.663 2.663 0 0 1-.638-.418 2.175 2.175 0 0 1-.451-.627c-.11-.25-.165-.55-.165-.902 0-.433.088-.803.264-1.111.176-.308.407-.561.693-.76.286-.197.609-.34.968-.428a3.996 3.996 0 0 1 2.431.099c.462.161.865.4 1.21.715L6.252 6.122ZM14.544 6.496c0 .455-.084.84-.253 1.155a2.09 2.09 0 0 1-.682.748 3.01 3.01 0 0 1-.99.418 5.074 5.074 0 0 1-1.166.132h-.935v2.937H8.637V4.098h2.86c.425 0 .821.044 1.188.132.374.08.697.216.968.407.279.183.495.429.649.737.161.3.242.675.242 1.122Zm-1.881.01a.838.838 0 0 0-.11-.45.766.766 0 0 0-.297-.275 1.278 1.278 0 0 0-.429-.132 3.421 3.421 0 0 0-.484-.033h-.825v1.837h.792c.176 0 .345-.015.506-.044.161-.03.304-.08.429-.154a.829.829 0 0 0 .308-.286.897.897 0 0 0 .11-.462Z"
  })));
};
var _g, _defs;
function _extends$4() {
  _extends$4 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$4.apply(this, arguments);
}
var SvgAccountStructure = function SvgAccountStructure(props) {
  return /*#__PURE__*/createElement("svg", _extends$4({
    viewBox: "0 0 25 25",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g || (_g = /*#__PURE__*/createElement("g", {
    clipPath: "url(#account-structure_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    d: "M12.865.001v6.2a5.8 5.8 0 0 1 4.41 3.426l1.938-.54 4.104-1.14A12.057 12.057 0 0 0 12.865.002ZM18.474 22.12A11.945 11.945 0 0 0 23.78 9.62l-6.066 1.686a5.8 5.8 0 0 1-5.763 6.434 5.759 5.759 0 0 1-2.983-.834l-4.063 4.781a11.947 11.947 0 0 0 13.57.435ZM3.595 20.578l4.046-4.766a5.781 5.781 0 0 1-1.486-3.883 5.8 5.8 0 0 1 4.997-5.743V-.003A12.064 12.064 0 0 0 .733 7.845a12.077 12.077 0 0 0 2.862 12.732Z"
  }))), _defs || (_defs = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "account-structure_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    d: "M0 0h24.023v24.024H0z"
  })))));
};
var _g$1, _defs$1;
function _extends$5() {
  _extends$5 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$5.apply(this, arguments);
}
var SvgAdFormats = function SvgAdFormats(props) {
  return /*#__PURE__*/createElement("svg", _extends$5({
    viewBox: "0 0 25 25",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$1 || (_g$1 = /*#__PURE__*/createElement("g", {
    clipPath: "url(#ad-formats_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    d: "m5.476 10.203 7.493 3.64 7.456-3.646L12.8 6.404l-7.324 3.799ZM21.601 21.15v-9.547l-7.703 3.747v9.553l7.703-3.753ZM12.064 24.903V15.35L4.36 11.602v9.547l7.704 3.753ZM13.153 5.482a1.16 1.16 0 0 0 .832-1.11V1.163a1.163 1.163 0 0 0-2.326 0v3.211a1.15 1.15 0 0 0 1.493 1.11l.002-.002ZM18.352 7.27A1.165 1.165 0 0 0 20 7.264l3.05-3.047a1.163 1.163 0 0 0-1.645-1.645l-3.051 3.05a1.165 1.165 0 0 0 0 1.646ZM7.292 7.27a1.163 1.163 0 0 1-1.65.002L2.593 4.218a1.163 1.163 0 0 1 1.645-1.644l3.053 3.05a1.164 1.164 0 0 1 0 1.647Z"
  }))), _defs$1 || (_defs$1 = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "ad-formats_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    d: "M0 0h24.899v24.899H0z"
  })))));
};
var _path$3;
function _extends$6() {
  _extends$6 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$6.apply(this, arguments);
}
var SvgBenchmarker = function SvgBenchmarker(props) {
  return /*#__PURE__*/createElement("svg", _extends$6({
    width: 16,
    height: 16,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$3 || (_path$3 = /*#__PURE__*/createElement("path", {
    d: "M8.657 5.243a1 1 0 0 1-1.414 0L4.707 2.707C4.077 2.077 4.523 1 5.414 1h5.071c.891 0 1.337 1.077.707 1.707L8.657 5.243ZM1 11.05a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1v-3ZM6 8.05a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-6ZM11 5.05a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-9Z"
  })));
};
var _g$2, _defs$2;
function _extends$7() {
  _extends$7 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$7.apply(this, arguments);
}
var SvgImpactDrivers = function SvgImpactDrivers(props) {
  return /*#__PURE__*/createElement("svg", _extends$7({
    viewBox: "0 0 25 26",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$2 || (_g$2 = /*#__PURE__*/createElement("g", {
    clipPath: "url(#impact-drivers_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    d: "M20.917 25.155H3.986a3.99 3.99 0 0 1-3.984-3.983V4.24A3.99 3.99 0 0 1 3.986.257h16.93a3.983 3.983 0 0 1 3.985 3.984v16.93a3.983 3.983 0 0 1-3.984 3.985ZM7.439 15.082v5.657a.996.996 0 0 0 1.992 0v-5.653l2.319 2.285a.998.998 0 1 0 1.4-1.42l-4.011-3.949v-.007h-.006l-.01-.01-.01-.011h-.008a.038.038 0 0 0-.017-.016v-.006a1.033 1.033 0 0 0-.344-.19H8.69a.036.036 0 0 1-.015 0 .083.083 0 0 0-.025 0h-.019a.019.019 0 0 1-.014 0H8.544a.05.05 0 0 0-.024 0h-.19a.028.028 0 0 1-.014 0h-.031a.033.033 0 0 1-.015 0 .01.01 0 0 0-.005 0H8.22a.013.013 0 0 0-.005 0H8.21a.04.04 0 0 0-.015 0h-.01l-.004.003-.006-.002a.015.015 0 0 1-.011 0H8.113a.021.021 0 0 0-.011 0 .027.027 0 0 1-.013 0h-.006a.023.023 0 0 0-.012 0h-.01l-.015.007h-.01l-.011.006h-.008s0 .006-.008.007H7.996s-.007.007-.01.007c-.005 0-.009.008-.012.008-.003 0-.006.007-.01.007h-.031s-.006.006-.01.006l-.007.008c-.002 0-.004 0-.005.002a.007.007 0 0 0-.002.005l-.015.007-.002.005a.007.007 0 0 1-.005.002v.005h-.012a.01.01 0 0 0-.006.003l-.002.005-.01.01H7.85l-4.018 3.96a.997.997 0 0 0 .697 1.706c.261 0 .511-.103.698-.286l2.317-2.29-.106.13Zm8.865-1.394c.004.002.009.002.013 0a.12.12 0 0 0 .035 0 .082.082 0 0 0 .025 0H16.543a.037.037 0 0 0 .018 0h.068a.01.01 0 0 0 .006 0H16.685a.025.025 0 0 1 .014 0 .014.014 0 0 1 .005-.001c.002 0 .005 0 .006.002h.02a.01.01 0 0 0 .005 0 .015.015 0 0 0 .01 0H16.8a.031.031 0 0 1 .013 0h.007a.02.02 0 0 1 .015 0h.019l.005-.002c.002 0 .003 0 .005.002h.049l.01-.008h.005l.006-.002a.013.013 0 0 0 .004-.005h.006c.008 0 .012-.009.015-.009H16.98s.006-.007.01-.007v-.005h.007l.01-.007h.006l.01-.012s.01 0 .01-.007a.022.022 0 0 1 .011-.007l.007-.006a.013.013 0 0 1 .004-.005.012.012 0 0 1 .006-.002s.007-.007.01-.007l4.018-3.96a1.002 1.002 0 0 0 .015-1.41 1.018 1.018 0 0 0-.71-.3c-.261.001-.51.104-.697.287L17.374 10.5V4.85a.996.996 0 0 0-1.992 0v5.652l-2.319-2.284a.997.997 0 0 0-1.4 1.42l4.012 3.948.007.007.008.008v.006h.007l.006.008a.945.945 0 0 0 .355.197h.077a.031.031 0 0 0 .017 0h.034l.118-.123Z"
  }))), _defs$2 || (_defs$2 = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "impact-drivers_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    transform: "translate(0 .258)",
    d: "M0 0h24.902v24.898H0z"
  })))));
};
var _g$3, _defs$3;
function _extends$8() {
  _extends$8 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$8.apply(this, arguments);
}
var SvgOpportunities = function SvgOpportunities(props) {
  return /*#__PURE__*/createElement("svg", _extends$8({
    viewBox: "0 0 25 25",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$3 || (_g$3 = /*#__PURE__*/createElement("g", {
    clipPath: "url(#opportunities_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    d: "m24.752 5.847-1.778-2.704a.887.887 0 0 0-.739-.405h-8.894V.902a.89.89 0 1 0-1.78 0v7.244H2.665a.886.886 0 0 0-.738.405l-1.78 2.706a.911.911 0 0 0 0 .995l1.78 2.704a.886.886 0 0 0 .737.406h8.896v8.64a.89.89 0 1 0 1.779 0V9.95h8.897a.886.886 0 0 0 .738-.406l1.78-2.705a.911.911 0 0 0 0-.992Z"
  }))), _defs$3 || (_defs$3 = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "opportunities_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    d: "M0 0h24.899v24.899H0z"
  })))));
};
var _g$4, _defs$4;
function _extends$9() {
  _extends$9 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$9.apply(this, arguments);
}
var SvgPerformanceMatrix = function SvgPerformanceMatrix(props) {
  return /*#__PURE__*/createElement("svg", _extends$9({
    viewBox: "0 0 25 26",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$4 || (_g$4 = /*#__PURE__*/createElement("g", {
    clipPath: "url(#performance-matrix_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    d: "M20.917 25.157H3.986a3.99 3.99 0 0 1-3.984-3.983V4.243A3.99 3.99 0 0 1 3.986.259h16.93a3.983 3.983 0 0 1 3.985 3.984v16.93a3.983 3.983 0 0 1-3.984 3.985Zm-9.018-10.49v6.01a.996.996 0 0 0 1.992 0v-6.972h6.526a.996.996 0 0 0 0-1.992h-5.561l6.268-6.268a1.009 1.009 0 0 0 0-1.41.994.994 0 0 0-1.41 0L13.892 9.86V4.742a.996.996 0 0 0-1.992 0v6.971H4.483a.996.996 0 0 0 0 1.992h5.565L3.78 19.974a.976.976 0 0 0-.292.697.994.994 0 0 0 1.187.977.996.996 0 0 0 .51-.27l6.712-6.712.002.001Z"
  }))), _defs$4 || (_defs$4 = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "performance-matrix_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    transform: "translate(0 .258)",
    d: "M0 0h24.902v24.899H0z"
  })))));
};
var _g$5, _defs$5;
function _extends$a() {
  _extends$a = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$a.apply(this, arguments);
}
var SvgProductAnalysis = function SvgProductAnalysis(props) {
  return /*#__PURE__*/createElement("svg", _extends$a({
    viewBox: "0 0 25 25",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$5 || (_g$5 = /*#__PURE__*/createElement("g", {
    clipPath: "url(#product-analysis_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    d: "M2.988 19.142a2.973 2.973 0 0 1-.996-2.21V4.98A2.988 2.988 0 0 1 4.98 1.992H16.93a2.973 2.973 0 0 1 2.211.996h1.773c.2 0 .398.01.59.03A4.98 4.98 0 0 0 16.93 0H4.98A4.986 4.986 0 0 0 0 4.98V16.93a4.98 4.98 0 0 0 3.018 4.573 6.07 6.07 0 0 1-.03-.59v-1.772Z"
  }), /*#__PURE__*/createElement("path", {
    d: "M4.98 19.919v.996c0 .105.023.2.03.306a3.977 3.977 0 0 0 3.954 3.678h11.95a3.989 3.989 0 0 0 3.985-3.984V8.964A3.977 3.977 0 0 0 21.22 5.01c-.1-.008-.2-.031-.306-.031H8.964c-.129 0-.25.026-.375.038a3.963 3.963 0 0 0-3.571 3.571c-.012.126-.038.246-.038.375v10.955Zm5.975-5.478h3.486v-3.486a.996.996 0 0 1 1.992 0v3.486h3.486a.996.996 0 0 1 0 1.992h-3.486v3.486a.996.996 0 1 1-1.992 0v-3.486h-3.486a.996.996 0 1 1 0-1.992Z"
  }))), _defs$5 || (_defs$5 = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "product-analysis_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    d: "M0 0h24.899v24.899H0z"
  })))));
};
var _g$6, _defs$6;
function _extends$b() {
  _extends$b = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$b.apply(this, arguments);
}
var SvgSummary = function SvgSummary(props) {
  return /*#__PURE__*/createElement("svg", _extends$b({
    viewBox: "0 0 25 26",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$6 || (_g$6 = /*#__PURE__*/createElement("g", {
    clipPath: "url(#summary_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    d: "M20.916 25.157H3.985A3.983 3.983 0 0 1 0 21.174V4.243A3.983 3.983 0 0 1 3.985.259h16.93A3.983 3.983 0 0 1 24.9 4.243v16.93a3.983 3.983 0 0 1-3.984 3.985ZM4.483 18.186a.996.996 0 0 0 0 1.992h10.955a.996.996 0 0 0 0-1.992H4.483Zm0-3.486a.996.996 0 0 0 0 1.992h15.935a.996.996 0 0 0 0-1.992H4.483ZM4.98 4.243a.996.996 0 0 0-.996.996v5.975a.996.996 0 0 0 .996.996h5.975a.996.996 0 0 0 .996-.996V5.24a.996.996 0 0 0-.996-.996H4.981Z"
  }))), _defs$6 || (_defs$6 = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "summary_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    transform: "translate(0 .258)",
    d: "M0 0h24.9v24.9H0z"
  })))));
};
var _g$7, _defs$7;
function _extends$c() {
  _extends$c = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$c.apply(this, arguments);
}
var SvgTakeAction = function SvgTakeAction(props) {
  return /*#__PURE__*/createElement("svg", _extends$c({
    viewBox: "0 0 25 25",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$7 || (_g$7 = /*#__PURE__*/createElement("g", {
    clipPath: "url(#take-action_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    d: "M7.174 10.713a1.061 1.061 0 0 0 .837-1.716 4.255 4.255 0 1 1 7.229-.887 1.06 1.06 0 1 0 1.933.868 6.374 6.374 0 1 0-10.834 1.33 1.06 1.06 0 0 0 .835.405Z"
  }), /*#__PURE__*/createElement("path", {
    d: "M19.078 11.364a1.407 1.407 0 0 0-1.325.557 1.812 1.812 0 0 0-1.407-.57 1.53 1.53 0 0 0-1.052.4 1.536 1.536 0 0 0-1.302-1.115 1.508 1.508 0 0 0-1.056.299V7.19a1.561 1.561 0 0 0-1.367-1.562 1.511 1.511 0 0 0-1.53.9 1.53 1.53 0 0 0-.13.617v7.03l-.847-1.267a1.785 1.785 0 0 0-2.324-.614 1.74 1.74 0 0 0-.87 1.839c.043.238.133.465.267.667l3.203 4.786c.375.576.68 1.194.908 1.842a5.294 5.294 0 0 0 10.264-1.865v-6.549a1.638 1.638 0 0 0-1.432-1.651Z"
  }))), _defs$7 || (_defs$7 = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "take-action_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    d: "M0 0h24.899v24.899H0z"
  })))));
};
var _g$8, _defs$8;
function _extends$d() {
  _extends$d = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$d.apply(this, arguments);
}
var SvgTrends = function SvgTrends(props) {
  return /*#__PURE__*/createElement("svg", _extends$d({
    viewBox: "0 0 25 25",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$8 || (_g$8 = /*#__PURE__*/createElement("g", {
    clipPath: "url(#trends_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    d: "M9.968 11.747 7.67 14.6a6.772 6.772 0 1 0 1.266-6.19 2.101 2.101 0 0 1 1.032 3.337Zm3.363-3.73a.847.847 0 1 1 1.693 0v4.155l2.809 1.33a.848.848 0 0 1-.726 1.532l-3.293-1.56c-.018-.009-.033-.022-.05-.03a.851.851 0 0 1-.078-.047l-.064-.053a.748.748 0 0 1-.113-.12l-.047-.063c-.015-.022-.027-.048-.04-.073-.012-.025-.024-.048-.034-.073-.01-.025-.017-.054-.025-.082-.008-.027-.015-.05-.02-.075a.923.923 0 0 1-.01-.1c0-.018-.004-.035-.004-.053l.002-4.688Z"
  }), /*#__PURE__*/createElement("path", {
    d: "M14.18 1.992A10.736 10.736 0 0 0 3.7 10.458c-.1.466-.168.94-.204 1.416l-1.61-2.002a1.061 1.061 0 1 0-1.652 1.334l3.586 4.46a1.058 1.058 0 0 0 1.282.292 1.06 1.06 0 0 0 .367-.293l3.685-4.57a1.06 1.06 0 1 0-1.65-1.33L5.6 12.127a8.599 8.599 0 1 1 8.578 9.188 1.06 1.06 0 0 0 0 2.12 10.72 10.72 0 0 0 0-21.44v-.003Z"
  }))), _defs$8 || (_defs$8 = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "trends_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    d: "M0 0h24.899v24.899H0z"
  })))));
};
var _path$4, _path2;
function _extends$e() {
  _extends$e = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$e.apply(this, arguments);
}
var SvgAdwords = function SvgAdwords(props) {
  return /*#__PURE__*/createElement("svg", _extends$e({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$4 || (_path$4 = /*#__PURE__*/createElement("path", {
    d: "M10.28 2.032a2.667 2.667 0 1 0-4.619 2.667l5.334 9.237a2.667 2.667 0 0 0 4.618-2.666L10.28 2.032ZM1.698 9.022 4.546 4.09c.074.255.18.506.318.746L7.569 9.52l-1.385 2.4a3.556 3.556 0 0 0-4.486-2.898Z"
  })), _path2 || (_path2 = /*#__PURE__*/createElement("path", {
    d: "M5.333 12.622a2.667 2.667 0 1 1-5.333 0 2.667 2.667 0 0 1 5.333 0Z"
  })));
};
var _g$9, _defs$9;
function _extends$f() {
  _extends$f = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$f.apply(this, arguments);
}
var SvgAmazon = function SvgAmazon(props) {
  return /*#__PURE__*/createElement("svg", _extends$f({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$9 || (_g$9 = /*#__PURE__*/createElement("g", {
    clipPath: "url(#amazon_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M.091 12.386c-.233-.275.036-.4.194-.299l.693.432c2.566 1.576 6.403 3.524 12.277.652.271-.116.462.078.192.431l-.062.075C12.93 14.179 10.808 16 7.369 16 3.729 16 .942 13.446.09 12.386Zm13.367-.443c.552-.07 1.43-.027 1.602.204.13.176-.005.966-.227 1.533-.224.563-.557.961-.742 1.115-.186.154-.324.094-.224-.137l.057-.138c.174-.433.581-1.562.386-1.825-.175-.235-.87-.2-1.346-.155l-.698.072c-.071.004-.1-.007-.118-.046-.072-.209.758-.556 1.31-.623ZM7.814 0c1.003 0 2.313.274 3.104 1.054 1.002.962.907 2.245.907 3.64v3.3c0 .99.4 1.426.776 1.962.133.19.162.42-.006.562l-.366.319c-.407.358-.904.805-1.213 1.086l-.006-.006a.42.42 0 0 1-.486.051c-.684-.584-.806-.854-1.183-1.411-1.13 1.185-1.93 1.539-3.396 1.539-1.732 0-3.083-1.098-3.083-3.299 0-1.717.907-2.887 2.197-3.459 1.118-.506 2.68-.595 3.874-.735l.001-.62c0-.414-.027-.85-.25-1.187-.252-.39-.733-.551-1.157-.551-.739 0-1.403.366-1.621 1.124l-.036.147c-.035.19-.17.378-.356.387l-2-.22c-.168-.039-.353-.179-.307-.444C3.668.75 5.856 0 7.814 0Zm1.119 6.3c-1.49 0-3.063.327-3.063 2.131 0 .914.46 1.533 1.252 1.533.58 0 1.098-.366 1.425-.961.332-.6.38-1.169.385-1.808V6.3Z"
  }))), _defs$9 || (_defs$9 = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "amazon_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    d: "M0 0h16v16H0z"
  })))));
};
var _g$a, _defs$a;
function _extends$g() {
  _extends$g = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$g.apply(this, arguments);
}
var SvgBing = function SvgBing(props) {
  return /*#__PURE__*/createElement("svg", _extends$g({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$a || (_g$a = /*#__PURE__*/createElement("g", {
    clipPath: "url(#bing_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    d: "M12.993 10.275c0 1.092-.36 2.144-1.008 2.943a1.946 1.946 0 0 0 .292-.516.9.9 0 0 0 .03-.083.888.888 0 0 0 .026-.083 1.053 1.053 0 0 0 .041-.165l.003-.01c.006-.027.01-.055.015-.084a2.515 2.515 0 0 0 .022-.17 2.151 2.151 0 0 0-.348-1.388 1.803 1.803 0 0 0-.664-.592 1.443 1.443 0 0 0-.252-.1h-.004l-.03-.011-.439-.173L9.53 9.39H9.52l-.072-.03a1.128 1.128 0 0 1-.54-.549l-.418-1.229-.48-1.406-.092-.27-.024-.056a.703.703 0 0 1-.04-.237.64.64 0 0 1 0-.063.676.676 0 0 1 .082-.268.577.577 0 0 1 .178-.197.474.474 0 0 1 .48-.037l2.139 1.258.421.247c.223.153.432.332.623.534.71.747 1.143 1.78 1.208 2.883.005.102.009.203.009.305Z"
  }), /*#__PURE__*/createElement("path", {
    d: "M12.421 11.913c0 .123-.01.245-.029.365-.006.03-.011.063-.018.093a2.704 2.704 0 0 1-.067.247l-.03.083a1.968 1.968 0 0 1-.292.516c-.311.396-1.369 1.102-1.759 1.389l-.865.606c-.635.449-1.234.767-1.989.788h-.105c-.049 0-.097 0-.145-.003a3.414 3.414 0 0 1-1.736-.575 4.061 4.061 0 0 1-1.304-1.437 4.693 4.693 0 0 1-.555-1.802c.047.298.154.58.314.82.159.242.366.436.604.568.237.132.498.197.762.19.264-.007.522-.085.755-.23l.005-.003.085-.058.345-.233.438-.298v-.014l.057-.039 3.924-2.67.302-.205.03.012h.003c.087.024.171.058.253.1a1.807 1.807 0 0 1 .78.787c.157.303.239.65.237 1.003Z"
  }), /*#__PURE__*/createElement("path", {
    d: "M6.835 3.002v9.94l-.44.297-.345.233-.085.059a1.503 1.503 0 0 1-.76.232 1.49 1.49 0 0 1-.762-.19 1.741 1.741 0 0 1-.604-.567 2.069 2.069 0 0 1-.329-.947 4.506 4.506 0 0 1-.01-.233V.641c0-.116.028-.23.08-.33a.6.6 0 0 1 .216-.236.5.5 0 0 1 .575.034L6.078 1.39a1.856 1.856 0 0 1 .565.714c.127.277.193.585.192.898Z"
  }), /*#__PURE__*/createElement("path", {
    d: "M12.421 11.913c0 .123-.01.245-.029.365-.006.03-.011.063-.018.093a2.704 2.704 0 0 1-.067.247l-.03.083a1.948 1.948 0 0 1-.292.516c-.311.396-1.369 1.102-1.759 1.389l-.865.606c-.635.449-1.234.767-1.989.788h-.105c-.049 0-.097 0-.145-.003a3.414 3.414 0 0 1-1.736-.575 4.061 4.061 0 0 1-1.304-1.437 4.693 4.693 0 0 1-.555-1.802c.047.298.154.58.314.82.159.242.366.436.604.568.237.132.498.197.762.19.264-.007.522-.085.755-.23l.005-.003.085-.058.345-.233.438-.298v-.014l.057-.039 3.924-2.67.302-.205.03.012h.003c.087.024.171.058.253.1a1.807 1.807 0 0 1 .78.787c.157.303.239.65.237 1.003Z",
    opacity: 0.149
  }), /*#__PURE__*/createElement("path", {
    d: "M6.835 3.002v9.94l-.44.297-.345.233-.085.059a1.503 1.503 0 0 1-.76.232 1.49 1.49 0 0 1-.762-.19 1.741 1.741 0 0 1-.604-.567 2.069 2.069 0 0 1-.329-.947 4.506 4.506 0 0 1-.01-.233V.641c0-.116.028-.23.08-.33a.6.6 0 0 1 .216-.236.5.5 0 0 1 .575.034L6.078 1.39a1.856 1.856 0 0 1 .565.714c.127.277.193.585.192.898Z",
    opacity: 0.098
  }))), _defs$a || (_defs$a = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "bing_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    d: "M0 0h16v16H0z"
  })))));
};
var _g$b, _defs$b;
function _extends$h() {
  _extends$h = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$h.apply(this, arguments);
}
var SvgBingColor = function SvgBingColor(props) {
  return /*#__PURE__*/createElement("svg", _extends$h({
    width: 16,
    height: 16,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$b || (_g$b = /*#__PURE__*/createElement("g", {
    clipPath: "url(#bing_color_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    d: "M12.993 10.275c0 1.092-.36 2.144-1.008 2.943a1.946 1.946 0 0 0 .292-.516.9.9 0 0 0 .03-.083.888.888 0 0 0 .026-.083 1.053 1.053 0 0 0 .041-.165l.003-.01c.006-.027.01-.055.015-.084a2.515 2.515 0 0 0 .022-.17 2.151 2.151 0 0 0-.348-1.388 1.803 1.803 0 0 0-.664-.592 1.443 1.443 0 0 0-.252-.1h-.004l-.03-.011-.439-.173L9.53 9.39H9.52l-.072-.03a1.128 1.128 0 0 1-.54-.549l-.418-1.229-.48-1.406-.092-.27-.024-.056a.703.703 0 0 1-.04-.237.64.64 0 0 1 0-.063.676.676 0 0 1 .082-.268.577.577 0 0 1 .178-.197.474.474 0 0 1 .48-.037l2.139 1.258.421.247c.223.153.432.332.623.534.71.747 1.143 1.78 1.208 2.883.005.102.009.203.009.305Z",
    fill: "url(#bing_color_svg__b)"
  }), /*#__PURE__*/createElement("path", {
    d: "M12.421 11.913c0 .123-.01.245-.029.365-.006.03-.011.063-.018.093a2.704 2.704 0 0 1-.067.247l-.03.083a1.968 1.968 0 0 1-.292.516c-.311.396-1.369 1.102-1.759 1.389l-.865.606c-.635.449-1.234.767-1.989.788h-.105c-.049 0-.097 0-.145-.003a3.414 3.414 0 0 1-1.736-.575 4.061 4.061 0 0 1-1.304-1.437 4.693 4.693 0 0 1-.555-1.802c.047.298.154.58.314.82.159.242.366.436.604.568.237.132.498.197.762.19.264-.007.522-.085.755-.23l.005-.003.085-.058.345-.233.438-.298v-.014l.057-.039 3.924-2.67.302-.205.03.012h.003c.087.024.171.058.253.1a1.807 1.807 0 0 1 .78.787c.157.303.239.65.237 1.003Z",
    fill: "url(#bing_color_svg__c)"
  }), /*#__PURE__*/createElement("path", {
    d: "M6.835 3.002v9.94l-.44.297-.345.233-.085.059a1.503 1.503 0 0 1-.76.232 1.49 1.49 0 0 1-.762-.19 1.741 1.741 0 0 1-.604-.567 2.069 2.069 0 0 1-.329-.947 4.506 4.506 0 0 1-.01-.233V.641c0-.116.028-.23.08-.33a.6.6 0 0 1 .216-.236.5.5 0 0 1 .575.034L6.078 1.39a1.856 1.856 0 0 1 .565.714c.127.277.193.585.192.898Z",
    fill: "url(#bing_color_svg__d)"
  }), /*#__PURE__*/createElement("path", {
    d: "M12.421 11.913c0 .123-.01.245-.029.365-.006.03-.011.063-.018.093a2.704 2.704 0 0 1-.067.247l-.03.083a1.948 1.948 0 0 1-.292.516c-.311.396-1.369 1.102-1.759 1.389l-.865.606c-.635.449-1.234.767-1.989.788h-.105c-.049 0-.097 0-.145-.003a3.414 3.414 0 0 1-1.736-.575 4.061 4.061 0 0 1-1.304-1.437 4.693 4.693 0 0 1-.555-1.802c.047.298.154.58.314.82.159.242.366.436.604.568.237.132.498.197.762.19.264-.007.522-.085.755-.23l.005-.003.085-.058.345-.233.438-.298v-.014l.057-.039 3.924-2.67.302-.205.03.012h.003c.087.024.171.058.253.1a1.807 1.807 0 0 1 .78.787c.157.303.239.65.237 1.003Z",
    fill: "url(#bing_color_svg__e)",
    opacity: 0.149
  }), /*#__PURE__*/createElement("path", {
    d: "M6.835 3.002v9.94l-.44.297-.345.233-.085.059a1.503 1.503 0 0 1-.76.232 1.49 1.49 0 0 1-.762-.19 1.741 1.741 0 0 1-.604-.567 2.069 2.069 0 0 1-.329-.947 4.506 4.506 0 0 1-.01-.233V.641c0-.116.028-.23.08-.33a.6.6 0 0 1 .216-.236.5.5 0 0 1 .575.034L6.078 1.39a1.856 1.856 0 0 1 .565.714c.127.277.193.585.192.898Z",
    fill: "url(#bing_color_svg__f)",
    opacity: 0.098
  }))), _defs$b || (_defs$b = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("linearGradient", {
    id: "bing_color_svg__b",
    x1: 7.11,
    y1: 7.067,
    x2: 13.616,
    y2: 10.341,
    gradientUnits: "userSpaceOnUse"
  }, /*#__PURE__*/createElement("stop", {
    stopColor: "#37BDFF"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.18,
    stopColor: "#33BFFD"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.36,
    stopColor: "#28C5F5"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.53,
    stopColor: "#15D0E9"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.55,
    stopColor: "#12D1E7"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.59,
    stopColor: "#1CD2E5"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.77,
    stopColor: "#42D8DC"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.91,
    stopColor: "#59DBD6"
  }), /*#__PURE__*/createElement("stop", {
    offset: 1,
    stopColor: "#62DCD4"
  })), /*#__PURE__*/createElement("linearGradient", {
    id: "bing_color_svg__c",
    x1: 3.529,
    y1: 13.152,
    x2: 12.428,
    y2: 13.152,
    gradientUnits: "userSpaceOnUse"
  }, /*#__PURE__*/createElement("stop", {
    stopColor: "#39D2FF"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.15,
    stopColor: "#38CEFE"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.29,
    stopColor: "#35C3FA"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.43,
    stopColor: "#2FB0F3"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.55,
    stopColor: "#299AEB"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.58,
    stopColor: "#2692EC"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.76,
    stopColor: "#1A6CF1"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.91,
    stopColor: "#1355F4"
  }), /*#__PURE__*/createElement("stop", {
    offset: 1,
    stopColor: "#104CF5"
  })), /*#__PURE__*/createElement("linearGradient", {
    id: "bing_color_svg__d",
    x1: 5.176,
    y1: 14.041,
    x2: 5.176,
    y2: 0.379,
    gradientUnits: "userSpaceOnUse"
  }, /*#__PURE__*/createElement("stop", {
    stopColor: "#1B48EF"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.12,
    stopColor: "#1C51F0"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.32,
    stopColor: "#1E69F5"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.57,
    stopColor: "#2190FB"
  }), /*#__PURE__*/createElement("stop", {
    offset: 1,
    stopColor: "#26B8F4"
  })), /*#__PURE__*/createElement("linearGradient", {
    id: "bing_color_svg__e",
    x1: 5.295,
    y1: 15.67,
    x2: 11.546,
    y2: 10.238,
    gradientUnits: "userSpaceOnUse"
  }, /*#__PURE__*/createElement("stop", {
    stopColor: "#fff"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.37,
    stopColor: "#FDFDFD"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.51,
    stopColor: "#F6F6F6"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.6,
    stopColor: "#EBEBEB"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.68,
    stopColor: "#DADADA"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.75,
    stopColor: "#C4C4C4"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.81,
    stopColor: "#A8A8A8"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.86,
    stopColor: "#888"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.91,
    stopColor: "#626262"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.95,
    stopColor: "#373737"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.99,
    stopColor: "#090909"
  }), /*#__PURE__*/createElement("stop", {
    offset: 1
  })), /*#__PURE__*/createElement("linearGradient", {
    id: "bing_color_svg__f",
    x1: 5.176,
    y1: 0.139,
    x2: 5.176,
    y2: 13.904,
    gradientUnits: "userSpaceOnUse"
  }, /*#__PURE__*/createElement("stop", {
    stopColor: "#fff"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.37,
    stopColor: "#FDFDFD"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.51,
    stopColor: "#F6F6F6"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.6,
    stopColor: "#EBEBEB"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.68,
    stopColor: "#DADADA"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.75,
    stopColor: "#C4C4C4"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.81,
    stopColor: "#A8A8A8"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.86,
    stopColor: "#888"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.91,
    stopColor: "#626262"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.95,
    stopColor: "#373737"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.99,
    stopColor: "#090909"
  }), /*#__PURE__*/createElement("stop", {
    offset: 1
  })), /*#__PURE__*/createElement("clipPath", {
    id: "bing_color_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    d: "M0 0h16v16H0z"
  })))));
};
var _path$5;
function _extends$i() {
  _extends$i = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$i.apply(this, arguments);
}
var SvgFacebook = function SvgFacebook(props) {
  return /*#__PURE__*/createElement("svg", _extends$i({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$5 || (_path$5 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M12 0v3h-2c-.6 0-1 .4-1 1v2h3v3H9v7H6V9H4V6h2V3.5C6 1.6 7.6 0 9.5 0H12Z"
  })));
};
var _path$6;
function _extends$j() {
  _extends$j = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$j.apply(this, arguments);
}
var SvgFacebookColor = function SvgFacebookColor(props) {
  return /*#__PURE__*/createElement("svg", _extends$j({
    viewBox: "0 0 520 520",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$6 || (_path$6 = /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M240.329.305c-45.922 4.282-86.288 18.06-122.5 41.812C53.463 84.335 12.174 150.315 1.708 227.678c-2.277 16.832-2.277 48.168 0 65 6.584 48.671 25.614 93.25 55.922 131 10.493 13.069 32.128 34.135 45.027 43.842 33.548 25.246 72.032 41.902 114.422 49.524l3.25.584v-181.95h-67v-76h66.84l.419-39.75c.386-36.601.589-40.402 2.561-47.982 6.717-25.821 18.198-43.14 36.537-55.111 9.578-6.253 20.173-10.402 33.72-13.206 9.565-1.98 12.357-2.108 33.923-1.561 13.174.335 29.212 1.369 36.5 2.354l13 1.756.264 32.141.263 32.142-22.263.455c-19.243.394-23.158.746-28.849 2.598-11.186 3.639-18.533 10.547-23.389 21.988-1.8 4.241-2.016 7.444-2.322 34.426l-.338 29.75h73.172l-.502 2.25c-.276 1.238-2.764 17.325-5.528 35.75-2.765 18.425-5.256 34.512-5.537 35.75l-.509 2.25h-60.962v181.76l7.75-1.417c40.376-7.38 82.037-26.536 115.25-52.991 12.988-10.345 33.526-31.493 43.56-44.852 27.775-36.982 43.837-75.168 51.106-121.5 2.564-16.349 2.545-56.589-.035-73.5-10.145-66.479-43.78-125.099-95.631-166.665-35.233-28.243-78.918-47.077-126-54.32-10.169-1.564-46.378-2.785-56-1.888Z",
    fill: "#1877F2"
  })));
};
var _g$c, _defs$c;
function _extends$k() {
  _extends$k = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$k.apply(this, arguments);
}
var SvgGoogle = function SvgGoogle(props) {
  return /*#__PURE__*/createElement("svg", _extends$k({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$c || (_g$c = /*#__PURE__*/createElement("g", {
    clipPath: "url(#google_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    d: "M15.83 8.18c0-.527-.047-1.027-.127-1.513H8.17v3.006h4.313a3.714 3.714 0 0 1-1.6 2.387v2h2.574c1.506-1.393 2.373-3.447 2.373-5.88Z"
  }), /*#__PURE__*/createElement("path", {
    d: "M8.17 16c2.16 0 3.967-.72 5.287-1.94l-2.574-2c-.72.48-1.633.773-2.713.773-2.087 0-3.853-1.406-4.487-3.306H1.03v2.06A7.988 7.988 0 0 0 8.17 16Z"
  }), /*#__PURE__*/createElement("path", {
    d: "M3.683 9.527A4.642 4.642 0 0 1 3.43 8c0-.533.093-1.047.253-1.527v-2.06H1.03a7.907 7.907 0 0 0 0 7.174l2.653-2.06Z"
  }), /*#__PURE__*/createElement("path", {
    d: "M8.17 3.167c1.18 0 2.233.406 3.067 1.2l2.28-2.28C12.137.793 10.33 0 8.17 0a7.988 7.988 0 0 0-7.14 4.413l2.653 2.06c.634-1.9 2.4-3.306 4.487-3.306Z"
  }))), _defs$c || (_defs$c = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "google_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    d: "M0 0h16v16H0z"
  })))));
};
var _g$d, _defs$d;
function _extends$l() {
  _extends$l = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$l.apply(this, arguments);
}
var SvgGoogleColor = function SvgGoogleColor(props) {
  return /*#__PURE__*/createElement("svg", _extends$l({
    width: 16,
    height: 16,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$d || (_g$d = /*#__PURE__*/createElement("g", {
    clipPath: "url(#google_color_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    d: "M15.83 8.18c0-.527-.047-1.027-.127-1.513H8.17v3.006h4.313a3.714 3.714 0 0 1-1.6 2.387v2h2.574c1.506-1.393 2.373-3.447 2.373-5.88Z",
    fill: "#4285F4"
  }), /*#__PURE__*/createElement("path", {
    d: "M8.17 16c2.16 0 3.967-.72 5.287-1.94l-2.574-2c-.72.48-1.633.773-2.713.773-2.087 0-3.853-1.406-4.487-3.306H1.03v2.06A7.988 7.988 0 0 0 8.17 16Z",
    fill: "#34A853"
  }), /*#__PURE__*/createElement("path", {
    d: "M3.683 9.527A4.642 4.642 0 0 1 3.43 8c0-.533.093-1.047.253-1.527v-2.06H1.03a7.907 7.907 0 0 0 0 7.174l2.653-2.06Z",
    fill: "#FBBC05"
  }), /*#__PURE__*/createElement("path", {
    d: "M8.17 3.167c1.18 0 2.233.406 3.067 1.2l2.28-2.28C12.137.793 10.33 0 8.17 0a7.988 7.988 0 0 0-7.14 4.413l2.653 2.06c.634-1.9 2.4-3.306 4.487-3.306Z",
    fill: "#EA4335"
  }))), _defs$d || (_defs$d = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "google_color_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    d: "M0 0h16v16H0z"
  })))));
};
var _path$7;
function _extends$m() {
  _extends$m = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$m.apply(this, arguments);
}
var SvgInstacart = function SvgInstacart(props) {
  return /*#__PURE__*/createElement("svg", _extends$m({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$7 || (_path$7 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "m10.464 6.368.038.038c.951.96 1.704 2.517 1.268 3.436-1.175 2.477-9.944 6.819-10.683 6.073-.753-.746 3.565-9.602 6.021-10.787.9-.434 2.416.3 3.356 1.24Zm.936.012c-.119-.466 1.558-2.357 2.628-2.238.569.066.947.615 1.112 1.175l.01.037.01.039c.133.578.073 1.273-.38 1.586-.884.653-3.261-.133-3.38-.6Zm.45-6.286c.792.213 1.584.772 1.69 1.598.171 1.505-2.47 3.889-3.09 3.716-.621-.174-1.73-3.516-.846-4.781.463-.64 1.453-.733 2.245-.533Z"
  })));
};
var _path$8;
function _extends$n() {
  _extends$n = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$n.apply(this, arguments);
}
var SvgInstagram = function SvgInstagram(props) {
  return /*#__PURE__*/createElement("svg", _extends$n({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$8 || (_path$8 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M6.866 0h2.266l1.134.01c.378.008.646.02 1.032.037.851.039 1.433.174 1.942.372.526.204.972.478 1.417.922.444.445.718.891.922 1.417.176.453.303.963.356 1.668l.016.274c.033.712.045 1.02.048 2.36l-.003 2.57a34.186 34.186 0 0 1-.038 1.53l-.007.138c-.039.851-.174 1.433-.372 1.942a3.922 3.922 0 0 1-.922 1.417 3.922 3.922 0 0 1-1.417.922c-.453.176-.963.303-1.668.356l-.572.03c-.653.027-1.1.034-3 .034l-1.774-.005a34.152 34.152 0 0 1-1.526-.043c-.851-.039-1.433-.174-1.942-.372a3.922 3.922 0 0 1-1.417-.922A3.922 3.922 0 0 1 .42 13.24c-.176-.453-.303-.963-.356-1.668l-.016-.274C.016 10.622.004 10.31 0 9.132L.002 6.41C.008 5.591.02 5.28.047 4.7.086 3.85.22 3.267.419 2.758c.204-.526.478-.972.922-1.417A3.921 3.921 0 0 1 2.758.42c.453-.176.963-.303 1.668-.356L4.7.047C5.376.016 5.688.004 6.866 0ZM9.57 1.443h-3.14a34.29 34.29 0 0 0-1.663.044c-.78.035-1.203.166-1.485.275-.374.145-.64.319-.92.599-.28.28-.454.546-.599.92l-.06.169c-.083.247-.166.59-.203 1.112l-.039.848c-.014.445-.019.94-.02 2.093l.003 2.066c.006.793.018 1.093.044 1.663.035.78.166 1.204.275 1.486.145.373.319.64.599.92.28.28.546.453.92.598l.169.061c.247.082.59.165 1.112.202l.89.04c.404.012.865.017 1.83.018l2.12-.001a35.248 35.248 0 0 0 1.704-.039l.126-.006c.78-.035 1.204-.166 1.486-.275a2.48 2.48 0 0 0 .92-.599c.28-.28.453-.546.598-.92l.061-.169c.082-.247.165-.59.202-1.112l.04-.89c.012-.404.017-.865.018-1.83l-.001-2.12a35.258 35.258 0 0 0-.039-1.704l-.006-.126c-.035-.78-.166-1.203-.275-1.485a2.479 2.479 0 0 0-.599-.92 2.48 2.48 0 0 0-.92-.599l-.169-.06a4.465 4.465 0 0 0-1.112-.203l-.848-.039a38.555 38.555 0 0 0-1.02-.017Zm-1.57 2.448a4.108 4.108 0 1 1 0 8.216 4.108 4.108 0 0 1 0-8.216Zm0 1.441a2.667 2.667 0 1 0 0 5.334 2.667 2.667 0 0 0 0-5.334Zm4.27-2.564a.96.96 0 1 1 0 1.92.96.96 0 0 1 0-1.92Z"
  })));
};
var _g$e, _defs$e;
function _extends$o() {
  _extends$o = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$o.apply(this, arguments);
}
var SvgInstagramColor = function SvgInstagramColor(props) {
  return /*#__PURE__*/createElement("svg", _extends$o({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$e || (_g$e = /*#__PURE__*/createElement("g", {
    clipPath: "url(#instagram_color_svg__a)",
    fillRule: "evenodd",
    clipRule: "evenodd"
  }, /*#__PURE__*/createElement("path", {
    d: "M6.866 0h2.266l1.134.01c.378.008.646.02 1.032.037.851.039 1.433.174 1.942.372.526.204.972.478 1.417.922.444.445.718.891.922 1.417.176.453.303.963.356 1.668l.016.274c.033.712.045 1.02.048 2.36l-.003 2.57a34.186 34.186 0 0 1-.038 1.53l-.007.138c-.039.851-.174 1.433-.372 1.942a3.922 3.922 0 0 1-.922 1.417 3.922 3.922 0 0 1-1.417.922c-.453.176-.963.303-1.668.356l-.572.03c-.653.027-1.1.034-3 .034l-1.774-.005a34.152 34.152 0 0 1-1.526-.043c-.851-.039-1.433-.174-1.942-.372a3.922 3.922 0 0 1-1.417-.922A3.922 3.922 0 0 1 .42 13.24c-.176-.453-.303-.963-.356-1.668l-.016-.274C.016 10.622.004 10.31 0 9.132L.002 6.41C.008 5.591.02 5.28.047 4.7.086 3.85.22 3.267.419 2.758c.204-.526.478-.972.922-1.417A3.921 3.921 0 0 1 2.758.42c.453-.176.963-.303 1.668-.356L4.7.047C5.376.016 5.688.004 6.866 0ZM9.57 1.443h-3.14a34.29 34.29 0 0 0-1.663.044c-.78.035-1.203.166-1.485.275-.374.145-.64.319-.92.599-.28.28-.454.546-.599.92l-.06.169c-.083.247-.166.59-.203 1.112l-.039.848c-.014.445-.019.94-.02 2.093l.003 2.066c.006.793.018 1.093.044 1.663.035.78.166 1.204.275 1.486.145.373.319.64.599.92.28.28.546.453.92.598l.169.061c.247.082.59.165 1.112.202l.89.04c.404.012.865.017 1.83.018l2.12-.001a35.248 35.248 0 0 0 1.704-.039l.126-.006c.78-.035 1.204-.166 1.486-.275a2.48 2.48 0 0 0 .92-.599c.28-.28.453-.546.598-.92l.061-.169c.082-.247.165-.59.202-1.112l.04-.89c.012-.404.017-.865.018-1.83l-.001-2.12a35.258 35.258 0 0 0-.039-1.704l-.006-.126c-.035-.78-.166-1.203-.275-1.485a2.479 2.479 0 0 0-.599-.92 2.48 2.48 0 0 0-.92-.599l-.169-.06a4.465 4.465 0 0 0-1.112-.203l-.848-.039a38.555 38.555 0 0 0-1.02-.017Zm-1.57 2.448a4.108 4.108 0 1 1 0 8.216 4.108 4.108 0 0 1 0-8.216Zm0 1.441a2.667 2.667 0 1 0 0 5.334 2.667 2.667 0 0 0 0-5.334Zm4.27-2.564a.96.96 0 1 1 0 1.92.96.96 0 0 1 0-1.92Z",
    fill: "url(#instagram_color_svg__b)"
  }), /*#__PURE__*/createElement("path", {
    d: "M6.866 0h2.266l1.134.01c.378.008.646.02 1.032.037.851.039 1.433.174 1.942.372.526.204.972.478 1.417.922.444.445.718.891.922 1.417.176.453.303.963.356 1.668l.016.274c.033.712.045 1.02.048 2.36l-.003 2.57a34.186 34.186 0 0 1-.038 1.53l-.007.138c-.039.851-.174 1.433-.372 1.942a3.922 3.922 0 0 1-.922 1.417 3.922 3.922 0 0 1-1.417.922c-.453.176-.963.303-1.668.356l-.572.03c-.653.027-1.1.034-3 .034l-1.774-.005a34.152 34.152 0 0 1-1.526-.043c-.851-.039-1.433-.174-1.942-.372a3.922 3.922 0 0 1-1.417-.922A3.922 3.922 0 0 1 .42 13.24c-.176-.453-.303-.963-.356-1.668l-.016-.274C.016 10.622.004 10.31 0 9.132L.002 6.41C.008 5.591.02 5.28.047 4.7.086 3.85.22 3.267.419 2.758c.204-.526.478-.972.922-1.417A3.921 3.921 0 0 1 2.758.42c.453-.176.963-.303 1.668-.356L4.7.047C5.376.016 5.688.004 6.866 0ZM9.57 1.443h-3.14a34.29 34.29 0 0 0-1.663.044c-.78.035-1.203.166-1.485.275-.374.145-.64.319-.92.599-.28.28-.454.546-.599.92l-.06.169c-.083.247-.166.59-.203 1.112l-.039.848c-.014.445-.019.94-.02 2.093l.003 2.066c.006.793.018 1.093.044 1.663.035.78.166 1.204.275 1.486.145.373.319.64.599.92.28.28.546.453.92.598l.169.061c.247.082.59.165 1.112.202l.89.04c.404.012.865.017 1.83.018l2.12-.001a35.248 35.248 0 0 0 1.704-.039l.126-.006c.78-.035 1.204-.166 1.486-.275a2.48 2.48 0 0 0 .92-.599c.28-.28.453-.546.598-.92l.061-.169c.082-.247.165-.59.202-1.112l.04-.89c.012-.404.017-.865.018-1.83l-.001-2.12a35.258 35.258 0 0 0-.039-1.704l-.006-.126c-.035-.78-.166-1.203-.275-1.485a2.479 2.479 0 0 0-.599-.92 2.48 2.48 0 0 0-.92-.599l-.169-.06a4.465 4.465 0 0 0-1.112-.203l-.848-.039a38.555 38.555 0 0 0-1.02-.017Zm-1.57 2.448a4.108 4.108 0 1 1 0 8.216 4.108 4.108 0 0 1 0-8.216Zm0 1.441a2.667 2.667 0 1 0 0 5.334 2.667 2.667 0 0 0 0-5.334Zm4.27-2.564a.96.96 0 1 1 0 1.92.96.96 0 0 1 0-1.92Z",
    fill: "url(#instagram_color_svg__c)"
  }))), _defs$e || (_defs$e = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("radialGradient", {
    id: "instagram_color_svg__b",
    cx: 0,
    cy: 0,
    r: 1,
    gradientUnits: "userSpaceOnUse",
    gradientTransform: "matrix(-5.75964 -20.15925 29.98326 -8.56642 13.519 18.16)"
  }, /*#__PURE__*/createElement("stop", {
    offset: 0.198,
    stopColor: "#F50B5E"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.52,
    stopColor: "#DF0897"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.769,
    stopColor: "#962FBF"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.933,
    stopColor: "#4F5BD5"
  })), /*#__PURE__*/createElement("radialGradient", {
    id: "instagram_color_svg__c",
    cx: 0,
    cy: 0,
    r: 1,
    gradientUnits: "userSpaceOnUse",
    gradientTransform: "matrix(5.9995 -8.55974 9.3625 6.56215 5.36 16)"
  }, /*#__PURE__*/createElement("stop", {
    offset: 0.094,
    stopColor: "#FEDA75"
  }), /*#__PURE__*/createElement("stop", {
    offset: 0.475,
    stopColor: "#FA7E1E"
  }), /*#__PURE__*/createElement("stop", {
    offset: 1,
    stopColor: "#FA7E1E",
    stopOpacity: 0
  })), /*#__PURE__*/createElement("clipPath", {
    id: "instagram_color_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    d: "M0 0h16v16H0z"
  })))));
};
var _path$9;
function _extends$p() {
  _extends$p = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$p.apply(this, arguments);
}
var SvgLinkedin = function SvgLinkedin(props) {
  return /*#__PURE__*/createElement("svg", _extends$p({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$9 || (_path$9 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M3.692 16H.336V5.93h3.356V16ZM2.014 4.028A2.006 2.006 0 0 1 0 2.014C0 .895.895 0 2.014 0c1.119 0 2.014.895 2.014 2.014a2.006 2.006 0 0 1-2.014 2.014ZM16 16h-3.357v-5.93c0-.895-.783-1.678-1.678-1.678-.895 0-1.678.783-1.678 1.678V16H5.93V5.93h3.357v1.343c.56-.895 1.79-1.567 2.797-1.567C14.21 5.706 16 7.496 16 9.622V16Z"
  })));
};
var _path$a;
function _extends$q() {
  _extends$q = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$q.apply(this, arguments);
}
var SvgPerpetua = function SvgPerpetua(props) {
  return /*#__PURE__*/createElement("svg", _extends$q({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$a || (_path$a = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M5.527 5.237a5.237 5.237 0 1 1 5.236 5.236h-1.29a.5.5 0 0 1-.5-.5v-1.62a.5.5 0 0 1 .5-.5h1.29a2.616 2.616 0 1 0-2.616-2.616l-.002 5.3.002 1.39a4.074 4.074 0 1 1-4.074-4.074h.124a.5.5 0 0 1 .5.5v1.62a.5.5 0 0 1-.5.5h-.124a1.453 1.453 0 1 0 1.454 1.454l-.003-4.074.003-2.616Z"
  })));
};
var _path$b;
function _extends$r() {
  _extends$r = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$r.apply(this, arguments);
}
var SvgSnap = function SvgSnap(props) {
  return /*#__PURE__*/createElement("svg", _extends$r({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 500 500"
  }, props), /*#__PURE__*/createElement("g", {
    "data-name": "PRIMARY - GHOST"
  }, /*#__PURE__*/createElement("path", {
    d: "M417.93 340.71c-60.61-29.34-70.27-74.64-70.7-78-.52-4.07-1.11-7.27 3.38-11.41 4.33-4 23.54-15.89 28.87-19.61 8.81-6.16 12.69-12.31 9.83-19.87-2-5.23-6.87-7.2-12-7.2a22.3 22.3 0 0 0-4.81.54c-9.68 2.1-19.08 6.95-24.52 8.26a8.56 8.56 0 0 1-2 .27c-2.9 0-4-1.29-3.72-4.78.68-10.58 2.12-31.23.45-50.52-2.29-26.54-10.85-39.69-21-51.32C316.8 101.43 294 77.2 250 77.2s-66.77 24.23-71.65 29.8c-10.18 11.63-18.73 24.78-21 51.32-1.67 19.29-.17 39.93.45 50.52.2 3.32-.82 4.78-3.72 4.78a8.64 8.64 0 0 1-2-.27c-5.43-1.31-14.83-6.16-24.51-8.26a22.3 22.3 0 0 0-4.81-.54c-5.15 0-10 2-12 7.2-2.86 7.56 1 13.71 9.84 19.87 5.33 3.72 24.54 15.6 28.87 19.61 4.48 4.14 3.9 7.34 3.38 11.41-.43 3.41-10.1 48.71-70.7 78-3.55 1.72-9.59 5.36 1.06 11.24 16.72 9.24 27.85 8.25 36.5 13.82 7.34 4.73 3 14.93 8.34 18.61 6.56 4.53 25.95-.32 51 7.95 21 6.92 33.76 26.47 71 26.47s50.37-19.64 71-26.47c25-8.27 44.43-3.42 51-7.95 5.33-3.68 1-13.88 8.34-18.61 8.65-5.57 19.77-4.58 36.5-13.82 10.63-5.81 4.59-9.45 1.04-11.17Z",
    style: {
      fill: "#fff"
    }
  }), _path$b || (_path$b = /*#__PURE__*/createElement("path", {
    d: "M444.3 337.26c-2.72-7.4-7.9-11.36-13.8-14.64a34.51 34.51 0 0 0-3-1.57c-1.76-.91-3.56-1.79-5.35-2.72-18.39-9.75-32.75-22.05-42.71-36.63a83.06 83.06 0 0 1-7.33-13c-.85-2.43-.81-3.81-.2-5.07a8.25 8.25 0 0 1 2.35-2.45c3.16-2.09 6.42-4.21 8.63-5.64 3.94-2.55 7.06-4.57 9.07-6 7.55-5.28 12.83-10.89 16.13-17.16a34.17 34.17 0 0 0 1.69-28.38c-5-13.16-17.43-21.33-32.49-21.33a44.75 44.75 0 0 0-9.45 1c-.83.18-1.66.37-2.47.58.14-9-.06-18.5-.86-27.85-2.84-32.87-14.35-50.1-26.35-63.84A105 105 0 0 0 311.37 71C293.16 60.6 272.51 55.32 250 55.32S206.94 60.6 188.71 71a104.62 104.62 0 0 0-26.84 21.6c-12 13.74-23.51 31-26.35 63.84-.8 9.35-1 18.9-.87 27.85-.81-.21-1.63-.4-2.46-.58a44.75 44.75 0 0 0-9.45-1c-15.07 0-27.52 8.17-32.5 21.33a34.21 34.21 0 0 0 1.65 28.41c3.31 6.27 8.59 11.88 16.14 17.16 2 1.4 5.13 3.42 9.07 6 2.13 1.38 5.24 3.4 8.29 5.42a8.9 8.9 0 0 1 2.66 2.67c.64 1.31.66 2.72-.29 5.32a82.29 82.29 0 0 1-7.21 12.73c-9.74 14.25-23.68 26.33-41.48 36-9.43 5-19.23 8.34-23.37 19.59-3.12 8.49-1.08 18.15 6.85 26.29a39.63 39.63 0 0 0 10 7.57 108.35 108.35 0 0 0 24.47 9.79 16.16 16.16 0 0 1 4.94 2.21c2.89 2.53 2.48 6.34 6.33 11.92a27.8 27.8 0 0 0 7.24 7.36c8.08 5.58 17.16 5.93 26.78 6.3 8.69.33 18.54.71 29.79 4.42 4.66 1.54 9.5 4.52 15.11 8 13.47 8.28 31.91 19.61 62.77 19.61s49.43-11.39 63-19.7c5.57-3.42 10.38-6.37 14.91-7.87 11.25-3.72 21.1-4.09 29.79-4.42 9.62-.37 18.7-.72 26.78-6.3a27.89 27.89 0 0 0 8.24-9c2.77-4.71 2.7-8 5.3-10.3a15.32 15.32 0 0 1 4.64-2.12 108.76 108.76 0 0 0 24.8-9.88 39 39 0 0 0 10.5-8.22l.1-.12c7.44-7.96 9.31-17.34 6.26-25.62ZM416.87 352c-16.73 9.24-27.85 8.25-36.5 13.82-7.35 4.73-3 14.93-8.34 18.61-6.56 4.53-25.95-.32-51 7.95-20.66 6.83-33.84 26.47-71 26.47S200 399.25 179 392.33c-25-8.27-44.43-3.42-51-7.95-5.33-3.68-1-13.88-8.34-18.61-8.66-5.57-19.78-4.58-36.5-13.77-10.65-5.88-4.61-9.52-1.06-11.24 60.6-29.34 70.27-74.64 70.7-78 .52-4.07 1.1-7.27-3.38-11.41-4.33-4-23.54-15.89-28.87-19.61-8.82-6.16-12.7-12.31-9.84-19.87 2-5.23 6.88-7.2 12-7.2a22.3 22.3 0 0 1 4.81.54c9.68 2.1 19.08 6.95 24.51 8.26a8.64 8.64 0 0 0 2 .27c2.9 0 3.92-1.46 3.72-4.78-.62-10.59-2.12-31.23-.45-50.52 2.29-26.54 10.84-39.69 21-51.32 4.88-5.59 27.81-29.82 71.66-29.82s66.84 24.13 71.72 29.7c10.17 11.63 18.73 24.78 21 51.32 1.67 19.29.23 39.94-.45 50.52-.23 3.49.82 4.78 3.72 4.78a8.56 8.56 0 0 0 2-.27c5.44-1.31 14.84-6.16 24.52-8.26a22.3 22.3 0 0 1 4.81-.54c5.15 0 10 2 12 7.2 2.86 7.56-1 13.71-9.83 19.87-5.33 3.72-24.54 15.6-28.87 19.61-4.49 4.14-3.9 7.34-3.38 11.41.43 3.41 10.09 48.71 70.7 78 3.58 1.79 9.62 5.43-1.03 11.36Z"
  }))));
};
var _path$c;
function _extends$s() {
  _extends$s = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$s.apply(this, arguments);
}
var SvgTarget = function SvgTarget(props) {
  return /*#__PURE__*/createElement("svg", _extends$s({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$c || (_path$c = /*#__PURE__*/createElement("path", {
    d: "M8 10.721a2.77 2.77 0 0 0 2.754-2.754A2.77 2.77 0 0 0 8 5.213a2.77 2.77 0 0 0-2.754 2.754A2.77 2.77 0 0 0 8 10.721Zm0 2.59A5.31 5.31 0 0 0 13.312 8 5.31 5.31 0 0 0 8 2.689 5.31 5.31 0 0 0 2.689 8 5.31 5.31 0 0 0 8 13.312ZM0 8c0-4.426 3.574-8 8-8s8 3.574 8 8-3.574 8-8 8a7.99 7.99 0 0 1-8-8Z"
  })));
};
var _path$d, _path2$1, _path3;
function _extends$t() {
  _extends$t = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$t.apply(this, arguments);
}
var SvgTiktokLight = function SvgTiktokLight(props) {
  return /*#__PURE__*/createElement("svg", _extends$t({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$d || (_path$d = /*#__PURE__*/createElement("path", {
    d: "M11.313 5.865a5.992 5.992 0 0 0 3.505 1.125V4.466c-.248 0-.494-.026-.736-.077v1.987a5.993 5.993 0 0 1-3.505-1.126v5.15c0 2.577-2.081 4.666-4.648 4.666-.958 0-1.849-.291-2.588-.79a4.626 4.626 0 0 0 3.323 1.404c2.568 0 4.65-2.088 4.65-4.665v-5.15h-.001Zm.908-2.547a3.523 3.523 0 0 1-.908-2.059V.934h-.697a3.534 3.534 0 0 0 1.605 2.384ZM4.965 12.3a2.13 2.13 0 0 1-.434-1.291 2.13 2.13 0 0 1 2.771-2.034v-2.58a4.672 4.672 0 0 0-.735-.043v2.008a2.13 2.13 0 0 0-2.772 2.034c0 .833.476 1.555 1.17 1.906Z",
    fill: "#FF004F"
  })), _path2$1 || (_path2$1 = /*#__PURE__*/createElement("path", {
    d: "M10.577 5.25a5.993 5.993 0 0 0 3.505 1.126V4.389a3.515 3.515 0 0 1-1.861-1.07A3.534 3.534 0 0 1 10.615.933H8.783v10.08a2.13 2.13 0 0 1-2.126 2.128 2.12 2.12 0 0 1-1.693-.843 2.134 2.134 0 0 1-1.17-1.906A2.13 2.13 0 0 1 6.567 8.36V6.351a4.657 4.657 0 0 0-4.55 4.664c0 1.27.505 2.42 1.325 3.262.74.498 1.63.789 2.588.789 2.567 0 4.648-2.089 4.648-4.665V5.25Z",
    fill: "#fff"
  })), _path3 || (_path3 = /*#__PURE__*/createElement("path", {
    d: "M14.082 4.389v-.537a3.495 3.495 0 0 1-1.861-.534 3.512 3.512 0 0 0 1.86 1.071ZM10.616.934a3.623 3.623 0 0 1-.039-.29V.32h-2.53V10.4a2.13 2.13 0 0 1-3.083 1.9 2.12 2.12 0 0 0 1.693.841 2.13 2.13 0 0 0 2.126-2.126V.935h1.833Zm-4.05 5.417v-.572a4.676 4.676 0 0 0-.637-.043C3.36 5.736 1.28 7.824 1.28 10.4c0 1.616.818 3.04 2.06 3.877a4.658 4.658 0 0 1-1.324-3.262 4.657 4.657 0 0 1 4.55-4.664Z",
    fill: "#00F2EA"
  })));
};
var _path$e, _path2$2, _path3$1;
function _extends$u() {
  _extends$u = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$u.apply(this, arguments);
}
var SvgTiktok = function SvgTiktok(props) {
  return /*#__PURE__*/createElement("svg", _extends$u({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$e || (_path$e = /*#__PURE__*/createElement("path", {
    d: "M11.313 5.865a5.992 5.992 0 0 0 3.505 1.125V4.466c-.248 0-.494-.026-.736-.077v1.987a5.993 5.993 0 0 1-3.505-1.126v5.15c0 2.577-2.081 4.666-4.648 4.666-.958 0-1.849-.291-2.588-.79a4.626 4.626 0 0 0 3.323 1.404c2.568 0 4.65-2.088 4.65-4.665v-5.15h-.001Zm.908-2.547a3.523 3.523 0 0 1-.908-2.059V.934h-.697a3.534 3.534 0 0 0 1.605 2.384ZM4.965 12.3a2.13 2.13 0 0 1-.434-1.291 2.13 2.13 0 0 1 2.771-2.034v-2.58a4.672 4.672 0 0 0-.735-.043v2.008a2.13 2.13 0 0 0-2.772 2.034c0 .833.476 1.555 1.17 1.906Z",
    fill: "#FF004F"
  })), _path2$2 || (_path2$2 = /*#__PURE__*/createElement("path", {
    d: "M10.577 5.25a5.993 5.993 0 0 0 3.505 1.126V4.389a3.515 3.515 0 0 1-1.861-1.07A3.534 3.534 0 0 1 10.615.933H8.783v10.08a2.13 2.13 0 0 1-2.126 2.128 2.12 2.12 0 0 1-1.693-.843 2.134 2.134 0 0 1-1.17-1.906A2.13 2.13 0 0 1 6.567 8.36V6.351a4.657 4.657 0 0 0-4.55 4.664c0 1.27.505 2.42 1.325 3.262.74.498 1.63.789 2.588.789 2.567 0 4.648-2.089 4.648-4.665V5.25Z",
    fill: "#000"
  })), _path3$1 || (_path3$1 = /*#__PURE__*/createElement("path", {
    d: "M14.082 4.389v-.537a3.495 3.495 0 0 1-1.861-.534 3.512 3.512 0 0 0 1.86 1.071ZM10.616.934a3.623 3.623 0 0 1-.039-.29V.32h-2.53V10.4a2.13 2.13 0 0 1-3.083 1.9 2.12 2.12 0 0 0 1.693.841 2.13 2.13 0 0 0 2.126-2.126V.935h1.833Zm-4.05 5.417v-.572a4.676 4.676 0 0 0-.637-.043C3.36 5.736 1.28 7.824 1.28 10.4c0 1.616.818 3.04 2.06 3.877a4.658 4.658 0 0 1-1.324-3.262 4.657 4.657 0 0 1 4.55-4.664Z",
    fill: "#00F2EA"
  })));
};
var _path$f;
function _extends$v() {
  _extends$v = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$v.apply(this, arguments);
}
var SvgTwitter = function SvgTwitter(props) {
  return /*#__PURE__*/createElement("svg", _extends$v({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$f || (_path$f = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M14.625 4.73c-.125 5.61-3.75 9.513-9.25 9.757-2.25.122-3.875-.61-5.375-1.464 1.625.244 3.75-.366 4.875-1.341-1.625-.122-2.625-.976-3.125-2.317.5.122 1 0 1.375 0C1.625 8.877.625 8.023.5 6.072c.375.244.875.366 1.375.366C.75 5.828 0 3.511.875 2.048 2.5 3.755 4.5 5.218 7.75 5.463 6.875 2.048 11.625.219 13.5 2.536c.875-.122 1.5-.488 2.125-.732-.25.854-.75 1.342-1.375 1.83.625-.123 1.25-.245 1.75-.488-.125.61-.75 1.097-1.375 1.585Z"
  })));
};
var _path$g;
function _extends$w() {
  _extends$w = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$w.apply(this, arguments);
}
var SvgWalmart = function SvgWalmart(props) {
  return /*#__PURE__*/createElement("svg", _extends$w({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$g || (_path$g = /*#__PURE__*/createElement("path", {
    d: "M8.135 5.387c.379 0 .688-.195.722-.447L9.224.756C9.224.344 8.743 0 8.135 0c-.607 0-1.088.344-1.088.756l.366 4.184c.035.252.344.447.722.447ZM5.866 6.693c.183-.32.172-.687-.023-.848L2.405 3.427C2.049 3.22 1.51 3.473 1.2 4c-.31.527-.252 1.112.103 1.318L5.11 7.095c.252.091.573-.08.756-.402ZM10.393 6.693c.184.321.516.493.745.402l3.805-1.777c.356-.206.413-.79.104-1.318-.31-.527-.849-.78-1.204-.573l-3.427 2.418c-.195.16-.206.527-.023.848ZM8.135 10.613c.379 0 .688.195.722.447l.367 4.184c0 .412-.481.756-1.089.756-.607 0-1.088-.344-1.088-.756l.366-4.184c.035-.252.344-.447.722-.447ZM10.393 9.307c.184-.321.516-.493.745-.402l3.805 1.777c.356.206.413.802.104 1.33-.31.527-.849.779-1.204.572l-3.438-2.406c-.184-.184-.195-.55-.012-.871ZM5.866 9.307c.183.32.172.687-.023.848l-3.438 2.407c-.356.206-.894-.046-1.204-.573-.31-.528-.252-1.112.103-1.33L5.11 8.883c.252-.07.573.103.756.424Z"
  })));
};
var _path$h, _path2$3;
function _extends$x() {
  _extends$x = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$x.apply(this, arguments);
}
var SvgWechat = function SvgWechat(props) {
  return /*#__PURE__*/createElement("svg", _extends$x({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$h || (_path$h = /*#__PURE__*/createElement("path", {
    d: "M10.805 5.809c-1.337.072-2.5.49-3.443 1.434-.954.953-1.39 2.122-1.27 3.57-.523-.067-.999-.14-1.477-.182-.166-.014-.362.006-.502.088-.464.27-.91.576-1.438.916.097-.453.16-.848.271-1.228.082-.28.044-.435-.206-.618C1.135 8.621.459 6.874.965 5.074c.468-1.664 1.618-2.674 3.18-3.2 2.134-.719 4.531.014 5.828 1.76.468.63.756 1.338.832 2.175Zm-6.153-.56a.627.627 0 0 0-.593-.637.606.606 0 0 0-.623.597.602.602 0 0 0 .586.633.617.617 0 0 0 .63-.594Zm3.21-.637a.627.627 0 0 0-.603.627.61.61 0 0 0 .619.606.601.601 0 0 0 .595-.625.604.604 0 0 0-.611-.608Z"
  })), _path2$3 || (_path2$3 = /*#__PURE__*/createElement("path", {
    d: "M13.808 14.4c-.423-.194-.812-.486-1.225-.53-.412-.044-.845.2-1.275.246-1.313.139-2.489-.239-3.458-1.163-1.844-1.758-1.58-4.454.553-5.895 1.896-1.28 4.676-.854 6.013.924 1.166 1.55 1.03 3.608-.395 4.91-.412.377-.56.687-.296 1.184.05.092.055.209.083.324ZM8.989 9.591c.27 0 .492-.217.502-.491a.51.51 0 0 0-.498-.536.515.515 0 0 0-.004 1.027Zm3.106-1.026a.505.505 0 0 0-.494.491.5.5 0 0 0 .488.532c.27 0 .484-.21.493-.487a.503.503 0 0 0-.487-.536Z"
  })));
};
var _path$i;
function _extends$y() {
  _extends$y = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$y.apply(this, arguments);
}
var SvgWhytespyder = function SvgWhytespyder(props) {
  return /*#__PURE__*/createElement("svg", _extends$y({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$i || (_path$i = /*#__PURE__*/createElement("path", {
    d: "m15.343 8.865-4.03 1.597s2.083 1.287 3.555.555c1.449-.732.475-2.152.475-2.152Zm-4.347 1.575-5.48-2.396s-1.426 1.908.567 2.973c1.992 1.087 4.913-.577 4.913-.577ZM16 5.825s-.272-.865-.34-.976v1.02s.136-.11.34-.044Zm-1.63.91s.362-.2.453-.288L14.8 3.34s-.34-.466-.476-.554l.046 3.949Zm-.181 6.656v-.067s-.453.111-.997.111v.866s.951-.777.997-.91Zm-1.042-6.168s.815-.288.974-.377l-.046-4.37s-.77-.777-.996-.866l.068 5.613Zm-2.015 8.231s.95-.377 1.064-.51v-1.73s-.09 0-.475-.267c0 0-.295-.2-.612-.177v2.684h.023Zm1.019-8.01L12.083.99S11.403.567 11.04.5l.068 7.01c-.022.023.476.067 1.042-.066ZM9.5 15.899s.635-.11.77-.155l-.022-2.752s-.634.311-.747.311v2.596Zm-.067-8.742.747.2-.068-7.144S9.66.102 9.388.08l.046 7.077Zm-.25 8.786-.022-2.551s-.589.177-1.11.222l.023 2.374c0 .022.68.022 1.11-.045ZM8.008 6.646s.906.377 1.087.4L9.026.056s-.702-.088-1.11-.044l.091 6.634Zm-.566 9.319v-2.33s-.498-.044-.747-.111l.023 2.374c-.023-.022.61.089.724.066Zm-.883-9.94.793.355V.057s-.385.022-.793.11v5.858Zm-.838 9.607s.634.177.77.2l-.023-2.375s-.633-.221-.747-.31v2.485ZM5.607 5.47l.793.466L6.332.234s-.634.156-.725.2V5.47Zm-2.151 9.097s1.087.665 1.313.71l-.022-2.951s-.951-.91-1.291-3.594v5.835Zm1.223-9.651V.789s-.793.355-1.29.732l.044 4.216c0-.023.114-1.265 1.246-.821Zm-2.582 8.453.43.443-.09-11.492s-.407.4-.43.444c-.023.022.09 10.605.09 10.605ZM1.554 3.296S.852 4.339.784 4.605c-.068.266 0 6.922 0 6.922s.77 1.198.86 1.287c.091.067-.09-9.518-.09-9.518ZM.31 10.174.286 5.936C-.37 8 .31 10.174.31 10.174Z"
  })));
};
var _path$j;
function _extends$z() {
  _extends$z = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$z.apply(this, arguments);
}
var SvgYahoo = function SvgYahoo(props) {
  return /*#__PURE__*/createElement("svg", _extends$z({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$j || (_path$j = /*#__PURE__*/createElement("path", {
    d: "M0 4.461h3.052l1.776 4.544 1.8-4.544H9.6L5.126 15.217h-2.99l1.224-2.85L0 4.461Zm13.059 3.63H9.73L12.685 1H16l-2.941 7.092Zm-2.456.68a1.848 1.848 0 1 1 0 3.696 1.848 1.848 0 0 1 0-3.697Z"
  })));
};
var _path$k;
function _extends$A() {
  _extends$A = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$A.apply(this, arguments);
}
var SvgYoutube = function SvgYoutube(props) {
  return /*#__PURE__*/createElement("svg", _extends$A({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$k || (_path$k = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M15.149 3.23c.248.249.426.56.517.9C16 5.386 16 8.006 16 8.006s0 2.62-.335 3.877a2.012 2.012 0 0 1-1.414 1.423C13 13.643 8 13.643 8 13.643s-5 0-6.25-.337a2.011 2.011 0 0 1-1.415-1.423C0 10.626 0 8.006 0 8.006s0-2.62.335-3.876a2.01 2.01 0 0 1 1.414-1.424C3 2.37 8 2.37 8 2.37s5 0 6.25.336c.34.093.65.273.899.523Zm-4.603 4.776L6.364 5.627v4.759l4.182-2.38Z"
  })));
};
var _path$l;
function _extends$B() {
  _extends$B = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$B.apply(this, arguments);
}
var SvgAccount = function SvgAccount(props) {
  return /*#__PURE__*/createElement("svg", _extends$B({
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$l || (_path$l = /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M2.167 1.583c0-.322.26-.583.583-.583h10.5c.322 0 .583.261.583.583v10.792c0 .161.13.292.292.292h.292c.322 0 .583.26.583.583v1.167a.583.583 0 0 1-.583.583h-3.792a.292.292 0 0 1-.292-.292v-1.75a.292.292 0 0 0-.291-.291H5.958a.292.292 0 0 0-.291.291v1.75c0 .161-.13.292-.292.292H1.583A.583.583 0 0 1 1 14.417V13.25c0-.322.261-.583.583-.583h.292c.161 0 .292-.13.292-.292V1.583Zm7 2.042c0-.161.13-.292.291-.292h1.75c.161 0 .292.13.292.292v1.75c0 .161-.13.292-.292.292h-1.75a.292.292 0 0 1-.291-.292v-1.75ZM9.458 8a.292.292 0 0 0-.291.292v1.75c0 .16.13.291.291.291h1.75c.161 0 .292-.13.292-.291v-1.75A.292.292 0 0 0 11.208 8h-1.75ZM4.5 8.292c0-.161.13-.292.292-.292h1.75c.16 0 .291.13.291.292v1.75c0 .16-.13.291-.291.291h-1.75a.292.292 0 0 1-.292-.291v-1.75Zm.292-4.959a.292.292 0 0 0-.292.292v1.75c0 .161.13.292.292.292h1.75c.16 0 .291-.13.291-.292v-1.75a.292.292 0 0 0-.291-.292h-1.75Z"
  })));
};
var _path$m, _path2$4;
function _extends$C() {
  _extends$C = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$C.apply(this, arguments);
}
var SvgActivity = function SvgActivity(props) {
  return /*#__PURE__*/createElement("svg", _extends$C({
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$m || (_path$m = /*#__PURE__*/createElement("path", {
    d: "M8 3.333a4.64 4.64 0 0 0-2.8.933c-.266.2-.71.693-1.025 1.076l.994.993a.292.292 0 0 1-.206.498h-3.38A.583.583 0 0 1 1 6.25V2.87c0-.259.314-.39.498-.205L2.5 3.668A7.035 7.035 0 0 1 3.8 2.4a7 7 0 1 1-1.03 10.252.547.547 0 0 1 .097-.802l.933-.7c.258-.194.621-.139.845.093A4.667 4.667 0 1 0 8 3.333Z"
  })), _path2$4 || (_path2$4 = /*#__PURE__*/createElement("path", {
    d: "M7.417 5.375c0-.161.13-.292.291-.292h.584c.16 0 .291.13.291.292v2.263c0 .077.031.151.086.206L9.956 9.13a.292.292 0 0 1 0 .413l-.412.412a.292.292 0 0 1-.413 0L7.502 8.327a.292.292 0 0 1-.085-.206V5.375Z"
  })));
};
var _path$n, _path2$5;
function _extends$D() {
  _extends$D = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$D.apply(this, arguments);
}
var SvgAdSchool = function SvgAdSchool(props) {
  return /*#__PURE__*/createElement("svg", _extends$D({
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$n || (_path$n = /*#__PURE__*/createElement("path", {
    d: "M7.806 2.937a.53.53 0 0 1 .193-.037h.004c.066 0 .131.013.193.037l6.466 2.57a.528.528 0 0 1 .334.53H15v3.871a.27.27 0 0 1-.149.241l-.694.347a.161.161 0 0 1-.234-.144V7.2a.27.27 0 0 0-.368-.25L8.196 9.08a.529.529 0 0 1-.39 0L1.34 6.51A.53.53 0 0 1 1 6.012v-.006a.53.53 0 0 1 .34-.498l6.466-2.57Z"
  })), _path2$5 || (_path2$5 = /*#__PURE__*/createElement("path", {
    d: "m12.564 8.501-3.976 1.581a1.579 1.579 0 0 1-1.174 0L3.438 8.501a.267.267 0 0 0-.365.25v2.077c0 .22.134.42.338.5l4.395 1.748a.53.53 0 0 0 .39 0l4.395-1.748a.538.538 0 0 0 .338-.5V8.752a.268.268 0 0 0-.365-.251Z"
  })));
};
var _path$o;
function _extends$E() {
  _extends$E = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$E.apply(this, arguments);
}
var SvgAdd = function SvgAdd(props) {
  return /*#__PURE__*/createElement("svg", _extends$E({
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$o || (_path$o = /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0ZM8.438 4.5c.241 0 .437.196.437.438v2.284h2.188c.241 0 .437.196.437.438v.875a.438.438 0 0 1-.438.437H8.876v2.09a.438.438 0 0 1-.437.438h-.875a.438.438 0 0 1-.438-.438v-2.09H4.938a.437.437 0 0 1-.438-.437V7.66c0-.242.196-.438.438-.438h2.187V4.937c0-.241.196-.437.438-.437h.875Z"
  })));
};
var _path$p;
function _extends$F() {
  _extends$F = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$F.apply(this, arguments);
}
var SvgAggregate = function SvgAggregate(props) {
  return /*#__PURE__*/createElement("svg", _extends$F({
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$p || (_path$p = /*#__PURE__*/createElement("path", {
    d: "M8.396 1.515a.5.5 0 0 0-.792 0L4.846 5.098a.25.25 0 0 0 .198.402H6.75a.25.25 0 0 1 .25.25v2.836l-4.354 4.353a.5.5 0 0 0 0 .707l.71.71a.5.5 0 0 0 .705.003l4.79-4.712A.5.5 0 0 0 9 9.29V5.75a.25.25 0 0 1 .25-.25h1.706a.25.25 0 0 0 .198-.402L8.396 1.514ZM11.94 14.354a.5.5 0 0 0 .707 0l.707-.707a.5.5 0 0 0 0-.708l-2.293-2.293a.5.5 0 0 0-.707 0l-.708.708a.5.5 0 0 0 0 .707l2.293 2.293Z"
  })));
};
var _path$q;
function _extends$G() {
  _extends$G = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$G.apply(this, arguments);
}
var SvgAmazonBetaTag = function SvgAmazonBetaTag(props) {
  return /*#__PURE__*/createElement("svg", _extends$G({
    viewBox: "0 0 62 9",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$q || (_path$q = /*#__PURE__*/createElement("path", {
    d: "M3.592.92h1.09L7.732 8h-1.44l-.66-1.62h-3.07L1.922 8H.512L3.592.92Zm1.6 4.38-1.09-2.88-1.11 2.88h2.2Zm8.91-2.24c.314 0 .58.057.8.17.227.107.41.25.55.43.147.18.254.387.32.62.067.233.1.473.1.72v3h-1.2V5.34c0-.14-.01-.287-.03-.44a1.139 1.139 0 0 0-.13-.41.834.834 0 0 0-.28-.31c-.113-.08-.266-.12-.46-.12a.916.916 0 0 0-.48.12c-.133.08-.243.183-.33.31-.086.127-.15.27-.19.43-.04.153-.06.307-.06.46V8h-1.2V5.1c0-.3-.073-.547-.22-.74-.14-.2-.363-.3-.67-.3-.18 0-.336.04-.47.12a.99.99 0 0 0-.33.29c-.08.12-.143.26-.19.42-.04.153-.06.31-.06.47V8h-1.2V3.2h1.14v.77h.02c.054-.12.124-.233.21-.34.094-.107.2-.203.32-.29.127-.087.27-.153.43-.2.16-.053.337-.08.53-.08.374 0 .684.083.93.25.247.167.437.387.57.66.16-.293.374-.517.64-.67.267-.16.58-.24.94-.24Zm6.147 4.33h-.03c-.12.213-.314.39-.58.53-.267.133-.57.2-.91.2-.194 0-.397-.027-.61-.08a1.843 1.843 0 0 1-.58-.24 1.612 1.612 0 0 1-.44-.46 1.383 1.383 0 0 1-.17-.71c0-.36.1-.647.3-.86.207-.213.47-.377.79-.49.32-.113.673-.187 1.06-.22.393-.04.776-.06 1.15-.06v-.12c0-.3-.11-.52-.33-.66a1.326 1.326 0 0 0-.77-.22c-.254 0-.497.053-.73.16a1.967 1.967 0 0 0-.58.39l-.62-.73c.273-.253.586-.443.94-.57.36-.127.723-.19 1.09-.19.427 0 .777.06 1.05.18.28.12.5.277.66.47.16.193.27.41.33.65.067.24.1.48.1.72V8h-1.12v-.61Zm-.02-1.58h-.27c-.194 0-.397.01-.61.03-.213.013-.41.05-.59.11-.18.053-.33.133-.45.24-.12.1-.18.24-.18.42 0 .113.023.21.07.29.053.073.12.133.2.18.08.047.17.08.27.1.1.02.2.03.3.03.413 0 .726-.11.94-.33.213-.22.32-.52.32-.9v-.17Zm2.158 1.13 2.59-2.78h-2.47V3.2h3.9v1.06l-2.6 2.76h2.72V8h-4.14V6.94Zm4.924-1.36c0-.38.067-.723.2-1.03.14-.313.327-.58.56-.8.233-.22.51-.39.83-.51.32-.12.66-.18 1.02-.18s.7.06 1.02.18a2.347 2.347 0 0 1 1.38 1.31c.14.307.21.65.21 1.03s-.07.727-.21 1.04a2.456 2.456 0 0 1-.55.81c-.233.22-.51.393-.83.52-.32.127-.66.19-1.02.19s-.7-.063-1.02-.19c-.32-.127-.597-.3-.83-.52a2.588 2.588 0 0 1-.56-.81 2.63 2.63 0 0 1-.2-1.04Zm1.22 0c0 .187.027.373.08.56.06.187.147.353.26.5.113.147.257.267.43.36.173.093.38.14.62.14s.447-.047.62-.14c.173-.093.317-.213.43-.36.113-.147.197-.313.25-.5.06-.187.09-.373.09-.56 0-.187-.03-.37-.09-.55a1.469 1.469 0 0 0-.25-.5 1.23 1.23 0 0 0-.43-.35 1.29 1.29 0 0 0-.62-.14c-.24 0-.447.047-.62.14a1.23 1.23 0 0 0-.43.35c-.113.147-.2.313-.26.5-.053.18-.08.363-.08.55Zm5.164-2.38h1.14v.77h.02c.106-.24.29-.45.55-.63.266-.187.58-.28.94-.28.313 0 .58.057.8.17.226.107.41.25.55.43.146.18.253.387.32.62.066.233.1.473.1.72v3h-1.2V5.34c0-.14-.01-.287-.03-.44a1.139 1.139 0 0 0-.13-.41.834.834 0 0 0-.28-.31c-.114-.08-.267-.12-.46-.12-.194 0-.36.04-.5.12a1.06 1.06 0 0 0-.35.29c-.087.12-.154.26-.2.42-.047.153-.07.31-.07.47V8h-1.2V3.2Zm8.38-2.28h2.76c.267 0 .53.033.79.1.26.06.49.163.69.31.207.14.373.323.5.55.127.227.19.5.19.82 0 .4-.113.73-.34.99a1.89 1.89 0 0 1-.89.56v.02c.447.06.813.233 1.1.52.287.287.43.67.43 1.15 0 .387-.077.713-.23.98-.153.26-.357.47-.61.63a2.62 2.62 0 0 1-.86.35c-.32.067-.647.1-.98.1h-2.55V.92Zm1.26 2.88h1.12c.42 0 .74-.083.96-.25.22-.167.33-.403.33-.71 0-.32-.113-.547-.34-.68-.227-.133-.58-.2-1.06-.2h-1.01V3.8Zm0 3.14h1.13c.16 0 .333-.01.52-.03.187-.027.357-.077.51-.15.16-.073.29-.18.39-.32.107-.14.16-.327.16-.56 0-.373-.127-.633-.38-.78-.253-.147-.637-.22-1.15-.22h-1.18v2.06Zm8.535-1.82c0-.153-.024-.3-.07-.44a.95.95 0 0 0-.2-.37.943.943 0 0 0-.36-.25c-.14-.067-.307-.1-.5-.1-.36 0-.667.11-.92.33-.247.213-.384.49-.41.83h2.46Zm1.2.54v.16c0 .053-.004.107-.01.16h-3.65c.013.173.056.333.13.48.08.14.183.263.31.37.126.1.27.18.43.24.16.06.326.09.5.09.3 0 .553-.053.76-.16.206-.113.376-.267.51-.46l.8.64c-.474.64-1.16.96-2.06.96-.374 0-.717-.057-1.03-.17a2.557 2.557 0 0 1-.82-.5 2.336 2.336 0 0 1-.54-.78 2.808 2.808 0 0 1-.19-1.06c0-.387.063-.737.19-1.05a2.398 2.398 0 0 1 1.34-1.33 2.67 2.67 0 0 1 1.01-.19c.333 0 .64.057.92.17.286.107.533.27.74.49.206.213.366.483.48.81.12.32.18.697.18 1.13Zm.472-1.5V3.2h.84V1.81h1.18V3.2h1.2v.96h-1.2v2.23c0 .213.036.39.11.53.08.14.253.21.52.21.08 0 .166-.007.26-.02.093-.02.176-.047.25-.08l.04.94c-.107.04-.234.07-.38.09-.147.027-.287.04-.42.04-.32 0-.58-.043-.78-.13-.2-.093-.36-.217-.48-.37a1.524 1.524 0 0 1-.24-.54 3.47 3.47 0 0 1-.06-.66V4.16h-.84Zm7.068 3.23h-.03c-.12.213-.313.39-.58.53-.267.133-.57.2-.91.2-.193 0-.397-.027-.61-.08a1.843 1.843 0 0 1-.58-.24 1.61 1.61 0 0 1-.44-.46 1.382 1.382 0 0 1-.17-.71c0-.36.1-.647.3-.86.207-.213.47-.377.79-.49.32-.113.673-.187 1.06-.22.394-.04.777-.06 1.15-.06v-.12c0-.3-.11-.52-.33-.66a1.326 1.326 0 0 0-.77-.22c-.253 0-.497.053-.73.16a1.964 1.964 0 0 0-.58.39l-.62-.73c.273-.253.587-.443.94-.57.36-.127.724-.19 1.09-.19.427 0 .777.06 1.05.18.28.12.5.277.66.47.16.193.27.41.33.65.067.24.1.48.1.72V8h-1.12v-.61Zm-.02-1.58h-.27c-.193 0-.397.01-.61.03-.213.013-.41.05-.59.11-.18.053-.33.133-.45.24-.12.1-.18.24-.18.42 0 .113.023.21.07.29.053.073.12.133.2.18.08.047.17.08.27.1.1.02.2.03.3.03.413 0 .727-.11.94-.33.213-.22.32-.52.32-.9v-.17Z"
  })));
};
var _path$r;
function _extends$H() {
  _extends$H = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$H.apply(this, arguments);
}
var SvgApprove = function SvgApprove(props) {
  return /*#__PURE__*/createElement("svg", _extends$H({
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$r || (_path$r = /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M14.622 4.196a.5.5 0 0 1 0 .723l-8.11 7.759c-.45.43-1.179.43-1.628 0L1.378 9.323a.5.5 0 0 1 0-.723l.904-.866a.5.5 0 0 1 .692 0l2.724 2.606 7.328-7.01a.5.5 0 0 1 .692 0l.904.865Z"
  })));
};
var _path$s, _path2$6;
function _extends$I() {
  _extends$I = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$I.apply(this, arguments);
}
var SvgArrowDown = function SvgArrowDown(props) {
  return /*#__PURE__*/createElement("svg", _extends$I({
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$s || (_path$s = /*#__PURE__*/createElement("path", {
    d: "M7.595 13.438a.5.5 0 0 0 .81 0l3.353-4.645A.5.5 0 0 0 11.353 8H4.647a.5.5 0 0 0-.405.793l3.353 4.645Z"
  })), _path2$6 || (_path2$6 = /*#__PURE__*/createElement("path", {
    d: "M7 3.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-6Z"
  })));
};
var _path$t, _path2$7;
function _extends$J() {
  _extends$J = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$J.apply(this, arguments);
}
var SvgArrowUp = function SvgArrowUp(props) {
  return /*#__PURE__*/createElement("svg", _extends$J({
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$t || (_path$t = /*#__PURE__*/createElement("path", {
    d: "M7.595 2.562a.5.5 0 0 1 .81 0l3.353 4.645a.5.5 0 0 1-.405.793H4.647a.5.5 0 0 1-.405-.793l3.353-4.645Z"
  })), _path2$7 || (_path2$7 = /*#__PURE__*/createElement("path", {
    d: "M7 6h2v6.5a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5V6Z"
  })));
};
var _path$u;
function _extends$K() {
  _extends$K = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$K.apply(this, arguments);
}
var SvgAsterisk = function SvgAsterisk(props) {
  return /*#__PURE__*/createElement("svg", _extends$K({
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$u || (_path$u = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M9.615 6.163V2H6.368v4.157L2.003 4.831 1 7.719l4.19 1.273-2.503 3.223L5.314 14l2.666-3.433L10.647 14l2.626-1.785-2.496-3.213L15 7.72 13.997 4.83 9.615 6.163Z"
  })));
};
var _path$v, _path2$8, _path3$2;
function _extends$L() {
  _extends$L = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$L.apply(this, arguments);
}
var SvgAttribution = function SvgAttribution(props) {
  return /*#__PURE__*/createElement("svg", _extends$L({
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$v || (_path$v = /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M15 7.39A7.008 7.008 0 0 0 8.598 1a.778.778 0 0 0-.586.754v5.468a.778.778 0 1 0 .779.754h5.454A.78.78 0 0 0 15 7.39Zm-4.456-.969a.973.973 0 1 0 .002-1.946.973.973 0 0 0-.002 1.946Z"
  })), _path2$8 || (_path2$8 = /*#__PURE__*/createElement("path", {
    d: "M8.012 15a7.012 7.012 0 0 0 6.838-5.444h-1.61a5.455 5.455 0 0 1-5.228 3.889A5.45 5.45 0 0 1 2.558 8a5.448 5.448 0 0 1 3.896-5.219V1.174C3.33 1.88 1 4.669 1 8c0 3.866 3.14 7 7.012 7Z"
  })), _path3$2 || (_path3$2 = /*#__PURE__*/createElement("path", {
    d: "M6.454 6.26V4.436a3.89 3.89 0 0 0 1.558 7.454c1.597 0 2.97-.96 3.571-2.333h-1.83a2.333 2.333 0 0 1-1.741.777A2.335 2.335 0 0 1 5.675 8c0-.691.3-1.312.779-1.74Z"
  })));
};
var _path$w;
function _extends$M() {
  _extends$M = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$M.apply(this, arguments);
}
var SvgBackArrow = function SvgBackArrow(props) {
  return /*#__PURE__*/createElement("svg", _extends$M({
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$w || (_path$w = /*#__PURE__*/createElement("path", {
    d: "M1.167 8.388a.538.538 0 0 1 0-.778l.804-.779.033-.03 4.794-4.64a.582.582 0 0 1 .804 0l.804.779a.538.538 0 0 1 0 .778L5.12 6.9h9.311a.56.56 0 0 1 .569.55V8.55a.56.56 0 0 1-.569.551h-9.31l3.285 3.18a.538.538 0 0 1 0 .78l-.804.778a.582.582 0 0 1-.804 0L1.167 8.388Z"
  })));
};
var _g$f, _defs$f;
function _extends$N() {
  _extends$N = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$N.apply(this, arguments);
}
var SvgBeak = function SvgBeak(props) {
  return /*#__PURE__*/createElement("svg", _extends$N({
    viewBox: "0 0 16 8",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$f || (_g$f = /*#__PURE__*/createElement("g", {
    clipPath: "url(#beak_svg__a)"
  }, /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M.5 8h16L9.914 1.414a2 2 0 0 0-2.828 0L.5 8Z"
  }))), _defs$f || (_defs$f = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "beak_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    d: "M0 0h16v16.955H0z"
  })))));
};
var _path$x;
function _extends$O() {
  _extends$O = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$O.apply(this, arguments);
}
var SvgBestSellerRank = function SvgBestSellerRank(props) {
  return /*#__PURE__*/createElement("svg", _extends$O({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$x || (_path$x = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M4 1.33a1 1 0 0 0-1 1v11.205a1 1 0 0 0 1.417.909l3.166-1.453a1 1 0 0 1 .834 0l3.166 1.453A1 1 0 0 0 13 13.535V2.33a1 1 0 0 0-1-1H4Zm3.51 4.225v4.772H9.05V3.83H7.638L5.5 5.399l.798 1.092 1.212-.936Z"
  })));
};
var _path$y, _path2$9;
function _extends$P() {
  _extends$P = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$P.apply(this, arguments);
}
var SvgBillingError = function SvgBillingError(props) {
  return /*#__PURE__*/createElement("svg", _extends$P({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$y || (_path$y = /*#__PURE__*/createElement("path", {
    d: "M11.951 1.224a.389.389 0 0 1 0-.554l.562-.555a.4.4 0 0 1 .562 0l.842.832.843-.832a.4.4 0 0 1 .562 0l.562.555a.389.389 0 0 1 0 .554l-.843.832.843.832a.389.389 0 0 1 0 .555l-.562.555a.4.4 0 0 1-.562 0l-.843-.832-.842.832a.4.4 0 0 1-.562 0l-.562-.555a.389.389 0 0 1 0-.555l.843-.832-.843-.832Z"
  })), _path2$9 || (_path2$9 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "m10.25 1.017.191.21c-.065.266-.099.544-.099.83 0 1.949 1.6 3.53 3.575 3.53v8.54c0 .39-.445.582-.7.302a.39.39 0 0 0-.585 0l-.396.434a.408.408 0 0 1-.61 0l-.383-.42a.408.408 0 0 0-.61 0l-.383.42a.408.408 0 0 1-.61 0l-.383-.42a.408.408 0 0 0-.61 0l-.383.42a.408.408 0 0 1-.61 0l-.383-.42a.408.408 0 0 0-.61 0l-.383.42a.408.408 0 0 1-.61 0l-.383-.42a.408.408 0 0 0-.61 0l-.384.42a.408.408 0 0 1-.61 0l-.395-.434a.39.39 0 0 0-.585 0c-.256.28-.701.087-.701-.303V1.754c0-.39.445-.583.7-.304a.39.39 0 0 0 .586 0l.395-.433a.408.408 0 0 1 .61 0l.383.42a.408.408 0 0 0 .61 0l.383-.42a.408.408 0 0 1 .61 0l.383.42a.408.408 0 0 0 .61 0l.383-.42a.408.408 0 0 1 .61 0l.384.42a.408.408 0 0 0 .61 0l.383-.42a.408.408 0 0 1 .61 0ZM4.383 5.292c0-.162.134-.294.298-.294h6.555c.164 0 .298.132.298.294v.589a.296.296 0 0 1-.298.294H4.68a.296.296 0 0 1-.298-.294v-.589Zm0 2.354c0-.163.134-.295.298-.295h6.555c.164 0 .298.132.298.295v.588a.296.296 0 0 1-.298.294H4.68a.296.296 0 0 1-.298-.294v-.588Zm6.853 2.059c.164 0 .298.132.298.294v.588a.296.296 0 0 1-.298.295H4.68a.296.296 0 0 1-.298-.295V10c0-.162.134-.294.298-.294h6.555Z"
  })));
};
var _path$z;
function _extends$Q() {
  _extends$Q = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$Q.apply(this, arguments);
}
var SvgBilling = function SvgBilling(props) {
  return /*#__PURE__*/createElement("svg", _extends$Q({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$z || (_path$z = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M2.705 14.434a.397.397 0 0 1 .59 0l.398.43a.415.415 0 0 0 .614 0l.386-.416a.415.415 0 0 1 .614 0l.386.416a.415.415 0 0 0 .614 0l.386-.416a.415.415 0 0 1 .614 0l.386.416a.415.415 0 0 0 .614 0l.386-.416a.415.415 0 0 1 .614 0l.386.416a.415.415 0 0 0 .614 0l.386-.416a.415.415 0 0 1 .614 0l.386.416a.415.415 0 0 0 .614 0l.398-.43a.397.397 0 0 1 .59 0c.257.277.705.087.705-.3V1.865c0-.387-.448-.578-.705-.3a.398.398 0 0 1-.59 0l-.398-.43a.415.415 0 0 0-.614 0l-.386.416a.415.415 0 0 1-.614 0l-.386-.416a.415.415 0 0 0-.614 0l-.386.416a.415.415 0 0 1-.614 0l-.386-.416a.415.415 0 0 0-.614 0l-.386.416a.415.415 0 0 1-.614 0l-.386-.416a.415.415 0 0 0-.614 0l-.386.416a.415.415 0 0 1-.614 0l-.386-.416a.415.415 0 0 0-.614 0l-.398.43a.398.398 0 0 1-.59 0c-.257-.278-.705-.087-.705.3v12.268c0 .386.448.577.705.3ZM4.4 5.375c0-.161.134-.292.3-.292h6.6c.166 0 .3.13.3.292v.583a.296.296 0 0 1-.3.292H4.7c-.166 0-.3-.13-.3-.292v-.583Zm0 2.333c0-.16.134-.291.3-.291h6.6c.166 0 .3.13.3.291v.584a.296.296 0 0 1-.3.291H4.7c-.166 0-.3-.13-.3-.291v-.584Zm6.9 2.042c.166 0 .3.13.3.292v.583a.296.296 0 0 1-.3.292H4.7c-.166 0-.3-.13-.3-.292v-.583c0-.161.134-.292.3-.292h6.6Z"
  })));
};
var _path$A;
function _extends$R() {
  _extends$R = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$R.apply(this, arguments);
}
var SvgBinoculars = function SvgBinoculars(props) {
  return /*#__PURE__*/createElement("svg", _extends$R({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$A || (_path$A = /*#__PURE__*/createElement("path", {
    d: "M14.05 6.694a3.574 3.574 0 0 0-.247-.213l-1.178-.986a3.113 3.113 0 0 0-1.579-.56l.064.063-.715-.709h-.006a1.128 1.128 0 0 0-1.82.477 1.033 1.033 0 0 0-.491-.156c-.03-.003-.06-.003-.09-.003-.203 0-.393.06-.554.159a1.12 1.12 0 0 0-1.065-.763c-.29 0-.554.108-.755.29h-.006L4.893 5l.064-.062a3.13 3.13 0 0 0-1.58.558L2.2 6.484A3.054 3.054 0 0 0 1 8.914C1 10.617 2.401 12 4.124 12c1.59 0 2.908-1.183 3.098-2.705.23.13.497.204.778.204.284 0 .548-.074.778-.204.192 1.52 1.508 2.702 3.098 2.702C13.6 11.997 15 10.613 15 8.912a3.04 3.04 0 0 0-.95-2.218Zm-9.842 3.592c-.79 0-1.458-.572-1.458-1.371 0-.915.583-1.486 1.458-1.486S5.671 8 5.671 8.915c0 .8-.503 1.37-1.463 1.37Zm3.795-1.66a.688.688 0 0 1-.692-.684c0-.377.31-.684.692-.684.382 0 .692.307.692.684 0 .377-.31.683-.692.683Zm3.789 1.66c-.875 0-1.459-.572-1.459-1.429 0-.857.584-1.428 1.459-1.428.875 0 1.458.571 1.458 1.428s-.583 1.429-1.458 1.429Z"
  })));
};
var _path$B;
function _extends$S() {
  _extends$S = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$S.apply(this, arguments);
}
var SvgBook = function SvgBook(props) {
  return /*#__PURE__*/createElement("svg", _extends$S({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$B || (_path$B = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M3.2 1C2.537 1 2 1.522 2 2.167v11.666C2 14.478 2.537 15 3.2 15h9.6c.663 0 1.2-.522 1.2-1.167V2.167C14 1.522 13.463 1 12.8 1H8v4.958c0 .28-.367.4-.54.175L6.44 4.811a.305.305 0 0 0-.48 0L4.94 6.133c-.173.225-.54.106-.54-.175V1H3.2Zm8.4 1.167h.6c.331 0 .6.26.6.583v10.5a.592.592 0 0 1-.6.583h-.6V2.167Z"
  })));
};
var _path$C, _path2$a, _path3$3;
function _extends$T() {
  _extends$T = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$T.apply(this, arguments);
}
var SvgBottomOfFunnel = function SvgBottomOfFunnel(props) {
  return /*#__PURE__*/createElement("svg", _extends$T({
    width: 16,
    height: 16,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$C || (_path$C = /*#__PURE__*/createElement("path", {
    d: "M15 1H1a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1Z",
    fill: "#A5B4FC"
  })), _path2$a || (_path2$a = /*#__PURE__*/createElement("path", {
    d: "M11 10.998H5a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1Z",
    fill: "#4338CA"
  })), _path3$3 || (_path3$3 = /*#__PURE__*/createElement("path", {
    d: "M13 6H3a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1Z",
    fill: "#A5B4FC"
  })));
};
var _path$D, _path2$b;
function _extends$U() {
  _extends$U = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$U.apply(this, arguments);
}
var SvgBrands = function SvgBrands(props) {
  return /*#__PURE__*/createElement("svg", _extends$U({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$D || (_path$D = /*#__PURE__*/createElement("path", {
    d: "M14 2.25a.25.25 0 0 0-.25-.25H2.25a.25.25 0 0 0-.25.25v.5c0 .138.112.25.25.25h11.5a.25.25 0 0 0 .25-.25v-.5Z"
  })), _path2$b || (_path2$b = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M1.053 5.894A.5.5 0 0 0 1 6.118V7.75c0 .138.112.25.25.25h.5a.25.25 0 0 1 .25.25v5.25a.5.5 0 0 0 .5.5h6.25a.25.25 0 0 0 .25-.25V9.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v4.25c0 .138.112.25.25.25h1.25a.5.5 0 0 0 .5-.5V8.25a.25.25 0 0 1 .25-.25h.5a.25.25 0 0 0 .25-.25V6.118a.5.5 0 0 0-.053-.224l-.809-1.618A.5.5 0 0 0 13.691 4H2.309a.5.5 0 0 0-.447.276l-.81 1.618ZM7 9.5a.5.5 0 0 0-.5-.5h-2a.5.5 0 0 0-.5.5v2a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5v-2Z"
  })));
};
var _path$E;
function _extends$V() {
  _extends$V = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$V.apply(this, arguments);
}
var SvgBug = function SvgBug(props) {
  return /*#__PURE__*/createElement("svg", _extends$V({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$E || (_path$E = /*#__PURE__*/createElement("path", {
    d: "M14 4.889h-2.107a4.594 4.594 0 0 0-1.366-1.525l1.223-1.267L10.693 1 9.065 2.688a4.288 4.288 0 0 0-2.122 0L5.308 1 4.25 2.097l1.215 1.267A4.654 4.654 0 0 0 4.108 4.89H2v1.555h1.567a5.35 5.35 0 0 0-.067.778V8H2v1.556h1.5v.777c0 .265.03.521.067.778H2v1.556h2.107C4.888 14.059 6.335 15 8 15c1.665 0 3.113-.941 3.893-2.333H14V11.11h-1.568c.038-.257.068-.513.068-.778v-.777H14V8h-1.5v-.778c0-.264-.03-.52-.068-.778H14V4.89ZM9.5 11.11h-3V9.556h3v1.555ZM9.5 8h-3V6.444h3V8Z"
  })));
};
var _path$F, _path2$c;
function _extends$W() {
  _extends$W = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$W.apply(this, arguments);
}
var SvgCalendar = function SvgCalendar(props) {
  return /*#__PURE__*/createElement("svg", _extends$W({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$F || (_path$F = /*#__PURE__*/createElement("path", {
    d: "M5.083 5.667a.583.583 0 0 0-.583.583v2.333c0 .322.261.584.583.584h2.334A.583.583 0 0 0 8 8.583V6.25a.583.583 0 0 0-.583-.583H5.083Z"
  })), _path2$c || (_path2$c = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M3.333 1.583c0-.322.261-.583.584-.583h1.166c.322 0 .584.261.584.583v.584h4.666v-.584c0-.322.261-.583.584-.583h1.166c.322 0 .584.261.584.583v.584h.583c.966 0 1.75.783 1.75 1.75v9.333A1.75 1.75 0 0 1 13.25 15H2.75A1.75 1.75 0 0 1 1 13.25V3.917c0-.967.784-1.75 1.75-1.75h.583v-.584Zm0 3.5c0-.322.261-.583.584-.583h8.166c.322 0 .584.261.584.583v7a.583.583 0 0 1-.584.584H3.917a.583.583 0 0 1-.584-.584v-7Z"
  })));
};
var _path$G, _path2$d;
function _extends$X() {
  _extends$X = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$X.apply(this, arguments);
}
var SvgCapital = function SvgCapital(props) {
  return /*#__PURE__*/createElement("svg", _extends$X({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$G || (_path$G = /*#__PURE__*/createElement("path", {
    d: "M7.193 6.738c.136.122.318.211.547.269V5.449a.982.982 0 0 0-.52.22c-.154.126-.231.306-.231.543 0 .229.068.404.204.526ZM8.481 8.768v1.697c.244-.014.435-.095.575-.242.14-.147.21-.345.21-.596 0-.25-.074-.438-.22-.564a1.586 1.586 0 0 0-.565-.295Z"
  })), _path2$d || (_path2$d = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm.481-3.117v.784h-.794v-.784a4.802 4.802 0 0 1-1.407-.263 3.555 3.555 0 0 1-1.246-.725l.564-.586c.19-.197.377-.395.564-.596.093.107.204.206.332.295a2.797 2.797 0 0 0 .806.387c.136.04.265.06.387.06v-1.88a10.278 10.278 0 0 1-.886-.29 2.744 2.744 0 0 1-.747-.42 1.95 1.95 0 0 1-.515-.628c-.13-.247-.194-.55-.194-.907 0-.351.072-.664.215-.94.143-.276.33-.508.559-.698.229-.19.486-.337.773-.44.286-.104.57-.163.848-.178v-.74h.784v.73c.409.014.827.086 1.257.214.43.13.813.33 1.15.602-.352.4-.699.791-1.043 1.17a1.894 1.894 0 0 0-.644-.418c-.25-.1-.49-.154-.72-.161v1.718c.28.086.562.183.849.29.286.107.548.25.784.43.236.179.43.397.58.655.15.258.225.58.225.966 0 .394-.07.736-.209 1.026-.14.29-.326.532-.559.725-.232.194-.497.34-.794.44-.297.1-.604.155-.919.162Z"
  })));
};
var _path$H;
function _extends$Y() {
  _extends$Y = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$Y.apply(this, arguments);
}
var SvgCard = function SvgCard(props) {
  return /*#__PURE__*/createElement("svg", _extends$Y({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$H || (_path$H = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M2 3a1 1 0 0 0-1 1v1.278h14V4a1 1 0 0 0-1-1H2Zm13 4.556H1v4.694a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7.556Zm-4.667 2.562c0-.157.13-.285.292-.285h1.75c.161 0 .292.128.292.285v.57c0 .157-.13.284-.292.284h-1.75a.288.288 0 0 1-.292-.284v-.57Zm-3.5 0c0-.157.13-.285.292-.285h1.75c.161 0 .292.128.292.285v.57c0 .157-.13.284-.292.284h-1.75a.288.288 0 0 1-.292-.284v-.57Z"
  })));
};
var _path$I;
function _extends$Z() {
  _extends$Z = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$Z.apply(this, arguments);
}
var SvgCheckboxDisabled = function SvgCheckboxDisabled(props) {
  return /*#__PURE__*/createElement("svg", _extends$Z({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$I || (_path$I = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M12 2H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2ZM4 0a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4V4a4 4 0 0 0-4-4H4Z"
  })));
};
var _rect, _rect2;
function _extends$_() {
  _extends$_ = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$_.apply(this, arguments);
}
var SvgCheckboxIndeterminate = function SvgCheckboxIndeterminate(props) {
  return /*#__PURE__*/createElement("svg", _extends$_({
    width: 16,
    height: 16,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _rect || (_rect = /*#__PURE__*/createElement("rect", {
    width: 16,
    height: 16,
    rx: 4
  })), _rect2 || (_rect2 = /*#__PURE__*/createElement("rect", {
    x: 3,
    y: 7,
    width: 10,
    height: 2,
    rx: 0.5,
    fill: "#fff"
  })));
};
var _rect$1, _path$J, _path2$e;
function _extends$$() {
  _extends$$ = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$$.apply(this, arguments);
}
var SvgCheckboxSelected = function SvgCheckboxSelected(props) {
  return /*#__PURE__*/createElement("svg", _extends$$({
    width: 16,
    height: 16,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _rect$1 || (_rect$1 = /*#__PURE__*/createElement("rect", {
    width: 16,
    height: 16,
    rx: 4
  })), _path$J || (_path$J = /*#__PURE__*/createElement("path", {
    d: "M11.536 4.379a.5.5 0 0 1 .707 0l.707.707a.5.5 0 0 1 0 .707L7.293 11.45a.5.5 0 0 1-.707 0l-.707-.707a.5.5 0 0 1 0-.707l5.657-5.657Z",
    fill: "#fff"
  })), _path2$e || (_path2$e = /*#__PURE__*/createElement("path", {
    d: "M3.757 6.5a.5.5 0 0 1 .707 0L8 10.036a.5.5 0 0 1 0 .707l-.707.707a.5.5 0 0 1-.707 0L3.05 7.914a.5.5 0 0 1 0-.707l.707-.707Z",
    fill: "#fff"
  })));
};
var _path$K;
function _extends$10() {
  _extends$10 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$10.apply(this, arguments);
}
var SvgCheckboxUnselected = function SvgCheckboxUnselected(props) {
  return /*#__PURE__*/createElement("svg", _extends$10({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$K || (_path$K = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M12 2H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2ZM4 0a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4V4a4 4 0 0 0-4-4H4Z"
  })));
};
var _path$L;
function _extends$11() {
  _extends$11 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$11.apply(this, arguments);
}
var SvgCheckedBullet = function SvgCheckedBullet(props) {
  return /*#__PURE__*/createElement("svg", _extends$11({
    viewBox: "0 0 9 9",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$L || (_path$L = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M2.855 4.335a.25.25 0 0 1 .353.021L4 5.247l1.792-2.016a.25.25 0 0 1 .353-.02l.374.331a.25.25 0 0 1 .02.353L4.375 6.332a.5.5 0 0 1-.748 0L2.46 5.02a.25.25 0 0 1 .021-.353l.374-.332ZM4.5 8a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm0 1a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Z"
  })));
};
var _path$M;
function _extends$12() {
  _extends$12 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$12.apply(this, arguments);
}
var SvgChevron = function SvgChevron(props) {
  return /*#__PURE__*/createElement("svg", _extends$12({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$M || (_path$M = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M9.9 8 4.217 3.079a.646.646 0 0 1-.078-.886l.775-.967a.59.59 0 0 1 .852-.08l6.798 5.888c.276.239.435.593.435.966s-.16.727-.435.966l-6.798 5.888a.59.59 0 0 1-.852-.08l-.775-.967a.646.646 0 0 1 .078-.886L9.899 8Z"
  })));
};
var _path$N;
function _extends$13() {
  _extends$13 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$13.apply(this, arguments);
}
var SvgClear = function SvgClear(props) {
  return /*#__PURE__*/createElement("svg", _extends$13({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$N || (_path$N = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M12.95 3.05a7 7 0 1 1-9.9 9.9 7 7 0 0 1 9.9-9.9ZM5.835 5.216c.17-.171.447-.171.618 0L8.07 6.83l1.547-1.547c.17-.17.447-.17.618 0l.619.62c.17.17.17.447 0 .618L9.306 8.069l1.478 1.478c.171.17.171.448 0 .618l-.619.62a.437.437 0 0 1-.618 0L8.069 9.305l-1.547 1.547a.438.438 0 0 1-.619 0l-.618-.619a.437.437 0 0 1 0-.618L6.83 8.069 5.216 6.453a.438.438 0 0 1 0-.619l.619-.618Z"
  })));
};
var _path$O;
function _extends$14() {
  _extends$14 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$14.apply(this, arguments);
}
var SvgClose = function SvgClose(props) {
  return /*#__PURE__*/createElement("svg", _extends$14({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$O || (_path$O = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M2.984 2.169a.576.576 0 0 1 .814 0L8 6.37l4.202-4.201a.576.576 0 0 1 .815 0l.814.815a.576.576 0 0 1 0 .814L9.63 8l4.201 4.202a.576.576 0 0 1 0 .815l-.814.814a.576.576 0 0 1-.815 0L8 9.63 3.798 13.83a.576.576 0 0 1-.814 0l-.815-.814a.576.576 0 0 1 0-.815L6.37 8 2.17 3.798a.576.576 0 0 1 0-.814l.815-.815Z"
  })));
};
var _path$P;
function _extends$15() {
  _extends$15 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$15.apply(this, arguments);
}
var SvgColor = function SvgColor(props) {
  return /*#__PURE__*/createElement("svg", _extends$15({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$P || (_path$P = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8 13.25c1.015 0 2.03-.36 2.839-1.078a3.909 3.909 0 0 0 .395-5.443L8 2.888V13.25ZM9.053 1.374a1.664 1.664 0 0 0-2.313.205L3.323 5.638a5.593 5.593 0 0 0 .576 7.81 6.197 6.197 0 0 0 8.202 0 5.593 5.593 0 0 0 .576-7.81L9.26 1.579a1.594 1.594 0 0 0-.207-.205Z"
  })));
};
var _mask, _path$Q, _path2$f, _path3$4;
function _extends$16() {
  _extends$16 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$16.apply(this, arguments);
}
var SvgCollapse = function SvgCollapse(props) {
  return /*#__PURE__*/createElement("svg", _extends$16({
    width: 16,
    height: 16,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _mask || (_mask = /*#__PURE__*/createElement("mask", {
    id: "collapse_svg__a",
    fill: "#fff"
  }, /*#__PURE__*/createElement("path", {
    d: "M5.717 8.496a1 1 0 0 0 0-.992L2.086 1.15A.1.1 0 0 1 2.172 1h2.183a.25.25 0 0 1 .217.126l3.645 6.378a1 1 0 0 1 0 .992l-3.645 6.378a.25.25 0 0 1-.217.126H2.172a.1.1 0 0 1-.086-.15l3.63-6.354Z"
  }), /*#__PURE__*/createElement("path", {
    d: "M11.217 8.496a1 1 0 0 0 0-.992L7.586 1.15A.1.1 0 0 1 7.673 1h2.182a.25.25 0 0 1 .217.126l3.645 6.378a1 1 0 0 1 0 .992l-3.645 6.378a.25.25 0 0 1-.217.126H7.673a.1.1 0 0 1-.087-.15l3.63-6.354Z"
  }))), _path$Q || (_path$Q = /*#__PURE__*/createElement("path", {
    d: "M5.717 8.496a1 1 0 0 0 0-.992L2.086 1.15A.1.1 0 0 1 2.172 1h2.183a.25.25 0 0 1 .217.126l3.645 6.378a1 1 0 0 1 0 .992l-3.645 6.378a.25.25 0 0 1-.217.126H2.172a.1.1 0 0 1-.086-.15l3.63-6.354Z"
  })), _path2$f || (_path2$f = /*#__PURE__*/createElement("path", {
    d: "M11.217 8.496a1 1 0 0 0 0-.992L7.586 1.15A.1.1 0 0 1 7.673 1h2.182a.25.25 0 0 1 .217.126l3.645 6.378a1 1 0 0 1 0 .992l-3.645 6.378a.25.25 0 0 1-.217.126H7.673a.1.1 0 0 1-.087-.15l3.63-6.354Z"
  })), _path3$4 || (_path3$4 = /*#__PURE__*/createElement("path", {
    d: "m4.572 14.874.868.496-.868-.496Zm3.645-7.37.868-.496-.868.496Zm0 .992.868.496-.868-.496Zm-3.645-7.37L5.44.63l-.868.496ZM2.086 14.85l.868.497-.868-.497Zm0-13.7-.869.496.869-.496Zm3.63 6.354.869-.496-.868.496Zm0 .992.869.496-.868-.496Zm4.356 6.378-.868-.496.868.496Zm3.645-7.37L12.848 8l.869-.496Zm0 .992.868.496-.868-.496Zm-3.645-7.37L10.94.63l-.868.496ZM7.586 14.85l-.869-.496.869.496Zm0-13.7-.869.496.869-.496Zm3.63 6.354.869-.496-.868.496Zm0 .992.869.496-.868-.496ZM6.586 7.008 2.954.653l-1.737.993L4.848 8l1.737-.992ZM4.848 8l-3.63 6.354 1.736.993 3.63-6.355L4.849 8ZM2.172 2h2.183V0H2.172v2Zm1.532-.378L7.348 8l1.737-.992L5.44.63l-1.736.992ZM7.348 8l-3.644 6.378 1.736.992 3.645-6.378L7.348 8Zm-2.993 6H2.172v2h2.183v-2Zm-.651.378A.75.75 0 0 1 4.355 14v2c.449 0 .863-.24 1.085-.63l-1.736-.992ZM7.348 8l1.737.992a2 2 0 0 0 0-1.984L7.348 8ZM4.355 2a.75.75 0 0 1-.651-.378L5.44.63A1.25 1.25 0 0 0 4.355 0v2ZM1.217 14.354A1.1 1.1 0 0 0 2.172 16v-2a.9.9 0 0 1 .782 1.347l-1.737-.993ZM2.954.654A.9.9 0 0 1 2.172 2V0a1.1 1.1 0 0 0-.955 1.646L2.954.653ZM4.848 8l1.737.992a2 2 0 0 0 0-1.984L4.848 8Zm7.237-.992L8.454.653l-1.737.993L10.348 8l1.737-.992ZM10.348 8l-3.63 6.354 1.736.993 3.63-6.355L10.349 8ZM7.673 2h2.182V0H7.673v2Zm1.53-.378L12.849 8l1.737-.992L10.94.63l-1.736.992ZM12.849 8l-3.644 6.378 1.736.992 3.645-6.378L12.848 8Zm-2.993 6H7.673v2h2.182v-2Zm-.651.378A.75.75 0 0 1 9.855 14v2c.449 0 .863-.24 1.085-.63l-1.736-.992ZM12.848 8l1.737.992a2 2 0 0 0 0-1.984L12.848 8ZM9.855 2a.75.75 0 0 1-.651-.378L10.94.63A1.25 1.25 0 0 0 9.855 0v2ZM6.717 14.354A1.1 1.1 0 0 0 7.673 16v-2a.9.9 0 0 1 .78 1.347l-1.736-.993ZM8.454.654A.9.9 0 0 1 7.673 2V0a1.1 1.1 0 0 0-.956 1.646L8.454.653ZM10.348 8l1.737.992a2 2 0 0 0 0-1.984L10.348 8Z",
    mask: "url(#collapse_svg__a)"
  })));
};
var _path$R;
function _extends$17() {
  _extends$17 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$17.apply(this, arguments);
}
var SvgColumns = function SvgColumns(props) {
  return /*#__PURE__*/createElement("svg", _extends$17({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$R || (_path$R = /*#__PURE__*/createElement("path", {
    d: "M1 1.583C1 1.261 1.261 1 1.583 1H4.5v14H1.583A.583.583 0 0 1 1 14.417V1.583ZM6.25 1h3.5v14h-3.5V1ZM14.417 1H11.5v14h2.917a.583.583 0 0 0 .583-.583V1.583A.583.583 0 0 0 14.417 1Z"
  })));
};
var _path$S;
function _extends$18() {
  _extends$18 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$18.apply(this, arguments);
}
var SvgComment = function SvgComment(props) {
  return /*#__PURE__*/createElement("svg", _extends$18({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$S || (_path$S = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8 3.333a4.667 4.667 0 0 0-3.944 7.163c.188.296.233.661.122.994l-.25.751 1.058-.238c.273-.061.558-.023.805.109a4.64 4.64 0 0 0 2.205.555H8a4.667 4.667 0 0 0 0-9.334ZM1 8a7 7 0 1 1 4.097 6.37l-2.674.602a1.167 1.167 0 0 1-1.363-1.508l.738-2.215A6.976 6.976 0 0 1 1 8Z"
  })));
};
var _path$T;
function _extends$19() {
  _extends$19 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$19.apply(this, arguments);
}
var SvgCompetitor = function SvgCompetitor(props) {
  return /*#__PURE__*/createElement("svg", _extends$19({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$T || (_path$T = /*#__PURE__*/createElement("path", {
    d: "M10.333 4.4c0 .637-.245 1.247-.683 1.697A2.301 2.301 0 0 1 8 6.8a2.301 2.301 0 0 1-1.65-.703A2.435 2.435 0 0 1 5.667 4.4c0-.637.245-1.247.683-1.697A2.301 2.301 0 0 1 8 2c.619 0 1.212.253 1.65.703.438.45.683 1.06.683 1.697Zm3.89 1.6c0 .424-.165.831-.456 1.131-.292.3-.688.469-1.1.469-.413 0-.809-.169-1.1-.469A1.624 1.624 0 0 1 11.11 6c0-.424.164-.831.456-1.131.291-.3.687-.469 1.1-.469.412 0 .808.169 1.1.469.291.3.455.707.455 1.131Zm-3.112 5.6c0-.849-.328-1.663-.911-2.263A3.068 3.068 0 0 0 8 8.4c-.825 0-1.616.337-2.2.937A3.247 3.247 0 0 0 4.89 11.6V14h6.222v-2.4ZM4.89 6c0 .424-.164.831-.456 1.131-.291.3-.687.469-1.1.469-.412 0-.808-.169-1.1-.469A1.623 1.623 0 0 1 1.778 6c0-.424.164-.831.455-1.131.292-.3.688-.469 1.1-.469.413 0 .809.169 1.1.469.292.3.456.707.456 1.131Zm7.778 8v-2.4a4.885 4.885 0 0 0-.584-2.325c.345-.09.706-.1 1.054-.025.349.074.676.23.957.454.282.224.51.512.667.84.157.329.238.69.239 1.056V14h-2.333Zm-8.75-4.725a4.886 4.886 0 0 0-.584 2.325V14H1v-2.4c0-.366.081-.728.238-1.057.157-.329.385-.617.666-.841a2.27 2.27 0 0 1 2.013-.427Z"
  })));
};
var _path$U;
function _extends$1a() {
  _extends$1a = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1a.apply(this, arguments);
}
var SvgCopyDown = function SvgCopyDown(props) {
  return /*#__PURE__*/createElement("svg", _extends$1a({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$U || (_path$U = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M13 4a2 2 0 0 1 2 2v5.5a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-5a.5.5 0 0 0-.5-.5h-9a.5.5 0 0 0-.5.5v5a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5V6a2 2 0 0 1 2-2h10Zm0-1.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V2a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v.5Zm-1.646 8.854a.5.5 0 0 1 0 .707l-3 3a.5.5 0 0 1-.707 0l-3-3a.5.5 0 0 1 0-.707l.707-.708a.5.5 0 0 1 .707 0l.939.94V8.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v3.086l.94-.94a.5.5 0 0 1 .706 0l.708.707Z"
  })));
};
var _path$V;
function _extends$1b() {
  _extends$1b = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1b.apply(this, arguments);
}
var SvgCopyLeft = function SvgCopyLeft(props) {
  return /*#__PURE__*/createElement("svg", _extends$1b({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$V || (_path$V = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M12 13a2 2 0 0 1-2 2H4.5a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h5a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5H10a2 2 0 0 1 2 2v10Zm1.5 0a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h.5a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-.5Zm-8.854-1.646a.5.5 0 0 1-.707 0l-3-3a.5.5 0 0 1 0-.707l3-3a.5.5 0 0 1 .707 0l.708.707a.5.5 0 0 1 0 .707L4.414 7H7.5a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H4.414l.94.94a.5.5 0 0 1 0 .706l-.708.708Z"
  })));
};
var _path$W;
function _extends$1c() {
  _extends$1c = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1c.apply(this, arguments);
}
var SvgCopyRight = function SvgCopyRight(props) {
  return /*#__PURE__*/createElement("svg", _extends$1c({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$W || (_path$W = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M4 3a2 2 0 0 1 2-2h5.5a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-5a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h5a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H6a2 2 0 0 1-2-2V3ZM2.5 3a.5.5 0 0 1 .5.5v9a.5.5 0 0 1-.5.5H2a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h.5Zm8.854 1.646a.5.5 0 0 1 .707 0l3 3a.5.5 0 0 1 0 .707l-3 3a.5.5 0 0 1-.707 0l-.708-.707a.5.5 0 0 1 0-.707l.94-.939H8.5a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3.086l-.94-.94a.5.5 0 0 1 0-.706l.707-.708Z"
  })));
};
var _path$X;
function _extends$1d() {
  _extends$1d = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1d.apply(this, arguments);
}
var SvgCopyUp = function SvgCopyUp(props) {
  return /*#__PURE__*/createElement("svg", _extends$1d({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$X || (_path$X = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M3 12a2 2 0 0 1-2-2V4.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v5a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5v-5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5V10a2 2 0 0 1-2 2H3Zm0 1.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 .5.5v.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-.5Zm1.646-8.854a.5.5 0 0 1 0-.707l3-3a.5.5 0 0 1 .707 0l3 3a.5.5 0 0 1 0 .707l-.707.708a.5.5 0 0 1-.707 0L9 4.414V7.5a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5V4.414l-.94.94a.5.5 0 0 1-.706 0l-.708-.708Z"
  })));
};
var _path$Y, _path2$g;
function _extends$1e() {
  _extends$1e = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1e.apply(this, arguments);
}
var SvgCopy = function SvgCopy(props) {
  return /*#__PURE__*/createElement("svg", _extends$1e({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$Y || (_path$Y = /*#__PURE__*/createElement("path", {
    d: "M2.75 1A1.75 1.75 0 0 0 1 2.75v7c0 .322.261.583.583.583H2.75a.583.583 0 0 0 .583-.583V3.917c0-.322.261-.584.584-.584H9.75a.583.583 0 0 0 .583-.583V1.583A.583.583 0 0 0 9.75 1h-7Z"
  })), _path2$g || (_path2$g = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M6.25 4.5A1.75 1.75 0 0 0 4.5 6.25v7c0 .966.784 1.75 1.75 1.75h7A1.75 1.75 0 0 0 15 13.25v-7a1.75 1.75 0 0 0-1.75-1.75h-7Zm.583 2.917c0-.322.261-.584.584-.584h4.666c.322 0 .584.261.584.584v4.666a.583.583 0 0 1-.584.584H7.417a.583.583 0 0 1-.584-.584V7.417Z"
  })));
};
var _path$Z, _path2$h;
function _extends$1f() {
  _extends$1f = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1f.apply(this, arguments);
}
var SvgDashboard = function SvgDashboard(props) {
  return /*#__PURE__*/createElement("svg", _extends$1f({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$Z || (_path$Z = /*#__PURE__*/createElement("path", {
    d: "M10.806 6.804 7.267 9.081C5.86 9.99 5.88 10.803 6.14 11.329c.106.21.243.403.405.574.103.107.22.2.347.277.198.137.434.21.675.21h.28a2.883 2.883 0 0 0 1.533-1.425l2.053-3.51a.447.447 0 0 0-.332-.692.456.456 0 0 0-.294.07v-.029Z"
  })), _path2$h || (_path2$h = /*#__PURE__*/createElement("path", {
    d: "M7.99 2.5a7.085 7.085 0 0 0-4.967 2.095A6.973 6.973 0 0 0 1 9.559a7.168 7.168 0 0 0 .964 3.549.816.816 0 0 0 .54.382h.154a.881.881 0 0 0 .521-.181.955.955 0 0 0 .212-1.158 5.437 5.437 0 0 1-.665-2.601 5.277 5.277 0 0 1 1.53-3.752c.99-1 2.34-1.57 3.754-1.586a5.206 5.206 0 0 1 3.056.957.891.891 0 0 0 1.138 0 .851.851 0 0 0 .28-.67.78.78 0 0 0-.319-.593A6.89 6.89 0 0 0 7.99 2.5ZM14.402 6.756a.766.766 0 0 0-.511-.449.864.864 0 0 0-.733.143l-.135.125a.952.952 0 0 0-.144.956 5.394 5.394 0 0 1-.27 4.62.95.95 0 0 0 .212 1.167c.149.115.332.179.52.182h.155a.823.823 0 0 0 .54-.383A7.169 7.169 0 0 0 15 9.57a6.985 6.985 0 0 0-.598-2.813Z"
  })));
};
var _path$_;
function _extends$1g() {
  _extends$1g = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1g.apply(this, arguments);
}
var SvgDefense = function SvgDefense(props) {
  return /*#__PURE__*/createElement("svg", _extends$1g({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$_ || (_path$_ = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M1.51 3.488a10.525 10.525 0 0 0 6.197-2.383.46.46 0 0 1 .586 0 10.526 10.526 0 0 0 6.198 2.384c.21.01.39.163.42.374.059.448.089.905.089 1.368 0 4.47-2.854 8.28-6.857 9.744a.415.415 0 0 1-.286 0C3.854 13.509 1 9.7 1 5.23c0-.463.03-.92.09-1.367a.439.439 0 0 1 .42-.375Zm9.35 2.362a.447.447 0 0 0-.62 0L7.25 8.748l-1.34-1.3a.447.447 0 0 0-.62 0l-.48.467a.415.415 0 0 0 0 .6l2.13 2.064a.443.443 0 0 0 .307.124c.113.001.226-.04.312-.124l3.781-3.663a.415.415 0 0 0 0-.6l-.48-.466Z"
  })));
};
var _path$$;
function _extends$1h() {
  _extends$1h = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1h.apply(this, arguments);
}
var SvgDown = function SvgDown(props) {
  return /*#__PURE__*/createElement("svg", _extends$1h({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 10 10"
  }, props), _path$$ || (_path$$ = /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    d: "m5.384 8.04 3.933-4.72a.5.5 0 0 0-.384-.82H1.067a.5.5 0 0 0-.385.82l3.933 4.72a.5.5 0 0 0 .768-.001z"
  })));
};
var _path$10;
function _extends$1i() {
  _extends$1i = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1i.apply(this, arguments);
}
var SvgDownload = function SvgDownload(props) {
  return /*#__PURE__*/createElement("svg", _extends$1i({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$10 || (_path$10 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M10.625 5.375V1.5a.5.5 0 0 0-.5-.5h-4.25a.5.5 0 0 0-.5.5v3.875H3.082a.5.5 0 0 0-.353.854l4.917 4.917a.5.5 0 0 0 .708 0L13.27 6.23a.5.5 0 0 0-.353-.854h-2.293ZM1 14.5a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-.75a.5.5 0 0 0-.5-.5h-13a.5.5 0 0 0-.5.5v.75Z"
  })));
};
var _path$11, _path2$i, _path3$5;
function _extends$1j() {
  _extends$1j = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1j.apply(this, arguments);
}
var SvgDsp = function SvgDsp(props) {
  return /*#__PURE__*/createElement("svg", _extends$1j({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$11 || (_path$11 = /*#__PURE__*/createElement("path", {
    d: "M1.583 1A.583.583 0 0 0 1 1.583V6.25c0 .322.261.583.583.583H6.25a.583.583 0 0 0 .583-.583V1.583A.583.583 0 0 0 6.25 1H1.583Z"
  })), _path2$i || (_path2$i = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M1 9.75c0-.322.261-.583.583-.583H6.25c.322 0 .583.26.583.583v4.667A.583.583 0 0 1 6.25 15H1.583A.583.583 0 0 1 1 14.417V9.75Zm1.75 1.458c0-.16.13-.291.292-.291h1.75c.16 0 .291.13.291.291v1.75c0 .161-.13.292-.291.292h-1.75a.292.292 0 0 1-.292-.292v-1.75ZM9.75 1a.583.583 0 0 0-.583.583V6.25c0 .322.26.583.583.583h4.667A.583.583 0 0 0 15 6.25V1.583A.583.583 0 0 0 14.417 1H9.75Zm1.458 1.75a.292.292 0 0 0-.291.292v1.75c0 .16.13.291.291.291h1.75c.161 0 .292-.13.292-.291v-1.75a.292.292 0 0 0-.292-.292h-1.75Z"
  })), _path3$5 || (_path3$5 = /*#__PURE__*/createElement("path", {
    d: "M9.75 9.167a.583.583 0 0 0-.583.583v4.667c0 .322.26.583.583.583h4.667a.583.583 0 0 0 .583-.583V9.75a.583.583 0 0 0-.583-.583H9.75Z"
  })));
};
var _path$12;
function _extends$1k() {
  _extends$1k = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1k.apply(this, arguments);
}
var SvgEditBid = function SvgEditBid(props) {
  return /*#__PURE__*/createElement("svg", _extends$1k({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$12 || (_path$12 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8.723 15v-1.176c.473-.01.932-.091 1.378-.242.446-.15.843-.37 1.192-.66.35-.29.628-.653.838-1.088.21-.435.314-.947.314-1.538 0-.58-.113-1.064-.338-1.45a3.319 3.319 0 0 0-.87-.983 4.624 4.624 0 0 0-1.176-.644c-.43-.161-.854-.306-1.273-.435V4.206c.344.01.703.091 1.08.242.375.15.697.36.966.628.516-.57 1.036-1.155 1.563-1.756a4.995 4.995 0 0 0-1.724-.902 7.46 7.46 0 0 0-1.885-.322V1H7.612v1.112c-.419.021-.843.11-1.273.265-.43.156-.816.376-1.16.661a3.343 3.343 0 0 0-.838 1.047 3.013 3.013 0 0 0-.322 1.41c0 .537.097.99.29 1.361.194.37.451.685.773.943.323.257.696.467 1.12.628.424.161.867.306 1.33.435v2.82c-.183 0-.377-.03-.58-.09a4.07 4.07 0 0 1-1.209-.58 2.677 2.677 0 0 1-.5-.442c-.279.3-.56.598-.845.894l-.846.878c.537.483 1.16.846 1.869 1.087a7.2 7.2 0 0 0 2.11.395V15h1.192ZM7.612 6.51c-.344-.086-.618-.22-.822-.403-.204-.183-.306-.446-.306-.79 0-.354.116-.625.347-.813.23-.188.491-.298.781-.33V6.51Zm1.111 5.187V9.152c.344.107.626.255.846.443.22.188.33.47.33.846s-.104.674-.314.894c-.21.22-.496.34-.862.362Z"
  })));
};
var _path$13;
function _extends$1l() {
  _extends$1l = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1l.apply(this, arguments);
}
var SvgEdit = function SvgEdit(props) {
  return /*#__PURE__*/createElement("svg", _extends$1l({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$13 || (_path$13 = /*#__PURE__*/createElement("path", {
    d: "M11.83 1.965a1 1 0 0 1 1.415 0l.707.707a1 1 0 0 1 0 1.415l-.707.707-2.122-2.122.707-.707ZM10.416 3.38l2.121 2.12-5.657 5.658-2.12-2.122 5.656-5.657ZM6.173 11.865l-2.12-2.122-1.297 1.297a.5.5 0 0 0-.142.283l-.283 1.98a.25.25 0 0 0 .283.282l1.98-.283a.5.5 0 0 0 .283-.141l1.296-1.296Z"
  })));
};
var _path$14;
function _extends$1m() {
  _extends$1m = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1m.apply(this, arguments);
}
var SvgEmail = function SvgEmail(props) {
  return /*#__PURE__*/createElement("svg", _extends$1m({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$14 || (_path$14 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M2 2.75a1 1 0 0 0-1 1v8.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8.5a1 1 0 0 0-1-1H2Zm2.425 2.205a.875.875 0 0 0-1.017 1.424l4.084 2.916a.875.875 0 0 0 1.017 0l4.083-2.916a.875.875 0 0 0-1.017-1.424L8 7.508 4.425 4.955Z"
  })));
};
var _path$15;
function _extends$1n() {
  _extends$1n = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1n.apply(this, arguments);
}
var SvgError = function SvgError(props) {
  return /*#__PURE__*/createElement("svg", _extends$1n({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$15 || (_path$15 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0ZM8.92 6.17l-.484 3.834h-.884L7.079 6.17V3.78h1.842v2.39Zm-1.89 4.58v1.917h1.953V10.75H7.03Z"
  })));
};
var _path$16;
function _extends$1o() {
  _extends$1o = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1o.apply(this, arguments);
}
var SvgExperiments = function SvgExperiments(props) {
  return /*#__PURE__*/createElement("svg", _extends$1o({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$16 || (_path$16 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M12.342 1c.218 0 .395.177.395.395l-.007.96a.395.395 0 0 1-.395.395h-.394l.006 3.813c0 .14.038.278.108.398l.059.087 2.22 2.855a3.158 3.158 0 0 1-2.301 5.091l-.191.006H4.158a3.158 3.158 0 0 1-2.605-4.943l.112-.154 2.221-2.855a.79.79 0 0 0 .16-.38l.006-.105-.007-3.813h-.394a.395.395 0 0 1-.395-.395l.007-.96c0-.218.177-.395.395-.395h8.684ZM10.37 2.75H5.63v3.235c0 .41-.106.81-.305 1.163l-.091.149-.102.142-1.487 1.91c1.173-.416 2.532-.34 4.08.23L8 9.685c2.132.852 3.918 1.234 5.36 1.144-.04-.142-.101-.28-.183-.409l-.089-.126-2.22-2.855a2.368 2.368 0 0 1-.493-1.28l-.006-.174V2.75Z"
  })));
};
var _path$17;
function _extends$1p() {
  _extends$1p = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1p.apply(this, arguments);
}
var SvgEngineVisibility = function SvgEngineVisibility(props) {
  return /*#__PURE__*/createElement("svg", _extends$1p({
    width: 16,
    height: 16,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$17 || (_path$17 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M9.59 15a.5.5 0 0 0 .5-.5v-3.45l.88-.4a5.17 5.17 0 0 0 2.2-1.875C13.722 7.942 14 7.017 14 6c0-1.383-.476-2.562-1.43-3.537C11.618 1.488 10.466 1 9.113 1 7.81 1 5.875 1 4.262 2.463c-.936.917-1.445 1.775-1.527 3.125l-1.19 1.715a.5.5 0 0 0 .41.785h.78v2.931a.5.5 0 0 0 .389.488l2.544.581.715 2.547a.5.5 0 0 0 .482.365H9.59ZM7.964 3.112c.155-.21.5-.105.5.151v2.255h2.26c.223 0 .354.24.225.414L8.036 9.888c-.155.21-.5.105-.5-.152V7.482h-2.26c-.223 0-.354-.24-.225-.415l2.913-3.955Z"
  })));
};
var _path$18;
function _extends$1q() {
  _extends$1q = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1q.apply(this, arguments);
}
var SvgFilter = function SvgFilter(props) {
  return /*#__PURE__*/createElement("svg", _extends$1q({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$18 || (_path$18 = /*#__PURE__*/createElement("path", {
    d: "M1 2.6c0-.331.261-.6.583-.6h12.834c.322 0 .583.269.583.6v1.2c0 .331-.261.6-.583.6H1.583A.592.592 0 0 1 1 3.8V2.6ZM3.333 7.4c0-.331.261-.6.584-.6h8.166c.322 0 .584.269.584.6v1.2c0 .331-.261.6-.584.6H3.917a.592.592 0 0 1-.584-.6V7.4ZM6.25 11.6a.592.592 0 0 0-.583.6v1.2c0 .331.26.6.583.6h3.5a.592.592 0 0 0 .583-.6v-1.2c0-.331-.26-.6-.583-.6h-3.5Z"
  })));
};
var _path$19;
function _extends$1r() {
  _extends$1r = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1r.apply(this, arguments);
}
var SvgFullscreenClose = function SvgFullscreenClose(props) {
  return /*#__PURE__*/createElement("svg", _extends$1r({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$19 || (_path$19 = /*#__PURE__*/createElement("path", {
    d: "M14.842 2.684a.54.54 0 0 0 0-.763l-.763-.763a.54.54 0 0 0-.762 0l-2.079 2.08V2.066a.54.54 0 0 0-.54-.54H9.62a.54.54 0 0 0-.539.54v4.316c0 .298.242.54.54.54h4.313a.54.54 0 0 0 .54-.54v-1.08a.54.54 0 0 0-.54-.539h-1.17l2.078-2.08ZM1.532 2.066a.54.54 0 0 1 .539-.54h4.314a.54.54 0 0 1 .54.54v1.079a.54.54 0 0 1-.54.54H4.228a.54.54 0 0 0-.54.539v2.158a.54.54 0 0 1-.539.54H2.071a.54.54 0 0 1-.54-.54V2.066ZM13.934 9.079a.54.54 0 0 1 .54.54v4.315a.54.54 0 0 1-.54.54H9.62a.54.54 0 0 1-.539-.54v-1.079a.54.54 0 0 1 .54-.54h2.156a.54.54 0 0 0 .54-.539V9.618a.54.54 0 0 1 .539-.54h1.078ZM1.526 9.618a.54.54 0 0 1 .54-.54H6.38a.54.54 0 0 1 .539.54v4.316a.54.54 0 0 1-.54.54H5.302a.54.54 0 0 1-.54-.54v-1.171l-2.078 2.079a.54.54 0 0 1-.762 0l-.763-.763a.54.54 0 0 1 0-.763l2.079-2.08H2.066a.54.54 0 0 1-.54-.539V9.618Z"
  })));
};
var _path$1a;
function _extends$1s() {
  _extends$1s = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1s.apply(this, arguments);
}
var SvgFullscreenOpen = function SvgFullscreenOpen(props) {
  return /*#__PURE__*/createElement("svg", _extends$1s({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1a || (_path$1a = /*#__PURE__*/createElement("path", {
    d: "M1 1.583C1 1.261 1.261 1 1.583 1H6.25c.322 0 .583.261.583.583V2.75a.583.583 0 0 1-.583.583H3.917a.583.583 0 0 0-.584.584V6.25a.583.583 0 0 1-.583.583H1.583A.583.583 0 0 1 1 6.25V1.583ZM14.417 9.167c.322 0 .583.26.583.583v4.667a.583.583 0 0 1-.583.583H9.75a.583.583 0 0 1-.583-.583V13.25c0-.322.26-.583.583-.583h2.333a.583.583 0 0 0 .584-.584V9.75c0-.322.26-.583.583-.583h1.167ZM9.167 1.583c0-.322.26-.583.583-.583h4.667c.322 0 .583.261.583.583V6.25a.583.583 0 0 1-.583.583H13.25a.583.583 0 0 1-.583-.583V4.983l-5.425 5.424a.494.494 0 0 1-.01.011l-2.249 2.249H6.25c.322 0 .583.26.583.583v1.167A.583.583 0 0 1 6.25 15H1.583A.583.583 0 0 1 1 14.417V9.75c0-.322.261-.583.583-.583H2.75c.322 0 .583.26.583.583v1.267l1.215-1.215a.602.602 0 0 1 .011-.011l6.458-6.458H9.75a.583.583 0 0 1-.583-.583V1.583Z"
  })));
};
var _path$1b;
function _extends$1t() {
  _extends$1t = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1t.apply(this, arguments);
}
var SvgFunnel = function SvgFunnel(props) {
  return /*#__PURE__*/createElement("svg", _extends$1t({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1b || (_path$1b = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M1.043 3.323C.795 2.131 1.644 1 2.787 1h10.426c1.143 0 1.992 1.131 1.744 2.323l-2.118 10.182c-.181.873-.906 1.495-1.744 1.495h-6.19c-.838 0-1.563-.621-1.744-1.495L1.043 3.323Zm2.794.222a.25.25 0 0 0-.245.301l.227 1.091a.25.25 0 0 0 .245.2h7.872a.25.25 0 0 0 .245-.2l.227-1.09a.25.25 0 0 0-.245-.302H3.837Zm1.02 5.41a.25.25 0 0 1-.244-.2L4.32 7.346a.25.25 0 0 1 .245-.3h6.87a.25.25 0 0 1 .245.3l-.293 1.41a.25.25 0 0 1-.245.199H4.858Zm.502 1.909a.25.25 0 0 0-.245.3l.227 1.091a.25.25 0 0 0 .245.2h4.828a.25.25 0 0 0 .245-.2l.227-1.09a.25.25 0 0 0-.245-.301H5.36Z"
  })));
};
var _path$1c, _path2$j;
function _extends$1u() {
  _extends$1u = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1u.apply(this, arguments);
}
var SvgGift = function SvgGift(props) {
  return /*#__PURE__*/createElement("svg", _extends$1u({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1c || (_path$1c = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M5.375 1a2.614 2.614 0 0 1 2.62 2.447L8 3.6C8 2.164 9.175 1 10.625 1h.875c.966 0 1.75.776 1.75 1.733 0 .958-.784 1.734-1.75 1.734h-7c-.966 0-1.75-.776-1.75-1.734a1.74 1.74 0 0 1 1.62-1.728L4.5 1h.875Zm.13.871-.13-.004H4.5a.87.87 0 0 0-.875.866c0 .445.338.811.773.861L4.5 3.6h2.625l-.005-.13a1.74 1.74 0 0 0-1.48-1.584l-.134-.015Zm5.995-.004h-.875c-.923 0-1.678.707-1.745 1.604l-.005.129H11.5a.872.872 0 0 0 .87-.766l.005-.1a.87.87 0 0 0-.773-.861l-.102-.006Z"
  })), _path2$j || (_path2$j = /*#__PURE__*/createElement("path", {
    d: "M1.583 5.333A.58.58 0 0 0 1 5.911v1.156h6.125V5.333H1.583ZM8.875 5.333v1.734H15V5.91a.58.58 0 0 0-.583-.578H8.875ZM1.875 12.844V8.8h5.25V14H3.042a1.161 1.161 0 0 1-1.167-1.156ZM8.875 14V8.8h5.25v4.044c0 .639-.522 1.156-1.167 1.156H8.875Z"
  })));
};
var _path$1d;
function _extends$1v() {
  _extends$1v = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1v.apply(this, arguments);
}
var SvgGoal = function SvgGoal(props) {
  return /*#__PURE__*/createElement("svg", _extends$1v({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1d || (_path$1d = /*#__PURE__*/createElement("path", {
    d: "M3.625 1.583A.583.583 0 0 0 3.042 1h-.584a.583.583 0 0 0-.583.583v12.542A.875.875 0 0 0 1 15h3.5a.875.875 0 0 0-.875-.875V1.583ZM7.882 9.75H4.5V1.875h4.593c.418 0 .777.295.858.705l.067.34c.081.41.44.705.859.705h3.685c.242 0 .438.196.438.438v7a.438.438 0 0 1-.438.437H9.666a.875.875 0 0 1-.858-.705l-.067-.34a.875.875 0 0 0-.859-.705Z"
  })));
};
var _path$1e, _path2$k, _path3$6;
function _extends$1w() {
  _extends$1w = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1w.apply(this, arguments);
}
var SvgGroup = function SvgGroup(props) {
  return /*#__PURE__*/createElement("svg", _extends$1w({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1e || (_path$1e = /*#__PURE__*/createElement("path", {
    d: "M8.323 1.07 14.782 4a.361.361 0 0 1 0 .664l-6.46 2.931a.783.783 0 0 1-.645 0L1.218 4.665a.361.361 0 0 1 0-.664l6.46-2.931a.783.783 0 0 1 .645 0Z"
  })), _path2$k || (_path2$k = /*#__PURE__*/createElement("path", {
    d: "M12.848 7.01a.583.583 0 0 1 .482 0l1.452.658a.361.361 0 0 1 0 .664l-6.46 2.931a.783.783 0 0 1-.645 0L1.218 8.332a.361.361 0 0 1 0-.664l1.45-.659a.583.583 0 0 1 .483 0l4.526 2.054c.17.078.363.09.541.039l.105-.039 4.525-2.054Z"
  })), _path3$6 || (_path3$6 = /*#__PURE__*/createElement("path", {
    d: "m14.782 11.335-1.452-.659a.584.584 0 0 0-.482 0L8.323 12.73l-.105.039a.783.783 0 0 1-.54-.039L3.15 10.676a.583.583 0 0 0-.482 0l-1.45.66a.362.362 0 0 0 0 .664l6.458 2.93c.205.093.441.093.646 0L14.782 12a.361.361 0 0 0 0-.664Z"
  })));
};
var _path$1f;
function _extends$1x() {
  _extends$1x = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1x.apply(this, arguments);
}
var SvgHashtag = function SvgHashtag(props) {
  return /*#__PURE__*/createElement("svg", _extends$1x({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1f || (_path$1f = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M7.394 2.424a.8.8 0 0 1 .582.97L7.624 4.8h2.352l.448-1.794a.8.8 0 1 1 1.552.388L11.624 4.8H13.6a.8.8 0 1 1 0 1.6h-2.376l-.8 3.2H12a.8.8 0 1 1 0 1.6h-1.976l-.448 1.794a.8.8 0 1 1-1.552-.388l.352-1.406H6.024l-.448 1.794a.8.8 0 1 1-1.552-.388l.352-1.406H2.4a.8.8 0 0 1 0-1.6h2.376l.8-3.2H4a.8.8 0 1 1 0-1.6h1.976l.448-1.794a.8.8 0 0 1 .97-.582ZM7.224 6.4l-.8 3.2h2.35l.8-3.2h-2.35Z"
  })));
};
var _path$1g, _path2$l;
function _extends$1y() {
  _extends$1y = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1y.apply(this, arguments);
}
var SvgHide = function SvgHide(props) {
  return /*#__PURE__*/createElement("svg", _extends$1y({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1g || (_path$1g = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M2.966 1.834a.8.8 0 0 0-1.132 1.132l11.2 11.2a.8.8 0 0 0 1.132-1.132l-1.179-1.178A8.01 8.01 0 0 0 15.634 8 8.003 8.003 0 0 0 4.39 3.26L2.966 1.833Zm3.408 3.408 1.212 1.212a1.602 1.602 0 0 1 1.96 1.96l1.21 1.212a3.2 3.2 0 0 0-4.382-4.384Z"
  })), _path2$l || (_path2$l = /*#__PURE__*/createElement("path", {
    d: "M9.963 13.358 7.8 11.194A3.2 3.2 0 0 1 4.806 8.2L1.868 5.262A7.984 7.984 0 0 0 .366 8a8.004 8.004 0 0 0 9.597 5.358Z"
  })));
};
var _path$1h;
function _extends$1z() {
  _extends$1z = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1z.apply(this, arguments);
}
var SvgHome = function SvgHome(props) {
  return /*#__PURE__*/createElement("svg", _extends$1z({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1h || (_path$1h = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M11.281 15a.663.663 0 0 1-.656-.669V11.21a.663.663 0 0 0-.656-.67H6.03c-.362 0-.656.3-.656.67v3.121c0 .37-.294.669-.656.669H1.656A.663.663 0 0 1 1 14.331V6.203c0-.448.22-.865.585-1.114l5.689-3.864c.44-.3 1.015-.3 1.456 0l5.686 3.864c.365.249.584.666.584 1.114v8.128c0 .37-.294.669-.656.669H11.28Z"
  })));
};
var _path$1i;
function _extends$1A() {
  _extends$1A = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1A.apply(this, arguments);
}
var SvgImage = function SvgImage(props) {
  return /*#__PURE__*/createElement("svg", _extends$1A({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1i || (_path$1i = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M15 12.5v-9c0-.825-.573-1.5-1.273-1.5H2.273C1.573 2 1 2.675 1 3.5v9c0 .825.573 1.5 1.273 1.5h11.454c.7 0 1.273-.675 1.273-1.5ZM5.008 7.557a.442.442 0 0 1 .753-.01L6.864 9.31a.442.442 0 0 0 .753-.011l1.871-3.208a.442.442 0 0 1 .763.005l2.642 4.696a.429.429 0 0 1-.384.636H3.504a.429.429 0 0 1-.38-.642l1.884-3.23Z"
  })));
};
var _path$1j;
function _extends$1B() {
  _extends$1B = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1B.apply(this, arguments);
}
var SvgInfo = function SvgInfo(props) {
  return /*#__PURE__*/createElement("svg", _extends$1B({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1j || (_path$1j = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0ZM5.115 6.699c.008-.422.08-.81.217-1.161.137-.352.328-.657.575-.915.246-.258.543-.46.89-.604a3.013 3.013 0 0 1 1.168-.217c.555 0 1.018.077 1.39.229a2.7 2.7 0 0 1 .897.569 2.106 2.106 0 0 1 .633 1.466c0 .375-.049.684-.147.926a2.285 2.285 0 0 1-.844 1.061c-.176.122-.342.243-.499.364-.156.121-.295.26-.416.416a1.196 1.196 0 0 0-.229.587v.445H7.167v-.527a2.4 2.4 0 0 1 .194-.845c.105-.226.228-.42.37-.58.14-.16.288-.3.445-.417.156-.117.3-.234.434-.351.133-.118.24-.247.322-.387a.96.96 0 0 0 .112-.528c0-.36-.088-.626-.264-.798-.176-.172-.42-.258-.733-.258-.211 0-.393.041-.545.124a1.088 1.088 0 0 0-.376.328c-.097.137-.17.297-.217.48-.047.184-.07.382-.07.593H5.115Zm1.888 3.87h1.841v1.806h-1.84v-1.806Z"
  })));
};
var _path$1k;
function _extends$1C() {
  _extends$1C = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1C.apply(this, arguments);
}
var SvgKeywordBoost = function SvgKeywordBoost(props) {
  return /*#__PURE__*/createElement("svg", _extends$1C({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1k || (_path$1k = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M2.325 4.094A.5.5 0 0 0 2 4.563v2.682a.5.5 0 0 0 .676.468l5.148-1.931a.5.5 0 0 1 .352 0l5.148 1.93A.5.5 0 0 0 14 7.246V4.562a.5.5 0 0 0-.324-.468L8.184 2.032a.5.5 0 0 0-.35 0l-5.51 2.062Zm0 6a.5.5 0 0 0-.325.469v2.681a.5.5 0 0 0 .676.469l5.148-1.931a.5.5 0 0 1 .352 0l5.148 1.93a.5.5 0 0 0 .676-.467v-2.683a.5.5 0 0 0-.324-.468L8.184 8.032a.5.5 0 0 0-.35 0l-5.51 2.062Z"
  })));
};
var _path$1l;
function _extends$1D() {
  _extends$1D = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1D.apply(this, arguments);
}
var SvgKeyword = function SvgKeyword(props) {
  return /*#__PURE__*/createElement("svg", _extends$1D({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1l || (_path$1l = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M14.44 8.609a5.254 5.254 0 0 0 .082-4.548 5.252 5.252 0 0 0-5.543-3.004 5.249 5.249 0 0 0-4.254 6.716L1 11.499V15h3.5v-1.75h1.75l1.975-1.976a5.248 5.248 0 0 0 6.215-2.665Zm-3.523-2.557a.972.972 0 1 0 0-1.944.972.972 0 0 0 0 1.944Z"
  })));
};
var _path$1m;
function _extends$1E() {
  _extends$1E = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1E.apply(this, arguments);
}
var SvgLanguage = function SvgLanguage(props) {
  return /*#__PURE__*/createElement("svg", _extends$1E({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1m || (_path$1m = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M2.823 7.125h1.702c.078-1.353.335-2.599.733-3.603a5.253 5.253 0 0 0-2.435 3.603ZM8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm0 1.75c-.066 0-.203.028-.407.23-.208.204-.435.544-.645 1.033-.34.794-.589 1.875-.67 3.112h3.444c-.081-1.237-.33-2.318-.67-3.112-.21-.49-.438-.829-.645-1.034-.204-.201-.34-.229-.407-.229Zm3.475 4.375c-.078-1.353-.335-2.599-.733-3.603a5.254 5.254 0 0 1 2.435 3.603h-1.702Zm-1.753 1.75H6.278c.081 1.237.33 2.318.67 3.111.21.49.438.83.645 1.035.204.201.34.229.407.229.066 0 .203-.028.407-.23.208-.204.436-.544.645-1.034.34-.793.589-1.874.67-3.111Zm1.02 3.603c.397-1.003.655-2.25.733-3.603h1.702a5.254 5.254 0 0 1-2.435 3.603Zm-5.484 0c-.397-1.004-.655-2.25-.732-3.603H2.823a5.253 5.253 0 0 0 2.435 3.603Z"
  })));
};
var _path$1n;
function _extends$1F() {
  _extends$1F = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1F.apply(this, arguments);
}
var SvgLegal = function SvgLegal(props) {
  return /*#__PURE__*/createElement("svg", _extends$1F({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1n || (_path$1n = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8 1a.875.875 0 0 1 .875.875v1.158l3.46 1.384 1.398-.7a.875.875 0 0 1 .783 1.566l-1.08.54 1.522 4.742a.874.874 0 0 1-.25.918 3.49 3.49 0 0 1-2.333.892 3.49 3.49 0 0 1-2.334-.892.875.875 0 0 1-.25-.918l1.501-4.68-2.417-.968v8.333h1.75a.875.875 0 0 1 0 1.75h-5.25a.875.875 0 0 1 0-1.75h1.75V4.917l-2.418.967 1.5 4.68a.875.875 0 0 1-.249.92 3.49 3.49 0 0 1-2.333.891 3.49 3.49 0 0 1-2.334-.892.875.875 0 0 1-.25-.918l1.521-4.743-1.078-.54a.875.875 0 0 1 .782-1.564l1.399.699 3.46-1.384V1.875A.875.875 0 0 1 8 1ZM3.625 8.24l-.716 2.233c.219.098.46.152.716.152.255 0 .498-.054.715-.152L3.625 8.24Zm8.75 0-.716 2.233c.219.098.46.152.716.152.255 0 .498-.054.715-.152l-.715-2.233Z"
  })));
};
var _path$1o, _path2$m;
function _extends$1G() {
  _extends$1G = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1G.apply(this, arguments);
}
var SvgLightbulb = function SvgLightbulb(props) {
  return /*#__PURE__*/createElement("svg", _extends$1G({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1o || (_path$1o = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M10.75 10.798c1.644-.908 2.75-2.605 2.75-4.548C13.5 3.35 11.038 1 8 1S2.5 3.35 2.5 6.25c0 1.943 1.106 3.64 2.75 4.548v1.14c0 .241.205.437.458.437h4.584a.448.448 0 0 0 .458-.438v-1.14ZM3.875 6.25c0-2.175 1.915-3.938 4.278-3.938.262 0 .475.196.475.438s-.213.438-.475.438c-1.838 0-3.327 1.37-3.327 3.062 0 .242-.213.438-.476.438-.262 0-.475-.196-.475-.438Z"
  })), _path2$m || (_path2$m = /*#__PURE__*/createElement("path", {
    d: "M5.25 13.688c0-.242.205-.438.458-.438h4.584c.253 0 .458.196.458.438v.874a.448.448 0 0 1-.458.438H5.708a.448.448 0 0 1-.458-.438v-.874Z"
  })));
};
var _path$1p;
function _extends$1H() {
  _extends$1H = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1H.apply(this, arguments);
}
var SvgLocation = function SvgLocation(props) {
  return /*#__PURE__*/createElement("svg", _extends$1H({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1p || (_path$1p = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M4.465 2.463a4.994 4.994 0 0 0-.819 5.99l3.376 5.976c.43.761 1.526.761 1.956 0l3.376-5.976a4.994 4.994 0 0 0-.819-5.99 5.002 5.002 0 0 0-7.07 0ZM8 8.503A2.873 2.873 0 1 0 5.126 5.63 2.873 2.873 0 0 0 8 8.503Z"
  })));
};
var _path$1q;
function _extends$1I() {
  _extends$1I = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1I.apply(this, arguments);
}
var SvgLock = function SvgLock(props) {
  return /*#__PURE__*/createElement("svg", _extends$1I({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1q || (_path$1q = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M4 5v1H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-1V5a4 4 0 0 0-8 0Zm4-2a2 2 0 0 0-2 2v1h4V5a2 2 0 0 0-2-2Zm1 6a1 1 0 0 1-.5.866v1.884a.25.25 0 0 1-.25.25h-.5a.25.25 0 0 1-.25-.25V9.866A1 1 0 1 1 9 9Z"
  })));
};
var _path$1r, _path2$n;
function _extends$1J() {
  _extends$1J = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1J.apply(this, arguments);
}
var SvgMarketShare = function SvgMarketShare(props) {
  return /*#__PURE__*/createElement("svg", _extends$1J({
    fill: "none",
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1r || (_path$1r = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M13.8.4a1 1 0 0 1-.2 1.4l-4 3a1 1 0 0 1-1.047.094L5.106 3.171 1.6 5.8A1 1 0 1 1 .4 4.2l4-3a1 1 0 0 1 1.047-.094l3.447 1.723L12.4.2a1 1 0 0 1 1.4.2Z"
  })), _path2$n || (_path2$n = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M13.8 4.4a1 1 0 0 1-.2 1.4l-4 3a1 1 0 0 1-1.047.094L5.106 7.171 1.6 9.8A1 1 0 1 1 .4 8.2l4-3a1 1 0 0 1 1.047-.094l3.447 1.723L12.4 4.2a1 1 0 0 1 1.4.2ZM0 13a1 1 0 0 1 1-1h12a1 1 0 0 1 0 2H1a1 1 0 0 1-1-1Z"
  })));
};
var _path$1s;
function _extends$1K() {
  _extends$1K = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1K.apply(this, arguments);
}
var SvgMegaphone = function SvgMegaphone(props) {
  return /*#__PURE__*/createElement("svg", _extends$1K({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1s || (_path$1s = /*#__PURE__*/createElement("path", {
    d: "M15 1.875a.875.875 0 0 0-1.266-.783L6.918 4.5H3.625a2.625 2.625 0 1 0 0 5.25h.244l1.55 4.652c.12.357.454.598.831.598h.875A.875.875 0 0 0 8 14.125v-3.834l5.734 2.867A.875.875 0 0 0 15 12.375v-10.5Z"
  })));
};
var _path$1t;
function _extends$1L() {
  _extends$1L = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1L.apply(this, arguments);
}
var SvgMenu = function SvgMenu(props) {
  return /*#__PURE__*/createElement("svg", _extends$1L({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1t || (_path$1t = /*#__PURE__*/createElement("path", {
    d: "M1 3.5c0-.276.261-.5.583-.5h12.834c.322 0 .583.224.583.5v1c0 .276-.261.5-.583.5H1.583C1.261 5 1 4.776 1 4.5v-1ZM1 7.5c0-.276.261-.5.583-.5h12.834c.322 0 .583.224.583.5v1c0 .276-.261.5-.583.5H1.583C1.261 9 1 8.776 1 8.5v-1ZM1.583 11c-.322 0-.583.224-.583.5v1c0 .276.261.5.583.5h12.834c.322 0 .583-.224.583-.5v-1c0-.276-.261-.5-.583-.5H1.583Z"
  })));
};
var _path$1u, _path2$o;
function _extends$1M() {
  _extends$1M = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1M.apply(this, arguments);
}
var SvgMiddleOfFunnel = function SvgMiddleOfFunnel(props) {
  return /*#__PURE__*/createElement("svg", _extends$1M({
    width: 16,
    height: 16,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1u || (_path$1u = /*#__PURE__*/createElement("path", {
    d: "M15 1H1a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1ZM11 10.998H5a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1Z",
    fill: "#A5B4FC"
  })), _path2$o || (_path2$o = /*#__PURE__*/createElement("path", {
    d: "M13 6H3a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1Z",
    fill: "#4338CA"
  })));
};
var _path$1v;
function _extends$1N() {
  _extends$1N = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1N.apply(this, arguments);
}
var SvgMore = function SvgMore(props) {
  return /*#__PURE__*/createElement("svg", _extends$1N({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1v || (_path$1v = /*#__PURE__*/createElement("path", {
    d: "M6.5 3a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0ZM6.5 8a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0ZM8 11.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"
  })));
};
var _path$1w;
function _extends$1O() {
  _extends$1O = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1O.apply(this, arguments);
}
var SvgName = function SvgName(props) {
  return /*#__PURE__*/createElement("svg", _extends$1O({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1w || (_path$1w = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "m2.7 11.994.622-1.963h2.844l.611 1.963h1.834L5.801 3.9h-1.99L1 11.994h1.7Zm3.107-3.231h-2.12l1.015-3.22h.101l1.004 3.22Zm5.715 3.326c.803 0 1.476-.342 1.795-.914h.101v.819H15V7.776c0-1.318-.925-2.092-2.563-2.092-1.554 0-2.603.712-2.71 1.845h1.504c.134-.387.527-.595 1.116-.595.673 0 1.043.309 1.043.842v.51l-1.497.09c-1.537.09-2.395.74-2.395 1.857 0 1.127.835 1.856 2.024 1.856Zm.539-1.195c-.567 0-.965-.286-.965-.762 0-.455.342-.718 1.043-.763l1.251-.084v.46c0 .65-.572 1.15-1.33 1.15Z"
  })));
};
var _path$1x, _path2$p;
function _extends$1P() {
  _extends$1P = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1P.apply(this, arguments);
}
var SvgNegativeTarget = function SvgNegativeTarget(props) {
  return /*#__PURE__*/createElement("svg", _extends$1P({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1x || (_path$1x = /*#__PURE__*/createElement("path", {
    d: "M6.25 6.833a.583.583 0 0 0-.583.584v1.166c0 .322.26.584.583.584h3.5a.583.583 0 0 0 .583-.584V7.417a.583.583 0 0 0-.583-.584h-3.5Z"
  })), _path2$p || (_path2$p = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0Zm-2.333 0a4.667 4.667 0 1 1-9.334 0 4.667 4.667 0 0 1 9.334 0Z"
  })));
};
var _g$g;
function _extends$1Q() {
  _extends$1Q = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1Q.apply(this, arguments);
}
var SvgNested = function SvgNested(props) {
  return /*#__PURE__*/createElement("svg", _extends$1Q({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 18 18"
  }, props), _g$g || (_g$g = /*#__PURE__*/createElement("g", {
    fillRule: "evenodd"
  }, /*#__PURE__*/createElement("path", {
    d: "m16.54 11.384-4.72 3.933a.5.5 0 0 1-.82-.385V7.068a.5.5 0 0 1 .82-.385l4.72 3.933a.5.5 0 0 1 0 .768z"
  }), /*#__PURE__*/createElement("path", {
    d: "M5.5 10H11v2H4a1 1 0 0 1-1-1V2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v7a.5.5 0 0 0 .5.5z"
  }))));
};
var _circle;
function _extends$1R() {
  _extends$1R = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1R.apply(this, arguments);
}
var SvgNeutral = function SvgNeutral(props) {
  return /*#__PURE__*/createElement("svg", _extends$1R({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 16
  }, props), _circle || (_circle = /*#__PURE__*/createElement("circle", {
    cx: 6,
    cy: 8,
    r: 3,
    fill: "#C4CDD5",
    fillRule: "evenodd"
  })));
};
var _g$h, _defs$g;
function _extends$1S() {
  _extends$1S = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1S.apply(this, arguments);
}
var SvgNoFunnel = function SvgNoFunnel(props) {
  return /*#__PURE__*/createElement("svg", _extends$1S({
    width: 16,
    height: 16,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$h || (_g$h = /*#__PURE__*/createElement("g", {
    clipPath: "url(#no-funnel_svg__a)",
    fill: "#A5B4FC"
  }, /*#__PURE__*/createElement("path", {
    d: "M15 1H1a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1ZM11 10.998H5a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1ZM13 6H3a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1Z"
  }))), _defs$g || (_defs$g = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "no-funnel_svg__a"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    d: "M0 0h16v16H0z"
  })))));
};
var _path$1y, _path2$q;
function _extends$1T() {
  _extends$1T = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1T.apply(this, arguments);
}
var SvgNoImage = function SvgNoImage(props) {
  return /*#__PURE__*/createElement("svg", _extends$1T({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1y || (_path$1y = /*#__PURE__*/createElement("path", {
    d: "M12.795 2.12a.403.403 0 0 1 .575 0l.5.507c.16.16.16.42 0 .58l-.787.798.788.797c.159.16.159.42 0 .58l-.501.507a.403.403 0 0 1-.575 0l-.788-.797-.788.797a.403.403 0 0 1-.575 0l-.5-.507a.414.414 0 0 1 0-.58l.787-.797-.788-.797a.414.414 0 0 1 0-.581l.501-.507a.403.403 0 0 1 .575 0l.788.797.788-.797Z"
  })), _path2$q || (_path2$q = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M12.007 7.849c.73 0 1.414-.208 1.993-.57v5.284c0 .79-.532 1.437-1.182 1.437H2.182C1.532 14 1 13.353 1 12.562V3.938C1 3.147 1.532 2.5 2.182 2.5h6.325c-.195.462-.303.97-.303 1.505 0 2.123 1.703 3.844 3.803 3.844ZM5.178 8.953a.404.404 0 0 1 .676-.01l.691 1.009c.165.24.518.234.675-.011l1.26-1.97a.404.404 0 0 1 .685.007l1.764 2.86a.41.41 0 0 1-.345.627H4.319a.41.41 0 0 1-.342-.633l1.201-1.879Z"
  })));
};
var _path$1z, _path2$r;
function _extends$1U() {
  _extends$1U = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1U.apply(this, arguments);
}
var SvgNoInventory = function SvgNoInventory(props) {
  return /*#__PURE__*/createElement("svg", _extends$1U({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1z || (_path$1z = /*#__PURE__*/createElement("path", {
    d: "M5.667 1.292A.292.292 0 0 0 5.375 1H1.583A.583.583 0 0 0 1 1.583v2.625c0 .161.13.292.292.292h13.416c.161 0 .292-.13.292-.292V1.583A.583.583 0 0 0 14.417 1h-3.792a.292.292 0 0 0-.292.292v1.75c0 .16-.13.291-.291.291H5.958a.292.292 0 0 1-.291-.291v-1.75Z"
  })), _path2$r || (_path2$r = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M15 7.125a.292.292 0 0 0-.292-.292H1.292A.292.292 0 0 0 1 7.125v7.292c0 .322.261.583.583.583h12.834a.583.583 0 0 0 .583-.583V7.125ZM8.92 9.223l-.484 1.694h-.884l-.473-1.694V8h1.842v1.223Zm-1.89 2.569v1.916h1.953v-1.916H7.03Z"
  })));
};
var _path$1A;
function _extends$1V() {
  _extends$1V = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1V.apply(this, arguments);
}
var SvgNotes = function SvgNotes(props) {
  return /*#__PURE__*/createElement("svg", _extends$1V({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1A || (_path$1A = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M2.167 1C1.522 1 1 1.522 1 2.167v11.666C1 14.478 1.522 15 2.167 15h11.666c.645 0 1.167-.522 1.167-1.167V2.167C15 1.522 14.478 1 13.833 1H2.167Zm1.166 2.917c0-.322.261-.584.584-.584h8.166c.322 0 .584.261.584.584V4.5a.583.583 0 0 1-.584.583H3.917a.583.583 0 0 1-.584-.583v-.583Zm0 3.791c0-.322.261-.583.584-.583h8.166c.322 0 .584.261.584.583v.584a.583.583 0 0 1-.584.583H3.917a.583.583 0 0 1-.584-.583v-.584Zm0 3.792c0-.322.261-.583.584-.583H9.75c.322 0 .583.26.583.583v.583a.583.583 0 0 1-.583.584H3.917a.583.583 0 0 1-.584-.584V11.5Z"
  })));
};
var _path$1B;
function _extends$1W() {
  _extends$1W = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1W.apply(this, arguments);
}
var SvgNotification = function SvgNotification(props) {
  return /*#__PURE__*/createElement("svg", _extends$1W({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1B || (_path$1B = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M14.796 11.292a.622.622 0 0 1-.418 1.083H1.622a.622.622 0 0 1-.418-1.083l.523-.475c.513-.466.92-1.373.92-2.037V6.741c0-2.319 1.73-4.189 4.118-4.712v-.524c0-.599.576-.499 1.235-.499.659 0 1.235-.1 1.235.499v.524c2.389.523 4.118 2.393 4.118 4.712v2.04c0 .662.412 1.574.92 2.036l.523.475ZM9.75 13.25C9.75 14.213 8.963 15 8 15s-1.75-.787-1.75-1.75h3.5Z"
  })));
};
var _path$1C;
function _extends$1X() {
  _extends$1X = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1X.apply(this, arguments);
}
var SvgOpen = function SvgOpen(props) {
  return /*#__PURE__*/createElement("svg", _extends$1X({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1C || (_path$1C = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M3.833 3.333a.5.5 0 0 0-.5.5v8.334a.5.5 0 0 0 .5.5h8.334a.5.5 0 0 0 .5-.5v-2.5a.5.5 0 0 1 .5-.5H14.5a.5.5 0 0 1 .5.5v3.583A1.75 1.75 0 0 1 13.25 15H2.75A1.75 1.75 0 0 1 1 13.25V2.75C1 1.784 1.784 1 2.75 1h3.583a.5.5 0 0 1 .5.5v1.333a.5.5 0 0 1-.5.5h-2.5ZM9.667 1a.5.5 0 0 0-.5.5v1.333a.5.5 0 0 0 .5.5h1.35L7.529 6.821a.5.5 0 0 0 0 .708l.942.942a.5.5 0 0 0 .707 0l3.489-3.488v1.35a.5.5 0 0 0 .5.5H14.5a.5.5 0 0 0 .5-.5V1.5a.5.5 0 0 0-.5-.5H9.667Z"
  })));
};
var _path$1D;
function _extends$1Y() {
  _extends$1Y = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1Y.apply(this, arguments);
}
var SvgOptimize = function SvgOptimize(props) {
  return /*#__PURE__*/createElement("svg", _extends$1Y({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1D || (_path$1D = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0ZM7.954 3.38a.359.359 0 0 1 .652.206V6.66h2.943c.29 0 .46.327.293.565l-3.796 5.395a.359.359 0 0 1-.652-.207V9.34H4.451a.359.359 0 0 1-.293-.565l3.796-5.395Z"
  })));
};
var _path$1E;
function _extends$1Z() {
  _extends$1Z = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1Z.apply(this, arguments);
}
var SvgOrganization = function SvgOrganization(props) {
  return /*#__PURE__*/createElement("svg", _extends$1Z({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1E || (_path$1E = /*#__PURE__*/createElement("path", {
    d: "M6.25 1a.583.583 0 0 0-.583.583v1.75h4.666v-1.75A.583.583 0 0 0 9.75 1h-3.5ZM3.333 5.083c0-.322.261-.583.584-.583h8.166c.322 0 .584.261.584.583v1.75H3.333v-1.75ZM1 9.167C1 8.522 1.522 8 2.167 8h11.666C14.478 8 15 8.522 15 9.167v4.666c0 .645-.522 1.167-1.167 1.167H2.167A1.167 1.167 0 0 1 1 13.833V9.167Z"
  })));
};
var _path$1F;
function _extends$1_() {
  _extends$1_ = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1_.apply(this, arguments);
}
var SvgOutOfBudget = function SvgOutOfBudget(props) {
  return /*#__PURE__*/createElement("svg", _extends$1_({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1F || (_path$1F = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M5.2 12.2c-.77 0-1.4.63-1.4 1.4 0 .77.63 1.4 1.4 1.4.77 0 1.4-.63 1.4-1.4 0-.77-.63-1.4-1.4-1.4ZM1.5 1a.5.5 0 0 0-.5.5v.4a.5.5 0 0 0 .5.5h.9l2.52 5.32-.98 1.68c-.07.21-.14.49-.14.7 0 .77.63 1.4 1.4 1.4h7.9a.5.5 0 0 0 .5-.5v-.4a.5.5 0 0 0-.5-.5H5.48a.15.15 0 0 1-.14-.14v-.07l.63-1.19h5.18c.56 0 .98-.28 1.19-.7l2.52-4.55c.14-.14.14-.21.14-.35 0-.42-.28-.7-.7-.7H3.94L3.31 1H1.5Zm10.7 11.2c-.77 0-1.4.63-1.4 1.4 0 .77.63 1.4 1.4 1.4.77 0 1.4-.63 1.4-1.4 0-.77-.63-1.4-1.4-1.4ZM7.225 6.991a.5.5 0 0 1 0-.707l.723-.722-.723-.723a.5.5 0 0 1 0-.707l.154-.153a.5.5 0 0 1 .707 0l.722.722.723-.722a.5.5 0 0 1 .707 0l.154.153a.5.5 0 0 1 0 .707l-.723.723.723.722a.5.5 0 0 1 0 .707l-.154.154a.5.5 0 0 1-.707 0l-.723-.722-.722.722a.5.5 0 0 1-.707 0l-.154-.154Z"
  })));
};
var _path$1G;
function _extends$1$() {
  _extends$1$ = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1$.apply(this, arguments);
}
var SvgPage = function SvgPage(props) {
  return /*#__PURE__*/createElement("svg", _extends$1$({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1G || (_path$1G = /*#__PURE__*/createElement("path", {
    d: "M1 2.167C1 1.522 1.522 1 2.167 1h11.666C14.478 1 15 1.522 15 2.167v4.375c0 .16-.13.291-.292.291H1.292A.292.292 0 0 1 1 6.542V2.167ZM1 9.458c0-.16.13-.291.292-.291h8.75c.16 0 .291.13.291.291v5.25c0 .161-.13.292-.291.292H2.167A1.167 1.167 0 0 1 1 13.833V9.458ZM12.958 9.167a.292.292 0 0 0-.291.291v5.25c0 .161.13.292.291.292h.875c.645 0 1.167-.522 1.167-1.167V9.458a.292.292 0 0 0-.292-.291h-1.75Z"
  })));
};
var _path$1H;
function _extends$20() {
  _extends$20 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$20.apply(this, arguments);
}
var SvgPartial = function SvgPartial(props) {
  return /*#__PURE__*/createElement("svg", _extends$20({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1H || (_path$1H = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm0 2.333v9.334a4.667 4.667 0 0 0 0-9.334Z"
  })));
};
var _path$1I;
function _extends$21() {
  _extends$21 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$21.apply(this, arguments);
}
var SvgPaused = function SvgPaused(props) {
  return /*#__PURE__*/createElement("svg", _extends$21({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1I || (_path$1I = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14ZM5.375 5.875a.5.5 0 0 1 .5-.5h.75a.5.5 0 0 1 .5.5v4.25a.5.5 0 0 1-.5.5h-.75a.5.5 0 0 1-.5-.5v-4.25Zm4-.5a.5.5 0 0 0-.5.5v4.25a.5.5 0 0 0 .5.5h.75a.5.5 0 0 0 .5-.5v-4.25a.5.5 0 0 0-.5-.5h-.75Z"
  })));
};
var _path$1J;
function _extends$22() {
  _extends$22 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$22.apply(this, arguments);
}
var SvgPending = function SvgPending(props) {
  return /*#__PURE__*/createElement("svg", _extends$22({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1J || (_path$1J = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0ZM8 2C4.7 2 2 4.7 2 8s2.7 6 6 6 6-2.7 6-6-2.7-6-6-6Zm0 11c-2.75 0-5-2.25-5-5s2.25-5 5-5 5 2.25 5 5-2.25 5-5 5Zm.35-7.2a.3.3 0 0 0-.3-.3H7.6a.3.3 0 0 0-.3.3v2.9l2.397 1.126a.3.3 0 0 0 .384-.114l.235-.382a.3.3 0 0 0-.141-.435L8.35 8.14V5.8Z"
  })));
};
var _path$1K;
function _extends$23() {
  _extends$23 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$23.apply(this, arguments);
}
var SvgPercent = function SvgPercent(props) {
  return /*#__PURE__*/createElement("svg", _extends$23({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1K || (_path$1K = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M15 10.86c0 .424-.082.823-.244 1.197-.163.375-.383.7-.66.977a3.11 3.11 0 0 1-4.379 0 3.132 3.132 0 0 1-.659-.977 2.968 2.968 0 0 1-.244-1.196c0-.424.081-.822.244-1.197.163-.374.383-.7.66-.976a3.108 3.108 0 0 1 4.378 0c.277.276.497.602.66.976.162.375.244.774.244 1.197Zm-1.823 0a1.2 1.2 0 0 0-.106-.496 1.35 1.35 0 0 0-.277-.407 1.26 1.26 0 0 0-.887-.366c-.347 0-.646.125-.895.374-.25.25-.375.549-.375.896s.125.643.375.887.548.366.895.366a1.26 1.26 0 0 0 .887-.366 1.38 1.38 0 0 0 .277-.399c.07-.152.106-.315.106-.488ZM7.186 5.099c0 .423-.081.822-.244 1.196-.163.375-.383.7-.66.977a3.107 3.107 0 0 1-4.379 0 3.132 3.132 0 0 1-.659-.977A2.968 2.968 0 0 1 1 5.098c0-.434.081-.838.244-1.213.163-.374.383-.7.66-.977a3.116 3.116 0 0 1 2.19-.887 3.116 3.116 0 0 1 2.189.887c.276.277.496.603.659.977.163.375.244.779.244 1.213Zm-1.823 0c0-.347-.125-.646-.375-.895a1.222 1.222 0 0 0-.895-.375c-.347 0-.646.125-.895.375-.25.25-.375.548-.375.895s.125.643.375.887.548.366.895.366.646-.122.895-.366c.25-.244.375-.54.375-.887Zm.217 8.79a.5.5 0 0 1-.655.199l-.553-.273a.5.5 0 0 1-.214-.695L10.39 2.104a.5.5 0 0 1 .653-.204l.61.295a.5.5 0 0 1 .216.698L5.58 13.887Z"
  })));
};
var _path$1L, _path2$s;
function _extends$24() {
  _extends$24 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$24.apply(this, arguments);
}
var SvgPieChart = function SvgPieChart(props) {
  return /*#__PURE__*/createElement("svg", _extends$24({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1L || (_path$1L = /*#__PURE__*/createElement("path", {
    d: "M.003 5.176c-.032.318.23.577.549.577h4.623c.32 0 .578-.258.578-.578V.552c0-.32-.26-.58-.577-.55A5.781 5.781 0 0 0 .003 5.177Z"
  })), _path2$s || (_path2$s = /*#__PURE__*/createElement("path", {
    d: "M1.154 8.643a.543.543 0 0 1 .554-.578h5.78c.318 0 .577-.259.577-.578v-5.78c0-.318.26-.58.578-.553a6.936 6.936 0 1 1-7.49 7.489Z"
  })));
};
var _path$1M;
function _extends$25() {
  _extends$25 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$25.apply(this, arguments);
}
var SvgPlay = function SvgPlay(props) {
  return /*#__PURE__*/createElement("svg", _extends$25({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1M || (_path$1M = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M13.573 7.284a.813.813 0 0 1 0 1.432L4.267 13.89C3.703 14.204 3 13.807 3 13.174V2.826c0-.633.703-1.03 1.267-.716l9.306 5.174Z"
  })));
};
var _path$1N;
function _extends$26() {
  _extends$26 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$26.apply(this, arguments);
}
var SvgPortfolios = function SvgPortfolios(props) {
  return /*#__PURE__*/createElement("svg", _extends$26({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1N || (_path$1N = /*#__PURE__*/createElement("path", {
    d: "M6.6 3H2.4c-.77 0-1.393.563-1.393 1.25L1 11.75c0 .688.63 1.25 1.4 1.25h11.2c.77 0 1.4-.563 1.4-1.25V5.5c0-.688-.63-1.25-1.4-1.25H8L6.6 3Z"
  })));
};
var _path$1O;
function _extends$27() {
  _extends$27 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$27.apply(this, arguments);
}
var SvgPosts = function SvgPosts(props) {
  return /*#__PURE__*/createElement("svg", _extends$27({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1O || (_path$1O = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M2.167 1C1.522 1 1 1.522 1 2.167v11.666C1 14.478 1.522 15 2.167 15h11.666c.645 0 1.167-.522 1.167-1.167V2.167C15 1.522 14.478 1 13.833 1H2.167Zm10.5 3.5a1.167 1.167 0 1 0 0-2.333 1.167 1.167 0 0 0 0 2.333ZM6.809 6.791a.583.583 0 0 0 1.021-.015l1.313-2.46a.583.583 0 0 1 1.033.008l2.053 3.993c.2.388-.082.85-.518.85H4.306a.583.583 0 0 1-.515-.858L5.17 5.724a.583.583 0 0 1 1.02-.015l.62 1.082Zm-3.476 5c0-.16.13-.291.292-.291h8.75c.161 0 .292.13.292.292v.583c0 .161-.13.292-.292.292h-8.75a.292.292 0 0 1-.292-.292v-.583Z"
  })));
};
var _path$1P, _path2$t, _path3$7;
function _extends$28() {
  _extends$28 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$28.apply(this, arguments);
}
var SvgProcessing = function SvgProcessing(props) {
  return /*#__PURE__*/createElement("svg", _extends$28({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1P || (_path$1P = /*#__PURE__*/createElement("path", {
    d: "M7.709 4.969a.292.292 0 0 0-.292.292V8.01c0 .077.03.151.085.206l1.63 1.631a.291.291 0 0 0 .412 0l.413-.413a.292.292 0 0 0 0-.413L8.669 7.732a.292.292 0 0 1-.085-.206V5.26a.292.292 0 0 0-.292-.292h-.583Z"
  })), _path2$t || (_path2$t = /*#__PURE__*/createElement("path", {
    d: "M8 1.484A.474.474 0 0 0 7.5 1a6.994 6.994 0 0 0-5.783 3.9 7.016 7.016 0 0 0 .742 7.373 7.001 7.001 0 0 0 6.936 2.585 6.996 6.996 0 0 0 5.233-4.613.474.474 0 0 0-.34-.606l-1.064-.28c-.267-.07-.538.092-.635.351a4.906 4.906 0 0 1-3.612 3.088 4.895 4.895 0 0 1-4.856-1.81 4.91 4.91 0 0 1 3.38-7.878c.275-.028.499-.25.499-.526V1.484ZM13.601 4.59a.474.474 0 0 0 .163-.677 6.937 6.937 0 0 0-.057-.081.473.473 0 0 0-.69-.08l-.938.773a.41.41 0 0 0-.065.552.41.41 0 0 0 .54.129l1.047-.617ZM12.239 2.954a.474.474 0 0 0-.065-.692 6.828 6.828 0 0 0-.08-.059.473.473 0 0 0-.678.148l-.639 1.035a.41.41 0 0 0 .117.543.41.41 0 0 0 .553-.053l.792-.922Z"
  })), _path3$7 || (_path3$7 = /*#__PURE__*/createElement("path", {
    d: "M10.224 1.774a.474.474 0 0 0-.305-.625 6.913 6.913 0 0 0-.096-.026.473.473 0 0 0-.582.378l-.232 1.194a.41.41 0 0 0 .301.467.41.41 0 0 0 .498-.245l.416-1.143ZM14.354 6.526a.474.474 0 0 0 .367-.59 7.821 7.821 0 0 0-.028-.096.473.473 0 0 0-.63-.293l-1.134.438a.41.41 0 0 0-.235.503.41.41 0 0 0 .472.293l1.188-.255ZM14.46 8.597a.473.473 0 0 0 .533-.444c.002-.033.002-.067.003-.1a.474.474 0 0 0-.505-.476l-1.214.058a.41.41 0 0 0-.381.404.41.41 0 0 0 .356.426l1.207.132Z"
  })));
};
var _path$1Q;
function _extends$29() {
  _extends$29 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$29.apply(this, arguments);
}
var SvgProductCollection = function SvgProductCollection(props) {
  return /*#__PURE__*/createElement("svg", _extends$29({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 16 16"
  }, props), _path$1Q || (_path$1Q = /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    d: "M15.111 0c.491 0 .889.398.889.889V15.11a.889.889 0 0 1-.889.889H.89a.889.889 0 0 1-.89-.888V.89A.89.89 0 0 1 .889 0H15.11zM4.89 10.667H2.222a.444.444 0 0 0-.444.444v2.667c0 .245.199.444.444.444H4.89a.444.444 0 0 0 .444-.444V11.11a.444.444 0 0 0-.444-.444zm4.444 0H6.667a.444.444 0 0 0-.445.444v2.667c0 .245.2.444.445.444h2.666a.444.444 0 0 0 .445-.444V11.11a.444.444 0 0 0-.445-.444zm4.445 0H11.11a.444.444 0 0 0-.444.444v2.667c0 .245.199.444.444.444h2.667a.444.444 0 0 0 .444-.444V11.11a.444.444 0 0 0-.444-.444zm-5.334-8.89H2.222a.444.444 0 0 0-.444.445v6.222c0 .246.199.445.444.445h6.222a.444.444 0 0 0 .445-.445V2.222a.444.444 0 0 0-.445-.444zm5.6 4h-3.2a.178.178 0 0 0-.177.179v.977c0 .099.08.178.177.178h3.2c.099 0 .178-.08.178-.178v-.977a.178.178 0 0 0-.178-.178zm-.888-2.221h-2.312a.178.178 0 0 0-.177.177v.978c0 .098.08.178.177.178h2.312c.098 0 .177-.08.177-.178v-.978a.178.178 0 0 0-.177-.177z"
  })));
};
var _path$1R;
function _extends$2a() {
  _extends$2a = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2a.apply(this, arguments);
}
var SvgProduct = function SvgProduct(props) {
  return /*#__PURE__*/createElement("svg", _extends$2a({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1R || (_path$1R = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M5.667 1.292A.292.292 0 0 0 5.375 1H1.583A.583.583 0 0 0 1 1.583v2.625c0 .161.13.292.292.292h13.416c.161 0 .292-.13.292-.292V1.583A.583.583 0 0 0 14.417 1h-3.792a.292.292 0 0 0-.292.292v1.75c0 .16-.13.291-.291.291H5.958a.292.292 0 0 1-.291-.291v-1.75ZM15 7.125a.292.292 0 0 0-.292-.292H1.292A.292.292 0 0 0 1 7.125v7.292c0 .322.261.583.583.583h12.834a.583.583 0 0 0 .583-.583V7.125Zm-4.667 1.167A.292.292 0 0 0 10.042 8H5.958a.292.292 0 0 0-.291.292v3.903c0 .217.228.358.422.26L8 11.5l1.911.956a.292.292 0 0 0 .422-.261V8.292Z"
  })));
};
var _path$1S;
function _extends$2b() {
  _extends$2b = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2b.apply(this, arguments);
}
var SvgQuote = function SvgQuote(props) {
  return /*#__PURE__*/createElement("svg", _extends$2b({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1S || (_path$1S = /*#__PURE__*/createElement("path", {
    d: "M8.95 9.456c0-2.183.516-3.891 1.55-5.126.948-1.12 2.24-1.89 3.876-2.314.267-.069.518.152.518.444v1.327a.484.484 0 0 1-.313.455c-1.258.461-2.116 1.23-2.574 2.306a3.66 3.66 0 0 0-.351 1.369.402.402 0 0 0 .405.419h2.502c.241 0 .437.208.437.465v4.733c0 .258-.196.466-.438.466H9.387c-.242 0-.438-.208-.438-.466V9.456Zm-7.95 0C1 7.302 1.502 5.6 2.507 4.35c.927-1.136 2.234-1.915 3.921-2.339.267-.067.516.154.516.445V3.79a.482.482 0 0 1-.31.453c-1.273.471-2.14 1.225-2.598 2.263a3.903 3.903 0 0 0-.324 1.398.413.413 0 0 0 .413.433h2.488c.242 0 .438.208.438.465v4.733c0 .258-.196.466-.438.466H1.437C1.196 14 1 13.792 1 13.534V9.456Z"
  })));
};
var _path$1T;
function _extends$2c() {
  _extends$2c = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2c.apply(this, arguments);
}
var SvgRadioSelected = function SvgRadioSelected(props) {
  return /*#__PURE__*/createElement("svg", _extends$2c({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1T || (_path$1T = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16Zm0-2A6 6 0 1 0 8 2a6 6 0 0 0 0 12Zm4-6a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z"
  })));
};
var _path$1U;
function _extends$2d() {
  _extends$2d = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2d.apply(this, arguments);
}
var SvgRadioUnselected = function SvgRadioUnselected(props) {
  return /*#__PURE__*/createElement("svg", _extends$2d({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1U || (_path$1U = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Zm8-6A8 8 0 1 1 0 8a8 8 0 0 1 16 0Z"
  })));
};
var _path$1V;
function _extends$2e() {
  _extends$2e = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2e.apply(this, arguments);
}
var SvgRearrangeHorizontal = function SvgRearrangeHorizontal(props) {
  return /*#__PURE__*/createElement("svg", _extends$2e({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1V || (_path$1V = /*#__PURE__*/createElement("path", {
    d: "M14.602 4.633a.5.5 0 0 1 0 .734l-2.932 2.71a.25.25 0 0 1-.42-.184V6H5.5a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h5.75V2.107a.25.25 0 0 1 .42-.183l2.932 2.709ZM4.75 12h5.75a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5H4.75V8.107a.25.25 0 0 0-.42-.183l-2.932 2.709a.5.5 0 0 0 0 .734l2.932 2.71a.25.25 0 0 0 .42-.184V12Z"
  })));
};
var _path$1W;
function _extends$2f() {
  _extends$2f = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2f.apply(this, arguments);
}
var SvgRearrangeVertical = function SvgRearrangeVertical(props) {
  return /*#__PURE__*/createElement("svg", _extends$2f({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1W || (_path$1W = /*#__PURE__*/createElement("path", {
    d: "M11.367 14.602a.5.5 0 0 1-.734 0l-2.71-2.932a.25.25 0 0 1 .184-.42H10V5.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v5.75h1.893a.25.25 0 0 1 .183.42l-2.709 2.932ZM4 4.75v5.75a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5V4.75h1.893a.25.25 0 0 0 .183-.42L5.367 1.398a.5.5 0 0 0-.734 0L1.923 4.33a.25.25 0 0 0 .184.42H4Z"
  })));
};
var _path$1X;
function _extends$2g() {
  _extends$2g = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2g.apply(this, arguments);
}
var SvgRestart = function SvgRestart(props) {
  return /*#__PURE__*/createElement("svg", _extends$2g({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1X || (_path$1X = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M5.2 4.266a4.667 4.667 0 1 1-.614 6.917c-.187-.203-.5-.25-.72-.083l-1.066.8a.473.473 0 0 0-.085.69 7 7 0 1 0-.213-8.921L1.426 2.592A.25.25 0 0 0 1 2.77v3.563a.5.5 0 0 0 .5.5h3.563a.25.25 0 0 0 .177-.426L4.175 5.342c.316-.383.759-.876 1.026-1.076Z"
  })));
};
var _path$1Y;
function _extends$2h() {
  _extends$2h = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2h.apply(this, arguments);
}
var SvgReturn = function SvgReturn(props) {
  return /*#__PURE__*/createElement("svg", _extends$2h({
    width: 14,
    height: 14,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1Y || (_path$1Y = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8.293 13.04a1 1 0 0 1 0-1.414l2.293-2.293H6.333A6.333 6.333 0 0 1 0 3V1.667a1 1 0 1 1 2 0V3a4.333 4.333 0 0 0 4.333 4.333h4.253L8.293 5.04a1 1 0 0 1 1.414-1.414l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0Z"
  })));
};
var _path$1Z, _path2$u;
function _extends$2i() {
  _extends$2i = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2i.apply(this, arguments);
}
var SvgRocket = function SvgRocket(props) {
  return /*#__PURE__*/createElement("svg", _extends$2i({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1Z || (_path$1Z = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M11.857 1.033a3.65 3.65 0 0 0-3.06 1.024l-1.531 1.52-5.582.616a.563.563 0 0 0-.338.96l2.167 2.151-1.124 1.117 5.139 5.103 1.124-1.116 2.264 2.249a.569.569 0 0 0 .966-.336l.645-5.761 1.409-1.4a3.593 3.593 0 0 0 1.03-3.038 3.621 3.621 0 0 0-3.11-3.089Zm.167 5.474c-.71.705-1.86.705-2.57 0a1.796 1.796 0 0 1 0-2.552 1.826 1.826 0 0 1 2.57 0c.71.705.71 1.847 0 2.552Z"
  })), _path2$u || (_path2$u = /*#__PURE__*/createElement("path", {
    d: "M5.071 12.633a1.753 1.753 0 0 0-1.687-1.676c-.85-.034-1.601.54-1.787 1.364l-.596 2.649a.025.025 0 0 0 .03.03l2.667-.593a1.749 1.749 0 0 0 1.373-1.774Z"
  })));
};
var _path$1_;
function _extends$2j() {
  _extends$2j = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2j.apply(this, arguments);
}
var SvgSales = function SvgSales(props) {
  return /*#__PURE__*/createElement("svg", _extends$2j({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1_ || (_path$1_ = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M1.875 1A.875.875 0 0 0 1 1.875v12.25c0 .483.392.875.875.875h12.25a.875.875 0 0 0 .875-.875V1.875A.875.875 0 0 0 14.125 1H1.875Zm6.574 10.56c.178.178.47.169.637-.02l4.249-4.816c.07-.08.11-.183.11-.29v-1.01a.438.438 0 0 0-.766-.29L9.086 9.206a.437.437 0 0 1-.637.02L5.976 6.754a.437.437 0 0 0-.619 0L2.684 9.427a.438.438 0 0 0-.128.31v1.096c0 .39.47.585.746.309l2.055-2.055c.171-.17.448-.17.62 0l2.472 2.473Z"
  })));
};
var _path$1$, _path2$v;
function _extends$2k() {
  _extends$2k = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2k.apply(this, arguments);
}
var SvgSearchInsights = function SvgSearchInsights(props) {
  return /*#__PURE__*/createElement("svg", _extends$2k({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1$ || (_path$1$ = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M7.941 12.707a4.474 4.474 0 1 1 1.265-1.265l2.163 2.162a.447.447 0 0 1 0 .632l-.633.633a.447.447 0 0 1-.633 0l-2.162-2.162Zm.217-3.733a2.684 2.684 0 1 1-5.368 0 2.684 2.684 0 0 1 5.368 0Z"
  })), _path2$v || (_path2$v = /*#__PURE__*/createElement("path", {
    d: "m7.18 4.194.318.037a3.227 3.227 0 0 1 2.821 2.718 3.52 3.52 0 0 1 2.755-3.14 3.192 3.192 0 0 1-3.127-2.676l-.013-.078-.01.246A3.048 3.048 0 0 1 7.18 4.194ZM12.382 10.305l.053-.164a2.72 2.72 0 0 1 2.535-1.889l-.105-.033a2.778 2.778 0 0 1-1.948-2.555l-.027.094a2.73 2.73 0 0 1-2.56 1.959l.152.046a2.675 2.675 0 0 1 1.9 2.542Z"
  })));
};
var _path$20;
function _extends$2l() {
  _extends$2l = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2l.apply(this, arguments);
}
var SvgSearch = function SvgSearch(props) {
  return /*#__PURE__*/createElement("svg", _extends$2l({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$20 || (_path$20 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M10.255 11.942a5.965 5.965 0 1 1 1.687-1.687l2.883 2.883a.597.597 0 0 1 0 .844l-.843.843a.597.597 0 0 1-.844 0l-2.883-2.883Zm.29-4.977a3.58 3.58 0 1 1-7.159 0 3.58 3.58 0 0 1 7.159 0Z"
  })));
};
var _path$21;
function _extends$2m() {
  _extends$2m = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2m.apply(this, arguments);
}
var SvgSetting = function SvgSetting(props) {
  return /*#__PURE__*/createElement("svg", _extends$2m({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$21 || (_path$21 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M13.24 8.739c0-.211.07-.422.07-.704 0-.281 0-.492-.07-.703l1.499-1.196c.143-.141.143-.282.071-.422L13.382 3.25c-.071-.07-.214-.14-.428-.07l-1.786.703c-.357-.281-.785-.492-1.214-.703L9.67 1.35C9.74 1.142 9.526 1 9.383 1H6.527c-.143 0-.357.14-.357.281l-.286 1.9c-.429.14-.786.422-1.214.703l-1.714-.703c-.214-.07-.357 0-.5.14L1.028 5.785c-.071.07 0 .281.143.422l1.5 1.126c0 .21-.072.422-.072.703 0 .282 0 .493.071.704l-1.5 1.196c-.142.14-.142.281-.07.422l1.428 2.462c.071.07.214.14.428.07l1.785-.703c.357.281.786.492 1.214.703l.286 1.83c0 .14.143.281.357.281h2.856c.143 0 .358-.14.358-.281l.285-1.83c.429-.21.857-.422 1.214-.703l1.785.703c.143.07.357 0 .429-.14l1.428-2.463c.072-.14.072-.351-.071-.422L13.239 8.74Zm-5.285 1.758c-1.357 0-2.5-1.125-2.5-2.462 0-1.336 1.143-2.462 2.5-2.462s2.5 1.126 2.5 2.462c0 1.337-1.143 2.463-2.5 2.463Z"
  })));
};
var _path$22;
function _extends$2n() {
  _extends$2n = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2n.apply(this, arguments);
}
var SvgShield = function SvgShield(props) {
  return /*#__PURE__*/createElement("svg", _extends$2n({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$22 || (_path$22 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M1.388 3.53a10.55 10.55 0 0 0 6.417-2.459.304.304 0 0 1 .39 0 10.55 10.55 0 0 0 6.417 2.46c.14.005.26.107.28.246.072.485.108.981.108 1.485 0 4.468-2.877 8.275-6.904 9.721a.283.283 0 0 1-.192 0C3.877 13.536 1 9.73 1 5.261c0-.504.037-1 .107-1.485.02-.139.14-.24.281-.246Zm2.529 2.405c0-.067 0-.133.003-.2A9.08 9.08 0 0 0 8 4.152a9.08 9.08 0 0 0 4.08 1.584c.002.066.003.133.003.2 0 2.715-1.674 5.06-4.083 6.068-2.409-1.008-4.083-3.354-4.083-6.069Zm3.887-3.101a7.912 7.912 0 0 1-4.702 1.8.291.291 0 0 0-.28.248 7.721 7.721 0 0 0-.072 1.053c0 3.343 2.146 6.192 5.154 7.283.062.023.13.023.192 0 3.008-1.09 5.154-3.94 5.154-7.282 0-.357-.024-.71-.072-1.054a.291.291 0 0 0-.28-.247 7.912 7.912 0 0 1-4.702-1.801.308.308 0 0 0-.392 0Z"
  })));
};
var _path$23;
function _extends$2o() {
  _extends$2o = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2o.apply(this, arguments);
}
var SvgShoppingCart = function SvgShoppingCart(props) {
  return /*#__PURE__*/createElement("svg", _extends$2o({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$23 || (_path$23 = /*#__PURE__*/createElement("path", {
    d: "M5.2 12.2c-.77 0-1.4.63-1.4 1.4 0 .77.63 1.4 1.4 1.4.77 0 1.4-.63 1.4-1.4 0-.77-.63-1.4-1.4-1.4ZM1.583 1A.583.583 0 0 0 1 1.583v.234c0 .322.261.583.583.583H2.4l2.52 5.32-.98 1.68c-.07.21-.14.49-.14.7 0 .77.63 1.4 1.4 1.4h7.817a.583.583 0 0 0 .583-.583v-.234a.583.583 0 0 0-.583-.583H5.48a.15.15 0 0 1-.14-.14v-.07l.63-1.19h5.18c.56 0 .98-.28 1.19-.7l2.52-4.55c.14-.14.14-.21.14-.35 0-.42-.28-.7-.7-.7H3.94L3.31 1H1.583ZM12.2 12.2c-.77 0-1.4.63-1.4 1.4 0 .77.63 1.4 1.4 1.4.77 0 1.4-.63 1.4-1.4 0-.77-.63-1.4-1.4-1.4Z"
  })));
};
var _path$24, _path2$w;
function _extends$2p() {
  _extends$2p = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2p.apply(this, arguments);
}
var SvgSignOut = function SvgSignOut(props) {
  return /*#__PURE__*/createElement("svg", _extends$2p({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$24 || (_path$24 = /*#__PURE__*/createElement("path", {
    d: "M12.692 3.625a.29.29 0 0 0-.288-.292h-3.75a.58.58 0 0 1-.577-.583V1.583A.58.58 0 0 1 8.654 1h5.77a.58.58 0 0 1 .576.583v12.834a.58.58 0 0 1-.577.583h-5.77a.58.58 0 0 1-.576-.583V13.25a.58.58 0 0 1 .577-.583h3.75a.29.29 0 0 0 .288-.292v-8.75Z"
  })), _path2$w || (_path2$w = /*#__PURE__*/createElement("path", {
    d: "m3.94 6.833 2.237-2.262a.588.588 0 0 0 0-.825l-.816-.825a.572.572 0 0 0-.816 0L.338 7.175a1.173 1.173 0 0 0 0 1.65l4.207 4.254c.226.228.591.228.816 0l.816-.825a.588.588 0 0 0 0-.825L3.94 9.167h4.715a.58.58 0 0 0 .577-.584V7.417a.58.58 0 0 0-.577-.584H3.939Z"
  })));
};
var _path$25;
function _extends$2q() {
  _extends$2q = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2q.apply(this, arguments);
}
var SvgSimilar = function SvgSimilar(props) {
  return /*#__PURE__*/createElement("svg", _extends$2q({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$25 || (_path$25 = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M13 5.861c0 .768-.183 1.494-.509 2.139.326.645.509 1.371.509 2.139C13 12.856 10.728 15 8 15s-5-2.144-5-4.861c0-.768.183-1.494.509-2.139A4.735 4.735 0 0 1 3 5.861C3 3.144 5.272 1 8 1s5 2.144 5 4.861Zm-8.863 0c0-2.007 1.697-3.694 3.863-3.694s3.863 1.687 3.863 3.694c0 .354-.053.697-.15 1.022A5.053 5.053 0 0 0 8 5.278a5.053 5.053 0 0 0-3.713 1.605 3.545 3.545 0 0 1-.15-1.022ZM8 6.444c1.308 0 2.453.62 3.15 1.556A3.912 3.912 0 0 1 8 9.556 3.912 3.912 0 0 1 4.85 8 3.912 3.912 0 0 1 8 6.444Z"
  })));
};
var _path$26;
function _extends$2r() {
  _extends$2r = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2r.apply(this, arguments);
}
var SvgSortDown = function SvgSortDown(props) {
  return /*#__PURE__*/createElement("svg", _extends$2r({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$26 || (_path$26 = /*#__PURE__*/createElement("path", {
    d: "M7.463 10.767c.271.31.803.31 1.074 0l3.321-3.804c.343-.393.025-.963-.537-.963H4.679c-.562 0-.88.57-.537.963l3.32 3.804Z"
  })));
};
var _path$27;
function _extends$2s() {
  _extends$2s = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2s.apply(this, arguments);
}
var SvgSortLeft = function SvgSortLeft(props) {
  return /*#__PURE__*/createElement("svg", _extends$2s({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$27 || (_path$27 = /*#__PURE__*/createElement("path", {
    d: "M5.233 7.463a.736.736 0 0 0 0 1.074l3.804 3.321c.393.343.963.025.963-.537V4.679c0-.562-.57-.88-.963-.537l-3.804 3.32Z"
  })));
};
var _path$28;
function _extends$2t() {
  _extends$2t = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2t.apply(this, arguments);
}
var SvgSortRight = function SvgSortRight(props) {
  return /*#__PURE__*/createElement("svg", _extends$2t({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$28 || (_path$28 = /*#__PURE__*/createElement("path", {
    d: "M10.767 7.463c.31.271.31.803 0 1.074l-3.804 3.321c-.393.343-.963.025-.963-.537V4.679c0-.562.57-.88.963-.537l3.804 3.32Z"
  })));
};
var _path$29;
function _extends$2u() {
  _extends$2u = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2u.apply(this, arguments);
}
var SvgSortUp = function SvgSortUp(props) {
  return /*#__PURE__*/createElement("svg", _extends$2u({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$29 || (_path$29 = /*#__PURE__*/createElement("path", {
    d: "M7.463 5.233a.736.736 0 0 1 1.074 0l3.321 3.804c.343.393.025.963-.537.963H4.679c-.562 0-.88-.57-.537-.963l3.32-3.804Z"
  })));
};
var _path$2a;
function _extends$2v() {
  _extends$2v = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2v.apply(this, arguments);
}
var SvgSort = function SvgSort(props) {
  return /*#__PURE__*/createElement("svg", _extends$2v({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2a || (_path$2a = /*#__PURE__*/createElement("path", {
    d: "M7.604 2.515a.5.5 0 0 1 .792 0l2.448 3.18a.5.5 0 0 1-.396.805H5.552a.5.5 0 0 1-.396-.805l2.448-3.18ZM7.604 13.485a.5.5 0 0 0 .792 0l2.448-3.18a.5.5 0 0 0-.396-.805H5.552a.5.5 0 0 0-.396.805l2.448 3.18Z"
  })));
};
var _path$2b;
function _extends$2w() {
  _extends$2w = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2w.apply(this, arguments);
}
var SvgSoundOff = function SvgSoundOff(props) {
  return /*#__PURE__*/createElement("svg", _extends$2w({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2b || (_path$2b = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M7.611 1c.215 0 .389.174.389.389v5.51l2.636-2.636a3.89 3.89 0 0 0-1.08-.152V2.556c.816 0 1.59.18 2.286.502l1.304-1.304a.5.5 0 0 1 .707 0l.393.393a.5.5 0 0 1 0 .707L2.854 14.246a.5.5 0 0 1-.707 0l-.393-.393a.5.5 0 0 1 0-.707l2.523-2.523a.778.778 0 0 0-.606-.29H1.389A.389.389 0 0 1 1 9.944V6.056c0-.215.174-.39.389-.39H3.67a.778.778 0 0 0 .667-.377l1.88-3.911A.778.778 0 0 1 6.885 1h.726ZM5.51 13.147 8 10.655v3.956a.389.389 0 0 1-.389.389h-.726a.778.778 0 0 1-.667-.378l-.71-1.475Zm6.152-6.153L9.555 9.1v1.233l.138-.004a2.334 2.334 0 0 0 1.968-3.335ZM13.444 8a3.87 3.87 0 0 0-.644-2.145l1.116-1.116a5.444 5.444 0 0 1-4.36 8.705V11.89A3.889 3.889 0 0 0 13.443 8Z"
  })));
};
var _path$2c;
function _extends$2x() {
  _extends$2x = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2x.apply(this, arguments);
}
var SvgSoundOn = function SvgSoundOn(props) {
  return /*#__PURE__*/createElement("svg", _extends$2x({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2c || (_path$2c = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M7.611 1c.215 0 .389.174.389.389V14.61a.389.389 0 0 1-.389.389h-.726a.778.778 0 0 1-.667-.378l-1.88-3.911a.778.778 0 0 0-.667-.378H1.389A.389.389 0 0 1 1 9.944V6.056c0-.215.174-.39.389-.39H3.67a.778.778 0 0 0 .667-.377l1.88-3.911A.778.778 0 0 1 6.885 1h.726Zm1.945 1.556a5.444 5.444 0 0 1 0 10.888V11.89a3.889 3.889 0 0 0 0-7.778V2.556Zm0 7.777V5.667a2.333 2.333 0 0 1 .137 4.662l-.137.004Z"
  })));
};
var _path$2d;
function _extends$2y() {
  _extends$2y = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2y.apply(this, arguments);
}
var SvgSponsoredBrands = function SvgSponsoredBrands(props) {
  return /*#__PURE__*/createElement("svg", _extends$2y({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2d || (_path$2d = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H2Zm1.333 2.917c0-.322.261-.584.584-.584h3.5c.322 0 .583.261.583.584v3.5A.583.583 0 0 1 7.417 8h-3.5a.583.583 0 0 1-.584-.583v-3.5Zm7 .291c0-.16.13-.291.292-.291h1.75c.161 0 .292.13.292.291v.584c0 .16-.13.291-.292.291h-1.75a.292.292 0 0 1-.292-.291v-.584Zm0 2.334c0-.161.13-.292.292-.292h1.75c.161 0 .292.13.292.292v.583c0 .161-.13.292-.292.292h-1.75a.292.292 0 0 1-.292-.292v-.583Zm-7 4.375c0-.322.261-.584.584-.584h1.166c.322 0 .584.261.584.584v1.166a.583.583 0 0 1-.584.584H3.917a.583.583 0 0 1-.584-.584v-1.166Zm3.5 0c0-.322.261-.584.584-.584h1.166c.322 0 .584.261.584.584v1.166a.583.583 0 0 1-.584.584H7.417a.583.583 0 0 1-.584-.584v-1.166Zm3.5 0c0-.322.261-.584.584-.584h1.166c.322 0 .584.261.584.584v1.166a.583.583 0 0 1-.584.584h-1.166a.583.583 0 0 1-.584-.584v-1.166Z"
  })));
};
var _path$2e;
function _extends$2z() {
  _extends$2z = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2z.apply(this, arguments);
}
var SvgSponsoredDisplay = function SvgSponsoredDisplay(props) {
  return /*#__PURE__*/createElement("svg", _extends$2z({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2e || (_path$2e = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M3.2 1C2.537 1 2 1.522 2 2.167v11.666C2 14.478 2.537 15 3.2 15h9.6c.663 0 1.2-.522 1.2-1.167V2.167C14 1.522 13.463 1 12.8 1H3.2ZM5 3.333c-.331 0-.6.261-.6.584V9.75c0 .322.269.583.6.583h6c.331 0 .6-.26.6-.583V3.917a.592.592 0 0 0-.6-.584H5ZM4.7 11.5c-.166 0-.3.13-.3.292v.583c0 .161.134.292.3.292h6.6c.166 0 .3-.13.3-.292v-.583a.296.296 0 0 0-.3-.292H4.7Z"
  })));
};
var _path$2f, _path2$x;
function _extends$2A() {
  _extends$2A = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2A.apply(this, arguments);
}
var SvgSponsoredProduct = function SvgSponsoredProduct(props) {
  return /*#__PURE__*/createElement("svg", _extends$2A({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2f || (_path$2f = /*#__PURE__*/createElement("path", {
    d: "M1 2.167C1 1.522 1.522 1 2.167 1h11.666C14.478 1 15 1.522 15 2.167v.875c0 .16-.13.291-.292.291H1.292A.292.292 0 0 1 1 3.042v-.875Z"
  })), _path2$x || (_path2$x = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M1.292 5.667A.292.292 0 0 0 1 5.958v7.875C1 14.478 1.522 15 2.167 15h11.666c.645 0 1.167-.522 1.167-1.167V5.958a.292.292 0 0 0-.292-.291H1.292ZM8.41 8.285a.438.438 0 0 0-.822 0L7.17 9.34l-1.093.09c-.394.034-.554.546-.253.814l.832.742-.254 1.11c-.092.4.326.717.664.502L8 12.003l.935.594c.338.215.756-.102.664-.502l-.254-1.11.832-.742c.3-.268.14-.78-.253-.813L8.83 9.339l-.42-1.054Z"
  })));
};
var _path$2g;
function _extends$2B() {
  _extends$2B = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2B.apply(this, arguments);
}
var SvgStarEmpty = function SvgStarEmpty(props) {
  return /*#__PURE__*/createElement("svg", _extends$2B({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2g || (_path$2g = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M6.615 1.726c.512-1.232 2.258-1.232 2.77 0l1.155 2.778 2.999.24c1.33.107 1.87 1.766.856 2.634L12.11 9.335l.698 2.927c.31 1.298-1.102 2.323-2.24 1.628L8 12.322 5.433 13.89c-1.139.695-2.55-.33-2.241-1.628l.698-2.927-2.285-1.957c-1.013-.868-.474-2.527.856-2.634l2.999-.24 1.155-2.778ZM8 3.604l-.81 1.95a1.5 1.5 0 0 1-1.266.919l-2.105.169 1.604 1.373a1.5 1.5 0 0 1 .483 1.487l-.49 2.054 1.802-1.1a1.5 1.5 0 0 1 1.564 0l1.802 1.1-.49-2.054a1.5 1.5 0 0 1 .483-1.487l1.604-1.373-2.105-.17a1.5 1.5 0 0 1-1.265-.918L8 3.604Z"
  })));
};
var _path$2h;
function _extends$2C() {
  _extends$2C = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2C.apply(this, arguments);
}
var SvgStarFull = function SvgStarFull(props) {
  return /*#__PURE__*/createElement("svg", _extends$2C({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2h || (_path$2h = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M6.615 1.726c.513-1.232 2.258-1.232 2.77 0l1.155 2.778 3 .24c1.33.107 1.868 1.766.855 2.634L12.11 9.335l.698 2.927c.31 1.298-1.102 2.323-2.24 1.628L8 12.322 5.433 13.89c-1.139.695-2.55-.33-2.241-1.628l.698-2.927-2.285-1.957c-1.013-.868-.474-2.527.856-2.634l2.999-.24 1.155-2.778ZM8 10.236Z"
  })));
};
var _path$2i;
function _extends$2D() {
  _extends$2D = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2D.apply(this, arguments);
}
var SvgStarHalf = function SvgStarHalf(props) {
  return /*#__PURE__*/createElement("svg", _extends$2D({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2i || (_path$2i = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M9.385 1.726c-.512-1.232-2.257-1.232-2.77 0L5.46 4.504l-2.999.24c-1.33.107-1.87 1.766-.856 2.634L3.89 9.335l-.698 2.927c-.31 1.298 1.102 2.323 2.24 1.628L8 12.322l2.567 1.568c1.14.695 2.551-.33 2.241-1.628l-.698-2.927 2.285-1.957c1.013-.868.474-2.527-.856-2.634l-2.999-.24-1.155-2.778ZM8 3.604l.811 1.95a1.5 1.5 0 0 0 1.265.919l2.105.169-1.604 1.373a1.5 1.5 0 0 0-.483 1.487l.49 2.054-1.802-1.1a1.5 1.5 0 0 0-.782-.22V3.604Z"
  })));
};
var _path$2j;
function _extends$2E() {
  _extends$2E = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2E.apply(this, arguments);
}
var SvgStop = function SvgStop(props) {
  return /*#__PURE__*/createElement("svg", _extends$2E({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2j || (_path$2j = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1ZM3.333 8a4.667 4.667 0 0 1 7.582-3.645L3.685 9.78A4.653 4.653 0 0 1 3.332 8Zm1.752 3.645a4.667 4.667 0 0 0 7.23-5.423l-7.23 5.423Z"
  })));
};
var _path$2k, _path2$y;
function _extends$2F() {
  _extends$2F = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2F.apply(this, arguments);
}
var SvgSubtract = function SvgSubtract(props) {
  return /*#__PURE__*/createElement("svg", _extends$2F({
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2k || (_path$2k = /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
  })), _path2$y || (_path2$y = /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M11.5 8.375a.5.5 0 0 1-.5.5H5a.5.5 0 0 1-.5-.5v-.75a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 .5.5v.75Z",
    fill: "#fff"
  })));
};
var _path$2l;
function _extends$2G() {
  _extends$2G = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2G.apply(this, arguments);
}
var SvgSupport = function SvgSupport(props) {
  return /*#__PURE__*/createElement("svg", _extends$2G({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2l || (_path$2l = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M1 3.204C1 2.54 1.522 2 2.167 2h11.666C14.478 2 15 2.54 15 3.204v8.432c0 .665-.522 1.205-1.167 1.205h-3.5l-1.92 1.983a.57.57 0 0 1-.825 0L5.667 12.84h-3.5C1.522 12.84 1 12.3 1 11.636V3.205Zm3.5 5.42c.644 0 1.167-.539 1.167-1.204 0-.665-.523-1.204-1.167-1.204s-1.167.539-1.167 1.204c0 .665.523 1.205 1.167 1.205ZM9.167 7.42c0 .665-.523 1.205-1.167 1.205s-1.167-.54-1.167-1.205S7.356 6.216 8 6.216s1.167.539 1.167 1.204ZM11.5 8.625c.644 0 1.167-.54 1.167-1.205s-.523-1.204-1.167-1.204-1.167.539-1.167 1.204c0 .665.523 1.205 1.167 1.205Z"
  })));
};
var _path$2m;
function _extends$2H() {
  _extends$2H = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2H.apply(this, arguments);
}
var SvgTeamManagement = function SvgTeamManagement(props) {
  return /*#__PURE__*/createElement("svg", _extends$2H({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2m || (_path$2m = /*#__PURE__*/createElement("path", {
    d: "M7.16 4.7c0 1.215-1.003 2.2-2.24 2.2-1.237 0-2.24-.985-2.24-2.2 0-1.215 1.003-2.2 2.24-2.2 1.237 0 2.24.985 2.24 2.2ZM13.32 4.7c0 1.215-1.003 2.2-2.24 2.2-1.237 0-2.24-.985-2.24-2.2 0-1.215 1.003-2.2 2.24-2.2 1.237 0 2.24.985 2.24 2.2ZM10.1 13.5v-1.65a4.48 4.48 0 0 0-1.286-3.142A3.95 3.95 0 0 1 11.08 8C13.245 8 15 9.724 15 11.85v.913a.743.743 0 0 1-.75.737H10.1ZM8.84 11.85C8.84 9.724 7.085 8 4.92 8S1 9.724 1 11.85v.913c0 .407.336.737.75.737h7.09v-1.65Z"
  })));
};
var _path$2n;
function _extends$2I() {
  _extends$2I = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2I.apply(this, arguments);
}
var SvgTerminated = function SvgTerminated(props) {
  return /*#__PURE__*/createElement("svg", _extends$2I({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2n || (_path$2n = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16ZM8 2C4.7 2 2 4.7 2 8a5.98 5.98 0 0 0 1.7 4.174.511.511 0 0 0 .126.126A5.98 5.98 0 0 0 8 14c3.3 0 6-2.7 6-6a5.98 5.98 0 0 0-1.7-4.174.507.507 0 0 0-.126-.126A5.98 5.98 0 0 0 8 2Zm3.16 2.133A4.973 4.973 0 0 0 8 3C5.25 3 3 5.25 3 8c0 1.196.426 2.297 1.133 3.16l7.027-7.027Zm-6.32 7.734A4.973 4.973 0 0 0 8 13c2.75 0 5-2.25 5-5a4.973 4.973 0 0 0-1.133-3.16L4.84 11.867Z"
  })));
};
var _path$2o;
function _extends$2J() {
  _extends$2J = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2J.apply(this, arguments);
}
var SvgThumbsDown = function SvgThumbsDown(props) {
  return /*#__PURE__*/createElement("svg", _extends$2J({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2o || (_path$2o = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M15 8.687c0 .684-.573 1.244-1.273 1.244H9.718l.636 2.862v.187c0 .249-.127.498-.254.684l-.352.313a.5.5 0 0 1-.681-.016L5.2 10.18c-.254-.188-.382-.499-.382-.872V3.087c0-.684.573-1.244 1.273-1.244h5.727c.51 0 .955.31 1.146.746l1.972 4.262c.064.125.064.311.064.436v1.4ZM1.5 1.843a.5.5 0 0 0-.5.5v6.466a.5.5 0 0 0 .5.5h1.546a.5.5 0 0 0 .5-.5V2.343a.5.5 0 0 0-.5-.5H1.5Z"
  })));
};
var _path$2p;
function _extends$2K() {
  _extends$2K = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2K.apply(this, arguments);
}
var SvgThumbsUp = function SvgThumbsUp(props) {
  return /*#__PURE__*/createElement("svg", _extends$2K({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2p || (_path$2p = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M15 7.313c0-.684-.573-1.244-1.273-1.244H9.718l.636-2.862V3.02c0-.249-.127-.498-.254-.684l-.352-.313a.5.5 0 0 0-.681.016L5.2 5.82c-.254.188-.382.499-.382.872v6.222c0 .684.573 1.244 1.273 1.244h5.727c.51 0 .955-.31 1.146-.746l1.972-4.262C15 9.024 15 8.838 15 8.713v-1.4ZM1.5 14.157a.5.5 0 0 1-.5-.5V7.191a.5.5 0 0 1 .5-.5h1.546a.5.5 0 0 1 .5.5v6.466a.5.5 0 0 1-.5.5H1.5Z"
  })));
};
var _path$2q, _path2$z;
function _extends$2L() {
  _extends$2L = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2L.apply(this, arguments);
}
var SvgTime = function SvgTime(props) {
  return /*#__PURE__*/createElement("svg", _extends$2L({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2q || (_path$2q = /*#__PURE__*/createElement("path", {
    d: "M7.708 5.083a.292.292 0 0 0-.291.292v2.746c0 .077.03.151.085.206l1.63 1.63a.292.292 0 0 0 .412 0l.412-.413a.292.292 0 0 0 0-.413L8.67 7.844a.292.292 0 0 1-.086-.206V5.375a.292.292 0 0 0-.291-.292h-.584Z"
  })), _path2$z || (_path2$z = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1ZM3.333 8a4.667 4.667 0 1 1 9.334 0 4.667 4.667 0 0 1-9.334 0Z"
  })));
};
var _path$2r;
function _extends$2M() {
  _extends$2M = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2M.apply(this, arguments);
}
var SvgTools = function SvgTools(props) {
  return /*#__PURE__*/createElement("svg", _extends$2M({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2r || (_path$2r = /*#__PURE__*/createElement("path", {
    d: "M1.657 4.828a2.237 2.237 0 0 1-.655-1.687c.01-.215.265-.288.418-.135l.633.633a.56.56 0 0 0 .793 0l.793-.793a.56.56 0 0 0 0-.793l-.633-.633c-.153-.153-.08-.408.135-.418A2.242 2.242 0 0 1 5.41 3.823L8 6.414l2.973-2.973a.28.28 0 0 1 .397 0l1.19 1.19a.28.28 0 0 1 0 .396L9.585 8l2.59 2.59a2.242 2.242 0 0 1 2.822 2.269c-.01.215-.265.288-.418.135l-.633-.633a.56.56 0 0 0-.793 0l-.793.793a.56.56 0 0 0 0 .793l.633.633c.153.153.08.408-.135.418a2.242 2.242 0 0 1-2.268-2.821L8 9.586l-2.973 2.973a.28.28 0 0 1-.397 0l-1.19-1.19a.28.28 0 0 1 0-.396L6.415 8l-2.59-2.59c-.749.199-1.58.005-2.167-.582ZM13.154 1.26a.56.56 0 0 1 .793 0l.793.793a.56.56 0 0 1 0 .793l-.992.991a.28.28 0 0 1-.396 0l-1.19-1.189a.28.28 0 0 1 0-.396l.992-.992ZM3.837 13.748a.28.28 0 0 0 0-.396l-1.19-1.19a.28.28 0 0 0-.396 0l-.594.595-.616 1.847a.28.28 0 0 0 .355.355l1.847-.616.594-.595Z"
  })));
};
var _path$2s, _path2$A;
function _extends$2N() {
  _extends$2N = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2N.apply(this, arguments);
}
var SvgTopOfFunnel = function SvgTopOfFunnel(props) {
  return /*#__PURE__*/createElement("svg", _extends$2N({
    width: 16,
    height: 16,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2s || (_path$2s = /*#__PURE__*/createElement("path", {
    d: "M15 1H1a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1Z",
    fill: "#4338CA"
  })), _path2$A || (_path2$A = /*#__PURE__*/createElement("path", {
    d: "M11 10.998H5a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1ZM13 6H3a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1Z",
    fill: "#A5B4FC"
  })));
};
var _path$2t;
function _extends$2O() {
  _extends$2O = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2O.apply(this, arguments);
}
var SvgTraffic = function SvgTraffic(props) {
  return /*#__PURE__*/createElement("svg", _extends$2O({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2t || (_path$2t = /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M12 1a1 1 0 0 0-.707 1.707l.293.293-2.713 2.713-2.318-1.545a1 1 0 0 0-1.262.125l-4 4a1 1 0 0 0 1.414 1.414l3.42-3.42 2.318 1.545a1 1 0 0 0 1.262-.125L13 4.414l.293.293A1 1 0 0 0 15 4V2a1 1 0 0 0-1-1h-2Zm2 12H2a1 1 0 1 0 0 2h12a1 1 0 1 0 0-2Z",
    fill: "#9CA3AF"
  })));
};
var _path$2u, _path2$B;
function _extends$2P() {
  _extends$2P = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2P.apply(this, arguments);
}
var SvgTracking = function SvgTracking(props) {
  return /*#__PURE__*/createElement("svg", _extends$2P({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2u || (_path$2u = /*#__PURE__*/createElement("path", {
    d: "M8 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
  })), _path2$B || (_path2$B = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M7 1.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v.583A6.005 6.005 0 0 1 13.917 7h.583a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-.583A6.005 6.005 0 0 1 9 13.917v.583a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-.583A6.005 6.005 0 0 1 2.083 9H1.5a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h.583A6.005 6.005 0 0 1 7 2.083V1.5ZM4.126 9H4.5a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-.374A4.007 4.007 0 0 1 7 4.126V4.5a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-.374A4.007 4.007 0 0 1 11.874 7H11.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h.374A4.007 4.007 0 0 1 9 11.874V11.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v.374A4.007 4.007 0 0 1 4.126 9Z"
  })));
};
var _path$2v;
function _extends$2Q() {
  _extends$2Q = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2Q.apply(this, arguments);
}
var SvgTrashCan = function SvgTrashCan(props) {
  return /*#__PURE__*/createElement("svg", _extends$2Q({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2v || (_path$2v = /*#__PURE__*/createElement("path", {
    d: "M5.083 1a.583.583 0 0 0-.583.583v.584H1.583A.583.583 0 0 0 1 2.75v1.167c0 .322.261.583.583.583h12.834A.583.583 0 0 0 15 3.917V2.75a.583.583 0 0 0-.583-.583H11.5v-.584A.583.583 0 0 0 10.917 1H5.083ZM2.167 6.833h11.666v7c0 .645-.522 1.167-1.166 1.167H3.333a1.167 1.167 0 0 1-1.166-1.167v-7Z"
  })));
};
var _path$2w, _path2$C, _path3$8;
function _extends$2R() {
  _extends$2R = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2R.apply(this, arguments);
}
var SvgUngroup = function SvgUngroup(props) {
  return /*#__PURE__*/createElement("svg", _extends$2R({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2w || (_path$2w = /*#__PURE__*/createElement("path", {
    d: "m8.832 8.832-.51.231-.104.039a.783.783 0 0 1-.54-.039L3.09 6.982a.438.438 0 0 0-.362 0l-1.51.686a.361.361 0 0 0 0 .664l6.458 2.931c.205.093.441.093.646 0l2.022-.918-1.513-1.513ZM12.291 9.462 10.778 7.95l2.13-.967a.437.437 0 0 1 .362 0l1.512.686a.361.361 0 0 1 0 .664l-2.491 1.13Z"
  })), _path2$C || (_path2$C = /*#__PURE__*/createElement("path", {
    d: "M11.354 11.354 8.323 12.73l-.105.039a.783.783 0 0 1-.54-.039L3.09 10.65a.438.438 0 0 0-.362 0l-1.51.686a.362.362 0 0 0 0 .664l6.458 2.931c.205.093.441.093.646 0l4.545-2.062-1.514-1.514ZM9.769 6.94 5.078 2.25l2.6-1.18a.783.783 0 0 1 .645 0L14.782 4a.361.361 0 0 1 0 .664L9.769 6.94Z"
  })), _path3$8 || (_path3$8 = /*#__PURE__*/createElement("path", {
    d: "M1.754 2.373c.171-.17.448-.17.62 0l11.252 11.253c.17.17.17.448 0 .619l-.619.618a.438.438 0 0 1-.618 0L1.136 3.61a.438.438 0 0 1 0-.618l.619-.619Z"
  })));
};
var _path$2x;
function _extends$2S() {
  _extends$2S = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2S.apply(this, arguments);
}
var SvgUploadFailed = function SvgUploadFailed(props) {
  return /*#__PURE__*/createElement("svg", _extends$2S({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2x || (_path$2x = /*#__PURE__*/createElement("path", {
    d: "m12.542 11.553.708.717.708-.717a.33.33 0 0 1 .472 0l.472.478c.13.133.13.347 0 .479l-.708.717.708.717c.13.132.13.347 0 .479l-.472.478a.33.33 0 0 1-.472 0l-.708-.717-.708.717a.33.33 0 0 1-.472 0l-.472-.478a.341.341 0 0 1 0-.479l.708-.717-.708-.717a.341.341 0 0 1 0-.479l.472-.478a.33.33 0 0 1 .472 0ZM10.625 11.01V7.023h2.444c.39 0 .585-.478.31-.757L8.308 1.13a.434.434 0 0 0-.618 0L2.62 6.265c-.275.28-.08.757.31.757h2.444v3.989a.44.44 0 0 0 .438.443h4.375a.44.44 0 0 0 .437-.443ZM1.438 15A.44.44 0 0 1 1 14.557v-.887a.44.44 0 0 1 .438-.443h9.63l-.089.09A1.236 1.236 0 0 0 10.933 15H1.438Z"
  })));
};
var _path$2y;
function _extends$2T() {
  _extends$2T = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2T.apply(this, arguments);
}
var SvgUpload = function SvgUpload(props) {
  return /*#__PURE__*/createElement("svg", _extends$2T({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2y || (_path$2y = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M10.625 7.006v3.941a.5.5 0 0 1-.5.5h-4.25a.5.5 0 0 1-.5-.5V7.006H3.069a.5.5 0 0 1-.356-.851l4.93-5.006a.5.5 0 0 1 .713 0l4.93 5.006a.5.5 0 0 1-.355.85h-2.306ZM1 14.5a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-.776a.5.5 0 0 0-.5-.5h-13a.5.5 0 0 0-.5.5v.776Z"
  })));
};
var _path$2z;
function _extends$2U() {
  _extends$2U = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2U.apply(this, arguments);
}
var SvgUser = function SvgUser(props) {
  return /*#__PURE__*/createElement("svg", _extends$2U({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2z || (_path$2z = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8.18 8.583c2.344 0 4.457.991 5.945 2.578a6.986 6.986 0 0 1-12.25.41 8.131 8.131 0 0 1 6.304-2.988ZM8 1a3.063 3.063 0 1 1-.163.004L8 1Z"
  })));
};
var _path$2A;
function _extends$2V() {
  _extends$2V = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2V.apply(this, arguments);
}
var SvgVerticalDots = function SvgVerticalDots(props) {
  return /*#__PURE__*/createElement("svg", _extends$2V({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 18 18"
  }, props), _path$2A || (_path$2A = /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    d: "M9 14.4A1.8 1.8 0 1 1 9 18a1.8 1.8 0 0 1 0-3.6zm0-7.2a1.8 1.8 0 1 1 0 3.6 1.8 1.8 0 0 1 0-3.6zM9 0a1.8 1.8 0 1 1 0 3.6A1.8 1.8 0 0 1 9 0z"
  })));
};
var _path$2B, _path2$D, _path3$9;
function _extends$2W() {
  _extends$2W = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2W.apply(this, arguments);
}
var SvgVideoTemplate = function SvgVideoTemplate(props) {
  return /*#__PURE__*/createElement("svg", _extends$2W({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2B || (_path$2B = /*#__PURE__*/createElement("path", {
    d: "M2.167 1C1.522 1 1 1.522 1 2.167v1.166h14V2.167C15 1.522 14.478 1 13.833 1H2.167Z"
  })), _path2$D || (_path2$D = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M15 4.5H1v7h14v-7Zm-4.848 3.778c.242-.123.242-.433 0-.556L6.204 5.709c-.24-.122-.537.033-.537.279v4.024c0 .246.298.4.537.279l3.948-2.013Z"
  })), _path3$9 || (_path3$9 = /*#__PURE__*/createElement("path", {
    d: "M1 12.667h14v1.166c0 .645-.522 1.167-1.167 1.167H2.167A1.167 1.167 0 0 1 1 13.833v-1.166Z"
  })));
};
var _path$2C;
function _extends$2X() {
  _extends$2X = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2X.apply(this, arguments);
}
var SvgVideo = function SvgVideo(props) {
  return /*#__PURE__*/createElement("svg", _extends$2X({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2C || (_path$2C = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm-1.29 9.737 4.125-2.357a.438.438 0 0 0 0-.76L6.71 5.263a.438.438 0 0 0-.654.38v4.714c0 .336.362.547.654.38Z"
  })));
};
var _path$2D, _path2$E;
function _extends$2Y() {
  _extends$2Y = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2Y.apply(this, arguments);
}
var SvgView = function SvgView(props) {
  return /*#__PURE__*/createElement("svg", _extends$2Y({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2D || (_path$2D = /*#__PURE__*/createElement("path", {
    d: "M8 9.6a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2Z"
  })), _path2$E || (_path2$E = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M.366 8a8.003 8.003 0 0 1 15.268 0A8.003 8.003 0 0 1 .366 8ZM11.2 8a3.2 3.2 0 1 1-6.4 0 3.2 3.2 0 0 1 6.4 0Z"
  })));
};
var _path$2E, _path2$F, _path3$a;
function _extends$2Z() {
  _extends$2Z = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2Z.apply(this, arguments);
}
var SvgWebinar = function SvgWebinar(props) {
  return /*#__PURE__*/createElement("svg", _extends$2Z({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2E || (_path$2E = /*#__PURE__*/createElement("path", {
    d: "M10.186 5.885c.301.15.301.58 0 .73L6.841 8.288a.408.408 0 0 1-.591-.365V4.577c0-.303.32-.5.591-.365l3.345 1.673Z"
  })), _path2$F || (_path2$F = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M1 2.75C1 1.784 1.784 1 2.75 1h10.5c.966 0 1.75.784 1.75 1.75v8.458c0 .161-.13.292-.292.292H1.292A.292.292 0 0 1 1 11.208V2.75Zm2.625.583a.292.292 0 0 0-.292.292v5.25c0 .161.13.292.292.292h8.75c.161 0 .292-.13.292-.292v-5.25a.292.292 0 0 0-.292-.292h-8.75Z"
  })), _path3$a || (_path3$a = /*#__PURE__*/createElement("path", {
    d: "M1.292 12.667a.292.292 0 0 0-.292.291v1.75c0 .161.13.292.292.292h13.416c.161 0 .292-.13.292-.292v-1.75a.292.292 0 0 0-.292-.291H1.292Z"
  })));
};
var _path$2F;
function _extends$2_() {
  _extends$2_ = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2_.apply(this, arguments);
}
var SvgXBullet = function SvgXBullet(props) {
  return /*#__PURE__*/createElement("svg", _extends$2_({
    viewBox: "0 0 9 10",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2F || (_path$2F = /*#__PURE__*/createElement("path", {
    clipRule: "evenodd",
    d: "M2.981 6.458a.25.25 0 0 1-.02-.353l.87-.98-.87-.98a.25.25 0 0 1 .02-.353l.374-.332a.25.25 0 0 1 .353.021l.792.891.792-.891a.25.25 0 0 1 .353-.02l.374.331a.25.25 0 0 1 .02.353l-.87.98.87.98a.25.25 0 0 1-.02.353l-.374.332a.25.25 0 0 1-.353-.021L4.5 5.878l-.792.891a.25.25 0 0 1-.353.02l-.374-.331ZM4.5 8.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm0 1a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Z"
  })));
};
var _path$2G;
function _extends$2$() {
  _extends$2$ = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2$.apply(this, arguments);
}
var SvgAed = function SvgAed(props) {
  return /*#__PURE__*/createElement("svg", _extends$2$({
    width: 10,
    height: 12,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2G || (_path$2G = /*#__PURE__*/createElement("path", {
    d: "M.27 0v8.973h1.307V0H.27Zm.691 11.335a.844.844 0 0 1-.108-.38c0-.273.184-.403.443-.403.13 0 .249.024.346.047l.108-.629a1.943 1.943 0 0 0-.551-.083c-.594 0-.972.38-.972.926 0 .178.043.356.13.522H0V12h1.847v-.665H.961ZM3.683 9.045c.476 0 .864-.416.864-.926s-.4-.902-.864-.902c-.475 0-.864.415-.864.925s.41.903.864.903ZM6.76 4.783c1.296.902 1.933 2.006 1.933 2.897h-3.24v1.293H10v-.937c0-1.78-.627-3.122-2.452-4.416l-.789 1.163Z",
    fill: "#374151"
  })));
};
var _path$2H;
function _extends$30() {
  _extends$30 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$30.apply(this, arguments);
}
var SvgAud = function SvgAud(props) {
  return /*#__PURE__*/createElement("svg", _extends$30({
    width: 8,
    height: 14,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2H || (_path$2H = /*#__PURE__*/createElement("path", {
    d: "M4.606 1.063c.584.011 1.174.113 1.769.306.594.183 1.11.462 1.545.838L6.63 3.56a2.26 2.26 0 0 0-.925-.628 2.838 2.838 0 0 0-1.1-.225V5.96c.404.129.808.28 1.212.451.403.172.765.392 1.083.66.33.258.595.575.797.951.202.376.303.838.303 1.386 0 .558-.096 1.052-.287 1.482-.18.419-.43.773-.749 1.063-.308.28-.67.494-1.084.644-.403.15-.828.231-1.274.242V14h-.861v-1.16a6.443 6.443 0 0 1-2.024-.387A5.11 5.11 0 0 1 0 11.342l1.402-1.354c.287.344.648.629 1.084.854.436.215.855.328 1.259.339V7.604a12.47 12.47 0 0 1-1.307-.435 4.237 4.237 0 0 1-1.067-.644 2.811 2.811 0 0 1-.702-.919C.5 5.241.414 4.801.414 4.286c0-.484.09-.914.271-1.29.191-.386.441-.714.75-.982a3.693 3.693 0 0 1 1.067-.628 4.191 4.191 0 0 1 1.243-.29V0h.86v1.063Zm-.861 1.644a1.858 1.858 0 0 0-.988.418c-.287.237-.43.591-.43 1.064 0 .45.132.795.398 1.03.276.226.616.398 1.02.516V2.707Zm.86 8.474c.447-.022.803-.183 1.068-.484.277-.3.415-.692.415-1.176 0-.472-.144-.827-.43-1.063a3.116 3.116 0 0 0-1.052-.596v3.319Z",
    fill: "#374151"
  })));
};
var _path$2I;
function _extends$31() {
  _extends$31 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$31.apply(this, arguments);
}
var SvgBrl = function SvgBrl(props) {
  return /*#__PURE__*/createElement("svg", _extends$31({
    width: 14,
    height: 11,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2I || (_path$2I = /*#__PURE__*/createElement("path", {
    d: "M1.611 9.952V6.163h1.176l2.097 3.789h1.944L4.411 5.986c1.266-.278 2.072-1.124 2.072-2.412 0-1.894-1.637-2.564-3.338-2.564H0v8.942h1.611Zm0-7.603H3.07c.83 0 1.803.24 1.803 1.212 0 1.087-1.036 1.301-1.944 1.301H1.611V2.35ZM11.264 11v-.922c1.47-.076 2.736-.985 2.736-2.64 0-1.616-1.304-2.247-2.583-2.652V2.21c.627.076 1.1.253 1.445.695l1.074-1.01c-.678-.657-1.688-.948-2.647-.998V0h-.78v.897c-1.368.139-2.608.997-2.608 2.538 0 1.351.78 2.008 2.506 2.539v2.765c-.767-.1-1.343-.404-1.726-.922l-1.113 1.01c.755.796 1.701 1.163 2.916 1.251V11h.78Zm-.588-6.44c-.819-.291-1.19-.594-1.19-1.226 0-.707.525-1.124 1.19-1.174v2.4Zm.473 1.64c.754.24 1.266.632 1.266 1.314 0 .86-.614 1.238-1.266 1.289V6.2Z",
    fill: "#374151"
  })));
};
var _path$2J;
function _extends$32() {
  _extends$32 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$32.apply(this, arguments);
}
var SvgCad = function SvgCad(props) {
  return /*#__PURE__*/createElement("svg", _extends$32({
    width: 8,
    height: 14,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2J || (_path$2J = /*#__PURE__*/createElement("path", {
    d: "M4.606 1.063c.584.011 1.174.113 1.769.306.594.183 1.11.462 1.545.838L6.63 3.56a2.26 2.26 0 0 0-.925-.628 2.838 2.838 0 0 0-1.1-.225V5.96c.404.129.808.28 1.212.451.403.172.765.392 1.083.66.33.258.595.575.797.951.202.376.303.838.303 1.386 0 .558-.096 1.052-.287 1.482-.18.419-.43.773-.749 1.063-.308.28-.67.494-1.084.644-.403.15-.828.231-1.274.242V14h-.861v-1.16a6.443 6.443 0 0 1-2.024-.387A5.11 5.11 0 0 1 0 11.342l1.402-1.354c.287.344.648.629 1.084.854.436.215.855.328 1.259.339V7.604a12.47 12.47 0 0 1-1.307-.435 4.237 4.237 0 0 1-1.067-.644 2.811 2.811 0 0 1-.702-.919C.5 5.241.414 4.801.414 4.286c0-.484.09-.914.271-1.29.191-.386.441-.714.75-.982a3.693 3.693 0 0 1 1.067-.628 4.191 4.191 0 0 1 1.243-.29V0h.86v1.063Zm-.861 1.644a1.858 1.858 0 0 0-.988.418c-.287.237-.43.591-.43 1.064 0 .45.132.795.398 1.03.276.226.616.398 1.02.516V2.707Zm.86 8.474c.447-.022.803-.183 1.068-.484.277-.3.415-.692.415-1.176 0-.472-.144-.827-.43-1.063a3.116 3.116 0 0 0-1.052-.596v3.319Z",
    fill: "#374151"
  })));
};
var _path$2K;
function _extends$33() {
  _extends$33 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$33.apply(this, arguments);
}
var SvgCny = function SvgCny(props) {
  return /*#__PURE__*/createElement("svg", _extends$33({
    width: 10,
    height: 12,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2K || (_path$2K = /*#__PURE__*/createElement("path", {
    d: "M6.038 12V9.373h3.045V8.05H6.038V6.746h3.045V5.424H6.661L10 0H7.682L5.104 4.881 2.422 0H0l3.426 5.424H.917v1.322h3.045V8.05H.917v1.322h3.045V12h2.076Z",
    fill: "#374151"
  })));
};
var _path$2L;
function _extends$34() {
  _extends$34 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$34.apply(this, arguments);
}
var SvgEur = function SvgEur(props) {
  return /*#__PURE__*/createElement("svg", _extends$34({
    width: 9,
    height: 12,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2L || (_path$2L = /*#__PURE__*/createElement("path", {
    d: "M6.216 12c1.123 0 2.167-.29 2.784-.694l-.649-1.548c-.6.371-1.265.532-2.072.532-1.581 0-2.768-.951-3.052-2.161h3.385l.427-1.387h-4.05c-.031-.21-.031-.5-.031-.694 0-.306.016-.564.047-.79h4.413l.443-1.387H3.306C3.733 2.468 5.03 1.694 6.31 1.694c.775 0 1.534.145 2.025.451l.6-1.613C8.368.226 7.419 0 6.407 0 3.891 0 2.009 1.516 1.392 3.871h-.87L.047 5.258h1.14a5.98 5.98 0 0 0-.048.774c0 .226 0 .468.016.71H.443L0 8.129h1.329C1.835 10.226 3.337 12 6.216 12Z",
    fill: "#374151"
  })));
};
var _path$2M;
function _extends$35() {
  _extends$35 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$35.apply(this, arguments);
}
var SvgGbp = function SvgGbp(props) {
  return /*#__PURE__*/createElement("svg", _extends$35({
    width: 9,
    height: 12,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2M || (_path$2M = /*#__PURE__*/createElement("path", {
    d: "M8.427 12v-1.769H3.822V6.793h2.85V5.405h-2.85v-1.29c0-1.04.452-2.462 2.12-2.462.73 0 1.39.264 1.824.71L9 .993C8.236.364 7.176 0 5.942 0c-3.197 0-4.24 2.397-4.24 4.033v1.372H0v1.388h1.703v3.438H0V12h8.427Z",
    fill: "#374151"
  })));
};
var _path$2N;
function _extends$36() {
  _extends$36 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$36.apply(this, arguments);
}
var SvgInr = function SvgInr(props) {
  return /*#__PURE__*/createElement("svg", _extends$36({
    width: 10,
    height: 12,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2N || (_path$2N = /*#__PURE__*/createElement("path", {
    d: "M9.48 1.39 10 0H.502L0 1.39h3.226c1.326 0 2.168.508 2.42 1.576H.501L0 4.373h5.663C5.43 5.61 4.463 6.27 2.383 6.27H1.238v1.373L5.95 12h2.706L3.763 7.593c2.384-.203 3.746-1.542 4.05-3.22H9.48L10 2.966H7.832a2.713 2.713 0 0 0-.753-1.576H9.48Z",
    fill: "#374151"
  })));
};
var _path$2O;
function _extends$37() {
  _extends$37 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$37.apply(this, arguments);
}
var SvgJpy = function SvgJpy(props) {
  return /*#__PURE__*/createElement("svg", _extends$37({
    width: 10,
    height: 12,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2O || (_path$2O = /*#__PURE__*/createElement("path", {
    d: "M6.038 12V9.373h3.045V8.05H6.038V6.746h3.045V5.424H6.661L10 0H7.682L5.104 4.881 2.422 0H0l3.426 5.424H.917v1.322h3.045V8.05H.917v1.322h3.045V12h2.076Z",
    fill: "#374151"
  })));
};
var _path$2P;
function _extends$38() {
  _extends$38 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$38.apply(this, arguments);
}
var SvgMxn = function SvgMxn(props) {
  return /*#__PURE__*/createElement("svg", _extends$38({
    width: 8,
    height: 14,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2P || (_path$2P = /*#__PURE__*/createElement("path", {
    d: "M4.606 1.063c.584.011 1.174.113 1.769.306.594.183 1.11.462 1.545.838L6.63 3.56a2.26 2.26 0 0 0-.925-.628 2.838 2.838 0 0 0-1.1-.225V5.96c.404.129.808.28 1.212.451.403.172.765.392 1.083.66.33.258.595.575.797.951.202.376.303.838.303 1.386 0 .558-.096 1.052-.287 1.482-.18.419-.43.773-.749 1.063-.308.28-.67.494-1.084.644-.403.15-.828.231-1.274.242V14h-.861v-1.16a6.443 6.443 0 0 1-2.024-.387A5.11 5.11 0 0 1 0 11.342l1.402-1.354c.287.344.648.629 1.084.854.436.215.855.328 1.259.339V7.604a12.47 12.47 0 0 1-1.307-.435 4.237 4.237 0 0 1-1.067-.644 2.811 2.811 0 0 1-.702-.919C.5 5.241.414 4.801.414 4.286c0-.484.09-.914.271-1.29.191-.386.441-.714.75-.982a3.693 3.693 0 0 1 1.067-.628 4.191 4.191 0 0 1 1.243-.29V0h.86v1.063Zm-.861 1.644a1.858 1.858 0 0 0-.988.418c-.287.237-.43.591-.43 1.064 0 .45.132.795.398 1.03.276.226.616.398 1.02.516V2.707Zm.86 8.474c.447-.022.803-.183 1.068-.484.277-.3.415-.692.415-1.176 0-.472-.144-.827-.43-1.063a3.116 3.116 0 0 0-1.052-.596v3.319Z",
    fill: "#374151"
  })));
};
var _path$2Q;
function _extends$39() {
  _extends$39 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$39.apply(this, arguments);
}
var SvgPln = function SvgPln(props) {
  return /*#__PURE__*/createElement("svg", _extends$39({
    width: 12,
    height: 12,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2Q || (_path$2Q = /*#__PURE__*/createElement("path", {
    d: "M6.72 12v-1.47H2.64l3.9-4.2V4.74H.69v1.44h3.705L.51 10.41V12h6.21ZM10.146 12V6.075l1.035-1.02V3.45l-1.035 1.035V.66h-1.8v5.235L7.311 6.93v1.635L8.346 7.53V12h1.8Z",
    fill: "#374151"
  })));
};
var _path$2R;
function _extends$3a() {
  _extends$3a = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$3a.apply(this, arguments);
}
var SvgSek = function SvgSek(props) {
  return /*#__PURE__*/createElement("svg", _extends$3a({
    width: 12,
    height: 12,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2R || (_path$2R = /*#__PURE__*/createElement("path", {
    d: "M1.741 11.5V7.892h.044L4.542 11.5h2.307L3.642 7.644 6.66 4.458H4.426L1.785 7.426H1.74V.5H0v11h1.741ZM9.591 11.5V7.702c0-.509.305-1.877 1.872-1.877.16 0 .305.015.537.073v-1.6a1.965 1.965 0 0 0-.42-.044c-.915 0-1.655.567-2.032 1.368h-.03V4.458H7.85V11.5H9.59Z",
    fill: "#374151"
  })));
};
var _path$2S;
function _extends$3b() {
  _extends$3b = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$3b.apply(this, arguments);
}
var SvgSgd = function SvgSgd(props) {
  return /*#__PURE__*/createElement("svg", _extends$3b({
    width: 14,
    height: 11,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2S || (_path$2S = /*#__PURE__*/createElement("path", {
    d: "M3.112 10.18c1.78 0 3.304-.935 3.304-2.855 0-1.756-1.486-2.26-2.651-2.614-1.024-.316-1.729-.556-1.729-1.377 0-.871.858-1.225 1.601-1.225.64 0 1.319.328 1.665.77l1.05-1.136c-.692-.644-1.729-.96-2.638-.96-1.64 0-3.317.808-3.317 2.627 0 1.667 1.191 2.197 2.292 2.538 1.14.354 2.088.62 2.088 1.554 0 .96-.884 1.326-1.64 1.326-.717 0-1.536-.38-1.984-.985L0 8.903c.717.834 1.934 1.276 3.112 1.276ZM11.26 11v-.922c1.472-.076 2.74-.985 2.74-2.64 0-1.616-1.306-2.247-2.587-2.652V2.21c.628.076 1.101.253 1.447.695l1.076-1.01c-.679-.657-1.69-.948-2.65-.998V0h-.782v.897c-1.37.139-2.613.997-2.613 2.538 0 1.351.782 2.008 2.51 2.539v2.765c-.768-.1-1.344-.404-1.728-.922l-1.115 1.01c.756.796 1.704 1.163 2.92 1.251V11h.781Zm-.59-6.44c-.82-.291-1.19-.594-1.19-1.226 0-.707.524-1.124 1.19-1.174v2.4Zm.474 1.64c.756.24 1.268.632 1.268 1.314 0 .86-.615 1.238-1.268 1.289V6.2Z",
    fill: "#374151"
  })));
};
var _path$2T;
function _extends$3c() {
  _extends$3c = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$3c.apply(this, arguments);
}
var SvgTry = function SvgTry(props) {
  return /*#__PURE__*/createElement("svg", _extends$3c({
    width: 10,
    height: 12,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2T || (_path$2T = /*#__PURE__*/createElement("path", {
    d: "M3.299 12C6.962 12 8.993 10.322 10 6.153H7.83c-.504 2.83-1.98 4.033-3.872 4.033V6.22l3.108-1.017.278-1.27-3.386 1.1V3.916l3.108-1.017.278-1.27-3.386 1.1V0H1.806v3.44L.19 3.967 0 5.22l1.806-.593v1.119L.19 6.27 0 7.525l1.806-.593V12h1.493Z",
    fill: "#374151"
  })));
};
var _path$2U;
function _extends$3d() {
  _extends$3d = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$3d.apply(this, arguments);
}
var SvgUsd = function SvgUsd(props) {
  return /*#__PURE__*/createElement("svg", _extends$3d({
    width: 8,
    height: 14,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2U || (_path$2U = /*#__PURE__*/createElement("path", {
    d: "M4.606 1.063c.584.011 1.174.113 1.769.306.594.183 1.11.462 1.545.838L6.63 3.56a2.26 2.26 0 0 0-.925-.628 2.838 2.838 0 0 0-1.1-.225V5.96c.404.129.808.28 1.212.451.403.172.765.392 1.083.66.33.258.595.575.797.951.202.376.303.838.303 1.386 0 .558-.096 1.052-.287 1.482-.18.419-.43.773-.749 1.063-.308.28-.67.494-1.084.644-.403.15-.828.231-1.274.242V14h-.861v-1.16a6.443 6.443 0 0 1-2.024-.387A5.11 5.11 0 0 1 0 11.342l1.402-1.354c.287.344.648.629 1.084.854.436.215.855.328 1.259.339V7.604a12.47 12.47 0 0 1-1.307-.435 4.237 4.237 0 0 1-1.067-.644 2.811 2.811 0 0 1-.702-.919C.5 5.241.414 4.801.414 4.286c0-.484.09-.914.271-1.29.191-.386.441-.714.75-.982a3.693 3.693 0 0 1 1.067-.628 4.191 4.191 0 0 1 1.243-.29V0h.86v1.063Zm-.861 1.644a1.858 1.858 0 0 0-.988.418c-.287.237-.43.591-.43 1.064 0 .45.132.795.398 1.03.276.226.616.398 1.02.516V2.707Zm.86 8.474c.447-.022.803-.183 1.068-.484.277-.3.415-.692.415-1.176 0-.472-.144-.827-.43-1.063a3.116 3.116 0 0 0-1.052-.596v3.319Z",
    fill: "#374151"
  })));
};
var _path$2V, _path2$G;
function _extends$3e() {
  _extends$3e = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$3e.apply(this, arguments);
}
var SvgLoading = function SvgLoading(props) {
  return /*#__PURE__*/createElement("svg", _extends$3e({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2V || (_path$2V = /*#__PURE__*/createElement("path", {
    opacity: 0.3,
    d: "M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0ZM2.495 8a5.505 5.505 0 1 0 11.01 0 5.505 5.505 0 0 0-11.01 0Z"
  })), _path2$G || (_path2$G = /*#__PURE__*/createElement("path", {
    d: "M14.753 8c.689 0 1.257-.562 1.15-1.242A8.003 8.003 0 0 0 9.243.097C8.561-.01 8 .558 8 1.247c0 .69.565 1.233 1.237 1.388a5.503 5.503 0 0 1 4.128 4.128C13.52 7.435 14.064 8 14.753 8Z"
  })));
};
var BlueIndigo = function BlueIndigo() {
  return React__default.createElement("linearGradient", {
    id: "gradient-blue-indigo",
    x1: "97.403%",
    x2: "3.855%",
    y1: "12.901%",
    y2: "77.108%"
  }, React__default.createElement("stop", {
    offset: "0%",
    stopColor: "#42A5F5"
  }), React__default.createElement("stop", {
    offset: "100%",
    stopColor: "#6366f1"
  }));
};
var _excluded = ["iconType", "iconColor", "iconGradientColor", "iconSize", "className"];
var _gradientMap;
var UIIconType;
(function (UIIconType) {
  /* Core Icons */
  UIIconType["account"] = "account";
  UIIconType["activity"] = "activity";
  UIIconType["adSchool"] = "ad-school";
  UIIconType["add"] = "add";
  UIIconType["aggregate"] = "aggregate";
  UIIconType["amazonBetaTag"] = "amazon-beta-tag";
  UIIconType["approve"] = "approve";
  UIIconType["arrowDown"] = "arrow-down";
  UIIconType["arrowUp"] = "arrow-up";
  UIIconType["asterisk"] = "asterisk";
  UIIconType["attribution"] = "attribution";
  UIIconType["backArrow"] = "back-arrow";
  UIIconType["beak"] = "beak";
  UIIconType["bestSellerRank"] = "best-seller-rank";
  UIIconType["billingError"] = "billing-error";
  UIIconType["billing"] = "billing";
  UIIconType["binoculars"] = "binoculars";
  UIIconType["book"] = "book";
  UIIconType["bottomOfFunnel"] = "bottom-of-funnel";
  UIIconType["brands"] = "brands";
  UIIconType["bug"] = "bug";
  UIIconType["calendar"] = "calendar";
  UIIconType["capital"] = "capital";
  UIIconType["card"] = "card";
  UIIconType["checkboxDisabled"] = "checkbox-disabled";
  UIIconType["checkboxIndeterminate"] = "checkbox-indeterminate";
  UIIconType["checkboxSelected"] = "checkbox-selected";
  UIIconType["checkboxUnselected"] = "checkbox-unselected";
  UIIconType["checkedBullet"] = "checked-bullet";
  UIIconType["chevron"] = "chevron";
  UIIconType["clear"] = "clear";
  UIIconType["close"] = "close";
  UIIconType["color"] = "color";
  UIIconType["collapse"] = "collapse";
  UIIconType["columns"] = "columns";
  UIIconType["comment"] = "comment";
  UIIconType["competitor"] = "competitor";
  UIIconType["copyDown"] = "copy-down";
  UIIconType["copyLeft"] = "copy-left";
  UIIconType["copyRight"] = "copy-right";
  UIIconType["copyUp"] = "copy-up";
  UIIconType["copy"] = "copy";
  UIIconType["dashboard"] = "dashboard";
  UIIconType["defense"] = "defense";
  UIIconType["down"] = "down";
  UIIconType["download"] = "download";
  UIIconType["dsp"] = "dsp";
  UIIconType["editBid"] = "edit-bid";
  UIIconType["edit"] = "edit";
  UIIconType["email"] = "email";
  UIIconType["error"] = "error";
  UIIconType["experiments"] = "experiments";
  UIIconType["engineVisibility"] = "engine-visibility";
  UIIconType["filter"] = "filter";
  UIIconType["fullscreenClose"] = "fullscreen-close";
  UIIconType["fullscreenOpen"] = "fullscreen-open";
  UIIconType["funnel"] = "funnel";
  UIIconType["gift"] = "gift";
  UIIconType["goal"] = "goal";
  UIIconType["group"] = "group";
  UIIconType["hashtag"] = "hashtag";
  UIIconType["hide"] = "hide";
  UIIconType["home"] = "home";
  UIIconType["image"] = "image";
  UIIconType["info"] = "info";
  UIIconType["keywordBoost"] = "keyword-boost";
  UIIconType["keyword"] = "keyword";
  UIIconType["language"] = "language";
  UIIconType["legal"] = "legal";
  UIIconType["lightbulb"] = "lightbulb";
  UIIconType["location"] = "location";
  UIIconType["lock"] = "lock";
  UIIconType["megaphone"] = "megaphone";
  UIIconType["menu"] = "menu";
  UIIconType["middleOfFunnel"] = "middle-of-funnel";
  UIIconType["more"] = "more";
  UIIconType["name"] = "name";
  UIIconType["negativeTarget"] = "negative-target";
  UIIconType["nested"] = "nested";
  UIIconType["neutral"] = "neutral";
  UIIconType["noFunnel"] = "no-funnel";
  UIIconType["noImage"] = "no-image";
  UIIconType["noInventory"] = "no-inventory";
  UIIconType["notes"] = "notes";
  UIIconType["notification"] = "notification";
  UIIconType["open"] = "open";
  UIIconType["optimize"] = "optimize";
  UIIconType["organization"] = "organization";
  UIIconType["outOfBudget"] = "out-of-budget";
  UIIconType["page"] = "page";
  UIIconType["partial"] = "partial";
  UIIconType["paused"] = "paused";
  UIIconType["pending"] = "pending";
  UIIconType["percent"] = "percent";
  UIIconType["pieChart"] = "pie-chart";
  UIIconType["play"] = "play";
  UIIconType["portfolio"] = "portfolio";
  UIIconType["posts"] = "posts";
  UIIconType["processing"] = "processing";
  UIIconType["productCollection"] = "product-collection";
  UIIconType["product"] = "product";
  UIIconType["quote"] = "quote";
  UIIconType["radioSelected"] = "radio-selected";
  UIIconType["radioUnselected"] = "radio-unselected";
  UIIconType["rearrangeHorizonal"] = "rearrange-horizontal";
  UIIconType["rearrangeVertical"] = "rearrange-vertical";
  UIIconType["restart"] = "restart";
  UIIconType["return"] = "return";
  UIIconType["rocket"] = "rocket";
  UIIconType["sales"] = "sales";
  UIIconType["search"] = "search";
  UIIconType["searchInsights"] = "search-insights";
  UIIconType["selectedRadio"] = "selected-radio";
  UIIconType["setting"] = "setting";
  UIIconType["shield"] = "shield";
  UIIconType["shoppingCart"] = "shopping-cart";
  UIIconType["signOut"] = "sign-out";
  UIIconType["similar"] = "similar";
  UIIconType["sortDown"] = "sort-down";
  UIIconType["sortLeft"] = "sort-left";
  UIIconType["sortRight"] = "sort-right";
  UIIconType["sortUp"] = "sort-up";
  UIIconType["sort"] = "sort";
  UIIconType["soundOn"] = "sound-on";
  UIIconType["soundOff"] = "sound-off";
  UIIconType["sponsoredBrands"] = "sponsored-brands";
  UIIconType["sponsoredDisplay"] = "sponsored-display";
  UIIconType["sponsoredProducts"] = "sponsored-products";
  UIIconType["starEmpty"] = "start-empty";
  UIIconType["starFull"] = "star-full";
  UIIconType["starHalf"] = "star-half";
  UIIconType["stop"] = "stop";
  UIIconType["subtract"] = "subtract";
  UIIconType["support"] = "support";
  UIIconType["teamMangement"] = "team-management";
  UIIconType["terminated"] = "terminated";
  UIIconType["thumbsDown"] = "thumbs-down";
  UIIconType["thumbsUp"] = "thumbs-up";
  UIIconType["time"] = "time";
  UIIconType["tools"] = "tools";
  UIIconType["topOfFunnel"] = "top-of-funnel";
  UIIconType["traffic"] = "traffic";
  UIIconType["tracking"] = "tracking";
  UIIconType["trashCan"] = "trash-can";
  UIIconType["ungroup"] = "ungroup";
  UIIconType["uploadFailed"] = "upload-failed";
  UIIconType["upload"] = "upload";
  UIIconType["user"] = "user";
  UIIconType["verticalDots"] = "vertical-dots";
  UIIconType["videoTemplate"] = "video-template";
  UIIconType["video"] = "video";
  UIIconType["view"] = "view";
  UIIconType["webinar"] = "webinar";
  UIIconType["xBullet"] = "x-bullet";
  UIIconType["marketShare"] = "market-share";
  /* Benchmarker Icons */
  UIIconType["accountStructure"] = "accountStructure";
  UIIconType["adFormats"] = "adFormats";
  UIIconType["benchmarker"] = "benchmarker";
  UIIconType["impactDrivers"] = "impactDrivers";
  UIIconType["opportunities"] = "opportunities";
  UIIconType["performanceMatrix"] = "performanceMatrix";
  UIIconType["productAnalysis"] = "productAnalysis";
  UIIconType["summary"] = "summary";
  UIIconType["takeAction"] = "takeAction";
  UIIconType["trends"] = "trends";
  /* Brand Icons */
  UIIconType["adwords"] = "adwords";
  UIIconType["amazon"] = "amazon";
  UIIconType["bing"] = "bing";
  UIIconType["bingColor"] = "bing_color";
  UIIconType["facebook"] = "facebook";
  UIIconType["facebookColor"] = "facebook_color";
  UIIconType["google"] = "google";
  UIIconType["googleColor"] = "google_color";
  UIIconType["instacart"] = "instacart";
  UIIconType["instagram"] = "instagram";
  UIIconType["instagramColor"] = "instagram_color";
  UIIconType["linkedin"] = "linkedin";
  UIIconType["perpetua"] = "perpetua";
  UIIconType["snap"] = "snap";
  UIIconType["target"] = "target";
  UIIconType["tiktokLight"] = "tiktok_light";
  UIIconType["tiktok"] = "tiktok";
  UIIconType["twitter"] = "twitter";
  UIIconType["walmart"] = "walmart";
  UIIconType["wechat"] = "wechat";
  UIIconType["whytespyder"] = "whytespyder";
  UIIconType["yahoo"] = "yahoo";
  UIIconType["youtube"] = "youtube";
  /* Loading */
  UIIconType["loading"] = "loading";
  /* Ad Type Icons */
  UIIconType["sp"] = "sp";
  UIIconType["sb"] = "sb";
  UIIconType["sd"] = "sd";
  /* Currency Icons */
  UIIconType["aed"] = "aed";
  UIIconType["aud"] = "aud";
  UIIconType["brl"] = "brl";
  UIIconType["cad"] = "cad";
  UIIconType["cny"] = "cny";
  UIIconType["eur"] = "eur";
  UIIconType["gbp"] = "gbp";
  UIIconType["inr"] = "inr";
  UIIconType["jpy"] = "jpy";
  UIIconType["mxn"] = "mxn";
  UIIconType["pln"] = "pln";
  UIIconType["sek"] = "sek";
  UIIconType["sgd"] = "sgd";
  UIIconType["try"] = "try";
  UIIconType["usd"] = "usd";
})(UIIconType || (UIIconType = {}));
var iconComponentMap = /*#__PURE__*/new Map([[UIIconType.account, SvgAccount], [UIIconType.activity, SvgActivity], [UIIconType.adSchool, SvgAdSchool], [UIIconType.add, SvgAdd], [UIIconType.aggregate, SvgAggregate], [UIIconType.amazonBetaTag, SvgAmazonBetaTag], [UIIconType.approve, SvgApprove], [UIIconType.arrowDown, SvgArrowDown], [UIIconType.arrowUp, SvgArrowUp], [UIIconType.asterisk, SvgAsterisk], [UIIconType.attribution, SvgAttribution], [UIIconType.backArrow, SvgBackArrow], [UIIconType.beak, SvgBeak], [UIIconType.bestSellerRank, SvgBestSellerRank], [UIIconType.billingError, SvgBillingError], [UIIconType.billing, SvgBilling], [UIIconType.binoculars, SvgBinoculars], [UIIconType.book, SvgBook], [UIIconType.bottomOfFunnel, SvgBottomOfFunnel], [UIIconType.brands, SvgBrands], [UIIconType.bug, SvgBug], [UIIconType.calendar, SvgCalendar], [UIIconType.capital, SvgCapital], [UIIconType.card, SvgCard], [UIIconType.checkboxDisabled, SvgCheckboxDisabled], [UIIconType.checkboxIndeterminate, SvgCheckboxIndeterminate], [UIIconType.checkboxSelected, SvgCheckboxSelected], [UIIconType.checkboxUnselected, SvgCheckboxUnselected], [UIIconType.checkedBullet, SvgCheckedBullet], [UIIconType.chevron, SvgChevron], [UIIconType.clear, SvgClear], [UIIconType.close, SvgClose], [UIIconType.collapse, SvgCollapse], [UIIconType.color, SvgColor], [UIIconType.columns, SvgColumns], [UIIconType.comment, SvgComment], [UIIconType.competitor, SvgCompetitor], [UIIconType.copyDown, SvgCopyDown], [UIIconType.copyLeft, SvgCopyLeft], [UIIconType.copyRight, SvgCopyRight], [UIIconType.copyUp, SvgCopyUp], [UIIconType.copy, SvgCopy], [UIIconType.dashboard, SvgDashboard], [UIIconType.defense, SvgDefense], [UIIconType.down, SvgDown], [UIIconType.download, SvgDownload], [UIIconType.dsp, SvgDsp], [UIIconType.editBid, SvgEditBid], [UIIconType.edit, SvgEdit], [UIIconType.email, SvgEmail], [UIIconType.error, SvgError], [UIIconType.experiments, SvgExperiments], [UIIconType.engineVisibility, SvgEngineVisibility], [UIIconType.filter, SvgFilter], [UIIconType.fullscreenClose, SvgFullscreenClose], [UIIconType.fullscreenOpen, SvgFullscreenOpen], [UIIconType.funnel, SvgFunnel], [UIIconType.gift, SvgGift], [UIIconType.goal, SvgGoal], [UIIconType.group, SvgGroup], [UIIconType.hashtag, SvgHashtag], [UIIconType.hide, SvgHide], [UIIconType.home, SvgHome], [UIIconType.image, SvgImage], [UIIconType.info, SvgInfo], [UIIconType.keywordBoost, SvgKeywordBoost], [UIIconType.keyword, SvgKeyword], [UIIconType.language, SvgLanguage], [UIIconType.legal, SvgLegal], [UIIconType.lightbulb, SvgLightbulb], [UIIconType.location, SvgLocation], [UIIconType.lock, SvgLock], [UIIconType.marketShare, SvgMarketShare], [UIIconType.megaphone, SvgMegaphone], [UIIconType.menu, SvgMenu], [UIIconType.middleOfFunnel, SvgMiddleOfFunnel], [UIIconType.more, SvgMore], [UIIconType.name, SvgName], [UIIconType.negativeTarget, SvgNegativeTarget], [UIIconType.nested, SvgNested], [UIIconType.neutral, SvgNeutral], [UIIconType.noFunnel, SvgNoFunnel], [UIIconType.noImage, SvgNoImage], [UIIconType.noInventory, SvgNoInventory], [UIIconType.notes, SvgNotes], [UIIconType.notification, SvgNotification], [UIIconType.open, SvgOpen], [UIIconType.optimize, SvgOptimize], [UIIconType.organization, SvgOrganization], [UIIconType.outOfBudget, SvgOutOfBudget], [UIIconType.page, SvgPage], [UIIconType.partial, SvgPartial], [UIIconType.paused, SvgPaused], [UIIconType.pending, SvgPending], [UIIconType.percent, SvgPercent], [UIIconType.pieChart, SvgPieChart], [UIIconType.play, SvgPlay], [UIIconType.portfolio, SvgPortfolios], [UIIconType.posts, SvgPosts], [UIIconType.processing, SvgProcessing], [UIIconType.product, SvgProduct], [UIIconType.productCollection, SvgProductCollection], [UIIconType.quote, SvgQuote], [UIIconType.radioSelected, SvgRadioSelected], [UIIconType.radioUnselected, SvgRadioUnselected], [UIIconType.rearrangeHorizonal, SvgRearrangeHorizontal], [UIIconType.rearrangeVertical, SvgRearrangeVertical], [UIIconType.restart, SvgRestart], [UIIconType["return"], SvgReturn], [UIIconType.rocket, SvgRocket], [UIIconType.sales, SvgSales], [UIIconType.search, SvgSearch], [UIIconType.searchInsights, SvgSearchInsights], [UIIconType.selectedRadio, SvgRadioSelected], [UIIconType.setting, SvgSetting], [UIIconType.shield, SvgShield], [UIIconType.shoppingCart, SvgShoppingCart], [UIIconType.signOut, SvgSignOut], [UIIconType.similar, SvgSimilar], [UIIconType.sortDown, SvgSortDown], [UIIconType.sortLeft, SvgSortLeft], [UIIconType.sortRight, SvgSortRight], [UIIconType.sortUp, SvgSortUp], [UIIconType.sort, SvgSort], [UIIconType.soundOff, SvgSoundOff], [UIIconType.soundOn, SvgSoundOn], [UIIconType.sponsoredBrands, SvgSponsoredBrands], [UIIconType.sponsoredDisplay, SvgSponsoredDisplay], [UIIconType.sponsoredProducts, SvgSponsoredProduct], [UIIconType.starEmpty, SvgStarEmpty], [UIIconType.starFull, SvgStarFull], [UIIconType.starHalf, SvgStarHalf], [UIIconType.stop, SvgStop], [UIIconType.subtract, SvgSubtract], [UIIconType.support, SvgSupport], [UIIconType.teamMangement, SvgTeamManagement], [UIIconType.terminated, SvgTerminated], [UIIconType.thumbsDown, SvgThumbsDown], [UIIconType.thumbsUp, SvgThumbsUp], [UIIconType.time, SvgTime], [UIIconType.tools, SvgTools], [UIIconType.topOfFunnel, SvgTopOfFunnel], [UIIconType.traffic, SvgTraffic], [UIIconType.tracking, SvgTracking], [UIIconType.trashCan, SvgTrashCan], [UIIconType.ungroup, SvgUngroup], [UIIconType.uploadFailed, SvgUploadFailed], [UIIconType.upload, SvgUpload], [UIIconType.user, SvgUser], [UIIconType.verticalDots, SvgVerticalDots], [UIIconType.videoTemplate, SvgVideoTemplate], [UIIconType.video, SvgVideo], [UIIconType.view, SvgView], [UIIconType.webinar, SvgWebinar], [UIIconType.xBullet, SvgXBullet], /* Benchmarker Icons */
[UIIconType.accountStructure, SvgAccountStructure], [UIIconType.adFormats, SvgAdFormats], [UIIconType.benchmarker, SvgBenchmarker], [UIIconType.impactDrivers, SvgImpactDrivers], [UIIconType.opportunities, SvgOpportunities], [UIIconType.performanceMatrix, SvgPerformanceMatrix], [UIIconType.productAnalysis, SvgProductAnalysis], [UIIconType.summary, SvgSummary], [UIIconType.takeAction, SvgTakeAction], [UIIconType.trends, SvgTrends], /* Brand Icons */
[UIIconType.adwords, SvgAdwords], [UIIconType.amazon, SvgAmazon], [UIIconType.bing, SvgBing], [UIIconType.bingColor, SvgBingColor], [UIIconType.facebook, SvgFacebook], [UIIconType.facebookColor, SvgFacebookColor], [UIIconType.google, SvgGoogle], [UIIconType.googleColor, SvgGoogleColor], [UIIconType.instacart, SvgInstacart], [UIIconType.instagram, SvgInstagram], [UIIconType.instagramColor, SvgInstagramColor], [UIIconType.linkedin, SvgLinkedin], [UIIconType.perpetua, SvgPerpetua], [UIIconType.snap, SvgSnap], [UIIconType.target, SvgTarget], [UIIconType.tiktokLight, SvgTiktokLight], [UIIconType.tiktok, SvgTiktok], [UIIconType.twitter, SvgTwitter], [UIIconType.walmart, SvgWalmart], [UIIconType.wechat, SvgWechat], [UIIconType.whytespyder, SvgWhytespyder], [UIIconType.yahoo, SvgYahoo], [UIIconType.youtube, SvgYoutube], /* Loading Icons */
[UIIconType.loading, SvgLoading], /* Ad Type Icons */
[UIIconType.sp, SvgSp], [UIIconType.sb, SvgSb], [UIIconType.sd, SvgSd], /* Currency Icons */
[UIIconType.aed, SvgAed], [UIIconType.aud, SvgAud], [UIIconType.brl, SvgBrl], [UIIconType.cad, SvgCad], [UIIconType.cny, SvgCny], [UIIconType.eur, SvgEur], [UIIconType.gbp, SvgGbp], [UIIconType.inr, SvgInr], [UIIconType.jpy, SvgJpy], [UIIconType.mxn, SvgMxn], [UIIconType.pln, SvgPln], [UIIconType.sek, SvgSek], [UIIconType.sgd, SvgSgd], [UIIconType["try"], SvgTry], [UIIconType.usd, SvgUsd]]);
var UIIconGradientColor;
(function (UIIconGradientColor) {
  UIIconGradientColor["BlueIndigo"] = "blue-indigo";
})(UIIconGradientColor || (UIIconGradientColor = {}));
var gradientMap = (_gradientMap = {}, _gradientMap[UIIconGradientColor.BlueIndigo] = BlueIndigo, _gradientMap);
var UIIcon = function UIIcon(props) {
  var _classNames, _classNames2;
  var iconType = props.iconType,
    iconColor = props.iconColor,
    iconGradientColor = props.iconGradientColor,
    _props$iconSize = props.iconSize,
    iconSize = _props$iconSize === void 0 ? "w-4 h-4" : _props$iconSize,
    className = props.className,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var Svg = iconComponentMap.get(iconType);
  var Gradient = iconGradientColor && gradientMap[iconGradientColor];
  if (Svg === null) {
    console.warn("No matching icon for " + iconType);
    return null;
  }
  return React__default.createElement("span", Object.assign({
    className: classNames("inline-flex justify-center items-center", iconSize, className, (_classNames = {}, _classNames["text-" + iconColor + " dark:text-" + iconColor + "-dark"] = Boolean(iconColor), _classNames))
  }, rest), Gradient && React__default.createElement("svg", {
    className: "w-0 h-0 absolute",
    "aria-hidden": "true",
    focusable: "false"
  }, React__default.createElement(Gradient, null)), React__default.createElement(Svg, Object.assign({
    className: classNames("block fill-current", (_classNames2 = {}, _classNames2["[&>path]:fill-[url(#gradient-" + iconGradientColor + ")]"] = Boolean(iconGradientColor), _classNames2)),
    width: "100%",
    height: "100%",
    fillRule: "evenodd"
  }, rest)));
};
var UIIcon$1 = /*#__PURE__*/React__default.memo(UIIcon);
var _excluded$1 = ["iconColor", "iconSize", "className"];
var UILoadingIndicator = function UILoadingIndicator(props) {
  var iconColor = props.iconColor,
    iconSize = props.iconSize,
    className = props.className,
    rest = _objectWithoutPropertiesLoose(props, _excluded$1);
  return React__default.createElement(UIIcon$1, Object.assign({
    className: classNames("animate-spin", className),
    iconType: UIIconType.loading,
    iconColor: iconColor,
    iconSize: iconSize,
    "data-cy": "loadingIndicator"
  }, rest));
};
var UILoadingIndicator$1 = /*#__PURE__*/React__default.memo(UILoadingIndicator);
var _excluded$2 = ["variant", "children", "className", "raised", "shadow", "rounded", "border", "dataCy"];
/**
 * Styling is taken from Banner in figma
 * figma link: https://www.figma.com/file/iNedOoN7sChzGjoJgZjQWF/Perpetua-Oasis?node-id=1420%3A0
 */
var UIContainerVariant;
(function (UIContainerVariant) {
  UIContainerVariant["default"] = "bg-white dark:bg-gray-5-dark";
  UIContainerVariant["gray"] = "bg-gray-6-light dark:bg-gray-3-dark";
  UIContainerVariant["blue"] = "bg-blue-2-light text-blue-3-dark dark:bg-blue-2-dark dark:text-blue-3-light";
  UIContainerVariant["orange"] = "bg-orange-2-light text-orange-3-dark dark:bg-orange-2-dark dark:text-orange-3-light";
  UIContainerVariant["red"] = "bg-red-2-light text-red-3-dark dark:bg-red-2-dark dark:text-red-3-light";
  UIContainerVariant["indigo"] = "bg-indigo-2-light text-indigo-3-dark dark:bg-indigo-2-dark dark:text-indigo-3-light";
  UIContainerVariant["indigoLight3"] = "bg-indigo-3-light text-indigo-3-dark dark:bg-indigo-2-dark dark:text-indigo-3-light";
  UIContainerVariant["greenLight3"] = "bg-green-3-light text-green-3-dark dark:bg-green-2-dark dark:text-green-3-light";
  UIContainerVariant["orangeLight3"] = "bg-orange-3-light text-orange-3-dark dark:bg-orange-2-dark dark:text-orange-3-light";
  UIContainerVariant["redLight3"] = "bg-red-3-light text-red-3-dark dark:bg-red-2-dark dark:text-red-3-light";
  // Leaving this empty to allow the parent component to set the background color
  UIContainerVariant["inherit"] = "";
})(UIContainerVariant || (UIContainerVariant = {}));
var UIContainer = /*#__PURE__*/React__default.forwardRef(function (props, ref) {
  var _props$variant = props.variant,
    variant = _props$variant === void 0 ? UIContainerVariant["default"] : _props$variant,
    children = props.children,
    className = props.className,
    raised = props.raised,
    shadow = props.shadow,
    rounded = props.rounded,
    border = props.border,
    dataCy = props.dataCy,
    rest = _objectWithoutPropertiesLoose(props, _excluded$2);
  return React__default.createElement("div", Object.assign({
    ref: ref,
    className: classNames("border-gray-4-light dark:border-gray-4-dark", {
      "shadow-raised dark:shadow-raised-dark": raised,
      "shadow dark:shadow-dark": shadow,
      "rounded-lg": rounded,
      "border": border
    }, variant, className),
    "data-cy": dataCy
  }, rest), children);
});
UIContainer.displayName = "UIContainer";
var UIContainer$1 = /*#__PURE__*/React__default.memo(UIContainer);
var UILineGraph = function UILineGraph(props) {
  var data = props.data,
    loading = props.loading,
    empty = props.empty,
    sources = props.sources,
    themeContextMode = props.themeContextMode,
    showActivityContent = props.showActivityContent,
    dots = props.dots,
    graphHeight = props.graphHeight,
    yAxisDomain = props.yAxisDomain,
    yAxisReversed = props.yAxisReversed,
    yAxisAllowDecimals = props.yAxisAllowDecimals,
    yAxisDataOverflow = props.yAxisDataOverflow,
    yAxisInterval = props.yAxisInterval,
    yAxisTickCount = props.yAxisTickCount,
    yAxisTickFormatter = props.yAxisTickFormatter,
    formatCurrency = props.formatCurrency,
    renderTooltip = props.renderTooltip;
  var _React$useState = React__default.useState(null),
    mouse = _React$useState[0],
    setMouse = _React$useState[1];
  var _React$useState2 = React__default.useState(false),
    showActivityTooltip = _React$useState2[0],
    setShowActivityTooltip = _React$useState2[1];
  var lineGraphRef = React__default.useRef(null);
  /** Adjusts the colours for dark mode */
  /** Since axes does accept just hex values as colors, I had to add these values */
  var gray2DarkColor = "#4B5563";
  var gray2LightColor = "#9CA3AF";
  var gray4DarkColor = "#1F2937";
  var gray4LightColor = "#E2E8F0";
  var axisGray = themeContextMode === ThemeContextMode.dark ? gray2DarkColor : gray2LightColor;
  var gridGrey = themeContextMode === ThemeContextMode.dark ? gray4DarkColor : gray4LightColor;
  /** Map the axes to the right color. If sources share the same axis, it should be gray */
  var axes = [];
  sources.forEach(function (source) {
    if (source.graphAxis === LineGraphAxis.number_auto || source.graphAxis === LineGraphAxis.currency_auto) {
      axes.push({
        id: source.graphAxisId,
        graphAxis: source.graphAxis,
        color: source.color
      });
    } else {
      var existingIndex = axes.findIndex(function (x) {
        return x.graphAxis === source.graphAxis;
      });
      if (existingIndex >= 0) {
        axes[existingIndex].color = axisGray;
      } else {
        axes.push({
          id: source.graphAxisId || source.graphAxis,
          graphAxis: source.graphAxis,
          color: source.color
        });
      }
    }
  });
  /** Formats the Y-axis values based on axis unit */
  var formatYAxisTick = function formatYAxisTick(value, axis) {
    if (value === undefined) {
      return "--";
    } else {
      var suffix = "";
      if (axis === LineGraphAxis.currency || axis === LineGraphAxis.number || axis === LineGraphAxis.currency_auto || axis === LineGraphAxis.number_auto) {
        if (value >= Math.pow(10, 9)) {
          value = value / Math.pow(10, 9);
          suffix = "B";
        } else if (value >= Math.pow(10, 6)) {
          value = value / Math.pow(10, 6);
          suffix = "M";
        } else if (value >= 1000) {
          value = value / 1000;
          suffix = "K";
        }
      }
      switch (axis) {
        case LineGraphAxis.currency_low:
          return formatCurrency(value, value > 0 ? 2 : 0);
        case LineGraphAxis.currency_auto:
        case LineGraphAxis.currency:
          // if value has a suffix show 1 decimal point eg. $1.5K
          return formatCurrency(value, value > 0 && suffix ? 1 : 0) + suffix;
        case LineGraphAxis.number_auto:
        case LineGraphAxis.number:
          return value.toLocaleString("en") + suffix;
        case LineGraphAxis.number_low:
          return value.toLocaleString("en");
        case LineGraphAxis.percent_low:
        case LineGraphAxis.percent:
          return value.toLocaleString("en", {
            style: "percent",
            maximumFractionDigits: value < 5 ? 2 : 0
          });
        case LineGraphAxis.percent_preformatted:
          return (value * 0.01).toLocaleString("en", {
            style: "percent",
            maximumFractionDigits: value < 5 ? 2 : 0
          });
        default:
          return value;
      }
    }
  };
  /** sets the y-axis domain */
  var setYAxisDomain = function setYAxisDomain(axis) {
    if (axis.graphAxis === LineGraphAxis.number_auto || axis.graphAxis === LineGraphAxis.currency_auto) {
      return ["dataMin", "dataMax"];
    }
    return yAxisDomain != null ? yAxisDomain : [0, "auto"];
  };
  /** Function to render custom dots. Only renders dots with activities on that date */
  var renderDots = function renderDots(props) {
    var hasActivity = dots[props.index];
    return hasActivity && props.cy ? React__default.createElement(UILineGraphDot, Object.assign({}, props, {
      color: props.stroke,
      themeContextMode: themeContextMode
    })) : null;
  };
  /** Sets mouse state when the mouse moves on the graph */
  var setHoverIndex = function setHoverIndex(position) {
    if (showActivityTooltip) {
      return;
    }
    if (!position || !position.activeCoordinate) {
      setMouse(null);
      return;
    }
    setMouse(position);
  };
  var toggleTooltip = function toggleTooltip(mouse) {
    // if click is inside graph area and showGraphTooltip is true, toggle it off
    if (showActivityTooltip) {
      setShowActivityTooltip(false);
      return;
    }
    if (dots[mouse == null ? void 0 : mouse.activeTooltipIndex] && showActivityContent) {
      setShowActivityTooltip(true);
    }
  };
  if (loading) {
    return React__default.createElement(UIContainer$1, {
      className: "flex items-center justify-center w-full",
      style: {
        height: (graphHeight || 285) + "px"
      }
    }, React__default.createElement(UILoadingIndicator$1, {
      iconColor: UIColor.indigo1,
      iconSize: "w-10 h-10"
    }));
  }
  if ((data == null ? void 0 : data.length) === 0 && empty) {
    return React__default.createElement(UIContainer$1, {
      className: "flex items-center justify-center w-full",
      style: {
        height: (graphHeight || 285) + "px"
      }
    }, empty);
  }
  return React__default.createElement("div", {
    className: classNames("relative mt-6 flex h-full w-full items-center", {
      "cursor-pointer": mouse && dots[mouse.activeTooltipIndex]
    })
  }, mouse && (renderTooltip == null ? void 0 : renderTooltip({
    showActivityTooltip: showActivityTooltip,
    x: mouse.activeCoordinate.x,
    y: mouse.activeCoordinate.y,
    index: mouse.activeTooltipIndex
  })), React__default.createElement(ResponsiveContainer, {
    width: "100%",
    height: graphHeight || 285
  }, React__default.createElement(AreaChart, {
    ref: lineGraphRef,
    data: data,
    margin: {
      top: 10,
      left: 16
    },
    onClick: toggleTooltip,
    onMouseMove: setHoverIndex,
    onMouseLeave: setHoverIndex,
    // @ts-ignore
    cursor: mouse && dots[mouse.activeTooltipIndex] && showActivityContent ? "pointer" : ""
  }, React__default.createElement(CartesianGrid, {
    vertical: false,
    stroke: gridGrey
  }), React__default.createElement(XAxis, {
    dataKey: "date",
    height: 46,
    tickLine: false,
    axisLine: false,
    tickFormatter: function tickFormatter(date) {
      return moment(date, "YYYY-MM-DD", true).isValid() ? moment(date, "YYYY-MM-DD").format("MMM D") : date;
    },
    tickMargin: 26,
    tick: {
      fontSize: 12
    },
    stroke: axisGray,
    padding: {
      left: 18,
      right: 18
    }
  }), axes.map(function (axis, i) {
    var _axis$color;
    return React__default.createElement(YAxis, {
      key: "axis-" + axis.graphAxis + "-" + i,
      yAxisId: "axis-" + axis.id,
      orientation: i % 2 ? "right" : "left",
      domain: setYAxisDomain(axis),
      axisLine: false,
      tickLine: false,
      tick: {
        fontSize: 12
      },
      tickCount: yAxisTickCount,
      tickFormatter: function tickFormatter(value) {
        return yAxisTickFormatter ? yAxisTickFormatter(value, axis.graphAxis) : formatYAxisTick(value, axis.graphAxis);
      },
      width: 48,
      stroke: ((_axis$color = axis.color) == null ? void 0 : _axis$color.hex) || axisGray,
      reversed: yAxisReversed,
      allowDecimals: yAxisAllowDecimals,
      allowDataOverflow: yAxisDataOverflow,
      interval: yAxisInterval
    });
  }), sources.map(function (source) {
    var _source$color;
    return React__default.createElement(Area, {
      key: "graph-metric-line-" + source.key,
      dataKey: "data." + source.key + ".value",
      yAxisId: "axis-" + (source.graphAxisId || source.graphAxis),
      type: "linear",
      strokeWidth: 2,
      stroke: (_source$color = source.color) == null ? void 0 : _source$color.hex,
      strokeDasharray: "3 5",
      fillOpacity: 0,
      isAnimationActive: false
    });
  }), sources.map(function (source) {
    var _source$color2;
    return React__default.createElement(Area, {
      key: "graph-metric-line-solid-" + source.key,
      dataKey: "data." + (source.key + LineGraphSuffix.COMPLETE) + ".value",
      yAxisId: "axis-" + (source.graphAxisId || source.graphAxis),
      type: "linear",
      // @ts-ignore
      dot: renderDots,
      strokeWidth: 2,
      stroke: (_source$color2 = source.color) == null ? void 0 : _source$color2.hex,
      fillOpacity: 0,
      isAnimationActive: false
    });
  }))));
};
// eslint-disable-line import/no-unused-modules

/**
 * Enum representing our design system typography
 * font size, line-height, letter spacing and font-weight can be found in tailwind.config.js
 * https://www.figma.com/file/iNedOoN7sChzGjoJgZjQWF/Perpetua-Master
 */
var UITypography;
(function (UITypography) {
  UITypography["caption"] = "text-sm font-medium";
  UITypography["body"] = "text-base font-medium";
  UITypography["body2"] = "text-lg font-medium";
  UITypography["body3"] = "text-xl font-medium";
  UITypography["label"] = "text-sm font-semibold";
  UITypography["label2"] = "text-base font-semibold";
  UITypography["label3"] = "text-lg font-semibold";
  UITypography["label4"] = "text-xs font-semibold";
  UITypography["header"] = "text-2xl font-semibold";
  UITypography["header2"] = "text-3xl font-semibold";
  UITypography["header3"] = "text-4xl font-semibold";
  UITypography["header4"] = "text-5xl font-bold";
  UITypography["header5"] = "text-6xl font-bold";
  UITypography["header6"] = "text-7xl font-bold";
})(UITypography || (UITypography = {}));
var _excluded$3 = ["typography", "color", "className"];
/**
 * A Component that displays one or more lines of read-only text.
 */
var UIText = function UIText(props) {
  var _classMapping, _classNames;
  var _props$typography = props.typography,
    typography = _props$typography === void 0 ? UITypography.body : _props$typography,
    _props$color = props.color,
    color = _props$color === void 0 ? UIColor.grayDark3 : _props$color,
    className = props.className,
    rest = _objectWithoutPropertiesLoose(props, _excluded$3);
  var classMapping = (_classMapping = {}, _classMapping[UIColor.gray1] = "text-gray-1 dark:text-gray-1", _classMapping[UIColor.gray2] = "text-gray-2-light dark:text-gray-2-dark", _classMapping[UIColor.grayLight2] = "text-gray-2-light dark:text-gray-2-dark", _classMapping[UIColor.grayDark2] = "text-gray-2-dark dark:text-gray-2-light", _classMapping[UIColor.gray3] = "text-gray-3-light dark:text-gray-3-dark", _classMapping[UIColor.grayLight3] = "text-gray-3-light dark:text-gray-3-dark", _classMapping[UIColor.grayDark3] = "text-gray-3-dark dark:text-gray-3-light", _classMapping[UIColor.purple1] = "text-purple-1 dark:text-purple-1", _classMapping[UIColor.indigo1] = "text-indigo-1 dark:text-indigo-1", _classMapping[UIColor.indigoDark3] = "text-indigo-3-dark dark:text-indigo-3-light", _classMapping[UIColor.redDark3] = "text-red-3-dark dark:text-red-3-light", _classMapping[UIColor.orange1] = "text-orange-1 dark:text-orange-1", _classMapping);
  return React__default.createElement("div", Object.assign({
    className: classNames(typography, (_classNames = {}, _classNames[classMapping[color]] = Boolean(classMapping[color]), _classNames["text-" + color] = Boolean(color) && !classMapping[color], _classNames), className)
  }, rest), props.children);
};
var UIText$1 = /*#__PURE__*/React__default.memo(UIText);

/**
 * Taken from https://usehooks.com/useOnClickOutside/ and slightly modified to check multiple refs
 * @param refs An array of refs to containers that are clickable
 * @param handler A function to execute when clicking outside the given refs
 */
function useOnClickOutside(refs, handler, options) {
  var _ref = options || {
      isActive: true
    },
    isActive = _ref.isActive;
  useEffect(function () {
    var listener = function listener(event) {
      // Do nothing if at least one of the refs contains the target
      var outsideClick = true;
      refs.forEach(function (ref) {
        if (ref && ref.current) {
          if (ref.current.contains(event.target)) {
            outsideClick = false;
          }
        }
      });
      if (outsideClick) {
        handler(event);
      }
    };
    if (isActive) {
      document.addEventListener("click", listener);
    }
    return function () {
      document.removeEventListener("click", listener);
    };
  },
  // Add ref and handler to effect dependencies
  // It's worth noting that because passed in handler is a new ...
  // ... function on every render that will cause this effect ...
  // ... callback/cleanup to run every render. It's not a big deal ...
  // ... but to optimize you can wrap handler in useCallback before ...
  // ... passing it into this hook.
  [refs, handler, isActive]);
}
var DeltaColor;
(function (DeltaColor) {
  DeltaColor["green"] = "green";
  DeltaColor["orange"] = "orange";
  DeltaColor["grey"] = "grey";
})(DeltaColor || (DeltaColor = {}));
var DeltaPercentageNotMemoized = function DeltaPercentageNotMemoized(props) {
  var deltaPercent = props.deltaPercent,
    invertDeltaColors = props.invertDeltaColors,
    hideArrow = props.hideArrow,
    nullDisplay = props.nullDisplay;
  var sign = Math.sign(Number(deltaPercent));
  var textColor;
  var renderDeltaIcon = function renderDeltaIcon() {
    if (sign > 0) {
      textColor = invertDeltaColors ? DeltaColor.orange : DeltaColor.green;
      return invertDeltaColors || invertDeltaColors ? React__default.createElement(UIIcon$1, {
        iconType: UIIconType.arrowUp,
        iconColor: UIColor.orange1
      }) : React__default.createElement(UIIcon$1, {
        iconType: UIIconType.arrowUp,
        iconColor: UIColor.green1
      });
    } else if (sign < 0) {
      textColor = invertDeltaColors ? DeltaColor.green : DeltaColor.orange;
      return invertDeltaColors || invertDeltaColors ? React__default.createElement(UIIcon$1, {
        iconType: UIIconType.arrowDown,
        iconColor: UIColor.green1
      }) : React__default.createElement(UIIcon$1, {
        iconType: UIIconType.arrowDown,
        iconColor: UIColor.orange1
      });
    }
    return React__default.createElement(UIIcon$1, {
      iconType: UIIconType.neutral
    });
  };
  if (isNaN(sign) || sign === 0) {
    return React__default.createElement("span", {
      className: classNames("flex")
    }, React__default.createElement(UIIcon$1, {
      iconType: UIIconType.neutral
    }), React__default.createElement("span", {
      className: classNames("text-gray-1")
    }, nullDisplay || "--"));
  }
  return React__default.createElement("span", {
    className: classNames("flex")
  }, !hideArrow && React__default.createElement("span", {
    className: classNames("h-auto", "mb-auto", "mt-auto", "flex items-center")
  }, renderDeltaIcon()), React__default.createElement("span", {
    className: classNames("font-semibold ml-1", "text-" + textColor + "-1")
  }, React__default.createElement("span", null, deltaPercent, "%")));
};
var UIDeltaPercentage = /*#__PURE__*/React__default.memo(DeltaPercentageNotMemoized);
var UIGraphTooltipType;
(function (UIGraphTooltipType) {
  UIGraphTooltipType["metric"] = "METRIC_TOOLTIP";
  UIGraphTooltipType["activity"] = "ACTIVITY_TOOLTIP";
})(UIGraphTooltipType || (UIGraphTooltipType = {}));
// NB: add this disable less this *entire* file is underlined with squiggly red lines
// eslint-disable-next-line react/display-name
var UILineGraphTooltip = /*#__PURE__*/React__default.forwardRef(function (props, ref) {
  var sources = props.sources,
    data = props.data,
    _props$activities = props.activities,
    activities = _props$activities === void 0 ? 0 : _props$activities,
    _props$type = props.type,
    type = _props$type === void 0 ? UIGraphTooltipType.metric : _props$type,
    x = props.x,
    y = props.y,
    _props$loading = props.loading,
    loading = _props$loading === void 0 ? false : _props$loading,
    getActivityContent = props.getActivityContent,
    onOutsideClick = props.onOutsideClick,
    customTooltipFooter = props.customTooltipFooter,
    loadingText = props.loadingText,
    activityCapitalizedText = props.activityCapitalizedText,
    activitiesCapitalizedText = props.activitiesCapitalizedText,
    renderMetric = props.renderMetric,
    formatDate = props.formatDate;
  // modal state to see more activities
  var _React$useState = React__default.useState(false),
    showActivityModal = _React$useState[0],
    setShowActivityModal = _React$useState[1];
  var tooltipRef = React__default.useRef();
  useOnClickOutside([tooltipRef], function (event) {
    var _ref$current;
    if (type !== UIGraphTooltipType.activity) {
      return;
    }
    if (type === UIGraphTooltipType.activity && showActivityModal) {
      return;
    }
    // If click is outside of graph area, call onOutside click
    // If it is inside the area toggleTooltip function in UILinegraph will handle the click
    if (ref &&
    // @ts-ignore
    ref.current &&
    // @ts-ignore
    !((_ref$current = ref.current) != null && _ref$current.container.contains(event.target)) && onOutsideClick) {
      onOutsideClick();
    }
  });
  var generateContentStyleFromPosition = function generateContentStyleFromPosition() {
    var curentMaxTooltipWidth = 400;
    // multiply by 0.75 because of the sidebar & header - it's just an estimate
    var windowWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0) * 0.75;
    var offset = 0;
    if (tooltipRef.current && tooltipRef.current.getBoundingClientRect) {
      offset = tooltipRef.current.getBoundingClientRect().height / 2;
    }
    var yPosition = y - offset;
    var style = x + curentMaxTooltipWidth >= windowWidth ? {
      right: 0,
      marginRight: "9px",
      top: yPosition
    } : {
      left: 0,
      marginLeft: "9px",
      top: yPosition
    };
    return style;
  };
  var tooltipContentBadge = React__default.useCallback(function (data) {
    if (customTooltipFooter) {
      return customTooltipFooter(data);
    }
    // don't display anything if there aren't any activites
    if (!getActivityContent || activities === 0) {
      return null;
    }
    var activityText = (activities > 9 ? "9+" : activities) + " " + (activities == 1 ? activityCapitalizedText : activitiesCapitalizedText);
    return React__default.createElement("div", {
      className: "mt-1 inline-flex flex-row items-center rounded-full bg-gray-5-light px-2 dark:bg-gray-4-dark"
    }, loading ? React__default.createElement(UILoadingIndicator$1, {
      iconColor: UIColor.grayLight2,
      iconSize: "w-3 h-3",
      className: "mr-1.5"
    }) : React__default.createElement(UIIcon$1, {
      iconType: UIIconType.activity,
      iconColor: UIColor.grayLight2,
      iconSize: "w-3 h-3",
      className: "mr-1.5"
    }), React__default.createElement(UIText$1, {
      color: UIColor.gray1,
      typography: UITypography.label
    }, loading ? loadingText : activityText));
  }, [activities, customTooltipFooter, loading]);
  var renderTooltipContent = function renderTooltipContent() {
    if (type === UIGraphTooltipType.activity && getActivityContent) {
      return getActivityContent(data.date, showActivityModal, setShowActivityModal);
    }
    var renderDate = function renderDate() {
      if (formatDate) return formatDate(data == null ? void 0 : data.date);
      return moment(data == null ? void 0 : data.date, "YYYY-MM-DD", true).isValid() ? moment(data == null ? void 0 : data.date, "YYYY-MM-DD").format("MMMM D, YYYY") : data == null ? void 0 : data.date;
    };
    return React__default.createElement(UIContainer$1, {
      className: "w-[210px] p-3",
      rounded: true,
      border: true,
      raised: true
    }, React__default.createElement(UIText$1, {
      typography: UITypography.label,
      color: UIColor.grayDark3
    }, renderDate()), sources == null ? void 0 : sources.map(function (source, index) {
      var _source$color, _data$data, _data$data$source$key, _source$metricMetadat, _data$data2, _data$data2$source$ke, _data$data3, _data$data3$source$ke, _source$metricMetadat2;
      return React__default.createElement("div", {
        key: "metric-" + index,
        className: "mt-3 mb-2 flex flex-row items-center justify-between border-l-4 pl-3",
        style: {
          borderColor: source == null ? void 0 : (_source$color = source.color) == null ? void 0 : _source$color.hex
        }
      }, React__default.createElement("div", null, React__default.createElement(UIText$1, {
        typography: UITypography.label,
        color: UIColor.grayLight2
      }, source.title), React__default.createElement(UIText$1, {
        typography: UITypography.label2
      }, renderMetric == null ? void 0 : renderMetric({
        value: data == null ? void 0 : (_data$data = data.data) == null ? void 0 : (_data$data$source$key = _data$data[source.key]) == null ? void 0 : _data$data$source$key.value,
        metricUnit: (_source$metricMetadat = source.metricMetadata) == null ? void 0 : _source$metricMetadat.metricUnit
      }))), (data == null ? void 0 : (_data$data2 = data.data) == null ? void 0 : (_data$data2$source$ke = _data$data2[source.key]) == null ? void 0 : _data$data2$source$ke.deltaPercent) && React__default.createElement(UIDeltaPercentage, {
        deltaPercent: data == null ? void 0 : (_data$data3 = data.data) == null ? void 0 : (_data$data3$source$ke = _data$data3[source.key]) == null ? void 0 : _data$data3$source$ke.deltaPercent,
        invertDeltaColors: (_source$metricMetadat2 = source.metricMetadata) == null ? void 0 : _source$metricMetadat2.invertDeltaColors
      }));
    }), tooltipContentBadge(data));
  };
  // HACK (Ben - August 22nd, 2019): to prevent the tooltip line from jumping we need to calculate the width
  // of the actual tooltip content and center the line. On first render this is 0 or doesn't exist
  // so we need to provide some default value to prevent the weird jump.
  var offset = 0;
  if (tooltipRef && tooltipRef.current && tooltipRef.current.getBoundingClientRect) {
    var _tooltipRef$current;
    offset = ((_tooltipRef$current = tooltipRef.current) == null ? void 0 : _tooltipRef$current.getBoundingClientRect().width) / 2;
  }
  var xPosition = x - offset;
  return React__default.createElement("div", {
    ref: tooltipRef,
    className: "border-box absolute top-2.5 h-full",
    style: {
      left: xPosition
    }
  }, React__default.createElement("span", {
    className: "block border-r border-gray-3-light",
    style: {
      height: "calc(100% - 56px)"
    }
  }), React__default.createElement("div", {
    className: classNames("absolute z-sticky", {
      "pointer-events-none": type === UIGraphTooltipType.metric
    }),
    style: generateContentStyleFromPosition()
  }, renderTooltipContent()));
});
var _excluded$4 = ["variant", "size", "text", "iconType", "danger", "loading", "block", "onClick", "className", "iconClassName", "triggerRef"];
var UIButtonVariant;
(function (UIButtonVariant) {
  UIButtonVariant["primary"] = "primary";
  UIButtonVariant["secondary"] = "secondary";
  UIButtonVariant["tertiary"] = "tertiary";
  UIButtonVariant["quaternary"] = "quaternary";
})(UIButtonVariant || (UIButtonVariant = {}));
var UIButtonSize;
(function (UIButtonSize) {
  UIButtonSize["small"] = "small";
  UIButtonSize["default"] = "default";
  UIButtonSize["large"] = "large";
})(UIButtonSize || (UIButtonSize = {}));
var UIButton = function UIButton(props) {
  var _buttonSizeToTypograp, _classNames;
  var variant = props.variant,
    _props$size = props.size,
    size = _props$size === void 0 ? UIButtonSize["default"] : _props$size,
    text = props.text,
    iconType = props.iconType,
    danger = props.danger,
    loading = props.loading,
    block = props.block,
    onClick = props.onClick,
    className = props.className,
    iconClassName = props.iconClassName,
    triggerRef = props.triggerRef,
    rest = _objectWithoutPropertiesLoose(props, _excluded$4);
  var buttonSizeToTypography = (_buttonSizeToTypograp = {}, _buttonSizeToTypograp[UIButtonSize.small] = UITypography.label, _buttonSizeToTypograp[UIButtonSize["default"]] = UITypography.label2, _buttonSizeToTypograp[UIButtonSize.large] = UITypography.label3, _buttonSizeToTypograp);
  var buttonContent = React__default.useMemo(function () {
    /**
     * - The main iconColor is inferred by the text-color on the button
     * The loading indicators iconColor is inferred by the text-color except
     * for tertiary variant.
     * */
    return React__default.createElement(React__default.Fragment, null, loading && React__default.createElement(UILoadingIndicator$1, {
      className: classNames("absolute left-1/2 -ml-2", {
        "dark:text-indigo-2-light": variant === UIButtonVariant.tertiary
      })
    }), iconType && React__default.createElement(UIIcon$1, {
      className: classNames({
        "mr-2": Boolean(text),
        "invisible": loading
      }, iconClassName),
      iconType: iconType
    }), text && React__default.createElement(UIText$1, {
      className: classNames({
        invisible: loading
      }),
      typography: buttonSizeToTypography[size],
      color: UIColor.inherit
    }, text));
  }, [iconType, loading, size, text, variant]);
  return React__default.createElement("button", Object.assign({}, rest, {
    ref: triggerRef,
    className: classNames("relative flex flex-row items-center justify-center border rounded-lg transition-colors outline-none focus:outline-none whitespace-nowrap disabled:cursor-not-allowed", (_classNames = {
      "w-max": !block,
      "w-full": block,
      "px-4 py-2": size === UIButtonSize["default"] && variant !== UIButtonVariant.tertiary && variant !== UIButtonVariant.quaternary,
      "px-6 py-3": size === UIButtonSize.large && variant !== UIButtonVariant.tertiary && variant !== UIButtonVariant.quaternary
    }, _classNames[["border-transparent", "text-white", "bg-indigo-1", "hover:bg-indigo-2-dark", "active:bg-indigo-3-dark", "disabled:bg-gray-4-light", "disabled:text-gray-2-light", "disabled:border-gray-4-light", "dark:disabled:bg-gray-4-dark", "dark:disabled:text-gray-2-dark", "dark:disabled:border-gray-3-dark", "white-label:bg-blue-1"].concat(danger ? ["bg-red-1", "hover:bg-red-2-dark", "active:bg-red-3-dark"] : []).join(" ")] = variant === UIButtonVariant.primary, _classNames[["bg-white", "text-gray-3-dark", "border-gray-3-light", "hover:border-gray-2-light", "active:bg-gray-6-light", "active:border-gray-1", "disabled:border-gray-4-light", "disabled:text-gray-3-light", "dark:bg-gray-5-dark", "dark:text-gray-3-light", "dark:border-gray-3-dark", "dark:hover:border-gray-2-dark", "dark:active:bg-gray-4-dark", "dark:active:border-gray-1", "dark:disabled:bg-gray-5-dark", "dark:disabled:text-gray-4-dark", "dark:disabled:border-gray-4-dark"].concat(danger ? ["text-red-1", "hover:text-red-2-dark", "active:text-red-3-dark", "dark:text-red-1", "dark:hover:text-red-2-dark", "dark:active:text-red-3-dark"] : []).join(" ")] = variant === UIButtonVariant.secondary, _classNames[["p-0", "bg-transparent", "border-transparent", "text-indigo-1", "hover:text-indigo-2-dark", "active:text-indigo-3-dark", "disabled:text-gray-3-light", "dark:disabled:text-gray-3-dark"].concat(danger ? ["text-red-1", "hover:text-red-2-dark", "active:text-red-3-dark", "dark:text-red-1", "dark:hover:text-red-2-dark", "dark:active:text-red-3-dark"] : []).join(" ")] = variant === UIButtonVariant.tertiary, _classNames[["p-0", "bg-transparent", "border-transparent", "text-gray-2-light", "hover:text-gray-1", "active:text-gray-2-dark", "disabled:text-gray-3-light", "dark:text-gray-2-dark", "dark:hover:text-gray-1", "dark:active:text-gray-2-light", "dark:disabled:text-gray-3-dark"].concat(danger ? ["text-red-1", "hover:text-red-2-dark", "active:text-red-3-dark", "dark:text-red-1", "dark:hover:text-red-2-dark", "dark:active:text-red-3-dark"] : []).join(" ")] = variant === UIButtonVariant.quaternary, _classNames), className),
    onClick: loading ? undefined : onClick
  }), buttonContent);
};
var UIButton$1 = /*#__PURE__*/React__default.memo(UIButton);
var UIPortal = function UIPortal(props) {
  var children = props.children;
  var _React$useState = React__default.useState(document.createElement("div")),
    container = _React$useState[0];
  React__default.useEffect(function () {
    if (!document.body.contains(container)) {
      document.body.appendChild(container);
    }
    return function () {
      if (container) {
        var _document, _document$body;
        (_document = document) == null ? void 0 : (_document$body = _document.body) == null ? void 0 : _document$body.removeChild(container);
      }
    };
  }, [container]);
  if (container) {
    return createPortal(children, container);
  }
  return null;
};
var UIPortal$1 = /*#__PURE__*/React__default.memo(UIPortal);
var DEFAULT_PADDING = 8;
var UIPopoverSide;
(function (UIPopoverSide) {
  UIPopoverSide["top"] = "top";
  UIPopoverSide["left"] = "left";
  UIPopoverSide["right"] = "right";
  UIPopoverSide["bottom"] = "bottom";
})(UIPopoverSide || (UIPopoverSide = {}));
var UIPopoverAlign;
(function (UIPopoverAlign) {
  UIPopoverAlign["center"] = "center";
  UIPopoverAlign["start"] = "start";
  UIPopoverAlign["end"] = "end";
})(UIPopoverAlign || (UIPopoverAlign = {}));
var UIPopover = function UIPopover(props) {
  var _props$side = props.side,
    side = _props$side === void 0 ? UIPopoverSide.top : _props$side,
    open = props.open,
    content = props.content,
    renderContent = props.renderContent,
    arrowRef = props.arrowRef,
    children = props.children,
    align = props.align,
    className = props.className,
    popoverContentClassName = props.popoverContentClassName,
    _props$block = props.block,
    block = _props$block === void 0 ? false : _props$block,
    dataCy = props.dataCy;
  var _React$useState = React__default.useState(null),
    triggerRef = _React$useState[0],
    setTriggerRef = _React$useState[1];
  var _React$useState2 = React__default.useState(null),
    popoverRef = _React$useState2[0],
    setPopoverRef = _React$useState2[1];
  /**
   * Own animation styles. Causes a rerender of the spring
   * so the popover can animate in & out
   */
  var _React$useState3 = React__default.useState({
      from: {
        opacity: 0
      }
    }),
    ownStyles = _React$useState3[0],
    setOwnStyles = _React$useState3[1];
  /**
   * usePopper requires their own Placement enum.
   * this converts our side & alignment into their placement format
   * https://popper.js.org/docs/v2/constructors/
   */
  var placement = React__default.useMemo(function () {
    if (!align || align === UIPopoverAlign.center) {
      return side;
    } else {
      return side + "-" + align;
    }
  }, [side, align]);
  var _usePopper = usePopper(triggerRef, popoverRef, {
      strategy: "fixed",
      placement: placement,
      modifiers: [].concat(arrowRef ? [{
        name: "arrow",
        options: {
          element: arrowRef
        }
      }] : [], [{
        name: "offset",
        options: {
          offset: [0, DEFAULT_PADDING]
        }
      }])
    }),
    styles = _usePopper.styles,
    attributes = _usePopper.attributes,
    update = _usePopper.update;
  var commonSpringOptions = {
    reverse: !open,
    config: {
      duration: 150
    }
  };
  var animatedStyles = useSpring(_extends({}, commonSpringOptions, ownStyles));
  var translationOffset = React__default.useMemo(function () {
    if (side === UIPopoverSide.top || side === UIPopoverSide.left) {
      return DEFAULT_PADDING;
    } else {
      return -1 * DEFAULT_PADDING;
    }
  }, [side]);
  React__default.useEffect(function () {
    update == null ? void 0 : update();
  }, [open, update]);
  React__default.useEffect(function () {
    if (side === UIPopoverSide.top || side === UIPopoverSide.bottom) {
      setOwnStyles({
        from: {
          opacity: 0,
          transform: "translateY(" + translationOffset + "px)"
        },
        to: {
          opacity: 1,
          transform: "translateY(0)"
        }
      });
    } else {
      setOwnStyles({
        from: {
          opacity: 0,
          transform: "translateX(" + translationOffset + "px)"
        },
        to: {
          opacity: 1,
          transform: "translateX(0)"
        }
      });
    }
  }, [open, side, translationOffset]);
  return React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
    className: classNames(className, {
      "w-full": block
    }, {
      flex: !className
    }),
    ref: setTriggerRef,
    "data-cy": dataCy
  }, children), open && React__default.createElement(UIPortal$1, null, React__default.createElement("div", Object.assign({
    className: classNames("fixed z-popover", popoverContentClassName, {
      "pointer-events-none": !open
    }),
    ref: setPopoverRef,
    style: styles.popper
  }, attributes.popper), React__default.createElement(animated.div, {
    style: animatedStyles
  }, content || (renderContent == null ? void 0 : renderContent(styles.arrow))))));
};
var UITooltip = function UITooltip(props) {
  var children = props.children,
    description = props.description,
    disabled = props.disabled,
    popoverClassName = props.popoverClassName,
    triggerClassName = props.triggerClassName,
    _props$side = props.side,
    side = _props$side === void 0 ? UIPopoverSide.top : _props$side,
    title = props.title,
    customContent = props.customContent,
    delay = props.delay;
  var _React$useState = React__default.useState(false),
    open = _React$useState[0],
    setOpen = _React$useState[1];
  // used to keep track of the "internal" open state
  // so that if a user enters and exits within a certain time frame
  // the promise with the delay will NOT trigger a tooltip spawn at all
  var internalOpen = React__default.useRef(false);
  // the ref for the arrow must be a callback ref
  // const [arrowRef, setArrowRef] = React.useState(null);
  /**
   * Callback for onMouseEnter and onTouchStart to open the tooltip
   */
  var openTooltip = React__default.useCallback(function () {
    internalOpen.current = true;
    if (delay) {
      new Promise(function (resolve) {
        return setTimeout(resolve, delay);
      }).then(function () {
        if (internalOpen.current) {
          setOpen(true);
        }
      });
    } else {
      setOpen(true);
    }
  }, []);
  /**
   * Callback for onMouseLeave and onTouchEnd to close the tooltip
   */
  var closeTooltip = React__default.useCallback(function () {
    internalOpen.current = false;
    setOpen(false);
  }, []);
  React__default.useEffect(function () {
    if (disabled) {
      closeTooltip();
    }
  }, [closeTooltip, disabled]);
  var renderTooltip = function renderTooltip( /* arrowStyle */
  ) {
    return React__default.createElement("div", {
      className: classNames("ui-tooltip__content flex items-center justify-center")
    }, React__default.createElement("div", {
      className: "max-w-xs rounded-lg bg-gray-3-dark p-4 shadow-raised dark:shadow-raised-dark"
    }, customContent != null ? customContent : React__default.createElement(React__default.Fragment, null, title && React__default.createElement(UIText$1, {
      typography: UITypography.label2,
      color: UIColor.white
    }, title), description && React__default.createElement(UIText$1, {
      typography: UITypography.caption,
      color: UIColor.white
    }, description))));
  };
  return React__default.createElement(UIPopover, {
    side: side,
    renderContent: renderTooltip,
    // arrowRef={arrowRef}
    open: open && !disabled,
    className: popoverClassName,
    popoverContentClassName: "ui-tooltip",
    dataCy: "ui-tooltip-popover"
  }, React__default.createElement("div", {
    className: classNames("flex", triggerClassName),
    onMouseEnter: openTooltip,
    onMouseLeave: closeTooltip
  }, children));
};
var UITooltip$1 = /*#__PURE__*/React__default.memo(UITooltip);
var _excluded$5 = ["value", "className", "containerClassName", "inputClassName", "danger", "dangerText", "disabled", "helperText", "iconType", "inputLabel", "labelColor", "tooltipDisabled", "tooltipText", "warning", "warningText", "inputRef", "prefix", "style", "onClear"];
var UIInput = /*#__PURE__*/React__default.forwardRef(function (props, ref) {
  var _classNames, _classNames2;
  var value = props.value,
    className = props.className,
    containerClassName = props.containerClassName,
    inputClassName = props.inputClassName,
    danger = props.danger,
    dangerText = props.dangerText,
    disabled = props.disabled,
    helperText = props.helperText,
    iconType = props.iconType,
    inputLabel = props.inputLabel,
    _props$labelColor = props.labelColor,
    labelColor = _props$labelColor === void 0 ? UIColor.grayDark3 : _props$labelColor,
    tooltipDisabled = props.tooltipDisabled,
    tooltipText = props.tooltipText,
    warning = props.warning,
    warningText = props.warningText,
    inputRef = props.inputRef,
    prefix = props.prefix,
    onClear = props.onClear,
    rest = _objectWithoutPropertiesLoose(props, _excluded$5);
  var ownRef = useRef();
  return React__default.createElement("div", {
    className: classNames("w-full", (_classNames = {}, _classNames[containerClassName || ""] = Boolean(containerClassName), _classNames))
  }, inputLabel && React__default.createElement(UIText$1, {
    typography: UITypography.label2,
    color: disabled ? UIColor.grayLight3 : labelColor,
    className: "mb-2 flex flex-row items-center"
  }, inputLabel, tooltipText && React__default.createElement(UITooltip$1, {
    title: tooltipText,
    disabled: tooltipDisabled != null ? tooltipDisabled : disabled
  }, React__default.createElement(UIIcon$1, {
    iconType: UIIconType.info,
    iconColor: UIColor.gray2,
    className: "ml-1"
  }))), React__default.createElement("div", {
    className: classNames(
    // Common
    "relative flex items-center border rounded-lg transition focus-within:ring-4 w-full",
    // Light Mode Default
    "text-gray-3-dark bg-white border-gray-3-light hover:border-gray-1 hover:focus-within:border-indigo-1 focus-within:border-indigo-1 ring-indigo-3 placeholder-gray-3",
    // Dark Mode Default
    "dark:text-gray-3 dark:bg-gray-5-dark dark:border-gray-3-dark hover:border-gray-1 dark:hover:focus-within:border-indigo-1 dark:focus-within:border-indigo-1 dark:ring-indigo-3-dark dark:placeholder-gray-3-dark",
    // Disabled
    "disabled:text-gray-3-light disabled:border-gray-4-light disabled:bg-gray-6-light dark:disabled:text-gray-4-dark dark:disabled:border-gray-4-dark dark:disabled:bg-gray-5-dark dark:disabled:placeholder-gray-4-dark", (_classNames2 = {
      // Warning
      "border-orange-1 hover:border-orange-2-dark hover:focus-within:border-orange-1 focus-within:border-orange-1 ring-orange-3 dark:border-orange-2-dark dark:hover:border-orange-2 dark:hover:focus-within:border-orange-1 dark:focus-within:border-orange-1 dark:ring-orange-3-dark": warning && !danger,
      // Danger
      "border-red-1 hover:border-red-2-dark hover:focus-within:border-red-1 focus-within:border-red-1 ring-red-3 dark:border-red-2-dark dark:hover:border-red-2 dark:hover:focus-within:border-red-1 dark:focus-within:border-red-1 dark:ring-red-3-dark": danger
    }, _classNames2[className || ""] = Boolean(className), _classNames2))
  }, warning && !danger && React__default.createElement("div", {
    className: "absolute top-[1px] right-12-px flex items-center h-[calc(100%-2px)] bg-white dark:bg-gray-5-dark disabled:bg-gray-6-light dark:disabled:bg-gray-6-dark"
  }, React__default.createElement(UITooltip$1, {
    title: warningText,
    disabled: !warningText
  }, React__default.createElement(UIIcon$1, {
    iconType: UIIconType.error,
    iconColor: UIColor.orange1
  }))), danger && React__default.createElement("div", {
    className: "absolute top-[1px] right-12-px flex items-center h-[calc(100%-2px)] bg-white dark:bg-gray-5-dark disabled:bg-gray-6-light dark:disabled:bg-gray-6-dark"
  }, React__default.createElement(UITooltip$1, {
    title: dangerText,
    disabled: !dangerText
  }, React__default.createElement(UIIcon$1, {
    iconType: UIIconType.error,
    iconColor: UIColor.red1
  }))), prefix && React__default.createElement("div", {
    className: classNames("py-2 px-3 bg-gray-6-light dark:bg-transparent rounded-l-lg border-r", {
      "border-gray-3-light dark:border-gray-3-dark": !disabled,
      "border-gray-4-light dark:border-gray-4-dark": disabled
    })
  }, React__default.createElement(UIText$1, {
    typography: UITypography.body,
    color: disabled ? UIColor.gray2 : UIColor.gray1
  }, prefix)), iconType && React__default.createElement("div", {
    className: "flex items-center ml-3"
  }, React__default.createElement(UIIcon$1, {
    iconType: iconType,
    iconColor: disabled ? UIColor.gray3 : UIColor.gray1
  })), React__default.createElement("input", Object.assign({
    ref: mergeRefs([ownRef, ref, inputRef])
  }, rest, {
    value: value,
    className: "bg-transparent w-full px-4 py-2 focus:outline-none " + (inputClassName || ""),
    disabled: disabled
  })), onClear && value && !danger && !warning && React__default.createElement(UIButton$1, {
    variant: UIButtonVariant.quaternary,
    className: "shrink-0 mr-3",
    iconType: UIIconType.clear,
    onClick: function onClick(e) {
      var _ownRef$current;
      onClear == null ? void 0 : onClear(e);
      (_ownRef$current = ownRef.current) == null ? void 0 : _ownRef$current.focus();
    }
  })), helperText && React__default.createElement(UIText$1, {
    typography: UITypography.caption,
    color: disabled ? UIColor.grayLight3 : UIColor.grayLight2,
    className: "mt-2"
  }, helperText));
});
var _excluded$6 = ["filters", "buttons", "search", "onSearchChange", "showSearch", "className", "text", "searchInputPlaceholder"];
var UIActionBar = function UIActionBar(props) {
  var _props$filters = props.filters,
    filters = _props$filters === void 0 ? [] : _props$filters,
    _props$buttons = props.buttons,
    buttons = _props$buttons === void 0 ? [] : _props$buttons,
    search = props.search,
    onSearchChange = props.onSearchChange,
    _props$showSearch = props.showSearch,
    showSearch = _props$showSearch === void 0 ? true : _props$showSearch,
    className = props.className,
    text = props.text,
    _props$searchInputPla = props.searchInputPlaceholder,
    searchInputPlaceholder = _props$searchInputPla === void 0 ? "Search" : _props$searchInputPla,
    rest = _objectWithoutPropertiesLoose(props, _excluded$6);
  /**
   * Callback to UIInput to notify that the content
   * of the search bar has changed.
   *
   * e: React.ChangeEvent<HTMLInputElement>
   */
  var onSearchInputChange = React__default.useCallback(function (e) {
    onSearchChange == null ? void 0 : onSearchChange(e.target.value);
  }, [onSearchChange]);
  /**
   * Callback to UIInput to notify that the content
   * of the search bar has been cleared.
   */
  var onSearchInputClear = React__default.useCallback(function () {
    onSearchChange == null ? void 0 : onSearchChange("");
  }, [onSearchChange]);
  return React__default.createElement(UIContainer$1, Object.assign({}, rest, {
    className: classNames("flex items-center justify-between overflow-hidden", className, {
      "p-4": showSearch,
      "p-6": !showSearch
    })
  }), text && React__default.createElement("div", {
    className: "flex items-center"
  }, React__default.createElement(UIText$1, {
    typography: UITypography.label2,
    color: UIColor.grayDark3
  }, text)), React__default.createElement("div", {
    className: "flex items-center space-x-3"
  }, showSearch && React__default.createElement(UIInput, {
    containerClassName: "w-45 max-w-45",
    iconType: UIIconType.search,
    placeholder: searchInputPlaceholder,
    value: search,
    onChange: onSearchInputChange,
    onClear: onSearchInputClear,
    "data-cy": "action-bar-search-input"
  }), filters.map(function (f) {
    return f;
  })), React__default.createElement("div", {
    className: "flex items-center space-x-5"
  }, buttons.map(function (button, idx) {
    return React__default.createElement(UITooltip$1, {
      key: idx,
      side: UIPopoverSide.top,
      title: button.tooltipTitle,
      description: button.tooltipDescription,
      disabled: button.disabled || !button.tooltipTitle
    }, button.customButton ? button.customButton : React__default.createElement(UIButton$1, {
      "data-cy": button == null ? void 0 : button.dataCy,
      variant: button.buttonVariant,
      iconType: button.buttonIconType,
      text: button.buttonText,
      loading: button.buttonLoading,
      onClick: button.onClick,
      disabled: button.disabled,
      danger: button.danger
    }));
  })));
};
var _path$2W;
function _extends$3f() {
  _extends$3f = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$3f.apply(this, arguments);
}
var SvgTriangle = function SvgTriangle(props) {
  return /*#__PURE__*/createElement("svg", _extends$3f({
    width: 10,
    height: 16,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2W || (_path$2W = /*#__PURE__*/createElement("path", {
    d: "M8.933 6a.5.5 0 0 0 .384-.82L5.384.46a.5.5 0 0 0-.768 0L.684 5.18a.5.5 0 0 0 .384.82h7.865Z",
    fill: "#9CA3AF"
  })));
};
var _excluded$7 = ["item", "onClick", "parentOpen", "nestedUnderGroup"];
var UIDropdownPopoutRow = function UIDropdownPopoutRow(props) {
  var _classNames, _item$iconColor;
  var item = props.item,
    onClick = props.onClick,
    parentOpen = props.parentOpen,
    nestedUnderGroup = props.nestedUnderGroup,
    rest = _objectWithoutPropertiesLoose(props, _excluded$7);
  var _React$useState = React__default.useState(false),
    hovering = _React$useState[0],
    setHovering = _React$useState[1];
  var openNested = React__default.useCallback(function () {
    if (parentOpen) {
      setHovering(true);
    }
  }, [parentOpen]);
  var closeNested = React__default.useCallback(function () {
    setHovering(false);
  }, []);
  var onRowClick = function onRowClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!item.disabled) {
      item.onClick == null ? void 0 : item.onClick();
      onClick == null ? void 0 : onClick();
    }
  };
  var row = React__default.createElement("div", Object.assign({
    className: classNames(
    // Common
    "transition flex flex-row items-center justify-between w-full px-4 py-2 bg-white dark:bg-gray-5-dark min-w-55", (_classNames = {
      "text-gray-1 hover:text-gray-3-dark hover:bg-gray-6 dark:text-gray-3 dark:hover:bg-gray-4-dark cursor-pointer": !item.disabled,
      "text-gray-3-light dark:text-gray-4-dark cursor-not-allowed": item.disabled
    }, _classNames[item.className || ""] = Boolean(item.className), _classNames["pl-6"] = nestedUnderGroup, _classNames)),
    onClick: onRowClick
  }, rest), React__default.createElement("div", {
    className: "flex flex-row items-center w-full"
  }, item.iconType && React__default.createElement(UIIcon$1, {
    iconType: item.iconType,
    iconColor: item.disabled ? UIColor.grayLight3 : (_item$iconColor = item.iconColor) != null ? _item$iconColor : UIColor.gray2,
    className: "mr-3 flex-none"
  }), item.image, React__default.createElement("div", {
    className: "flex flex-col flex-1"
  }, React__default.createElement("div", {
    className: "flex flex-row"
  }, React__default.createElement(UIText$1, {
    typography: UITypography.label2,
    color: UIColor.inherit
  }, item.title), item.badge), item.description && React__default.createElement(UIText$1, {
    typography: UITypography.caption,
    color: UIColor.gray2,
    className: "mt-0.5"
  }, item.description))), item.onHoverElement && React__default.createElement(SvgTriangle, {
    className: "transform rotate-90 -ml-1.5"
  }));
  // Might have to rethink the nesting
  if (item.onHoverElement) {
    return React__default.createElement("div", {
      onMouseOver: openNested,
      onMouseLeave: closeNested,
      onTouchStart: openNested,
      onTouchEnd: closeNested
    }, React__default.createElement(UIPopover, {
      side: UIPopoverSide.right,
      content: item.onHoverElement,
      open: hovering
    }, row));
  }
  return row;
};
var UIDropdownPopout = function UIDropdownPopout(props) {
  var _ref, _groupedItemsKeys$sor;
  var className = props.className,
    _props$items = props.items,
    items = _props$items === void 0 ? [] : _props$items,
    _onClick = props.onClick,
    open = props.open,
    _props$side = props.side,
    side = _props$side === void 0 ? UIPopoverSide.bottom : _props$side,
    _props$align = props.align,
    align = _props$align === void 0 ? UIPopoverAlign.center : _props$align,
    trigger = props.trigger,
    _props$block = props.block,
    block = _props$block === void 0 ? false : _props$block,
    _props$searchable = props.searchable,
    searchable = _props$searchable === void 0 ? false : _props$searchable,
    dataCy = props.dataCy,
    popoutWidth = props.popoutWidth,
    _props$popoutMaxHeigh = props.popoutMaxHeight,
    popoutMaxHeight = _props$popoutMaxHeigh === void 0 ? 384 : _props$popoutMaxHeigh,
    searchRef = props.searchRef,
    groupTitles = props.groupTitles,
    onScroll = props.onScroll,
    _props$forceNoResultP = props.forceNoResultPopup,
    forceNoResultPopup = _props$forceNoResultP === void 0 ? false : _props$forceNoResultP,
    renderNoResultContent = props.renderNoResultContent,
    _props$loading = props.loading,
    loading = _props$loading === void 0 ? false : _props$loading;
  // State
  var _React$useState = React__default.useState(""),
    searchText = _React$useState[0],
    setSearchText = _React$useState[1];
  var _React$useState2 = React__default.useState(open),
    isPopoverOpen = _React$useState2[0],
    setIsPopoverOpen = _React$useState2[1];
  // Adds an 'id' property which can be used to get an item's original index from filteredItems
  var newItems = React__default.useMemo(function () {
    return items.map(function (item, index) {
      return _extends({}, item, {
        index: index
      });
    });
  }, [items]);
  // Returns a filtered set of items based on user search text
  var filteredItems = React__default.useMemo(function () {
    return newItems.filter(function (item) {
      var _item$title;
      return (_item$title = item.title) == null ? void 0 : _item$title.toLowerCase().includes(searchText.toLowerCase());
    });
  }, [newItems, searchText]);
  var groupedItems = (_ref = searchable ? filteredItems : newItems) == null ? void 0 : _ref.reduce(function (acc, item) {
    var _item$group;
    var key = (_item$group = item.group) != null ? _item$group : 0;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});
  var groupedItemsKeys = Object.keys(groupedItems);
  var renderNoResultPopout = function renderNoResultPopout(value) {
    return React__default.createElement("div", {
      className: "py-2 grow flex justify-center"
    }, renderNoResultContent == null ? void 0 : renderNoResultContent(value));
  };
  useEffect(function () {
    setIsPopoverOpen(open && (groupedItemsKeys.length > 0 || forceNoResultPopup));
  }, [open, groupedItemsKeys, forceNoResultPopup]);
  // Render
  var popout = React__default.createElement("div", {
    className: classNames("flex items-center justify-center", {
      "flex-col-reverse": side === UIPopoverSide.top,
      "flex-row": side === UIPopoverSide.right,
      "flex-col": side === UIPopoverSide.bottom,
      "flex-row-reverse": side === UIPopoverSide.left
    }),
    "data-cy": dataCy
  }, React__default.createElement(UIContainer$1, {
    className: classNames(className, "rounded-lg overflow-y-auto overflow-x-hidden flex flex-col"),
    style: {
      width: popoutWidth,
      maxHeight: popoutMaxHeight
    },
    raised: true,
    border: true,
    onScroll: onScroll
  }, loading && React__default.createElement("div", {
    className: "flex py-8 items-center justify-center",
    "data-cy": "ui-dropdown-popout-container"
  }, React__default.createElement(UILoadingIndicator$1, {
    iconColor: UIColor.indigo1
  })), !loading && React__default.createElement(React__default.Fragment, null, searchable && React__default.createElement("div", {
    className: "sticky top-0 p-4 border-b border-gray-4",
    style: {
      // Not sure why tailwind's bg-inherit isn't working. It's in the docs.
      backgroundColor: "inherit"
    }
  }, React__default.createElement(UIInput, {
    "data-cy": "UIDropdownPopout-search-box",
    placeholder: "Search",
    onChange: function onChange(e) {
      setSearchText(e.target.value);
    },
    value: searchText,
    inputRef: searchRef,
    iconType: UIIconType.search
  })), Boolean(groupedItemsKeys.length) && React__default.createElement("div", {
    className: "py-2 grow",
    "data-cy": "ui-dropdown-popout-container"
  }, groupedItemsKeys == null ? void 0 : (_groupedItemsKeys$sor = groupedItemsKeys.sort()) == null ? void 0 : _groupedItemsKeys$sor.map(function (key, idx) {
    var group = groupedItems[key];
    return React__default.createElement("div", {
      key: idx
    }, (groupTitles == null ? void 0 : groupTitles[idx]) && React__default.createElement(UIText$1, {
      typography: UITypography.label2,
      className: "px-4 py-2"
    }, groupTitles[idx]), group == null ? void 0 : group.map(function (item) {
      return React__default.createElement(UIDropdownPopoutRow, {
        key: item.index,
        item: item,
        parentOpen: open,
        onClick: function onClick() {
          setSearchText("");
          _onClick == null ? void 0 : _onClick(item.index);
        },
        nestedUnderGroup: Boolean(groupTitles == null ? void 0 : groupTitles[idx]),
        "data-cy": (item == null ? void 0 : item.id) || "ui-dropdown-popout-row"
      });
    }), idx < groupedItemsKeys.length - 1 && React__default.createElement("div", {
      className: "m-auto my-2 h-[1px] w-[85%] px-4 bg-gray-4-light dark:bg-gray-4-dark"
    }));
  })), (forceNoResultPopup || Boolean(searchText) && !filteredItems.length) && renderNoResultPopout(searchText))));
  return React__default.createElement(UIPopover, {
    side: side,
    align: align,
    content: popout,
    open: isPopoverOpen,
    block: block
  }, trigger);
};
var UIAutoCompleteInput = function UIAutoCompleteInput(props) {
  var _measureRef$current;
  var dropdownPopoutClassName = props.dropdownPopoutClassName,
    items = props.items,
    _onClick = props.onClick,
    side = props.side,
    _props$dynamicPopoutS = props.dynamicPopoutSize,
    dynamicPopoutSize = _props$dynamicPopoutS === void 0 ? false : _props$dynamicPopoutS,
    _props$showNoResultPo = props.showNoResultPopout,
    showNoResultPopout = _props$showNoResultPo === void 0 ? false : _props$showNoResultPo,
    renderNoResultContent = props.renderNoResultContent,
    _props$loading = props.loading,
    loading = _props$loading === void 0 ? false : _props$loading,
    onInputValueChange = props.onInputValueChange,
    selectedItem = props.selectedItem,
    disabled = props.disabled,
    _props$defaultValue = props.defaultValue,
    defaultValue = _props$defaultValue === void 0 ? "" : _props$defaultValue,
    placeholder = props.placeholder,
    _props$autoFocus = props.autoFocus,
    autoFocus = _props$autoFocus === void 0 ? false : _props$autoFocus,
    _props$openOnClick = props.openOnClick,
    openOnClick = _props$openOnClick === void 0 ? false : _props$openOnClick,
    inputClassName = props.inputClassName,
    inputDataCy = props.inputDataCy,
    inputRef = props.inputRef,
    onClear = props.onClear;
  var _React$useState = React__default.useState(false),
    open = _React$useState[0],
    setOpen = _React$useState[1];
  var _React$useState2 = React__default.useState(defaultValue),
    inputValue = _React$useState2[0],
    setInputValue = _React$useState2[1];
  var measureRef = useMeasure()[1];
  var triggerRef = React__default.useRef();
  React__default.useEffect(function () {
    if (selectedItem) {
      setInputValue(selectedItem.title);
    }
  }, [selectedItem]);
  React__default.useEffect(function () {
    onInputValueChange(inputValue);
  }, [inputValue]);
  var onInputChangeInternal = function onInputChangeInternal(event) {
    var textWithoutWhitespace = event.target.value.trimLeft();
    setInputValue(textWithoutWhitespace);
    if (textWithoutWhitespace && textWithoutWhitespace !== "") {
      return openAutoComplete();
    }
    return false;
  };
  var openAutoComplete = React__default.useCallback(function () {
    setOpen(true);
  }, []);
  var closeAutoComplete = React__default.useCallback(function () {
    setOpen(false);
  }, []);
  useOnClickOutside([triggerRef], closeAutoComplete, {
    isActive: open
  });
  var trigger = React__default.createElement(UIInput, {
    className: inputClassName,
    iconType: UIIconType.search,
    "data-cy": inputDataCy,
    disabled: disabled,
    value: inputValue,
    onChange: onInputChangeInternal,
    placeholder: placeholder,
    autoFocus: autoFocus,
    onClear: onClear,
    inputRef: inputRef ? mergeRefs([measureRef, triggerRef, inputRef]) : mergeRefs([measureRef, triggerRef]),
    onClick: function onClick() {
      openOnClick && openAutoComplete();
    }
  });
  var forceNoResultPopout = function forceNoResultPopout() {
    if (!(items != null && items.length) && inputValue && showNoResultPopout) {
      return true;
    }
    return false;
  };
  return React__default.createElement(UIDropdownPopout, {
    className: dropdownPopoutClassName,
    items: items,
    onClick: function onClick(index) {
      closeAutoComplete();
      _onClick == null ? void 0 : _onClick(index);
    },
    open: open,
    side: side,
    trigger: trigger,
    // Using bounds from useMeasure does not account for padding
    popoutWidth: dynamicPopoutSize ? (_measureRef$current = measureRef.current) == null ? void 0 : _measureRef$current.getBoundingClientRect().width : undefined,
    forceNoResultPopup: forceNoResultPopout(),
    renderNoResultContent: renderNoResultContent,
    loading: loading
  });
};
var _excluded$8 = ["variant", "size", "text", "textColor", "className", "icon", "iconClassName"];
var UIBadgeVariant;
(function (UIBadgeVariant) {
  UIBadgeVariant["primary"] = "primary";
  UIBadgeVariant["secondary"] = "secondary";
  UIBadgeVariant["neutral"] = "neutral";
  UIBadgeVariant["gray"] = "gray";
  UIBadgeVariant["black"] = "black";
  UIBadgeVariant["rainbow"] = "rainbow";
  UIBadgeVariant["custom"] = "custom";
})(UIBadgeVariant || (UIBadgeVariant = {}));
var UIBadgeSize;
(function (UIBadgeSize) {
  UIBadgeSize["default"] = "default";
  UIBadgeSize["small"] = "small";
})(UIBadgeSize || (UIBadgeSize = {}));
var UIBadge = function UIBadge(props) {
  var _classNames;
  var variant = props.variant,
    _props$size = props.size,
    size = _props$size === void 0 ? UIBadgeSize["default"] : _props$size,
    text = props.text,
    _props$textColor = props.textColor,
    textColor = _props$textColor === void 0 ? UIColor.inherit : _props$textColor,
    className = props.className,
    icon = props.icon,
    iconClassName = props.iconClassName,
    rest = _objectWithoutPropertiesLoose(props, _excluded$8);
  if (variant === UIBadgeVariant.rainbow) {
    return React__default.createElement("div", {
      className: classNames("bg-blue-3 border border-transparent", className)
    }, React__default.createElement("div", {
      className: "bg-clip-text bg-gradient-to-r from-indigo-1 to-blue-1"
    }, text && React__default.createElement(UIText$1, {
      color: UIColor.transparent,
      typography: UITypography.label
    }, text)));
  }
  return React__default.createElement(React__default.Fragment, null, React__default.createElement("div", Object.assign({}, rest, {
    className: classNames("inline-flex items-center px-[0.31rem] rounded-xl whitespace-nowrap group", (_classNames = {}, _classNames[["text-white", "bg-indigo-1", "dark:text-gray-3-light"].join(" ")] = variant === UIBadgeVariant.primary, _classNames[["text-blue-1", "bg-blue-3-light", "dark:bg-blue-3-dark"].join(" ")] = variant === UIBadgeVariant.secondary, _classNames[["text-gray-2-light", "bg-gray-5-light", "dark:bg-gray-3-dark"].join(" ")] = variant === UIBadgeVariant.neutral, _classNames[["text-white", "bg-gray-3-dark"].join(" ")] = variant === UIBadgeVariant.black, _classNames[["text-gray-1", "bg-gray-5-light", "dark:bg-gray-3-dark"].join(" ")] = variant === UIBadgeVariant.gray, _classNames), className)
  }), icon && React__default.createElement(UIIcon$1, {
    iconType: icon,
    className: classNames("mr-0.5", iconClassName),
    iconSize: size === UIBadgeSize.small ? "w-[9px] h-[9px]" : "w-4 h-4"
  }), text && React__default.createElement(UIText$1, {
    color: textColor,
    typography: size === UIBadgeSize["default"] ? UITypography.label : UITypography.label4
  }, text)));
};
var UIBadge$1 = /*#__PURE__*/React__default.memo(UIBadge);
var UIBanner = function UIBanner(props) {
  var title = props.title,
    description = props.description,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? UIContainerVariant["default"] : _props$variant,
    buttons = props.buttons,
    className = props.className,
    children = props.children;
  // TODO: This is kinda hacky
  var content = children ? children : React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
    className: "space-y-2"
  }, React__default.createElement(UIText$1, {
    typography: UITypography.body2,
    color: UIColor.inherit
  }, title), description && React__default.createElement(UIText$1, {
    typography: UITypography.body,
    color: UIColor.inherit
  }, description)), React__default.createElement("div", {
    className: "flex items-center space-x-3"
  }, buttons == null ? void 0 : buttons.map(function (button, idx) {
    return React__default.createElement(UIButton$1, Object.assign({
      key: idx
    }, button));
  })));
  return React__default.createElement(UIContainer$1, {
    variant: variant,
    className: classNames("flex items-center justify-between p-6", className),
    rounded: true,
    shadow: variant === UIContainerVariant["default"]
  }, content);
};
var _excluded$9 = ["checked", "indeterminate", "className", "disabled", "onChange"];
var UICheckbox = function UICheckbox(props) {
  var _props$checked = props.checked,
    checked = _props$checked === void 0 ? false : _props$checked,
    indeterminate = props.indeterminate,
    className = props.className,
    disabled = props.disabled,
    onChange = props.onChange,
    rest = _objectWithoutPropertiesLoose(props, _excluded$9);
  var checkedIconType = React__default.useMemo(function () {
    if (checked && indeterminate) {
      return UIIconType.checkboxIndeterminate;
    }
    if (checked) {
      return UIIconType.checkboxSelected;
    }
    return UIIconType.checkboxUnselected;
  }, [checked, indeterminate, disabled]);
  return React__default.createElement(UIButton$1, Object.assign({
    iconType: checkedIconType,
    variant: checked ? UIButtonVariant.tertiary : UIButtonVariant.quaternary,
    onClick: function onClick() {
      return onChange == null ? void 0 : onChange(!checked);
    },
    disabled: disabled,
    className: classNames("border-none", className)
  }, rest));
};
var UIDropdownDefaultTrigger = function UIDropdownDefaultTrigger(props) {
  var active = props.active,
    block = props.block,
    className = props.className,
    danger = props.danger,
    dangerText = props.dangerText,
    dataCy = props.dataCy,
    disabled = props.disabled,
    onClick = props.onClick,
    _props$placeholder = props.placeholder,
    placeholder = _props$placeholder === void 0 ? "Select" : _props$placeholder,
    placeholderIconType = props.placeholderIconType,
    selectedItem = props.selectedItem,
    triggerRef = props.triggerRef,
    dynamicPopoutSize = props.dynamicPopoutSize;
  var noSelectedItem = !selectedItem;
  var text = noSelectedItem ? "- " + placeholder + " -" : selectedItem == null ? void 0 : selectedItem.title;
  var icon = noSelectedItem ? placeholderIconType : selectedItem == null ? void 0 : selectedItem.iconType;
  var iconColor = noSelectedItem ? UIColor.gray2 : selectedItem == null ? void 0 : selectedItem.iconColor;
  var image = selectedItem == null ? void 0 : selectedItem.image;
  return React__default.createElement("div", {
    className: classNames(
    // Common
    "flex flex-row justify-between border rounded-lg transition whitespace-nowrap pl-4 pr-3 py-2", {
      "min-w-55": !dynamicPopoutSize,
      "w-max": !block,
      "w-full": block,
      "bg-white border-gray-3-light hover:border-gray-1 ring-indigo-3 dark:bg-gray-5-dark dark:border-gray-3-dark dark:hover:border-gray-5-dark dark:ring-indigo-3-dark cursor-pointer": !disabled,
      "text-gray-2 dark:text-gray-2-dark": noSelectedItem,
      "text-gray-3-dark dark:text-gray-3": !noSelectedItem,
      "text-gray-3-light border-gray-4-light bg-gray-6-light dark:text-gray-4-dark dark:border-gray-4-dark dark:bg-gray-5-dark cursor-not-allowed": disabled,
      "ring-4 border-indigo-1 dark:border-indigo-1 hover:border-indigo-1 dark:hover:border-indigo-1": active,
      // Danger
      "border-red-1 hover:border-red-3-dark ring-red-3 dark:border-red-2-dark dark:hover:border-red-1 dark:ring-red-3-dark": danger
    }, className),
    onClick: disabled ? undefined : onClick,
    "data-cy": dataCy,
    ref: triggerRef
  }, React__default.createElement("div", {
    className: classNames("flex flex-row items-center", {
      "w-[90%]": block
    })
  }, icon && React__default.createElement(UIIcon$1, {
    iconType: icon,
    iconColor: iconColor,
    className: "mr-3"
  }), image, React__default.createElement(UIText$1, {
    typography: UITypography.label2,
    color: UIColor.inherit,
    className: "overflow-hidden text-ellipsis"
  }, text), selectedItem == null ? void 0 : selectedItem.badge), React__default.createElement("div", {
    className: "flex flex-row items-center"
  }, danger && React__default.createElement(UITooltip$1, {
    title: dangerText,
    disabled: !dangerText
  }, React__default.createElement(UIIcon$1, {
    iconType: UIIconType.error,
    iconColor: UIColor.red1,
    className: "mr-2"
  })), React__default.createElement(UIIcon$1, {
    iconType: UIIconType.sort,
    iconColor: UIColor.grayLight2
  })));
};
var UIDropdownTextTrigger = function UIDropdownTextTrigger(props) {
  var _classNames;
  var active = props.active,
    block = props.block,
    className = props.className,
    danger = props.danger,
    dangerText = props.dangerText,
    dataCy = props.dataCy,
    disabled = props.disabled,
    onClick = props.onClick,
    _props$placeholder = props.placeholder,
    placeholder = _props$placeholder === void 0 ? "Select" : _props$placeholder,
    selectedItem = props.selectedItem,
    triggerRef = props.triggerRef;
  var noSelectedItem = !selectedItem;
  var text = danger ? dangerText : noSelectedItem ? "- " + placeholder + " -" : selectedItem == null ? void 0 : selectedItem.title;
  return React__default.createElement("div", {
    className: classNames((_classNames = {
      "w-full": block,
      "cursor-pointer": !disabled,
      "text-indigo-1 hover:text-indigo-2-dark active:text-indigo-3-dark": !disabled && !danger && !active,
      "text-indigo-3-dark": !disabled && !danger && active,
      "text-red-1 hover:text-red-2-dark active:text-red-3-dark dark:text-red-1 dark:hover:text-red-2-dark dark:active:text-red-3-dark": danger && !disabled
    }, _classNames[className || ""] = Boolean(className), _classNames)),
    onClick: disabled ? undefined : onClick,
    "data-cy": dataCy,
    ref: triggerRef
  }, React__default.createElement("div", {
    className: "flex items-center space-x-2"
  }, React__default.createElement(UIText$1, {
    typography: UITypography.label3,
    color: UIColor.inherit
  }, text), React__default.createElement(UIIcon$1, {
    iconType: UIIconType.sortDown,
    iconColor: UIColor.inherit
  })));
};
var _excluded$a = ["variant"];
var UIDropdownTriggerVariant;
(function (UIDropdownTriggerVariant) {
  UIDropdownTriggerVariant["default"] = "default";
  UIDropdownTriggerVariant["text"] = "text";
})(UIDropdownTriggerVariant || (UIDropdownTriggerVariant = {}));
var UIDropdownTrigger = function UIDropdownTrigger(props) {
  var _props$variant = props.variant,
    variant = _props$variant === void 0 ? UIDropdownTriggerVariant["default"] : _props$variant,
    rest = _objectWithoutPropertiesLoose(props, _excluded$a);
  switch (variant) {
    case UIDropdownTriggerVariant.text:
      return React__default.createElement(UIDropdownTextTrigger, Object.assign({}, rest));
    default:
      return React__default.createElement(UIDropdownDefaultTrigger, Object.assign({}, rest));
  }
};
var UIDropdown = function UIDropdown(props) {
  var _measureRef$current;
  var dropdownPopoutClassName = props.dropdownPopoutClassName,
    items = props.items,
    _onClick = props.onClick,
    onOpen = props.onOpen,
    side = props.side,
    dropdownPopoutDataCy = props.dropdownPopoutDataCy,
    _props$dynamicPopoutS = props.dynamicPopoutSize,
    dynamicPopoutSize = _props$dynamicPopoutS === void 0 ? false : _props$dynamicPopoutS,
    popoutMaxHeight = props.popoutMaxHeight,
    groupTitles = props.groupTitles,
    onScrollPopout = props.onScrollPopout,
    triggerVariant = props.triggerVariant,
    block = props.block,
    dropdownTriggerClassName = props.dropdownTriggerClassName,
    danger = props.danger,
    dangerText = props.dangerText,
    disabled = props.disabled,
    placeholder = props.placeholder,
    placeholderIconType = props.placeholderIconType,
    selectedItem = props.selectedItem,
    searchable = props.searchable,
    triggerDataCy = props.triggerDataCy,
    popoutWidth = props.popoutWidth;
  var measureRef = useMeasure()[1];
  var _React$useState = React__default.useState(false),
    open = _React$useState[0],
    setOpen = _React$useState[1];
  var triggerRef = React__default.useRef();
  var popoutRef = React__default.useRef();
  var searchRef = React__default.useRef();
  var openDropdown = React__default.useCallback(function () {
    onOpen == null ? void 0 : onOpen(true);
    setOpen(true);
  }, []);
  var closeDropdown = React__default.useCallback(function () {
    onOpen == null ? void 0 : onOpen(false);
    setOpen(false);
  }, []);
  var toggleDropdown = React__default.useCallback(function () {
    if (open) {
      closeDropdown();
    } else {
      openDropdown();
    }
  }, [open, openDropdown, closeDropdown]);
  // Setup Hooks
  useOnClickOutside([popoutRef, triggerRef, searchRef], closeDropdown, {
    isActive: open
  });
  var trigger = React__default.createElement(UIDropdownTrigger, {
    variant: triggerVariant,
    active: open,
    block: block,
    className: dropdownTriggerClassName,
    danger: danger,
    dangerText: dangerText,
    disabled: disabled,
    onClick: toggleDropdown,
    placeholder: placeholder,
    placeholderIconType: placeholderIconType,
    selectedItem: selectedItem,
    dataCy: triggerDataCy,
    triggerRef: mergeRefs([measureRef, triggerRef]),
    dynamicPopoutSize: dynamicPopoutSize
  });
  return React__default.createElement(UIDropdownPopout, {
    className: dropdownPopoutClassName,
    items: items,
    onClick: function onClick(index) {
      closeDropdown();
      _onClick == null ? void 0 : _onClick(index);
    },
    open: open,
    side: side,
    trigger: trigger,
    block: block,
    searchable: searchable,
    dataCy: dropdownPopoutDataCy,
    // Using bounds from useMeasure does not account for padding
    popoutWidth: popoutWidth || dynamicPopoutSize ? (_measureRef$current = measureRef.current) == null ? void 0 : _measureRef$current.getBoundingClientRect().width : undefined,
    popoutMaxHeight: popoutMaxHeight,
    searchRef: searchRef,
    groupTitles: groupTitles,
    onScroll: onScrollPopout
  });
};

/**
 * Component implentation of https://flagpedia.net/download/api
 */
var UIFlagSize;
(function (UIFlagSize) {
  UIFlagSize["s16x12"] = "16x12";
  UIFlagSize["s32x24"] = "32x24";
})(UIFlagSize || (UIFlagSize = {}));
// TODO: The circle needs to have a fixed width and height and the flag returned by the api needs to be @2x the size of the width of the actual image. Need to think of a good way to calculate all of these.
var UIFlag = function UIFlag(props) {
  var countryCode = props.countryCode,
    _props$size = props.size,
    size = _props$size === void 0 ? UIFlagSize.s16x12 : _props$size,
    className = props.className;
  return React__default.createElement("span", {
    className: classNames("flex content-center p-2 w-8 h-8 rounded-full bg-gray-5-light dark:bg-gray-5-dark", className)
  }, React__default.createElement("img", {
    className: "m-auto w-4",
    src: "https://flagcdn.com/" + size + "/" + (countryCode == null ? void 0 : countryCode.toLowerCase()) + ".png"
  }));
};
var index = /*#__PURE__*/React__default.memo(UIFlag);
var _path$2X;
function _extends$3g() {
  _extends$3g = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$3g.apply(this, arguments);
}
var SvgFunnelHead = function SvgFunnelHead(props) {
  return /*#__PURE__*/createElement("svg", _extends$3g({
    width: 38,
    height: 80,
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2X || (_path$2X = /*#__PURE__*/createElement("path", {
    d: "M0 0h14l22.765 37.942a4 4 0 0 1 0 4.116L14 80H0V0Z"
  })));
};
var _path$2Y;
function _extends$3h() {
  _extends$3h = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$3h.apply(this, arguments);
}
var SvgFunnelTail = function SvgFunnelTail(props) {
  return /*#__PURE__*/createElement("svg", _extends$3h({
    height: 80,
    viewBox: "0 0 25 80",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2Y || (_path$2Y = /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M0 80h24.5V0H0l22.765 37.942a4 4 0 0 1 0 4.116L0 80Z"
  })));
};
var UIFunnelSliceVariant;
(function (UIFunnelSliceVariant) {
  UIFunnelSliceVariant["top"] = "top";
  UIFunnelSliceVariant["middle"] = "middle";
  UIFunnelSliceVariant["bottom"] = "bottom";
})(UIFunnelSliceVariant || (UIFunnelSliceVariant = {}));
var UIFunnelSlice = function UIFunnelSlice(props) {
  var _variantColors, _classNames, _classNames2, _classNames3;
  var variant = props.variant,
    title = props.title,
    amount = props.amount,
    inactive = props.inactive,
    _props$hideTail = props.hideTail,
    hideTail = _props$hideTail === void 0 ? false : _props$hideTail,
    onClick = props.onClick,
    className = props.className;
  var variantColors = (_variantColors = {}, _variantColors[UIFunnelSliceVariant.top] = {
    tailAndArrowClass: "text-indigo-3-light group-hover:text-indigo-2-light",
    baseClass: "bg-indigo-3-light group-hover:bg-indigo-2-light",
    textColor: UIColor.indigoDark2
  }, _variantColors[UIFunnelSliceVariant.middle] = {
    tailAndArrowClass: "text-indigo-1 group-hover:text-indigo-2-dark",
    baseClass: "bg-indigo-1 group-hover:bg-indigo-2-dark",
    textColor: UIColor.white
  }, _variantColors[UIFunnelSliceVariant.bottom] = {
    tailAndArrowClass: "text-indigo-3-dark group-hover:text-indigo-4-dark",
    baseClass: "bg-indigo-3-dark group-hover:bg-indigo-4-dark",
    textColor: UIColor.white
  }, _variantColors);
  var colors = variantColors == null ? void 0 : variantColors[variant];
  return React__default.createElement("div", {
    className: classNames("relative flex items-center h-[80px] min-h-[80px] min-w-0 w-full bg-transparent overflow-hidden cursor-pointer group", className),
    onClick: onClick
  }, !hideTail && React__default.createElement(SvgFunnelTail, {
    className: classNames("flex-none w-[25px] min-w-[25px] fill-current", (_classNames = {}, _classNames[colors.tailAndArrowClass] = !inactive, _classNames["text-gray-5-light group-hover:text-gray-4-light"] = inactive, _classNames))
  }), React__default.createElement("div", {
    className: classNames("flex items-center h-full flex-1 min-w-0 w-full fill-current", (_classNames2 = {}, _classNames2[colors.baseClass] = !inactive, _classNames2["bg-gray-5-light group-hover:bg-gray-4-light text-gray-2-light"] = inactive, _classNames2)),
    style: {
      width: "calc(100% + 1px)",
      marginLeft: "-1px"
    }
  }), React__default.createElement(SvgFunnelHead, {
    className: classNames("flex-none fill-current", (_classNames3 = {}, _classNames3[colors.tailAndArrowClass] = !inactive, _classNames3["text-gray-5-light group-hover:text-gray-4-light"] = inactive, _classNames3))
  }), React__default.createElement("div", {
    className: "absolute text-center pointer-events-none",
    style: {
      left: "50%",
      transform: "translateX(-50%)"
    }
  }, React__default.createElement(UIText$1, {
    typography: UITypography.label,
    color: inactive ? UIColor.grayLight2 : colors == null ? void 0 : colors.textColor
  }, title), React__default.createElement(UIText$1, {
    typography: UITypography.label3,
    color: inactive ? UIColor.grayLight2 : colors == null ? void 0 : colors.textColor
  }, amount)));
};
var UIFunnel = function UIFunnel(props) {
  var activeFunnelSlice = props.activeFunnelSlice,
    topOfFunnelAmount = props.topOfFunnelAmount,
    middleOfFunnelAmount = props.middleOfFunnelAmount,
    bottomOfFunnelAmount = props.bottomOfFunnelAmount,
    onTopOfFunnelClick = props.onTopOfFunnelClick,
    onMiddleOfFunnelClick = props.onMiddleOfFunnelClick,
    onBottomOfFunnelClick = props.onBottomOfFunnelClick;
  return React__default.createElement("div", {
    className: "w-full flex items-center"
  }, React__default.createElement(UIFunnelSlice, {
    variant: UIFunnelSliceVariant.top,
    title: "Top of Funnel",
    amount: topOfFunnelAmount,
    hideTail: true,
    className: "rounded-l-lg",
    onClick: onTopOfFunnelClick,
    inactive: activeFunnelSlice && activeFunnelSlice !== UIFunnelSliceVariant.top
  }), React__default.createElement(UIFunnelSlice, {
    variant: UIFunnelSliceVariant.middle,
    title: "Middle of Funnel",
    amount: middleOfFunnelAmount,
    className: "ml-[-18px]",
    onClick: onMiddleOfFunnelClick,
    inactive: activeFunnelSlice && activeFunnelSlice !== UIFunnelSliceVariant.middle
  }), React__default.createElement(UIFunnelSlice, {
    variant: UIFunnelSliceVariant.bottom,
    title: "Bottom of Funnel",
    amount: bottomOfFunnelAmount,
    className: "ml-[-18px]",
    onClick: onBottomOfFunnelClick,
    inactive: activeFunnelSlice && activeFunnelSlice !== UIFunnelSliceVariant.bottom
  }));
};
var UIModalOverlay = function UIModalOverlay(props) {
  var open = props.open,
    children = props.children;
  /**
   * Own animation styles. Causes a rerender of the spring
   * so the popover can animate in & out
   */
  var _React$useState = React__default.useState({
      // initial position
      from: {
        opacity: 0,
        transform: "translateY(20px)"
      }
    }),
    ownStyles = _React$useState[0],
    setOwnStyles = _React$useState[1];
  var commonSpringOptions = {
    reverse: !open,
    config: {
      duration: 200
    }
  };
  var animatedStyles = useSpring(_extends({}, commonSpringOptions, ownStyles));
  React__default.useEffect(function () {
    setOwnStyles({
      from: {
        opacity: 0,
        transform: "translateY(20px)"
      },
      to: {
        opacity: 1,
        transform: "translateY(0)"
      }
    });
  }, []);
  return React__default.createElement(UIPortal$1, null, React__default.createElement("div", {
    className: classNames("flex fixed top-0 left-0 w-screen h-screen z-modal bg-gray-6-dark bg-opacity-40", {
      visible: open,
      invisible: !open
    })
  }, React__default.createElement(animated.div, {
    className: "m-auto",
    style: animatedStyles
  }, children)));
};
var _excluded$b = ["open", "width", "height", "heightUnit", "headerText", "customHeader", "footerText", "customFooter", "footerTextClassNames", "footerButtons", "border", "children", "contentClassName", "onClose", "onScroll"];
var UIModal = function UIModal(props) {
  var open = props.open,
    width = props.width,
    height = props.height,
    _props$heightUnit = props.heightUnit,
    heightUnit = _props$heightUnit === void 0 ? "px" : _props$heightUnit,
    headerText = props.headerText,
    customHeader = props.customHeader,
    footerText = props.footerText,
    customFooter = props.customFooter,
    footerTextClassNames = props.footerTextClassNames,
    footerButtons = props.footerButtons,
    _props$border = props.border,
    border = _props$border === void 0 ? true : _props$border,
    children = props.children,
    contentClassName = props.contentClassName,
    onClose = props.onClose,
    onScroll = props.onScroll,
    rest = _objectWithoutPropertiesLoose(props, _excluded$b);
  return React__default.createElement(UIModalOverlay, {
    open: open
  }, React__default.createElement("div", {
    className: "w-full h-full flex items-center justify-center"
  }, React__default.createElement(UIContainer$1, Object.assign({
    className: classNames("flex flex-col pointer-events-auto overflow-hidden", {
      // 64px based on the margin-top and margin-bottom from my-8 (32px top, 32px bottom)
      "max-h-[calc(100vh-64px)] my-8": heightUnit !== "vh",
      // Don't want to apply the margin if the unit is vh
      // since sometimes a modal will need to span the entire screen
      "max-h-screen": heightUnit === "vh"
    }),
    style: {
      width: width + "px",
      height: "" + height + heightUnit
    },
    raised: true,
    rounded: true,
    border: border
  }, rest), (headerText || customHeader || onClose) && React__default.createElement("div", {
    className: classNames("flex items-baseline border-gray-4-light dark:border-gray-4-dark", {
      "p-6 border-b justify-between": headerText || customHeader,
      "pt-6 pr-6 justify-end": !headerText
    })
  }, headerText && React__default.createElement(UIText$1, {
    typography: UITypography.header
  }, headerText), customHeader, open && onClose && React__default.createElement(UIButton$1, {
    variant: UIButtonVariant.quaternary,
    iconType: UIIconType.close,
    onClick: function onClick(e) {
      e.stopPropagation();
      onClose();
    },
    "data-cy": "close_modal_button"
  })), React__default.createElement("div", {
    className: classNames("flex-1 overflow-y-auto", contentClassName),
    onScroll: onScroll
  }, children), (footerText || footerButtons || customFooter) && React__default.createElement("div", {
    className: "flex items-center p-6 justify-between border-t border-gray-4-light dark:border-gray-4-dark"
  }, footerText && typeof footerText === "string" ? React__default.createElement(UIText$1, {
    typography: UITypography.caption,
    color: UIColor.grayLight2,
    className: footerTextClassNames
  }, footerText) : footerText, customFooter, React__default.createElement("div", {
    className: classNames("flex justify-end items-center space-x-3", {
      "w-full": !footerText
    })
  }, footerButtons == null ? void 0 : footerButtons.map(function (button, idx) {
    return React__default.createElement(UIButton$1, Object.assign({
      className: classNames({
        "ml-6": idx === 0 && footerText
      }),
      key: idx
    }, button, {
      "data-cy": "modalFooterButton"
    }));
  }))))));
};
var UIMultiselectDropdownPopoutRow = function UIMultiselectDropdownPopoutRow(props) {
  var _classNames, _item$iconColor;
  var item = props.item,
    onClick = props.onClick,
    parentOpen = props.parentOpen,
    selected = props.selected,
    dataCy = props.dataCy;
  var _React$useState = React__default.useState(false),
    hovering = _React$useState[0],
    setHovering = _React$useState[1];
  var openNested = React__default.useCallback(function () {
    if (parentOpen) {
      setHovering(true);
    }
  }, [parentOpen]);
  var closeNested = React__default.useCallback(function () {
    setHovering(false);
  }, []);
  var onRowClick = function onRowClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!item.disabled) {
      item.onClick == null ? void 0 : item.onClick();
      onClick == null ? void 0 : onClick();
    }
  };
  var row = React__default.createElement("div", {
    className: classNames(
    // Common
    "transition flex flex-row items-center justify-between w-full px-4 py-2 bg-white dark:bg-gray-5-dark min-w-55", (_classNames = {
      "text-gray-3-dark hover:bg-gray-6 dark:text-gray-3 dark:hover:bg-gray-4-dark cursor-pointer": !item.disabled,
      "text-gray-3-light dark:text-gray-4-dark cursor-not-allowed": item.disabled
    }, _classNames[item.className || ""] = Boolean(item.className), _classNames)),
    onClick: onRowClick,
    "data-cy": dataCy
  }, React__default.createElement("div", {
    className: "flex flex-row items-center"
  }, selected && React__default.createElement(UIIcon$1, {
    iconType: UIIconType.checkboxSelected,
    iconColor: UIColor.indigo1,
    className: "mr-3",
    "data-cy": "ui-checkbox-selected"
  }), !selected && React__default.createElement(UIIcon$1, {
    iconType: UIIconType.checkboxUnselected,
    iconColor: UIColor.transparent,
    className: "mr-3",
    "data-cy": "ui-checkbox-deselected"
  }), item.iconType && React__default.createElement(UIIcon$1, {
    iconType: item.iconType,
    iconColor: item.disabled ? UIColor.grayLight3 : (_item$iconColor = item.iconColor) != null ? _item$iconColor : UIColor.gray2,
    className: "mr-3"
  }), React__default.createElement("div", {
    className: "flex flex-col"
  }, React__default.createElement(UIText$1, {
    typography: UITypography.label2,
    color: UIColor.inherit
  }, item.title), item.description && React__default.createElement(UIText$1, {
    typography: UITypography.caption,
    color: UIColor.gray2,
    className: "mt-0.5"
  }, item.description))), item.onHoverElement && React__default.createElement(UIIcon$1, {
    iconType: UIIconType.sortRight,
    iconColor: UIColor.gray2
  }));
  // Might have to rethink the nesting
  if (item.onHoverElement) {
    return React__default.createElement("div", {
      onMouseOver: openNested,
      onMouseLeave: closeNested,
      onTouchStart: openNested,
      onTouchEnd: closeNested
    }, React__default.createElement(UIPopover, {
      side: UIPopoverSide.right,
      content: item.onHoverElement,
      open: hovering
    }, row));
  }
  return row;
};
var UIMultiselectDropdownPopout = function UIMultiselectDropdownPopout(props) {
  var className = props.className,
    closePopout = props.closePopout,
    _props$items = props.items,
    items = _props$items === void 0 ? [] : _props$items,
    _onClick = props.onClick,
    open = props.open,
    _props$side = props.side,
    side = _props$side === void 0 ? UIPopoverSide.bottom : _props$side,
    _props$align = props.align,
    align = _props$align === void 0 ? UIPopoverAlign.center : _props$align,
    trigger = props.trigger,
    searchRef = props.searchRef,
    _props$selectedItems = props.selectedItems,
    selectedItems = _props$selectedItems === void 0 ? [] : _props$selectedItems,
    popoutDataCy = props.popoutDataCy,
    loading = props.loading,
    minPopoutWidth = props.minPopoutWidth,
    _props$popoutMaxHeigh = props.popoutMaxHeight,
    popoutMaxHeight = _props$popoutMaxHeigh === void 0 ? 384 : _props$popoutMaxHeigh,
    forceNoResultPopup = props.forceNoResultPopup,
    renderNoResultContent = props.renderNoResultContent,
    searchText = props.searchText;
  var popoutRef = React__default.useRef();
  // Setup Hooks
  useOnClickOutside([popoutRef, searchRef], closePopout, {
    isActive: open
  });
  var renderNoResultPopout = function renderNoResultPopout(value) {
    return React__default.createElement("div", {
      className: "py-2 grow flex justify-center"
    }, renderNoResultContent == null ? void 0 : renderNoResultContent(value));
  };
  // Render
  var popout = React__default.createElement("div", {
    className: classNames("flex items-center justify-center", {
      "flex-col-reverse": side === UIPopoverSide.top,
      "flex-row": side === UIPopoverSide.right,
      "flex-col": side === UIPopoverSide.bottom,
      "flex-row-reverse": side === UIPopoverSide.left
    })
  }, React__default.createElement(UIContainer$1, {
    className: classNames("rounded-lg py-2 overflow-y-auto overflow-x-hidden max-h-96", className),
    raised: true,
    border: true,
    dataCy: popoutDataCy,
    style: {
      minWidth: minPopoutWidth,
      maxHeight: popoutMaxHeight
    }
  }, loading && React__default.createElement("div", {
    className: "flex py-8 items-center justify-center",
    "data-cy": "ui-dropdown-popout-container"
  }, React__default.createElement(UILoadingIndicator$1, {
    iconColor: UIColor.indigo1
  })), !loading && items.map(function (item, index) {
    var selected = selectedItems.some(function (selectedItem) {
      return selectedItem.title === item.title;
    });
    return React__default.createElement(UIMultiselectDropdownPopoutRow, {
      key: index,
      item: item,
      parentOpen: open,
      onClick: function onClick() {
        return _onClick == null ? void 0 : _onClick(index);
      },
      selected: selected,
      dataCy: "ui-multi-select-row-" + item.title
    });
  }), (forceNoResultPopup || Boolean(searchText) && !items.length) && renderNoResultPopout(searchText || "")));
  return React__default.createElement(UIPopover, {
    side: side,
    align: align,
    content: popout,
    open: open
  }, trigger);
};
var UIMultiselectDropdownTrigger = function UIMultiselectDropdownTrigger(props) {
  var _classNames;
  var active = props.active,
    block = props.block,
    className = props.className,
    danger = props.danger,
    dangerText = props.dangerText,
    disabled = props.disabled,
    onClick = props.onClick,
    _props$placeholder = props.placeholder,
    placeholder = _props$placeholder === void 0 ? "Select" : _props$placeholder,
    placeholderIconType = props.placeholderIconType,
    _props$selectedItems = props.selectedItems,
    selectedItems = _props$selectedItems === void 0 ? [] : _props$selectedItems,
    triggerRef = props.triggerRef;
  var noSelectedItem = !selectedItems.length;
  var text = noSelectedItem ? "- " + placeholder + " -" : selectedItems.length + " Selected -";
  var icon = noSelectedItem ? placeholderIconType : null;
  return React__default.createElement("div", {
    className: classNames(
    // Common
    "flex flex-row justify-between border rounded-lg transition whitespace-nowrap pl-4 pr-3 py-2 min-w-55 text-gray-2 dark:text-gray-2-dark", (_classNames = {
      "w-max": !block,
      "w-full": block,
      "bg-white border-gray-3-light hover:border-gray-1 ring-indigo-3 dark:bg-gray-5-dark dark:border-gray-3-dark dark:hover:border-gray-5-dark dark:ring-indigo-3-dark cursor-pointer": !disabled,
      "text-gray-3-light border-gray-4-light bg-gray-6-light dark:text-gray-4-dark dark:border-gray-4-dark dark:bg-gray-5-dark cursor-not-allowed": disabled,
      "ring-4 border-indigo-1 dark:border-indigo-1 hover:border-indigo-1 dark:hover:border-indigo-1": active,
      // Danger
      "border-red-1 hover:border-red-3-dark ring-red-3 dark:border-red-2-dark dark:hover:border-red-1 dark:ring-red-3-dark": danger
    }, _classNames[className || ""] = Boolean(className), _classNames)),
    onClick: disabled ? undefined : onClick,
    ref: triggerRef
  }, React__default.createElement("div", {
    className: "flex flex-row items-center"
  }, icon && React__default.createElement(UIIcon$1, {
    iconType: icon,
    iconColor: UIColor.gray2,
    className: "mr-3"
  }), React__default.createElement(UIText$1, {
    color: UIColor.inherit
  }, text)), React__default.createElement("div", {
    className: "flex flex-row items-center"
  }, danger && React__default.createElement(UITooltip$1, {
    title: dangerText,
    disabled: !dangerText
  }, React__default.createElement(UIIcon$1, {
    iconType: UIIconType.error,
    iconColor: UIColor.red1,
    className: "mr-2"
  })), React__default.createElement(UIIcon$1, {
    iconType: UIIconType.sort,
    iconColor: UIColor.grayLight2
  })));
};
var UIMultiselectDropdown = function UIMultiselectDropdown(props) {
  var dropdownPopoutClassName = props.dropdownPopoutClassName,
    items = props.items,
    onClick = props.onClick,
    side = props.side,
    align = props.align,
    block = props.block,
    dropdownTriggerClassName = props.dropdownTriggerClassName,
    danger = props.danger,
    dangerText = props.dangerText,
    disabled = props.disabled,
    placeholder = props.placeholder,
    useSearch = props.useSearch,
    placeholderIconType = props.placeholderIconType,
    showNoResultPopout = props.showNoResultPopout,
    renderNoResultContent = props.renderNoResultContent,
    loading = props.loading,
    _props$inputValue = props.inputValue,
    inputValue = _props$inputValue === void 0 ? "" : _props$inputValue,
    _props$selectedItems = props.selectedItems,
    selectedItems = _props$selectedItems === void 0 ? [] : _props$selectedItems,
    onInputValueChange = props.onInputValueChange,
    onInputBlur = props.onInputBlur,
    inputRef = props.inputRef,
    inputDataCy = props.inputDataCy,
    inputName = props.inputName,
    onClear = props.onClear,
    onClickOutside = props.onClickOutside,
    _props$enableLocalFil = props.enableLocalFiltering,
    enableLocalFiltering = _props$enableLocalFil === void 0 ? true : _props$enableLocalFil,
    popoutDataCy = props.popoutDataCy;
  var _useMeasure = useMeasure(),
    bounds = _useMeasure[0],
    measureRef = _useMeasure[1];
  var triggerRef = React__default.useRef();
  var _React$useState = React__default.useState(false),
    open = _React$useState[0],
    setOpen = _React$useState[1];
  var openDropdown = React__default.useCallback(function () {
    setOpen(true);
  }, []);
  var closeDropdown = React__default.useCallback(function () {
    onClickOutside && onClickOutside();
    setOpen(false);
  }, [onClickOutside]);
  var _React$useState2 = React__default.useState(""),
    search = _React$useState2[0],
    setSearch = _React$useState2[1];
  var onSearchInputChange = function onSearchInputChange(event) {
    onInputValueChange && onInputValueChange(event);
    setSearch(event.target.value);
  };
  var filteredItems = items || [];
  if (enableLocalFiltering) filteredItems = items ? items.filter(function (element) {
    return element.title.toLowerCase().includes(search.toLowerCase());
  }) : [];
  var selectItems = function selectItems(index) {
    if (!items) return;
    var itemsIndex = items.findIndex(function (element) {
      return element.title === filteredItems[index].title;
    });
    onClick == null ? void 0 : onClick(itemsIndex);
  };
  useEffect(function () {
    setSearch(inputValue);
  }, [inputValue]);
  var trigger = useSearch ? React__default.createElement(UIInput, {
    "data-cy": inputDataCy,
    danger: danger,
    iconType: UIIconType.search,
    value: search,
    placeholder: placeholder,
    onFocus: openDropdown,
    onClear: onClear,
    disabled: disabled,
    dangerText: dangerText,
    className: dropdownTriggerClassName,
    name: inputName,
    onChange: onSearchInputChange,
    onBlur: onInputBlur,
    inputRef: inputRef ? mergeRefs([measureRef, triggerRef, inputRef]) : mergeRefs([measureRef, triggerRef])
  }) : React__default.createElement(UIMultiselectDropdownTrigger, {
    active: open,
    block: block,
    className: dropdownTriggerClassName,
    danger: danger,
    dangerText: dangerText,
    disabled: disabled,
    onClick: openDropdown,
    placeholder: placeholder,
    placeholderIconType: placeholderIconType,
    selectedItems: selectedItems,
    triggerRef: triggerRef
  });
  return React__default.createElement(UIMultiselectDropdownPopout, {
    popoutDataCy: popoutDataCy,
    className: dropdownPopoutClassName,
    closePopout: closeDropdown,
    items: filteredItems,
    onClick: selectItems,
    open: (loading || showNoResultPopout || (items == null ? void 0 : items.length) !== 0) && open,
    side: side,
    align: align,
    trigger: trigger,
    searchRef: triggerRef,
    selectedItems: selectedItems,
    forceNoResultPopup: !(items != null && items.length) && search !== "" && showNoResultPopout,
    renderNoResultContent: renderNoResultContent,
    loading: loading,
    minPopoutWidth: bounds == null ? void 0 : bounds.width,
    searchText: search
  });
};
var UINestedRowDropdown = function UINestedRowDropdown(props) {
  var isCollapsed = props.isCollapsed,
    hovering = props.hovering,
    toggleNestedRows = props.toggleNestedRows;
  // const { darkMode } = useTheme();
  return React__default.createElement(UIContainer$1, {
    "data-cy": "ui-table-nested-dropdown",
    onClick: toggleNestedRows,
    className: "sticky left-0 flex items-center z-sticky bg-transparent group-hover:bg-gray-6-light dark:group-hover:bg-gray-4-dark cursor-pointer",
    variant: UIContainerVariant.inherit
  }, React__default.createElement(UIIcon$1, {
    iconType: isCollapsed ? UIIconType.sortRight : UIIconType.sortDown,
    iconColor: hovering ? UIColor.gray1 : UIColor.grayLight2
  }));
};
var PRESET_PAGE_SIZES = [1, 5, 25, 50, 75, 100, 300];
var UIPagination = function UIPagination(props) {
  var pageSize = props.pageSize,
    _props$minPageSize = props.minPageSize,
    minPageSize = _props$minPageSize === void 0 ? 25 : _props$minPageSize,
    _props$maxPageSize = props.maxPageSize,
    maxPageSize = _props$maxPageSize === void 0 ? 300 : _props$maxPageSize,
    _props$offset = props.offset,
    offset = _props$offset === void 0 ? 0 : _props$offset,
    totalRows = props.totalRows,
    onPageSizeChange = props.onPageSizeChange,
    onOffsetChange = props.onOffsetChange,
    _props$tableId = props.tableId,
    tableId = _props$tableId === void 0 ? "" : _props$tableId,
    _props$paginationLabe = props.paginationLabel,
    paginationLabel = _props$paginationLabe === void 0 ? offset + 1 + "-" + Math.min(offset + pageSize, totalRows) + " of " + totalRows + " results" : _props$paginationLabe;
  var pageSizeItems = useMemo(function () {
    return PRESET_PAGE_SIZES.filter(function (size) {
      return size <= maxPageSize && size >= minPageSize;
    }).map(function (size) {
      return {
        title: "Results per page: " + size,
        value: size,
        id: tableId + "-pagination-dropdown-popout-row-size-" + size
      };
    });
  }, []);
  var _useState = useState(pageSizeItems.findIndex(function (item) {
      return item.value === pageSize;
    }) || 0),
    selectedPageSizeIndex = _useState[0],
    setSelectedPageSizeIndex = _useState[1];
  var totalNumberOfPages = Math.ceil(totalRows / pageSize);
  var _useState2 = useState(1),
    internalPageNumber = _useState2[0],
    setInternalPageNumber = _useState2[1];
  useEffect(function () {
    setInternalPageNumber(Math.ceil(offset / pageSize + 1));
  }, [offset, pageSize]);
  var onPageSizeDropdownChange = function onPageSizeDropdownChange(index) {
    setSelectedPageSizeIndex(index);
    onPageSizeChange == null ? void 0 : onPageSizeChange(pageSizeItems[index].value);
    onOffsetChange == null ? void 0 : onOffsetChange(0);
  };
  var onPageNumberInputChange = function onPageNumberInputChange(e) {
    var num = Number(e.target.value);
    if (!Number.isInteger(num)) return;
    setInternalPageNumber(num);
  };
  var onPageNumberChange = function onPageNumberChange(pageNum) {
    if (!Number.isInteger(pageNum)) return;
    setInternalPageNumber(pageNum);
    onSubmitNewPageNumber(pageNum);
  };
  var onSubmitNewPageNumber = function onSubmitNewPageNumber(pageNum) {
    var newPage = pageNum || internalPageNumber;
    if (newPage <= totalNumberOfPages && newPage >= 1) {
      onOffsetChange == null ? void 0 : onOffsetChange((newPage - 1) * pageSize);
    } else {
      setInternalPageNumber(totalNumberOfPages);
      onOffsetChange == null ? void 0 : onOffsetChange((totalNumberOfPages - 1) * pageSize);
    }
  };
  var onKeyDown = function onKeyDown(e) {
    if (e.key === "Enter") {
      onSubmitNewPageNumber();
      e.target.blur();
    }
  };
  return React__default.createElement("div", {
    className: "flex flex-nowrap items-center justify-end space-x-4",
    "data-cy": tableId + "-Pagination"
  }, React__default.createElement(UIText$1, {
    className: "whitespace-nowrap",
    typography: UITypography.body,
    color: UIColor.grayLight2,
    "data-cy": tableId + "-Pagination-Label"
  }, paginationLabel), onPageSizeChange && React__default.createElement(UIDropdown, {
    dropdownTriggerClassName: "min-w-55",
    dropdownPopoutClassName: "min-w-55",
    side: UIPopoverSide.top,
    items: pageSizeItems,
    selectedItem: pageSizeItems[selectedPageSizeIndex],
    onClick: onPageSizeDropdownChange,
    triggerDataCy: tableId + "-Pagination-PageSizeDropdown"
  }), React__default.createElement(UIText$1, {
    typography: UITypography.body,
    color: UIColor.grayLight2
  }, "Page"), React__default.createElement("div", {
    className: "w-20"
  }, React__default.createElement(UIInput, {
    inputClassName: "text-center px-2",
    value: internalPageNumber,
    onChange: onPageNumberInputChange,
    onKeyDown: onKeyDown,
    onBlur: function onBlur() {
      return onSubmitNewPageNumber();
    },
    "data-cy": tableId + "-Pagination-PageNumberInput"
  })), React__default.createElement(UIText$1, {
    className: "whitespace-nowrap",
    typography: UITypography.body,
    color: UIColor.grayLight2,
    "data-cy": tableId + "-Pagination-TotalPages"
  }, "of " + totalNumberOfPages), React__default.createElement(UIButton$1, {
    className: "rotate-180 transform",
    variant: UIButtonVariant.quaternary,
    iconType: UIIconType.chevron,
    onClick: function onClick() {
      return onPageNumberChange(internalPageNumber - 1);
    },
    disabled: internalPageNumber <= 1,
    "data-cy": tableId + "-Pagination-PreviousPage"
  }), React__default.createElement(UIButton$1, {
    variant: UIButtonVariant.quaternary,
    iconType: UIIconType.chevron,
    onClick: function onClick() {
      return onPageNumberChange(internalPageNumber + 1);
    },
    disabled: internalPageNumber >= totalNumberOfPages,
    "data-cy": tableId + "-Pagination-NextPage"
  }));
};
var RADIAN = Math.PI / 180;
var MIN_CUTOFF = 8;
var LINE_OFFSET = 4;
var LINE_LENGTH = LINE_OFFSET + 24;
var TEXT_OFFSET = LINE_LENGTH + 16;
var polarToCartesian = function polarToCartesian(cx, cy, radius, angle) {
  return {
    x: cx + Math.cos(-RADIAN * angle) * radius,
    y: cy + Math.sin(-RADIAN * angle) * radius
  };
};
/**
 * UIPieChartLabel is rendered inside of UIPieChart which is an SVG.
 * We need to use only SVG elements in this component
 */
var UIPieChartLabel = function UIPieChartLabel(props) {
  var cx = props.cx,
    cy = props.cy,
    midAngle = props.midAngle,
    innerRadius = props.innerRadius,
    outerRadius = props.outerRadius,
    percent = props.percent,
    color = props.color;
  /** Radius of the PieChart */
  var radius = (Number(innerRadius) + Number(outerRadius)) / 2;
  var percentAsWhole = Number((percent * 100).toFixed(0));
  if (percentAsWhole > MIN_CUTOFF) {
    var _polarToCartesian = polarToCartesian(Number(cx), Number(cy), radius, midAngle),
      centerX = _polarToCartesian.x,
      centerY = _polarToCartesian.y;
    return React__default.createElement("text", {
      x: centerX,
      y: centerY,
      fill: "white",
      className: "text-base font-semibold",
      textAnchor: "middle",
      dominantBaseline: "central"
    }, percentAsWhole + "%");
  }
  var _polarToCartesian2 = polarToCartesian(Number(cx), Number(cy), Number(outerRadius) + LINE_OFFSET, midAngle),
    startOuterX = _polarToCartesian2.x,
    startOuterY = _polarToCartesian2.y;
  var _polarToCartesian3 = polarToCartesian(Number(cx), Number(cy), Number(outerRadius) + LINE_LENGTH, midAngle),
    endOuterX = _polarToCartesian3.x,
    endOuterY = _polarToCartesian3.y;
  var _polarToCartesian4 = polarToCartesian(Number(cx), Number(cy), Number(outerRadius) + TEXT_OFFSET, midAngle),
    textX = _polarToCartesian4.x,
    textY = _polarToCartesian4.y;
  // TODO: on hover show tooltip
  return React__default.createElement("g", null, React__default.createElement("path", {
    cx: cx,
    cy: cy,
    fill: "none",
    stroke: color,
    color: color,
    strokeWidth: "2",
    type: "linear",
    d: "M" + startOuterX + "," + startOuterY + "L" + endOuterX + "," + endOuterY
  }), React__default.createElement("text", {
    cx: cx,
    cy: cy,
    fill: color,
    color: color,
    stroke: "none",
    strokeWidth: "2",
    x: textX,
    y: textY,
    className: "text-base font-semibold",
    textAnchor: "middle",
    alignmentBaseline: "middle",
    dominantBaseline: "central"
  }, React__default.createElement("tspan", {
    x: textX,
    dy: "0em"
  }, percentAsWhole + "%")));
};
var UIPieChartLegend = function UIPieChartLegend(_ref) {
  var payload = _ref.payload;
  return React__default.createElement("div", {
    className: "flex flex-row flex-wrap justify-center gap-4"
  }, payload == null ? void 0 : payload.map(function (entry) {
    return React__default.createElement("div", {
      key: entry.key,
      className: "flex flex-row items-center space-x-2"
    }, React__default.createElement("div", {
      className: "h-3 w-3 rounded",
      style: {
        backgroundColor: entry.color
      }
    }), React__default.createElement(UIText$1, {
      typography: UITypography.label,
      color: UIColor.grayLight2
    }, entry.value));
  }));
};
var UIPieChartTooltip = function UIPieChartTooltip(_ref) {
  var payload = _ref.payload,
    _ref$valueFormatter = _ref.valueFormatter,
    valueFormatter = _ref$valueFormatter === void 0 ? function (value) {
      return "" + value;
    } : _ref$valueFormatter;
  return React__default.createElement(UIContainer$1, {
    className: "p-3",
    rounded: true,
    border: true,
    raised: true
  }, payload == null ? void 0 : payload.map(function (entry) {
    return React__default.createElement("div", {
      key: entry.payload.key,
      className: "border-l-4 pl-3",
      style: {
        borderColor: entry.payload.color
      }
    }, React__default.createElement(UIText$1, {
      typography: UITypography.label,
      color: UIColor.grayLight2
    }, entry.name), React__default.createElement(UIText$1, {
      typography: UITypography.label2
    }, valueFormatter(entry.value)));
  }));
};
var UIPieChart = function UIPieChart(_ref) {
  var data = _ref.data,
    valueFormatter = _ref.valueFormatter,
    _ref$legend = _ref.legend,
    legend = _ref$legend === void 0 ? true : _ref$legend,
    _ref$tooltip = _ref.tooltip,
    tooltip = _ref$tooltip === void 0 ? true : _ref$tooltip;
  var _React$useState = React__default.useState(),
    activeSliceIndex = _React$useState[0],
    setActiveSliceIndex = _React$useState[1];
  return React__default.createElement(ResponsiveContainer, {
    width: "100%",
    height: "100%"
  }, React__default.createElement(PieChart, null, React__default.createElement(Pie, {
    isAnimationActive: false,
    data: data,
    cx: "50%",
    cy: "50%",
    labelLine: false,
    label: function label(props) {
      return React__default.createElement(UIPieChartLabel, Object.assign({}, props));
    },
    innerRadius: 61,
    outerRadius: 125,
    dataKey: "value",
    onMouseEnter: function onMouseEnter(_, index) {
      return setActiveSliceIndex(index);
    },
    onMouseLeave: function onMouseLeave() {
      return setActiveSliceIndex(undefined);
    }
  }, data.map(function (entry, index) {
    return React__default.createElement(Cell, {
      key: entry.key,
      fill: entry.color,
      fillOpacity: !tooltip || activeSliceIndex === index ? 1 : 0.75,
      strokeWidth: 2
    });
  })), tooltip && React__default.createElement(Tooltip, {
    content: function content(props) {
      return React__default.createElement(UIPieChartTooltip, Object.assign({}, props, {
        valueFormatter: valueFormatter
      }));
    }
  }), legend && React__default.createElement(Legend, {
    align: "center",
    content: function content(_ref2) {
      var payload = _ref2.payload;
      return React__default.createElement(UIPieChartLegend, {
        payload: payload
      });
    }
  })));
};
var _excluded$c = ["text", "textColor", "textSize", "selected", "isStatic", "onClick", "onClear", "containerClassName", "textClassName"];
var UIPill = function UIPill(props) {
  var text = props.text,
    textColor = props.textColor,
    textSize = props.textSize,
    selected = props.selected,
    isStatic = props.isStatic,
    onClick = props.onClick,
    onClear = props.onClear,
    containerClassName = props.containerClassName,
    textClassName = props.textClassName,
    rest = _objectWithoutPropertiesLoose(props, _excluded$c);
  return React__default.createElement("div", Object.assign({
    className: classNames("group rounded-full px-2 py-1 bg-gray-5-light dark:bg-gray-4-dark ", containerClassName, {
      "hover:bg-gray-4-light dark:hover:bg-gray-3-dark": !selected && !isStatic,
      "bg-indigo-3-light": selected,
      "cursor-pointer": onClick && !isStatic
    }),
    style: {
      maxWidth: "fit-content"
    },
    onClick: onClick
  }, rest), React__default.createElement("div", {
    className: "flex items-center space-x-2"
  }, React__default.createElement(UIText$1, {
    typography: textSize ? textSize : UITypography.label,
    color: textColor ? textColor : selected ? UIColor.indigo1 : UIColor.gray2,
    className: textClassName,
    "data-cy": "ui-pill-text"
  }, text), onClear && React__default.createElement("div", {
    className: "flex items-center",
    onClick: onClear,
    "data-cy": "ui-pill-close-icon"
  }, React__default.createElement(UIIcon$1, {
    iconSize: "h-3 w-3",
    iconType: UIIconType.clear,
    iconColor: UIColor.gray1,
    className: "self-center cursor-pointer opacity-60 group-hover:opacity-90 group-active:opacity-100"
  }))));
};
var UIRadioButton = function UIRadioButton(props) {
  var checked = props.checked,
    className = props.className,
    disabled = props.disabled,
    onChange = props.onChange;
  var checkedIconType = React__default.useMemo(function () {
    if (checked) {
      return UIIconType.radioSelected;
    }
    return UIIconType.radioUnselected;
  }, [checked, disabled]);
  return React__default.createElement(UIButton$1, {
    variant: checked ? UIButtonVariant.tertiary : UIButtonVariant.quaternary,
    onClick: function onClick() {
      return onChange == null ? void 0 : onChange(!checked);
    },
    iconType: checkedIconType,
    disabled: disabled,
    className: classNames("border-none", className)
  });
};
var UIRangeInput = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(UIRangeInput, _React$Component);
  function UIRangeInput() {
    var _this;
    _this = _React$Component.apply(this, arguments) || this;
    _this.handleChange = function (value) {
      var onChange = _this.props.onChange;
      onChange == null ? void 0 : onChange(value);
    };
    // Helper function to display range steps
    // Creates an object of all marks between the min and max provided for the range slider
    _this.handleRangeSteps = function () {
      var showMarks = _this.props.showMarks;
      var rangeSteps = {};
      if (showMarks) {
        var _this$props = _this.props,
          min = _this$props.min,
          max = _this$props.max;
        for (var i = min; i <= max; i++) {
          rangeSteps[i] = "" + i;
        }
      }
      return rangeSteps;
    };
    return _this;
  }
  var _proto = UIRangeInput.prototype;
  _proto.render = function render() {
    var _this$props2 = this.props,
      className = _this$props2.className,
      containerClassName = _this$props2.containerClassName,
      trackStyle = _this$props2.trackStyle,
      railStyle = _this$props2.railStyle,
      handleStyle = _this$props2.handleStyle,
      dotStyle = _this$props2.dotStyle,
      activeDotStyle = _this$props2.activeDotStyle,
      value = _this$props2.value,
      min = _this$props2.min,
      max = _this$props2.max,
      defaultValue = _this$props2.defaultValue,
      step = _this$props2.step,
      allowCross = _this$props2.allowCross,
      showMarks = _this$props2.showMarks,
      dots = _this$props2.dots,
      tabIndex = _this$props2.tabIndex,
      disabled = _this$props2.disabled,
      draggableTrack = _this$props2.draggableTrack,
      count = _this$props2.count,
      pushable = _this$props2.pushable;
    return React__default.createElement("div", {
      className: classNames(containerClassName)
    }, React__default.createElement(Slider, {
      range: true,
      className: className,
      trackStyle: trackStyle,
      railStyle: railStyle,
      handleStyle: handleStyle,
      dotStyle: showMarks && !dots ? {
        display: "none"
      } : dotStyle,
      activeDotStyle: activeDotStyle,
      value: value,
      onChange: this.handleChange,
      min: min,
      max: max,
      defaultValue: defaultValue,
      step: step,
      allowCross: allowCross,
      marks: this.handleRangeSteps(),
      dots: dots,
      tabIndex: tabIndex,
      disabled: disabled,
      draggableTrack: draggableTrack,
      count: count,
      pushable: pushable
    }));
  };
  return UIRangeInput;
}(React__default.Component);
UIRangeInput.defaultProps = {
  step: 1,
  dots: false,
  allowCross: false,
  draggableTrack: false,
  disabled: false,
  pushable: false,
  showMarks: true,
  count: 1,
  // Increase handleStyle and trackStyles based on number of handles needed
  handleStyle: [{
    backgroundColor: "#FFFFFF",
    border: "solid 1px #6366F1",
    width: "13px",
    height: "13px"
  }, {
    backgroundColor: "#FFFFFF",
    border: "solid 1px #6366F1",
    width: "13px",
    height: "13px"
  }],
  trackStyle: [{
    background: "#6366F1",
    height: "4px",
    borderRadius: "4px"
  }, {
    background: "#6366F1",
    height: "4px",
    borderRadius: "4px"
  }],
  railStyle: {
    background: "#F1F5F9",
    height: "4px",
    borderRadius: "4px"
  }
};
var _excluded$d = ["selected", "disabled", "onClick", "className", "children"];
var UISelectableContainer = function UISelectableContainer(props) {
  var selected = props.selected,
    disabled = props.disabled,
    onClick = props.onClick,
    className = props.className,
    children = props.children,
    rest = _objectWithoutPropertiesLoose(props, _excluded$d);
  /**
   * Callback when the container is clicked
   */
  var onContainerClick = React__default.useCallback(function () {
    if (disabled) {
      return;
    }
    onClick == null ? void 0 : onClick(!selected);
  }, [onClick, selected, disabled]);
  return React__default.createElement("div", Object.assign({
    onClick: onContainerClick,
    className: classNames("rounded-lg border transition-colors bg-white dark:bg-gray-5-dark active:bg-gray-6-light dark:active:bg-gray-4-dark", {
      "border-gray-3-light dark:border-gray-4-dark hover:border-gray-2-light dark:hover:border-gray-3-dark active:border-gray-1 dark:active:border-gray-2-dark": !selected && !disabled,
      "ring-2 ring-indigo-2-light dark:ring-indigo-2-dark  border-indigo-1 hover:border-indigo-2-dark active:border-indigo-3-dark dark:hover:border-indigo-2-light dark:active:border-indigo-3-light": selected,
      "border-gray-4-light dark:border-gray-4-dark": disabled,
      "cursor-pointer": onClick && !disabled,
      "cursor-not-allowed": disabled
    }, className)
  }, rest), children);
};
var UISelectablePill = function UISelectablePill(props) {
  var selected = props.selected,
    text = props.text,
    disabled = props.disabled,
    onClick = props.onClick,
    _props$iconType = props.iconType,
    iconType = _props$iconType === void 0 ? selected ? UIIconType.checkboxSelected : UIIconType.add : _props$iconType,
    _props$iconColor = props.iconColor,
    iconColor = _props$iconColor === void 0 ? selected ? UIColor.indigo1 : UIColor.grayLight2 : _props$iconColor,
    _props$textColor = props.textColor,
    textColor = _props$textColor === void 0 ? selected ? UIColor.indigo1 : UIColor.grayLight2 : _props$textColor;
  var onPillSelect = function onPillSelect(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      onClick == null ? void 0 : onClick(!selected);
    }
  };
  return React__default.createElement("div", {
    onClick: onPillSelect,
    className: classNames("inline-flex flex-row items-center justify-center h-[30px] rounded-full py-1 px-2 cursor-pointer", selected ? "bg-indigo-3-light" : "bg-gray-5-light")
  }, React__default.createElement(UIIcon$1, {
    iconType: iconType,
    iconColor: iconColor,
    className: "mr-1.5"
  }), React__default.createElement(UIText$1, {
    typography: UITypography.label2,
    color: textColor
  }, text));
};
var UISelector = function UISelector(props) {
  var tooltipTitle = props.tooltipTitle,
    onClick = props.onClick,
    selected = props.selected,
    disabled = props.disabled,
    className = props.className,
    children = props.children;
  var content = React__default.createElement("div", {
    className: classNames("w-12 h-12 rounded-xl flex justify-center items-center border-2 transition", "bg-gray-4-dark light:bg-white", {
      "ring-2 ring-indigo-1 hover:ring-indigo-2 focus:ring-indigo-3": selected,
      "border-gray-3-dark hover:ring-indigo-1 focus:ring-indigo-2 light:border-gray-3-light": !selected,
      "cursor-pointer": !disabled && onClick,
      "hover:ring-2 hover:border-gray-5-dark hover:bg-gray-3-dark light:hover:border-gray-5-light": !disabled
    }, className),
    onClick: disabled ? undefined : onClick
  }, children);
  if (!tooltipTitle) {
    return content;
  }
  return React__default.createElement(UITooltip$1, {
    title: tooltipTitle,
    side: UIPopoverSide.right,
    disabled: disabled
  }, content);
};
var UISidebar = function UISidebar(_ref) {
  var children = _ref.children,
    logo = _ref.logo,
    collapsed = _ref.collapsed,
    onToggle = _ref.onToggle,
    footer = _ref.footer;
  var _useState = useState(false),
    manualToggle = _useState[0],
    setManualToggle = _useState[1];
  var _useState2 = useState(false),
    contentScroll = _useState2[0],
    setContentScroll = _useState2[1];
  var contentRef = useRef(null);
  useEffect(function () {
    function handleContentScroll() {
      if (contentRef.current) {
        setContentScroll(contentRef.current.clientHeight < contentRef.current.scrollHeight);
      }
    }
    handleContentScroll();
    window.addEventListener("resize", handleContentScroll);
    return function () {
      return window.removeEventListener("resize", handleContentScroll);
    };
  }, [collapsed, setContentScroll]);
  useEffect(function () {
    function handleAutomaticToggle() {
      if (manualToggle) {
        return;
      }
      var TRIGGER_WIDTH = 955 + 242;
      if (window.innerWidth < TRIGGER_WIDTH && !collapsed || window.innerWidth >= TRIGGER_WIDTH && collapsed) {
        onToggle();
      }
    }
    window.addEventListener("resize", handleAutomaticToggle);
    return function () {
      return window.removeEventListener("resize", handleAutomaticToggle);
    };
  }, [onToggle, collapsed, manualToggle]);
  var handleToggle = useCallback(function () {
    setManualToggle(true);
    onToggle();
  }, [setManualToggle, onToggle]);
  return React__default.createElement("div", {
    className: "sidebar flex flex-col border-r border-gray-4-dark bg-gray-5-dark light:border-gray-4-light light:bg-white hover:cursor-pointer",
    style: {
      maxWidth: collapsed ? "48px" : "242px",
      minWidth: collapsed ? "48px" : "242px"
    }
  }, React__default.createElement("div", {
    className: classNames("flex items-center py-6 px-4", {
      "border-none": !contentScroll,
      "light:border-gray-4-light border-b border-gray-4-dark": contentScroll
    })
  }, React__default.createElement("div", {
    className: "group mr-4 flex cursor-pointer",
    onClick: handleToggle,
    "data-cy": "ui-sidebar-toggle-icon"
  }, React__default.createElement(UIIcon$1, {
    className: classNames("text-gray-2-light transition-colors hover:text-gray-3-light active:text-white light:hover:text-gray-1 light:active:text-indigo-1", {
      "rotate-180": !collapsed
    }),
    iconType: UIIconType.collapse,
    iconColor: UIColor.grayLight2
  })), !collapsed && logo), React__default.createElement("div", {
    ref: contentRef,
    className: "sidebar__content flex flex-grow flex-col overflow-x-hidden"
  }, children), footer && React__default.createElement("div", {
    className: classNames("flex flex-col pb-4", {
      "border-none": !contentScroll,
      "border-t border-gray-4-dark light:border-gray-4-light": contentScroll
    })
  }, footer));
};
var _excluded$e = ["title", "subtitle", "iconType", "accessory", "chevron", "loading", "popup", "active", "showTitleWhenCollapsed", "collapsed"];
var UISidebarItem = function UISidebarItem(_ref) {
  var title = _ref.title,
    subtitle = _ref.subtitle,
    iconType = _ref.iconType,
    accessory = _ref.accessory,
    chevron = _ref.chevron,
    loading = _ref.loading,
    popup = _ref.popup,
    active = _ref.active,
    showTitleWhenCollapsed = _ref.showTitleWhenCollapsed,
    collapsed = _ref.collapsed,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$e);
  return React__default.createElement(UITooltip$1, {
    disabled: !collapsed,
    side: UIPopoverSide.right,
    title: title,
    triggerClassName: "block w-full"
  }, React__default.createElement("div", Object.assign({
    className: classNames("flex w-full items-center py-3 px-4 transition-colors", {
      "pointer-events-none": loading,
      "bg-gray-3-dark text-white light:bg-indigo-3-light light:text-indigo-1": active,
      "bg-gray-5-dark text-gray-2-light hover:bg-gray-4-dark hover:text-gray-3-light light:bg-white light:text-gray-1 light:hover:bg-gray-6-light light:hover:text-gray-3-dark": !active
    })
  }, rest), iconType && React__default.createElement(UIIcon$1, {
    className: classNames("min-w-[1rem] min-h-[1rem] max-w-[1rem] max-h-[1rem]", {
      "mr-4": !(collapsed && !popup),
      "h-4 w-4rounded-3xl bg-gray-4-dark text-gray-2-light light:bg-gray-4-light": loading
    }),
    iconType: iconType
  }), (!collapsed || showTitleWhenCollapsed) && React__default.createElement(React__default.Fragment, null, React__default.createElement("span", {
    className: "flex flex-1 flex-col"
  }, React__default.createElement("div", {
    className: classNames("text-base font-medium leading-5", {
      "h-4 w-32 rounded-full bg-gray-4-dark text-transparent light:bg-gray-4-light": loading
    })
  }, title), React__default.createElement("div", {
    className: classNames("text-sm font-medium leading-4", {
      "mt-2 h-2 w-16 rounded-full bg-gray-4-dark text-transparent light:bg-gray-4-light": loading,
      "text-gray-1 light:text-indigo-1": active,
      "text-gray-2-light": !active
    })
  }, subtitle)), accessory, chevron && !loading && React__default.createElement(UIIcon$1, {
    className: "origin-center -rotate-90 scale-75 transform",
    iconType: UIIconType.down
  }))));
};
var UISidebarSubheader = function UISidebarSubheader(_ref) {
  var children = _ref.children,
    collapsed = _ref.collapsed;
  if (collapsed) {
    return null;
  }
  return React__default.createElement("div", {
    className: "py-2 px-4 text-sm font-semibold text-gray-2-dark light:text-gray-2-light"
  }, children);
};
var UITab = function UITab(props) {
  var title = props.title,
    count = props.count,
    countTooltip = props.countTooltip,
    className = props.className,
    selected = props.selected,
    onTabSelected = props.onTabSelected,
    onTabHovered = props.onTabHovered,
    index = props.index;
  var tabCounter = React__default.useMemo(function () {
    return count ? React__default.createElement(UIBadge$1, {
      variant: UIBadgeVariant.primary,
      text: count.toString(),
      className: "ml-1.5"
    }) : null;
  }, [count]);
  return React__default.createElement(UIContainer$1, {
    className: classNames("flex flex-1 justify-center items-center cursor-pointer font-semibold", "h-14 hover:bg-gray-6 text-lg dark:hover:bg-gray-4-dark", {
      "text-indigo-1 border-b-2 !border-indigo-1 hover:text-indigo-2-dark active:text-indigo-3-dark dark:!border-indigo-1 dark:active:text-indigo-3-light dark:hover:text-indigo-2-light": selected,
      "text-gray-2 hover:text-gray-1 active:text-gray-2-dark dark:text-gray-2-dark dark:hover:text-gray-1 dark:active:text-gray-2-light": !selected
    }, className),
    onClick: onTabSelected,
    onMouseOver: onTabHovered,
    "data-cy": "UITab-" + index
  }, title, React__default.createElement(UITooltip$1, {
    side: UIPopoverSide.top,
    title: countTooltip,
    disabled: false
  }, tabCounter));
};
var UITab$1 = /*#__PURE__*/React__default.memo(UITab);
var UITabBar = function UITabBar(props) {
  var tabs = props.tabs,
    className = props.className,
    onTabChange = props.onTabChange,
    _onTabHovered = props.onTabHovered,
    selectedTabIndex = props.selectedTabIndex;
  return React__default.createElement(UIContainer$1, {
    border: true,
    className: classNames("flex rounded-t-lg overflow-hidden border-b-0", className)
  }, tabs.map(function (tab, index) {
    return React__default.createElement(UITab$1, {
      index: index,
      key: index,
      title: tab.title,
      selected: index === selectedTabIndex,
      onTabSelected: function onTabSelected() {
        return onTabChange(index);
      },
      onTabHovered: function onTabHovered() {
        return _onTabHovered == null ? void 0 : _onTabHovered(index);
      },
      count: tab.count,
      countTooltip: tab.countTooltip
    });
  }));
};
var TableSortDirection;
(function (TableSortDirection) {
  TableSortDirection["ascending"] = "asc";
  TableSortDirection["descending"] = "desc";
})(TableSortDirection || (TableSortDirection = {}));
var TableRowType;
(function (TableRowType) {
  // row is styled light gray
  TableRowType["infoRow"] = "infoRow";
  // to load additional rows
  TableRowType["loadRow"] = "loadRow";
})(TableRowType || (TableRowType = {}));
var nestedRowSelect;
(function (nestedRowSelect) {
  nestedRowSelect[nestedRowSelect["dont_select"] = 0] = "dont_select";
  nestedRowSelect[nestedRowSelect["select_on_expand"] = 1] = "select_on_expand";
  nestedRowSelect[nestedRowSelect["select_on_load_more"] = 2] = "select_on_load_more";
})(nestedRowSelect || (nestedRowSelect = {}));
var UITableRowContext = /*#__PURE__*/React__default.createContext({
  hovering: false,
  isCollapsed: false,
  toggleNestedRows: undefined
});
var _excluded$f = ["colspan", "children", "className", "style"];
var UITableCell = function UITableCell(props) {
  var _props$colspan = props.colspan,
    colspan = _props$colspan === void 0 ? 1 : _props$colspan,
    children = props.children,
    className = props.className,
    _props$style = props.style,
    style = _props$style === void 0 ? {} : _props$style,
    rest = _objectWithoutPropertiesLoose(props, _excluded$f);
  var widthAsPixel = colspan === "max" ? "100%" : isNaN(colspan) ? "auto" : 180 * colspan + "px";
  return React__default.createElement(UIContainer$1, Object.assign({
    className: classNames("flex items-center p-4", className),
    style: _extends({
      minWidth: widthAsPixel,
      width: widthAsPixel,
      maxWidth: widthAsPixel
    }, style),
    variant: UIContainerVariant.inherit
  }, rest), children);
};
var UITableDataLoadMoreRow = function UITableDataLoadMoreRow(props) {
  var _row$data, _row$metadata2;
  var context = props.context,
    onLoadMoreRows = props.onLoadMoreRows,
    row = props.row,
    hovering = props.hovering,
    onMouseEnter = props.onMouseEnter,
    onMouseLeave = props.onMouseLeave,
    setCounter = props.setCounter;
  var tableContext = React__default.useContext(context);
  return React__default.createElement("div", {
    onClick: function onClick(e) {
      var _row$metadata;
      e.stopPropagation();
      e.preventDefault();
      if ((_row$metadata = row.metadata) != null && _row$metadata.disabled) return;
      onLoadMoreRows == null ? void 0 : onLoadMoreRows(row);
      tableContext.onLoadMoreRows(row);
      setCounter == null ? void 0 : setCounter(nestedRowSelect.select_on_load_more);
    },
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    className: classNames("flex p-3 bg-gray-6-light hover:bg-gray-5 items-stretch flex-1 cursor-pointer", {
      "ml-24": row.isNested
    })
  }, React__default.createElement(UIIcon$1, {
    className: "mr-4",
    iconType: UIIconType.nested,
    iconColor: hovering ? UIColor.gray1 : UIColor.grayLight2
  }), React__default.createElement(UIText$1, {
    className: "cursor-pointer",
    color: hovering ? UIColor.gray1 : UIColor.grayLight2
  }, (_row$data = row.data) == null ? void 0 : _row$data.title), (_row$metadata2 = row.metadata) != null && _row$metadata2.loading ? React__default.createElement(UILoadingIndicator$1, {
    iconColor: UIColor.grayLight2,
    className: "ml-2"
  }) : null);
};
var _excluded$g = ["className"];
var UITableDataMoreMenuTrigger = function UITableDataMoreMenuTrigger(props) {
  var className = props.className,
    rest = _objectWithoutPropertiesLoose(props, _excluded$g);
  return React__default.createElement(UIButton$1, Object.assign({
    className: classNames("h-8 w-8 !rounded-full !p-0 shadow-raised dark:shadow-raised-dark", className),
    variant: UIButtonVariant.secondary,
    iconType: UIIconType.more
  }, rest));
};
var UITableDataMoreMenu = function UITableDataMoreMenu(props) {
  var rowDropdownItems = props.rowDropdownItems,
    onRowDropdownClose = props.onRowDropdownClose,
    onRowDropdownOpen = props.onRowDropdownOpen;
  var _React$useState = React__default.useState(false),
    isDropdownOpen = _React$useState[0],
    setIsDropdownOpen = _React$useState[1];
  // Refs
  var triggerRef = React__default.useRef();
  var popoutRef = React__default.useRef();
  // Setup Hook
  useOnClickOutside([popoutRef, triggerRef], function () {
    return onClosePopout();
  }, {
    isActive: isDropdownOpen
  });
  var onTriggerClick = React__default.useCallback(function (e) {
    e.preventDefault();
    e.stopPropagation();
    isDropdownOpen ? onRowDropdownClose == null ? void 0 : onRowDropdownClose() : onRowDropdownOpen == null ? void 0 : onRowDropdownOpen();
    setIsDropdownOpen(function (prev) {
      return !prev;
    });
  }, [isDropdownOpen]);
  var onClosePopout = function onClosePopout() {
    onRowDropdownClose == null ? void 0 : onRowDropdownClose();
    setIsDropdownOpen(false);
  };
  if (!(rowDropdownItems != null && rowDropdownItems.length)) {
    return null;
  }
  var trigger = React__default.createElement(UITableDataMoreMenuTrigger, {
    triggerRef: triggerRef,
    onClick: onTriggerClick
  });
  return React__default.createElement(UIDropdownPopout, {
    ref: popoutRef,
    trigger: trigger,
    items: rowDropdownItems,
    open: isDropdownOpen,
    // closePopout={onClosePopout}
    onClick: onClosePopout,
    align: UIPopoverAlign.end
  });
};
var _excluded$h = ["context", "onLoadMoreRows", "headers", "row", "rowDropdownItems", "multiselect", "multiselectConfirmationModal", "selected", "indeterminate", "hasNestedRows", "hasRowClickSelect", "isCollapsed", "shouldShowNestedRowIcon", "onSelect", "onRowClick", "onRowHover", "toggleNestedRows", "customRowMenuElement", "alwaysShowCustomRowMenuElement", "disableMultiselect", "alwaysShowExpand", "isChild", "setCounter"];
var UITableData = function UITableData(props) {
  var context = props.context,
    onLoadMoreRows = props.onLoadMoreRows,
    headers = props.headers,
    row = props.row,
    rowDropdownItems = props.rowDropdownItems,
    multiselect = props.multiselect,
    multiselectConfirmationModal = props.multiselectConfirmationModal,
    selected = props.selected,
    indeterminate = props.indeterminate,
    hasNestedRows = props.hasNestedRows,
    hasRowClickSelect = props.hasRowClickSelect,
    isCollapsed = props.isCollapsed,
    shouldShowNestedRowIcon = props.shouldShowNestedRowIcon,
    onSelect = props.onSelect,
    onRowClick = props.onRowClick,
    _props$onRowHover = props.onRowHover,
    onRowHover = _props$onRowHover === void 0 ? function () {} : _props$onRowHover,
    toggleNestedRows = props.toggleNestedRows,
    customRowMenuElement = props.customRowMenuElement,
    _props$alwaysShowCust = props.alwaysShowCustomRowMenuElement,
    alwaysShowCustomRowMenuElement = _props$alwaysShowCust === void 0 ? false : _props$alwaysShowCust,
    _props$disableMultise = props.disableMultiselect,
    disableMultiselect = _props$disableMultise === void 0 ? false : _props$disableMultise,
    alwaysShowExpand = props.alwaysShowExpand,
    isChild = props.isChild,
    setCounter = props.setCounter,
    rest = _objectWithoutPropertiesLoose(props, _excluded$h);
  var _React$useState = React__default.useState(false),
    hovering = _React$useState[0],
    setHovering = _React$useState[1];
  var _React$useState2 = React__default.useState(false),
    rowActive = _React$useState2[0],
    setRowActive = _React$useState2[1];
  var _React$useState3 = React__default.useState(false),
    showConfirmationModal = _React$useState3[0],
    setShowConfirmationModal = _React$useState3[1];
  /**
   * To update the selected state of the row on click anywhere in the UICheckbox container
   */
  var onRowSelectionChanged = function onRowSelectionChanged(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!disableMultiselect) {
      multiselectConfirmationModal ? function () {
        return setShowConfirmationModal(Boolean(selected));
      } : onChange == null ? void 0 : onChange(!selected);
    }
  };
  /**
   * Callback to UICheckbox to update the
   * selected state of the row
   */
  var onChange = React__default.useCallback(function (checked) {
    onSelect == null ? void 0 : onSelect(row, checked);
  }, [onSelect, row]);
  /**
   * Callback when the row is clicked
   */
  var onClick = React__default.useCallback(function () {
    if (hasRowClickSelect && !disableMultiselect) {
      !selected && multiselectConfirmationModal && !showConfirmationModal ? setShowConfirmationModal(true) : onChange == null ? void 0 : onChange(!selected);
    } else {
      onRowClick == null ? void 0 : onRowClick(row);
    }
  }, [onChange, onRowClick, row]);
  /**
   * Callback when the row is being hovered
   */
  var onMouseEnter = React__default.useCallback(function () {
    setHovering(true);
    onRowHover == null ? void 0 : onRowHover(row);
  }, []);
  /**
   * Callback when the row loses its hovered state
   */
  var onMouseLeave = React__default.useCallback(function () {
    setHovering(false);
    onRowHover == null ? void 0 : onRowHover(undefined);
  }, []);
  /**
   *  Function called when the row dropdown is closed
   */
  var onRowDropdownClose = function onRowDropdownClose() {
    setRowActive(false);
  };
  /**
   *  Function called when the row dropdown is active
   */
  var onRowDropdownOpen = function onRowDropdownOpen() {
    setRowActive(true);
  };
  var shouldShowMultiselect = multiselect && (row == null ? void 0 : row.selectable);
  var shouldLoadMoreRow = row.rowType === TableRowType.loadRow;
  var shouldAlwaysShowCustomRowMenuElement = typeof alwaysShowCustomRowMenuElement === "function" ? alwaysShowCustomRowMenuElement(row) : alwaysShowCustomRowMenuElement;
  return React__default.createElement(UITableRowContext.Provider, {
    value: {
      hovering: hovering,
      isCollapsed: isCollapsed,
      toggleNestedRows: toggleNestedRows
    }
  }, showConfirmationModal && multiselectConfirmationModal && multiselectConfirmationModal(function () {
    onChange == null ? void 0 : onChange(!selected);
    setShowConfirmationModal(false);
  }, function () {
    return setShowConfirmationModal(false);
  }), React__default.createElement("div", Object.assign({
    "data-cy": "ui-table-data-row-" + row.key,
    className: classNames("flex flex-nowrap items-stretch w-fit min-w-full border-b border-gray-4-light dark:border-gray-4-dark", "group hover:bg-gray-6-light dark:hover:bg-gray-4-dark", {
      "group": onRowClick || rowDropdownItems,
      "cursor-pointer hover:bg-gray-6-light dark:hover:bg-gray-4-dark": onRowClick || hasRowClickSelect,
      "bg-gray-6-light dark:bg-gray-4-dark": rowActive || selected || row.rowType === TableRowType.infoRow,
      "w-full bg-gray-6-light dark:bg-gray-4-dark": row.rowType === TableRowType.loadRow
    }),
    onClick: onClick,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave
  }, rest), shouldLoadMoreRow ? React__default.createElement(UITableDataLoadMoreRow, {
    context: context,
    onLoadMoreRows: onLoadMoreRows,
    setCounter: setCounter,
    row: row,
    hovering: hovering,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave
  }) : React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
    className: "flex items-stretch flex-1 group"
  }, headers.map(function (header, idx) {
    var _row$data;
    var isFirst = idx === 0;
    var cellMetadata = _extends({}, header.cellMetadata, {
      hovering: hovering
    });
    return React__default.createElement(UITableCell, {
      key: row.key + "-" + idx + "-" + header.key,
      colspan: header.colspan,
      className: classNames("group-hover:bg-gray-6-light dark:group-hover:bg-gray-4-dark", {
        "bg-white dark:bg-gray-5-dark": !rowActive && !selected,
        "sticky z-sticky left-0 pl-4": isFirst,
        "pl-0": (shouldShowMultiselect || hasNestedRows || row.rowType === TableRowType.infoRow) && isFirst,
        "bg-gray-6-light dark:bg-gray-4-dark": rowActive || selected,
        "bg-gray-6-light dark:bg-gray-4-dark text-gray-2-light font-semibold": row.rowType === TableRowType.infoRow
      })
    }, row.isNested && isFirst && React__default.createElement("div", {
      className: alwaysShowExpand ? "pl-[110px]" : shouldShowMultiselect ? "pl-20" : "pl-12"
    }), isFirst && (alwaysShowExpand && !isChild || hasNestedRows) && shouldShowNestedRowIcon && React__default.createElement("div", {
      className: "pl-4"
    }, React__default.createElement(UINestedRowDropdown, {
      hovering: hovering,
      isCollapsed: isCollapsed,
      toggleNestedRows: toggleNestedRows
    })), shouldShowMultiselect && isFirst && React__default.createElement(UIContainer$1, {
      onClick: onRowSelectionChanged,
      className: classNames("flex items-center px-4 z-sticky group-hover:bg-gray-6-light dark:group-hover:bg-gray-4-dark", {
        "bg-gray-6-light dark:bg-gray-4-dark": rowActive || selected
      }),
      variant: UIContainerVariant.inherit
    }, React__default.createElement(UICheckbox, {
      "data-cy": "ui-table-data-checkbox",
      checked: selected,
      indeterminate: indeterminate,
      onChange: !selected && multiselectConfirmationModal && !showConfirmationModal ? function (checked) {
        return setShowConfirmationModal(checked);
      } : onChange,
      disabled: disableMultiselect
    })), header.cell ? React__default.createElement(header.cell, {
      header: header,
      row: row,
      metadata: cellMetadata
    }) : React__default.createElement(UIText$1, {
      className: "break-all",
      typography: UITypography.body,
      color: UIColor.grayDark3
    }, (_row$data = row.data) == null ? void 0 : _row$data[header.key]));
  })), React__default.createElement("div", {
    className: classNames("sticky flex items-center justify-center right-4 z-sticky pointer-events-none group-hover:pointer-events-auto group-hover:visible", {
      invisible: !rowActive && !shouldAlwaysShowCustomRowMenuElement
    }, !customRowMenuElement && "w-8")
  }, !customRowMenuElement && React__default.createElement(UITableDataMoreMenu, {
    rowDropdownItems: rowDropdownItems,
    onRowDropdownClose: onRowDropdownClose,
    onRowDropdownOpen: onRowDropdownOpen
  }), customRowMenuElement == null ? void 0 : customRowMenuElement(row, {
    setRowActive: setRowActive
  })))));
};
var _excluded$i = ["row", "selectedRows", "renderRowMenuDropdownItems", "rowDropdownItems", "selected", "shouldDisableMultiselect", "onExpandRow", "isCollapsed", "onSelect", "hasRowClickSelect"];
var UITableDataWithNested = function UITableDataWithNested(props) {
  var _shouldDisableMultise, _row$nestedRows3;
  var row = props.row,
    selectedRows = props.selectedRows,
    renderRowMenuDropdownItems = props.renderRowMenuDropdownItems,
    rowDropdownItems = props.rowDropdownItems,
    selected = props.selected,
    shouldDisableMultiselect = props.shouldDisableMultiselect,
    onExpandRow = props.onExpandRow,
    _props$isCollapsed = props.isCollapsed,
    isCollapsed = _props$isCollapsed === void 0 ? true : _props$isCollapsed,
    _onSelect = props.onSelect,
    hasRowClickSelect = props.hasRowClickSelect,
    rest = _objectWithoutPropertiesLoose(props, _excluded$i);
  var _React$useState = React__default.useState(isCollapsed),
    collapsed = _React$useState[0],
    setCollapsed = _React$useState[1];
  // to keep track of whether nestedRows should be selected or not
  // in the case where children are added only on dropDown click
  // ie. Targets and Search List
  // 0 - don't select nested rows
  // 1 - select nested rows on initial expand
  // 2 - select nested rows on load more
  var _React$useState2 = React__default.useState(nestedRowSelect.dont_select),
    counter = _React$useState2[0],
    setCounter = _React$useState2[1];
  var toggleCollapse = function toggleCollapse(e) {
    e.preventDefault();
    e.stopPropagation();
    if (collapsed && onExpandRow) {
      onExpandRow(row);
      setCounter(nestedRowSelect.select_on_expand);
    }
    setCollapsed(function (prevState) {
      return !prevState;
    });
  };
  var indeterminate = React__default.useMemo(function () {
    var _row$nestedRows, _row$nestedRows$filte, _row$nestedRows2, _row$nestedRows2$filt;
    return ((_row$nestedRows = row.nestedRows) == null ? void 0 : (_row$nestedRows$filte = _row$nestedRows.filter(function (nestedRow) {
      return nestedRow.rowType !== TableRowType.loadRow;
    })) == null ? void 0 : _row$nestedRows$filte.some(function (nestedRow) {
      return selectedRows == null ? void 0 : selectedRows.has(nestedRow.key);
    })) && !((_row$nestedRows2 = row.nestedRows) != null && (_row$nestedRows2$filt = _row$nestedRows2.filter(function (nestedRow) {
      return nestedRow.rowType !== TableRowType.loadRow;
    })) != null && _row$nestedRows2$filt.every(function (nestedRow) {
      return selectedRows == null ? void 0 : selectedRows.has(nestedRow.key);
    }));
  }, [selectedRows, row]);
  var disableMultiselectParent = (_shouldDisableMultise = shouldDisableMultiselect == null ? void 0 : shouldDisableMultiselect(row)) != null ? _shouldDisableMultise : false;
  var parentRow = React__default.createElement(UITableData, Object.assign({
    hasNestedRows: true,
    toggleNestedRows: toggleCollapse,
    indeterminate: indeterminate,
    isCollapsed: collapsed,
    row: row,
    rowDropdownItems: rowDropdownItems,
    selected: selected || indeterminate,
    disableMultiselect: disableMultiselectParent,
    onSelect: _onSelect,
    hasRowClickSelect: hasRowClickSelect
  }, rest));
  if (collapsed) {
    return parentRow;
  }
  // if parent is selected and nested rows have not loaded yet
  // nested rows should be selected on load
  if (selected && row.nestedRows) {
    if (counter === nestedRowSelect.select_on_expand && !indeterminate) {
      // initial expand case
      row.nestedRows.forEach(function (nestedRow) {
        if (selectedRows && nestedRow.rowType !== TableRowType.loadRow) {
          selectedRows.set(nestedRow.key, nestedRow);
        }
      });
      setCounter(nestedRowSelect.dont_select);
    } else if (counter === nestedRowSelect.select_on_load_more && indeterminate) {
      // load more case
      row.nestedRows.forEach(function (nestedRow) {
        if (selectedRows && nestedRow.rowType !== TableRowType.loadRow) {
          selectedRows.set(nestedRow.key, nestedRow);
        }
      });
      setCounter(nestedRowSelect.dont_select);
    }
  }
  var nestedRows = (_row$nestedRows3 = row.nestedRows) == null ? void 0 : _row$nestedRows3.map(function (nestedRow) {
    var _shouldDisableMultise2;
    var isSelected = Boolean(selectedRows == null ? void 0 : selectedRows.get(nestedRow == null ? void 0 : nestedRow.key));
    var dropdownItems = renderRowMenuDropdownItems == null ? void 0 : renderRowMenuDropdownItems(nestedRow);
    var disableMultiselectChild = (_shouldDisableMultise2 = shouldDisableMultiselect == null ? void 0 : shouldDisableMultiselect(nestedRow)) != null ? _shouldDisableMultise2 : false;
    return React__default.createElement(UITableData, Object.assign({}, rest, {
      isChild: true,
      setCounter: setCounter,
      key: nestedRow.key,
      row: nestedRow,
      rowDropdownItems: dropdownItems,
      selected: isSelected,
      disableMultiselect: disableMultiselectChild,
      onSelect: function onSelect(nestedRow, checked) {
        return _onSelect == null ? void 0 : _onSelect(nestedRow, checked, row);
      },
      hasRowClickSelect: hasRowClickSelect
    }));
  });
  return React__default.createElement(React__default.Fragment, null, parentRow, nestedRows);
};
var UITableFooter = function UITableFooter(props) {
  var children = props.children;
  return React__default.createElement(UIContainer$1, {
    className: "flex items-center justify-end p-4 border-t rounded-b-lg overflow-hidden"
  }, children);
};
var UITableRow = function UITableRow(props) {
  var children = props.children,
    className = props.className;
  return React__default.createElement(UIContainer$1, {
    className: classNames("flex flex-nowrap", className)
  }, children);
};
var UITableHeaderCell = function UITableHeaderCell(props) {
  var header = props.header,
    sortDirection = props.sortDirection,
    onTableCellClick = props.onTableCellClick,
    className = props.className,
    children = props.children;
  var onClick = function onClick() {
    if (header.sortable) {
      var sort = sortDirection === TableSortDirection.descending ? TableSortDirection.ascending : TableSortDirection.descending;
      onTableCellClick == null ? void 0 : onTableCellClick(header, sort);
    }
  };
  var sortIconType = React__default.useMemo(function () {
    if (!sortDirection) {
      return UIIconType.sort;
    }
    if (sortDirection === TableSortDirection.ascending) {
      return UIIconType.sortUp;
    }
    return UIIconType.sortDown;
  }, [sortDirection]);
  return React__default.createElement(UITooltip$1, {
    disabled: !header.description,
    title: header.description,
    delay: 500
  }, React__default.createElement(UITableCell, {
    colspan: header.colspan,
    className: classNames("cursor-pointer items-center ", {
      "hover:bg-gray-6-light hover:text-gray-5-dark transition": header.description
    }, className),
    "data-cy": "ui-table-cell-" + header.key
  }, children, React__default.createElement("div", {
    className: "flex items-center",
    onClick: onClick
  }, React__default.createElement(UIText$1, {
    "data-cy": "ui-table-header-" + header.key,
    typography: UITypography.label2
  }, header.title), header.sortable && React__default.createElement("div", {
    className: "flex items-center text-gray-2-light dark:text-gray-2-dark ml-1",
    "data-cy": "ui-table-sort-icon"
  }, React__default.createElement(UIIcon$1, {
    iconType: sortIconType,
    iconColor: UIColor.inherit
  })))));
};
var UITableHeader = function UITableHeader(props) {
  var headers = props.headers,
    multiselect = props.multiselect,
    selected = props.selected,
    indeterminate = props.indeterminate,
    onSelect = props.onSelect,
    onTableCellClick = props.onTableCellClick;
  var _React$useState = React__default.useState(function () {
      var defaultSortHeader = headers.find(function (h) {
        return h.defaultSort;
      });
      if (defaultSortHeader) {
        return defaultSortHeader.defaultSortDirection || TableSortDirection.descending;
      }
      return TableSortDirection.descending;
    }),
    sortDirection = _React$useState[0],
    setSortDirection = _React$useState[1];
  var _React$useState2 = React__default.useState(headers.find(function (h) {
      return h.defaultSort;
    })),
    sortHeader = _React$useState2[0],
    setSortHeader = _React$useState2[1];
  var onClick = React__default.useCallback(function (header, sortDirection) {
    setSortHeader(header);
    setSortDirection(sortDirection);
    onTableCellClick == null ? void 0 : onTableCellClick(header, sortDirection);
  }, [onTableCellClick]);
  // To update the selected state of the header on click anywhere in the UICheckbox container
  var onHeaderMultiSelectChanged = function onHeaderMultiSelectChanged(e) {
    e.preventDefault();
    e.stopPropagation();
    onSelect == null ? void 0 : onSelect(!selected);
  };
  return React__default.createElement(UITableRow, null, headers.map(function (header, idx) {
    var isFirst = idx === 0;
    return React__default.createElement(UITableHeaderCell, {
      key: header.key,
      header: header,
      sortDirection: (sortHeader == null ? void 0 : sortHeader.key) === header.key ? sortDirection : undefined,
      className: classNames(header.headerClassName, {
        "sticky left-0 rounded-tl-lg bg-white dark:bg-gray-5-dark pl-4": isFirst,
        "pl-0": multiselect && isFirst
      }),
      onTableCellClick: onClick
    }, isFirst && multiselect && React__default.createElement(UIContainer$1, {
      onClick: onHeaderMultiSelectChanged,
      className: "flex items-center px-4 cursor-pointer"
    }, React__default.createElement(UICheckbox, {
      "data-cy": "multiselectCheckbox",
      checked: selected,
      indeterminate: indeterminate,
      onChange: onSelect
    })));
  }));
};
var _excluded$j = ["context", "onLoadMoreRows", "headers", "rows", "footer", "loading", "empty", "rounded", "onRowClick", "onRowHover", "renderRowMenuDropdownItems", "actionBar", "actionBarButtons", "actionBarFilters", "actionBarSearch", "actionBarSearchValue", "actionBarText", "onActionBarSearchChange", "customActionBar", "customRowMenuElement", "alwaysShowCustomRowMenuElement", "infoBars", "multiselect", "multiselectConfirmationModal", "hideHeaderSelect", "hasRowClickSelect", "selectedRows", "onSelectedRowsChange", "onSortChange", "warningBars", "shouldShowFooter", "shouldShowNestedRowIcon", "showBorder", "dynamicTableStyling", "dynamicTableHeightEnabled", "shouldDisableMultiselect", "hideHeader", "tableBodyHeight", "minTableBodyHeight", "showDeselectBar", "numberOfSelectedProducts", "deselectBarAction", "showSelectAll", "selectAllBarAction", "onExpandRow", "alwaysShowExpand", "isCollapsed", "searchInputPlaceholder"];
var defaultTableContext = /*#__PURE__*/React__default.createContext({
  onLoadMoreRows: function onLoadMoreRows() {}
});
var UITable = function UITable(props) {
  var _headers$;
  var _props$context = props.context,
    context = _props$context === void 0 ? defaultTableContext : _props$context,
    onLoadMoreRows = props.onLoadMoreRows,
    headers = props.headers,
    rows = props.rows,
    footer = props.footer,
    loading = props.loading,
    empty = props.empty,
    _props$rounded = props.rounded,
    rounded = _props$rounded === void 0 ? true : _props$rounded,
    onRowClick = props.onRowClick,
    onRowHover = props.onRowHover,
    renderRowMenuDropdownItems = props.renderRowMenuDropdownItems,
    _props$actionBar = props.actionBar,
    actionBar = _props$actionBar === void 0 ? true : _props$actionBar,
    _props$actionBarButto = props.actionBarButtons,
    actionBarButtons = _props$actionBarButto === void 0 ? [] : _props$actionBarButto,
    _props$actionBarFilte = props.actionBarFilters,
    actionBarFilters = _props$actionBarFilte === void 0 ? [] : _props$actionBarFilte,
    _props$actionBarSearc = props.actionBarSearch,
    actionBarSearch = _props$actionBarSearc === void 0 ? true : _props$actionBarSearc,
    actionBarSearchValue = props.actionBarSearchValue,
    actionBarText = props.actionBarText,
    onActionBarSearchChange = props.onActionBarSearchChange,
    customActionBar = props.customActionBar,
    customRowMenuElement = props.customRowMenuElement,
    alwaysShowCustomRowMenuElement = props.alwaysShowCustomRowMenuElement,
    _props$infoBars = props.infoBars,
    infoBars = _props$infoBars === void 0 ? [] : _props$infoBars,
    multiselect = props.multiselect,
    multiselectConfirmationModal = props.multiselectConfirmationModal,
    _props$hideHeaderSele = props.hideHeaderSelect,
    hideHeaderSelect = _props$hideHeaderSele === void 0 ? false : _props$hideHeaderSele,
    hasRowClickSelect = props.hasRowClickSelect,
    selectedRows = props.selectedRows,
    onSelectedRowsChange = props.onSelectedRowsChange,
    onSortChange = props.onSortChange,
    warningBars = props.warningBars,
    _props$shouldShowFoot = props.shouldShowFooter,
    shouldShowFooter = _props$shouldShowFoot === void 0 ? true : _props$shouldShowFoot,
    _props$shouldShowNest = props.shouldShowNestedRowIcon,
    shouldShowNestedRowIcon = _props$shouldShowNest === void 0 ? true : _props$shouldShowNest,
    _props$showBorder = props.showBorder,
    showBorder = _props$showBorder === void 0 ? true : _props$showBorder,
    dynamicTableStyling = props.dynamicTableStyling,
    _props$dynamicTableHe = props.dynamicTableHeightEnabled,
    dynamicTableHeightEnabled = _props$dynamicTableHe === void 0 ? false : _props$dynamicTableHe,
    shouldDisableMultiselect = props.shouldDisableMultiselect,
    _props$hideHeader = props.hideHeader,
    hideHeader = _props$hideHeader === void 0 ? false : _props$hideHeader,
    tableBodyHeight = props.tableBodyHeight,
    _props$minTableBodyHe = props.minTableBodyHeight,
    minTableBodyHeight = _props$minTableBodyHe === void 0 ? 400 : _props$minTableBodyHe,
    _props$showDeselectBa = props.showDeselectBar,
    showDeselectBar = _props$showDeselectBa === void 0 ? false : _props$showDeselectBa,
    numberOfSelectedProducts = props.numberOfSelectedProducts,
    deselectBarAction = props.deselectBarAction,
    showSelectAll = props.showSelectAll,
    selectAllBarAction = props.selectAllBarAction,
    onExpandRow = props.onExpandRow,
    alwaysShowExpand = props.alwaysShowExpand,
    isCollapsed = props.isCollapsed,
    searchInputPlaceholder = props.searchInputPlaceholder,
    rest = _objectWithoutPropertiesLoose(props, _excluded$j);
  var tableBodyScrollRef = React__default.useRef(null);
  var tableHeaderScrollRef = React__default.useRef(null);
  var _React$useState = React__default.useState(false),
    suggestScrollRight = _React$useState[0],
    setSuggestScrollRight = _React$useState[1];
  var _React$useState2 = React__default.useState(false),
    suggestScrollLeft = _React$useState2[0],
    setSuggestScrollLeft = _React$useState2[1];
  var onBodyScroll = function onBodyScroll(e, receiver, sender) {
    var target = e.currentTarget;
    var shadowThresholdDistance = 30;
    if (sender && receiver) {
      if (target.scrollLeft != receiver.scrollLeft) {
        receiver.scrollLeft = target.scrollLeft;
      }
      var atRight = sender.scrollWidth - sender.clientWidth <= sender.scrollLeft + shadowThresholdDistance;
      var atLeft = sender.scrollLeft <= shadowThresholdDistance;
      if (atRight && suggestScrollRight) {
        setSuggestScrollRight(false);
      } else if (!atRight && !suggestScrollRight) {
        setSuggestScrollRight(true);
      }
      if (atLeft && suggestScrollLeft) {
        setSuggestScrollLeft(false);
      } else if (!atLeft && !suggestScrollLeft) {
        setSuggestScrollLeft(true);
      }
    }
  };
  //Check if scroll shadow is necessary
  React__default.useEffect(function () {
    var ref = tableBodyScrollRef.current;
    if (ref && ref.clientWidth < ref.scrollWidth) {
      setSuggestScrollRight(true);
    }
  }, [loading]);
  /**
   * Callback to UITableRow to update the selected state of a row
   *
   * @param row the row in question
   * @param selected the new selected state of the row
   */
  var onRowSelect = React__default.useCallback(function (row, selected, parentRow) {
    var tmp = new Map(selectedRows || []);
    if (selected) {
      var _row$nestedRows;
      tmp.set(row.key, row);
      // If it is a Parent row, select the nested rows also
      (_row$nestedRows = row.nestedRows) == null ? void 0 : _row$nestedRows.forEach(function (nestedRow) {
        if (nestedRow.selectable && nestedRow.rowType !== TableRowType.loadRow) {
          tmp.set(nestedRow.key, nestedRow);
        }
      });
      // If it is a Nested row, select the parent to change it to intermediate state
      if (row.isNested && row.parentId && parentRow) {
        tmp.set(row.parentId, parentRow);
      }
    } else {
      var _row$nestedRows2;
      tmp["delete"](row.key);
      (_row$nestedRows2 = row.nestedRows) == null ? void 0 : _row$nestedRows2.forEach(function (nestedRow) {
        tmp["delete"](nestedRow.key);
      });
      // When unselecting a nested row, check if any of the other nested rows are selected.
      // If yes, its parent remains selected. Else unselect the parent.
      if (row.isNested && row.parentId && !Array.from(tmp.values()).some(function (value) {
        return value.parentId === row.parentId;
      })) {
        tmp["delete"](row.parentId);
      }
    }
    onSelectedRowsChange == null ? void 0 : onSelectedRowsChange(tmp, [row]);
  }, [onSelectedRowsChange, selectedRows]);
  /**
   * Callback to UITableHeader to update the selected state of
   *
   * @param selected the new selected state of the header
   */
  var onHeaderSelect = React__default.useCallback(function (selected) {
    var tmp = new Map();
    if (selected) {
      rows.forEach(function (row) {
        if (row.selectable) {
          var _row$nestedRows3;
          tmp.set(row.key, row);
          (_row$nestedRows3 = row.nestedRows) == null ? void 0 : _row$nestedRows3.forEach(function (nestedRow) {
            return nestedRow.selectable && tmp.set(nestedRow.key, nestedRow);
          });
        }
      });
    }
    onSelectedRowsChange == null ? void 0 : onSelectedRowsChange(tmp, rows);
  }, [onSelectedRowsChange, rows]);
  // End Action Bar Setup
  var gradientClasses = "absolute h-full pointer-events-none w-[20px] transition-opacity";
  var colSpan = (headers == null ? void 0 : (_headers$ = headers[0]) == null ? void 0 : _headers$.colspan) || 1;
  var leftDistance = 180 * colSpan + "px";
  var getRowsLength = function getRowsLength() {
    var numRows = 0;
    rows.forEach(function (row) {
      var _row$nestedRows4;
      numRows++;
      (_row$nestedRows4 = row.nestedRows) == null ? void 0 : _row$nestedRows4.forEach(function () {
        numRows++;
      });
    });
    return numRows;
  };
  var header = function header() {
    return React__default.createElement(UIContainer$1, {
      className: "border-b relative"
    }, React__default.createElement("div", {
      className: classNames(gradientClasses, "right-0", suggestScrollRight ? "opacity-[0.06]" : "opacity-0"),
      style: {
        backgroundImage: "linear-gradient(to left, black, white)"
      }
    }), React__default.createElement("div", {
      style: {
        left: leftDistance,
        backgroundImage: "linear-gradient(to right, black, white)"
      },
      className: classNames(gradientClasses, suggestScrollLeft ? "opacity-[0.06]" : "opacity-0")
    }), React__default.createElement("div", {
      className: "flex flex-row flex-nowrap overflow-x-auto no-scrollbar",
      onScroll: function onScroll(e) {
        return onBodyScroll(e, tableBodyScrollRef.current, tableHeaderScrollRef.current);
      },
      ref: tableHeaderScrollRef
    }, React__default.createElement(UITableHeader, {
      headers: headers,
      multiselect: multiselect && !hideHeaderSelect,
      selected: Boolean(selectedRows == null ? void 0 : selectedRows.size),
      indeterminate: Boolean(selectedRows && selectedRows.size < getRowsLength()),
      onSelect: onHeaderSelect,
      onTableCellClick: onSortChange
    }), React__default.createElement("div", {
      className: "flex w-12 p-4 bg-transparent",
      style: {
        minWidth: "48px"
      }
    })));
  };
  var body = function body() {
    var tableHeightClassName = classNames({
      "h-table-body-height": !dynamicTableHeightEnabled && !tableBodyHeight,
      "h-auto": !tableBodyHeight,
      "h-auto max-h-table-body-height": dynamicTableHeightEnabled && !tableBodyHeight
    });
    var tableHeightStyle = {
      maxHeight: tableBodyHeight,
      height: tableBodyHeight,
      minHeight: tableBodyHeight || minTableBodyHeight
    };
    /**
     * Loading State
     */
    if (loading) {
      return React__default.createElement(UIContainer$1, {
        className: classNames("flex items-center justify-center w-full", tableHeightClassName),
        style: tableHeightStyle
      }, React__default.createElement(UILoadingIndicator$1, {
        iconColor: UIColor.indigo1,
        iconSize: "w-10 h-10"
      }));
    }
    /**
     * Empty State
     */
    if (rows.length === 0) {
      return React__default.createElement(UIContainer$1, {
        ref: tableBodyScrollRef,
        className: classNames("flex items-center justify-center w-full", tableHeightClassName),
        style: tableHeightStyle
      }, empty);
    }
    /**
     * Row State
     */
    return React__default.createElement(UIContainer$1, {
      className: classNames("relative", tableHeightClassName),
      style: tableHeightStyle
    }, React__default.createElement("div", {
      className: classNames(gradientClasses, "right-0", suggestScrollRight ? "opacity-[0.06]" : "opacity-0"),
      style: {
        backgroundImage: "linear-gradient(to left, black, white)"
      }
    }), React__default.createElement("div", {
      style: {
        left: leftDistance,
        backgroundImage: "linear-gradient(to right, black, white)"
      },
      className: classNames(gradientClasses, suggestScrollLeft ? "opacity-[0.06]" : "opacity-0")
    }), React__default.createElement("div", {
      className: classNames("overflow-y-auto", tableHeightClassName),
      style: tableHeightStyle,
      onScroll: function onScroll(e) {
        return onBodyScroll(e, tableHeaderScrollRef.current, tableBodyScrollRef.current);
      },
      ref: tableBodyScrollRef
    }, rows.map(function (row) {
      var _shouldDisableMultise;
      var isSelected = Boolean(selectedRows == null ? void 0 : selectedRows.get(row.key));
      var dropdownItems = renderRowMenuDropdownItems == null ? void 0 : renderRowMenuDropdownItems(row);
      var disableMultiselect = (_shouldDisableMultise = shouldDisableMultiselect == null ? void 0 : shouldDisableMultiselect(row)) != null ? _shouldDisableMultise : false;
      if (row.nestedRows || alwaysShowExpand) {
        return React__default.createElement(UITableDataWithNested, {
          context: context,
          onLoadMoreRows: onLoadMoreRows,
          isCollapsed: isCollapsed,
          key: row.key,
          headers: headers,
          row: row,
          rowDropdownItems: dropdownItems,
          renderRowMenuDropdownItems: renderRowMenuDropdownItems,
          multiselect: multiselect,
          selected: isSelected,
          onSelect: onRowSelect,
          onRowClick: onRowClick,
          onRowHover: onRowHover,
          shouldShowNestedRowIcon: shouldShowNestedRowIcon,
          selectedRows: selectedRows,
          customRowMenuElement: customRowMenuElement,
          shouldDisableMultiselect: shouldDisableMultiselect,
          onExpandRow: onExpandRow,
          alwaysShowExpand: alwaysShowExpand,
          alwaysShowCustomRowMenuElement: alwaysShowCustomRowMenuElement,
          hasRowClickSelect: hasRowClickSelect,
          multiselectConfirmationModal: multiselectConfirmationModal
        });
      }
      return React__default.createElement(UITableData, {
        context: context,
        key: row.key,
        headers: headers,
        row: row,
        "data-cy": "ui-table-data-" + row.key,
        rowDropdownItems: dropdownItems,
        multiselect: multiselect,
        selected: isSelected,
        onSelect: onRowSelect,
        onRowClick: onRowClick,
        onRowHover: onRowHover,
        disableMultiselect: disableMultiselect,
        customRowMenuElement: customRowMenuElement,
        alwaysShowCustomRowMenuElement: alwaysShowCustomRowMenuElement
      });
    })));
  };
  var deselectAllBar = React__default.createElement("div", {
    className: "flex flex-row w-full h-9 border-t border-b border-gray-4-light bg-white items-center px-4"
  }, React__default.createElement(UIText$1, {
    typography: UITypography.caption,
    color: UIColor.gray1
  }, numberOfSelectedProducts, " Selected"), showSelectAll && React__default.createElement("div", {
    onClick: selectAllBarAction
  }, React__default.createElement(UIText$1, {
    typography: UITypography.label,
    color: UIColor.indigo1,
    className: "ml-3 hover:text-indigo-2-dark cursor-pointer"
  }, "Select All")), React__default.createElement("div", {
    onClick: deselectBarAction
  }, React__default.createElement(UIText$1, {
    typography: UITypography.label,
    color: UIColor.indigo1,
    className: "ml-3 hover:text-indigo-2-dark cursor-pointer"
  }, "Deslect All")));
  var table = React__default.createElement(React__default.Fragment, null, customActionBar, actionBar && React__default.createElement(UIActionBar, {
    "data-cy": "uitable-actionbar",
    text: actionBarText,
    filters: actionBarFilters,
    buttons: actionBarButtons,
    className: "border-b rounded-tl-lg rounded-tr-lg",
    search: actionBarSearchValue,
    onSearchChange: onActionBarSearchChange,
    showSearch: actionBarSearch,
    searchInputPlaceholder: searchInputPlaceholder
  }), infoBars.length > 0 && React__default.createElement("div", {
    className: "flex flex-col w-full border-b border-gray-4-light dark:border-gray-4-dark"
  }, infoBars.map(function (infoBar) {
    return infoBar;
  })), React__default.createElement("div", {
    className: "flex flex-col w-full overflow-hidden"
  }, !hideHeader && header(), warningBars, body()), showDeselectBar && deselectAllBar, shouldShowFooter && footer && React__default.createElement(UITableFooter, null, footer));
  return React__default.createElement(UIContainer$1, Object.assign({}, rest, {
    border: showBorder,
    shadow: true,
    rounded: rounded,
    className: classNames("w-full max-w-full", {
      "overflow-hidden": rounded
    }, dynamicTableStyling)
  }), table);
};
var UITableFilter = function UITableFilter(_ref) {
  var labels = _ref.labels,
    onApply = _ref.onApply,
    tableFilterConfigs = _ref.tableFilterConfigs,
    onOpen = _ref.onOpen;
  var _useState = useState(false),
    open = _useState[0],
    setOpen = _useState[1];
  var _useState2 = useState(tableFilterConfigs[0]),
    selectedTableFilterConfig = _useState2[0],
    setSelectedTableFilterConfig = _useState2[1];
  var _useState3 = useState(),
    selectedFilterDropdownItem = _useState3[0],
    setSelectedFilterDropdownItem = _useState3[1];
  var _useState4 = useState(),
    selectedOperationDropdownItem = _useState4[0],
    setSelectedOperationDropdownItem = _useState4[1];
  var _useState5 = useState(),
    selectedValueDropdownItem = _useState5[0],
    setSelectedValueDropdownItem = _useState5[1];
  var _useState6 = useState(),
    customValue = _useState6[0],
    setCustomValue = _useState6[1];
  var _useState7 = useState(),
    filterKey = _useState7[0],
    setFilterKey = _useState7[1];
  var _useState8 = useState(),
    filterTitle = _useState8[0],
    setFilterTitle = _useState8[1];
  var _useState9 = useState(),
    filterOperation = _useState9[0],
    setFilterOperation = _useState9[1];
  var _useState10 = useState(),
    filterValue = _useState10[0],
    setFilterValue = _useState10[1];
  useEffect(function () {
    /**
     * Reset states when selectedTableFilterConfig changes
     * to avoid passing stale data to setFilter
     */
    setSelectedFilterDropdownItem(undefined);
    setSelectedValueDropdownItem(undefined);
    setSelectedOperationDropdownItem(undefined);
    setCustomValue("");
    // Set the filter value if there is only 1 option
    if (selectedTableFilterConfig.filters.length === 1) {
      var _selectedTableFilterC = selectedTableFilterConfig.filters,
        _selectedTableFilterC2 = _selectedTableFilterC[0],
        value = _selectedTableFilterC2.value,
        _title = _selectedTableFilterC2.title;
      setFilterKey(value);
      setFilterTitle(_title);
    }
    if (selectedTableFilterConfig.operations.length === 1) {
      var _selectedTableFilterC3 = selectedTableFilterConfig.operations,
        _value = _selectedTableFilterC3[0].value;
      setFilterOperation(_value);
    }
  }, [selectedTableFilterConfig]);
  useEffect(function () {
    setSelectedTableFilterConfig(tableFilterConfigs.find(function (filterConfig) {
      return filterConfig.type === selectedTableFilterConfig.type;
    }) || tableFilterConfigs[0]);
  }, [tableFilterConfigs]);
  useEffect(function () {
    if (values.length === 0) {
      setFilterValue(customValue === "" ? "" : "" + Number(customValue));
    }
    if (selectedValueDropdownItem) {
      setFilterValue(selectedValueDropdownItem.value);
    }
  }, [customValue, selectedValueDropdownItem]);
  useEffect(function () {
    if (selectedOperationDropdownItem) {
      setFilterOperation(selectedOperationDropdownItem.value);
    }
  }, [selectedOperationDropdownItem]);
  useEffect(function () {
    if (selectedFilterDropdownItem) {
      setFilterKey(selectedFilterDropdownItem.value);
      setFilterTitle(selectedFilterDropdownItem.title);
    }
  }, [selectedFilterDropdownItem]);
  var filters = selectedTableFilterConfig.filters,
    operations = selectedTableFilterConfig.operations,
    _selectedTableFilterC4 = selectedTableFilterConfig.values,
    values = _selectedTableFilterC4 === void 0 ? [] : _selectedTableFilterC4,
    title = selectedTableFilterConfig.title,
    valuesOnScroll = selectedTableFilterConfig.valuesOnScroll;
  var handleOpen = useCallback(function (open) {
    setOpen(open);
    onOpen == null ? void 0 : onOpen(open);
  }, []);
  return React__default.createElement(UIPopover, {
    open: open,
    side: UIPopoverSide.bottom,
    content: React__default.createElement(UIContainer$1, {
      className: "max-w-[254px] space-y-4 p-4",
      rounded: true,
      border: true,
      raised: true
    }, React__default.createElement("div", {
      className: "space-y-2"
    }, React__default.createElement(UIText$1, {
      typography: UITypography.label
    }, labels.filterBy), React__default.createElement(UIDropdown, {
      items: tableFilterConfigs.map(function (config) {
        return {
          title: config.title,
          value: config.type
        };
      }),
      selectedItem: selectedTableFilterConfig,
      onClick: function onClick(index) {
        return setSelectedTableFilterConfig(tableFilterConfigs[index]);
      }
    })), filters.length > 1 && React__default.createElement("div", {
      className: "space-y-2"
    }, React__default.createElement(UIText$1, {
      typography: UITypography.label
    }, "Select " + title), React__default.createElement(UIDropdown, {
      items: filters,
      selectedItem: selectedFilterDropdownItem,
      onClick: function onClick(index) {
        return setSelectedFilterDropdownItem(filters[index]);
      }
    })), operations.length > 1 && React__default.createElement("div", {
      className: "space-y-2"
    }, React__default.createElement(UIText$1, {
      typography: UITypography.label
    }, labels.selectRule), React__default.createElement(UIDropdown, {
      items: operations,
      selectedItem: selectedOperationDropdownItem,
      onClick: function onClick(index) {
        return setSelectedOperationDropdownItem(operations[index]);
      }
    })), values.length > 0 && React__default.createElement("div", {
      className: "space-y-2"
    }, React__default.createElement(UIText$1, {
      typography: UITypography.label
    }, "Select " + title), React__default.createElement(UIDropdown, {
      items: values,
      selectedItem: selectedValueDropdownItem,
      onClick: function onClick(index) {
        return setSelectedValueDropdownItem(values[index]);
      },
      onScrollPopout: valuesOnScroll,
      block: true,
      dynamicPopoutSize: true
    })), values.length === 0 && React__default.createElement("div", {
      className: "space-y-2"
    }, React__default.createElement(UIText$1, {
      typography: UITypography.label
    }, labels.enterValue), React__default.createElement(UIInput, {
      type: "number",
      value: customValue,
      onChange: function onChange(e) {
        return setCustomValue(e.target.value);
      }
    })), React__default.createElement("div", {
      className: "mt-8 flex items-center gap-3"
    }, React__default.createElement(UIButton$1, {
      variant: UIButtonVariant.secondary,
      text: labels.cancel,
      onClick: function onClick() {
        return handleOpen(false);
      },
      block: true
    }), React__default.createElement(UIButton$1, {
      variant: UIButtonVariant.primary,
      text: labels.apply,
      disabled: !filterKey || !filterTitle || !filterOperation || !filterValue,
      onClick: function onClick() {
        onApply({
          key: filterKey,
          title: filterTitle,
          operation: filterOperation,
          value: filterValue
        });
        handleOpen(false);
      },
      block: true
    })))
  }, React__default.createElement(UIButton$1, {
    variant: UIButtonVariant.secondary,
    text: labels.addFilter,
    iconType: UIIconType.filter,
    onClick: function onClick(e) {
      e.preventDefault();
      e.stopPropagation();
      handleOpen(!open);
    },
    "data-cy": "add-filter-button"
  }));
};
var FilterOperations;
(function (FilterOperations) {
  FilterOperations["greaterThan"] = "GREATER_THAN";
  FilterOperations["equals"] = "EQUAL_TO";
  FilterOperations["lessThan"] = "LESS_THAN";
  FilterOperations["greaterThanOrEqualTo"] = "GREATER_THAN_EQUAL";
  FilterOperations["lessThanOrEqualTo"] = "LESS_THAN_EQUAL";
})(FilterOperations || (FilterOperations = {}));
var _OPERATION_LABELS;
var OPERATION_LABELS = (_OPERATION_LABELS = {}, _OPERATION_LABELS[FilterOperations.equals] = "=", _OPERATION_LABELS[FilterOperations.greaterThan] = ">", _OPERATION_LABELS[FilterOperations.greaterThanOrEqualTo] = ">=", _OPERATION_LABELS[FilterOperations.lessThan] = "<", _OPERATION_LABELS[FilterOperations.lessThanOrEqualTo] = "<=", _OPERATION_LABELS);
var FilterPill = function FilterPill(_ref) {
  var filter = _ref.filter,
    clearFilter = _ref.clearFilter,
    _ref$warning = _ref.warning,
    warning = _ref$warning === void 0 ? false : _ref$warning;
  return React__default.createElement("div", {
    className: classNames("group flex rounded-full px-2 py-1 items-center gap-2", {
      "bg-orange-3-light hover:bg-orange-2-light": warning,
      "bg-gray-5-light hover:bg-gray-4-light": !warning
    }),
    "data-cy": "filter-pill-" + filter.key
  }, React__default.createElement(UIText$1, {
    typography: UITypography.label,
    color: warning ? UIColor.orangeDark2 : UIColor.gray1
  }, filter.title + " " + OPERATION_LABELS[filter.operation] + " " + filter.value), React__default.createElement(UIIcon$1, {
    "data-cy": "filter-pill-x-icon",
    onClick: clearFilter,
    iconType: UIIconType.clear,
    iconColor: warning ? UIColor.orangeDark2 : UIColor.gray1,
    className: "cursor-pointer opacity-60 group-hover:opacity-90 group-active:opacity-100"
  }));
};
var FilterInfoBar = function FilterInfoBar(_ref) {
  var labels = _ref.labels,
    filters = _ref.filters,
    onClearIndividualFilter = _ref.onClearIndividualFilter,
    onClearAllFilters = _ref.onClearAllFilters;
  return React__default.createElement(UIContainer$1, {
    className: "flex justify-between items-center p-4",
    "data-cy": "filter-info-bar"
  }, React__default.createElement("div", {
    className: "flex w-full items-center gap-3"
  }, React__default.createElement(UIText$1, {
    typography: UITypography.label2
  }, labels.filters + ":"), React__default.createElement("div", {
    className: "flex flex-wrap gap-2"
  }, filters.map(function (filter, index) {
    return React__default.createElement(FilterPill, {
      key: filter.key + "_" + filter.operation,
      filter: filter,
      clearFilter: function clearFilter() {
        return onClearIndividualFilter(index);
      }
    });
  }))), !!filters.length && React__default.createElement(UIButton$1, {
    "data-cy": "ui-filter-info-bar-clearall",
    variant: UIButtonVariant.tertiary,
    text: labels.clearAll,
    onClick: onClearAllFilters
  }));
};
var _excluded$k = ["className", "containerClassName", "danger", "dangerText", "disabled", "highlights", "inputLabel", "labelColor", "spellCheck", "textareaRef", "tooltipDisabled", "tooltipText", "warning", "warningText"];
var UITextArea = function UITextArea(props) {
  // Props
  var className = props.className,
    containerClassName = props.containerClassName,
    danger = props.danger,
    dangerText = props.dangerText,
    disabled = props.disabled,
    highlights = props.highlights,
    inputLabel = props.inputLabel,
    _props$labelColor = props.labelColor,
    labelColor = _props$labelColor === void 0 ? UIColor.grayDark3 : _props$labelColor,
    _props$spellCheck = props.spellCheck,
    spellCheck = _props$spellCheck === void 0 ? false : _props$spellCheck,
    textareaRef = props.textareaRef,
    tooltipDisabled = props.tooltipDisabled,
    tooltipText = props.tooltipText,
    warning = props.warning,
    warningText = props.warningText,
    rest = _objectWithoutPropertiesLoose(props, _excluded$k);
  // Refs
  var highlightAreaRef = React__default.useRef(null);
  return React__default.createElement(React__default.Fragment, null, inputLabel && React__default.createElement(UIText$1, {
    typography: UITypography.label2,
    color: disabled ? UIColor.grayLight3 : labelColor,
    className: "mb-2 flex flex-row items-center"
  }, inputLabel, tooltipText && React__default.createElement(UITooltip$1, {
    title: tooltipText,
    disabled: tooltipDisabled != null ? tooltipDisabled : disabled
  }, React__default.createElement(UIIcon$1, {
    iconType: UIIconType.info,
    iconColor: UIColor.gray2,
    className: "ml-1"
  }))), React__default.createElement("div", {
    className: classNames("relative w-full", containerClassName)
  }, warning && !danger && React__default.createElement("div", {
    className: "absolute top-12-px right-12-px flex items-center"
  }, React__default.createElement(UITooltip$1, {
    title: warningText,
    disabled: !warningText
  }, React__default.createElement(UIIcon$1, {
    iconType: UIIconType.error,
    iconColor: UIColor.orange1
  }))), danger && React__default.createElement("div", {
    className: "absolute top-12-px right-12-px flex items-center"
  }, React__default.createElement(UITooltip$1, {
    title: dangerText,
    disabled: !dangerText
  }, React__default.createElement(UIIcon$1, {
    iconType: UIIconType.error,
    iconColor: UIColor.red1
  }))), React__default.createElement("div", {
    className: "overflow-y-hidden pointer-events-none inline absolute top-0 left-0 w-full h-full p-3 bg-transparent",
    ref: highlightAreaRef
  }, highlights == null ? void 0 : highlights.map(function (highlight, idx) {
    return React__default.createElement("span", {
      key: idx,
      className: "flex break-words text-transparent select-none w-fit h-[21px]",
      style: {
        width: "fit-content"
      }
    }, React__default.createElement("span", {
      className: classNames({
        underline: highlight.underline
      }),
      style: {
        textDecorationColor: "#ffa726"
      }
    }, highlight.value), highlight.value !== "" && highlight.errorMessages.length > 0 && React__default.createElement(UITooltip$1, {
      title: first(highlight.errorMessages)
    }, React__default.createElement(UIText$1, {
      typography: UITypography.body,
      color: UIColor.orange1,
      className: "ml-3 pointer-events-auto"
    }, "Invalid")));
  })), React__default.createElement("textarea", Object.assign({}, rest, {
    disabled: disabled,
    className: classNames(
    // Common
    "resize-none p-3 border rounded-lg transition focus:outline-none focus:ring-4 w-full h-full",
    // Light Mode Default
    "text-gray-3-dark bg-white border-gray-3-light hover:border-gray-1 focus:border-indigo-1 ring-indigo-3 placeholder-gray-3",
    // Dark Mode Default
    "dark:text-gray-3 dark:bg-gray-5-dark dark:border-gray-3-dark hover:border-gray-1 dark:focus:border-indigo-1 dark:ring-indigo-3-dark dark:placeholder-gray-3-dark",
    // Disabled
    "disabled:text-gray-3-light disabled:border-gray-4-light disabled:bg-gray-6-light dark:disabled:text-gray-4-dark dark:disabled:border-gray-4-dark dark:disabled:bg-gray-5-dark dark:disabled:placeholder-gray-4-dark", className, {
      // Warning
      "pr-8 border-orange-1 hover:border-orange-2-dark focus:border-orange-1 ring-orange-3 dark:border-orange-2-dark dark:hover:border-orange-1 dark:focus:border-orange-1 dark:ring-orange-3-dark": warning && !danger,
      // Danger
      "pr-8 border-red-1 hover:border-red-3-dark focus:border-red-1 ring-red-3 dark:border-red-2-dark dark:hover:border-red-1 dark:focus:border-red-1 dark:ring-red-3-dark": danger
    }),
    ref: textareaRef,
    onScroll: function onScroll(event) {
      if (event.target && highlightAreaRef != null && highlightAreaRef.current) {
        var st = event.target.scrollTop;
        highlightAreaRef.current.scrollTop = st;
      }
    },
    spellCheck: spellCheck
  }))));
};
var UIThumbnailType;
(function (UIThumbnailType) {
  UIThumbnailType["single"] = "single";
  UIThumbnailType["multiple"] = "multiple";
})(UIThumbnailType || (UIThumbnailType = {}));
var multipleClasses = /*#__PURE__*/["w-10", "h-10", "after:top-[0.3125rem]", "before:w-[1.875rem]", "before:h-[1.875rem]", "before:absolute", "before:border", "before:border-gray-4-light", "before:bg-white", "before:rounded", "before:z-[0.5]", "after:left-[0.3125rem]", "after:w-[1.875rem]", "after:h-[1.875rem]", "after:absolute", "after:border", "after:border-gray-4-light", "after:bg-white", "after:rounded", "after:z-0.5", "before:top-[0.625rem]", "before:left-[0.625rem]"].join(" ");
var UIThumbnail = function UIThumbnail(props) {
  var _classNames;
  var variant = props.variant,
    image = props.image,
    className = props.className,
    children = props.children;
  return React__default.createElement("div", {
    className: classNames("relative inline-flex align-middle", (_classNames = {}, _classNames[multipleClasses] = variant === UIThumbnailType.multiple, _classNames)),
    "data-cy": "thumbnail-" + variant
  }, React__default.createElement("div", {
    className: classNames({
      "border border-gray-4-light rounded z-0 p-0.5 bg-white w-10 h-10": variant === UIThumbnailType.single,
      "border border-gray-4-light rounded z-[1] p-0.5 bg-white w-[1.875rem] h-[1.875rem]": variant === UIThumbnailType.multiple
    }, className)
  }, React__default.createElement("img", {
    src: image,
    className: "object-contain w-full h-full"
  }), children));
};
var UIThumbnail$1 = /*#__PURE__*/React__default.memo(UIThumbnail);
var ToastType;
(function (ToastType) {
  ToastType["error"] = "BURNT";
  ToastType["warning"] = "WARNING";
  ToastType["default"] = "GOLDEN";
  ToastType["loading"] = "LOADING";
  ToastType["dismissableLoading"] = "DISMISSABLE_LOADING";
})(ToastType || (ToastType = {}));
var UIToast = function UIToast(props) {
  var title = props.title,
    description = props.description,
    onClose = props.onClose,
    danger = props.danger,
    buttonText = props.buttonText,
    buttonOnClick = props.buttonOnClick,
    isLoading = props.isLoading;
  var isLoadingToast = isLoading === ToastType.loading;
  React__default.useEffect(function () {
    if (!isLoadingToast) {
      setTimeout(function () {
        onClose();
      }, 5000);
    }
    return function () {
      onClose();
    };
  }, []);
  return React__default.createElement("div", {
    className: classNames("w-96 px-5 py-4 mb-4\n        border-indigo-1 dark:border-indigo-1\n        bg-indigo-3-light dark:bg-indigo-3-dark\n        shadow-raised dark:shadow-raised-dark\n        rounded-lg border", {
      "border-red-1 dark:border-red-1 bg-red-3-light dark:bg-red-3-dark": danger
    })
  }, React__default.createElement("div", {
    className: "flex items-center justify-between"
  }, isLoadingToast && React__default.createElement(UIButton$1, {
    variant: UIButtonVariant.tertiary,
    loading: true,
    className: "mr-6"
  }), React__default.createElement(UIText$1, {
    typography: UITypography.label3,
    className: classNames("break-all mb-1 text-indigo-3-dark dark:text-indigo-3-light", {
      "text-red-3-dark dark:text-red-3-light": danger,
      "mr-auto": isLoadingToast
    })
  }, title), React__default.createElement(UIButton$1, {
    variant: UIButtonVariant.tertiary,
    iconType: UIIconType.close,
    onClick: onClose,
    danger: danger
  })), description && React__default.createElement(UIText$1, {
    typography: UITypography.body,
    className: classNames("break-all text-indigo-3-dark dark:text-indigo-3-light", {
      "text-red-3-dark dark:text-red-3-light": danger
    })
  }, description), buttonText && React__default.createElement(UIButton$1, {
    className: "mt-3",
    variant: UIButtonVariant.tertiary,
    onClick: buttonOnClick,
    text: buttonText,
    danger: danger
  }));
};
var UIToastWithoutRedux = function UIToastWithoutRedux(props) {
  var title = props.title,
    description = props.description,
    danger = props.danger,
    isLoading = props.isLoading,
    onCloseClick = props.onCloseClick,
    buttonText = props.buttonText,
    buttonOnClick = props.buttonOnClick;
  return React__default.createElement("div", {
    className: classNames("w-96 px-5 py-4 mb-4 shadow-raised dark:shadow-raised-dark rounded-lg border flex flex-row items-center space-x-4", {
      "border-indigo-1 dark:border-indigo-1 bg-indigo-3-light dark:bg-indigo-3-dark": !danger,
      "border-red-1 dark:border-red-1 bg-red-3-light dark:bg-red-3-dark": danger
    })
  }, isLoading && React__default.createElement(UILoadingIndicator$1, {
    iconColor: UIColor.indigo1
  }), React__default.createElement("div", {
    className: classNames("flex flex-col w-full", {
      // 34px is the width(18px) + spacing(16px) of the spinner
      "max-w-[calc(100%-34px)]": !!isLoading,
      "max-w-full": !isLoading
    })
  }, React__default.createElement("div", {
    className: "flex flex-row space-x-1 items-start justify-between max-w-full"
  }, React__default.createElement(UIText$1, {
    typography: UITypography.label3,
    className: classNames("break-words", {
      // This is necessary for similar reasons it would be necessary for ellipsis
      // 22px is the width(18px) + spacing(4px) of the button
      "max-w-[calc(100%-22px)]": !!onCloseClick,
      "max-w-full": !onCloseClick
    }),
    color: danger ? UIColor.redDark3 : UIColor.indigoDark3
  }, title), onCloseClick && React__default.createElement(UIButton$1, {
    "data-cy": "uitoast-close-button",
    variant: UIButtonVariant.tertiary,
    iconType: UIIconType.close,
    onClick: onCloseClick,
    danger: danger,
    className: "mt-1"
  })), description && React__default.createElement(UIText$1, {
    typography: UITypography.caption,
    className: "mt-1 break-words max-w-full",
    color: danger ? UIColor.redDark3 : UIColor.indigoDark3,
    "data-cy": "toast"
  }, description), buttonText && React__default.createElement(UIButton$1, {
    className: "mt-3",
    variant: UIButtonVariant.tertiary,
    onClick: buttonOnClick,
    text: buttonText,
    danger: danger,
    size: UIButtonSize.small
  })));
};
var UIToggle = function UIToggle(props) {
  var className = props.className,
    checked = props.checked,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    onChange = props.onChange;
  return React__default.createElement("div", {
    className: classNames("inline-flex w-7 h-4 cursor-pointer rounded-full border-3 border-transparent", checked ? "bg-indigo-1" : "bg-gray-2-light", {
      "cursor-not-allowed opacity-40": disabled
    }, className),
    onClick: function onClick() {
      return !disabled && onChange(!checked);
    }
  }, React__default.createElement("span", {
    className: classNames("pointer-events-none inline-block m-0.5 h-3 w-3 rounded-full bg-white shadow transform transition ease-in-out duration-200", checked ? "translate-x-3" : "translate-x-0")
  }));
};
var ChartAxis$1;
(function (ChartAxis) {
  ChartAxis["default"] = "DEFAULT";
  ChartAxis["smallValue"] = "SMALL_VALUE";
})(ChartAxis$1 || (ChartAxis$1 = {}));
var UIMetricCardVariant;
(function (UIMetricCardVariant) {
  UIMetricCardVariant["fullSize"] = "fullSize";
  UIMetricCardVariant["mini"] = "mini";
})(UIMetricCardVariant || (UIMetricCardVariant = {}));
var UIMetricCardColor$1;
(function (UIMetricCardColor) {
  UIMetricCardColor["indigo"] = "bg-indigo-1 group-hover:bg-indigo-2-dark group-active:bg-indigo-3-dark dark:group-hover:bg-indigo-2 dark:group-active:bg-indigo-3";
  UIMetricCardColor["teal"] = "bg-teal-1 group-hover:bg-teal-2-dark group-active:bg-teal-3-dark dark:group-hover:bg-teal-2 dark:group-active:bg-teal-3";
  UIMetricCardColor["orange"] = "bg-orange-1 group-hover:bg-orange-2-dark group-active:bg-orange-3-dark dark:group-hover:bg-orange-2 dark:group-active:bg-orange-3";
  UIMetricCardColor["yellow"] = "bg-yellow-1 group-hover:bg-yellow-2-dark group-active:bg-yellow-3-dark dark:group-hover:bg-yellow-2 dark:group-active:bg-yellow-3";
  UIMetricCardColor["pink"] = "bg-pink-1 group-hover:bg-pink-2-dark group-active:bg-pink-3-dark dark:group-hover:bg-pink-2 dark:group-active:bg-pink-3";
  UIMetricCardColor["gray3light"] = "bg-gray-3 group-hover:bg-gray-2 group-active:bg-gray-1 dark:bg-gray-3-dark dark:group-hover:bg-gray-1 dark:group-active:bg-gray-2";
  UIMetricCardColor["gray3dark"] = "bg-gray-3-dark group-hover:bg-gray-2-dark group-active:bg-gray-1 dark:bg-gray-3-light dark:group-hover:bg-gray-1 dark:group-active:bg-gray-2-light";
})(UIMetricCardColor$1 || (UIMetricCardColor$1 = {}));
var UIMetricCard = function UIMetricCard(props) {
  var _metric$metricMetadat;
  var metric = props.metric,
    value = props.value,
    _props$color = props.color,
    color = _props$color === void 0 ? UIMetricCardColor$1.gray3light : _props$color,
    _props$selected = props.selected,
    selected = _props$selected === void 0 ? false : _props$selected,
    className = props.className,
    _props$loading = props.loading,
    loading = _props$loading === void 0 ? false : _props$loading,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? UIMetricCardVariant.fullSize : _props$variant,
    _props$split = props.split,
    split = _props$split === void 0 ? false : _props$split,
    _props$showDeltaValue = props.showDeltaValue,
    showDeltaValue = _props$showDeltaValue === void 0 ? true : _props$showDeltaValue,
    deltaPercent = props.deltaPercent,
    onMetricSelected = props.onMetricSelected,
    onClose = props.onClose;
  var loadingTile = variant == UIMetricCardVariant.fullSize ? React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
    "data-cy": "ui-metric-card-loading-fullsize",
    className: "h-4 w-[80px] rounded-[100px] bg-gray-5-light dark:bg-gray-4-dark"
  }), React__default.createElement("div", {
    className: "h-6 w-[120px] rounded-[1000px] bg-gray-5-light dark:bg-gray-4-dark"
  }), React__default.createElement("div", {
    className: "h-4 w-[40px] rounded-[100px] bg-gray-5-light dark:bg-gray-4-dark"
  })) : React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
    "data-cy": "ui-metric-card-loading",
    className: "h-3 w-[80px] rounded-[100px] bg-gray-5-light dark:bg-gray-4-dark"
  }), React__default.createElement("div", {
    className: "h-[20px] w-[120px] rounded-[100px] bg-gray-5-light dark:bg-gray-4-dark"
  }));
  if (variant == UIMetricCardVariant.mini) {
    return React__default.createElement(UISelectableContainer, {
      className: classNames("flex h-[80px] w-full cursor-pointer flex-col justify-between p-4", {
        "pointer-events-none cursor-default active:bg-white dark:active:bg-gray-5-dark": disabled || loading
      }, className),
      onClick: function onClick() {
        return onMetricSelected && onMetricSelected(metric);
      },
      selected: selected,
      disabled: disabled || loading,
      "data-cy": metric ? "ui-metric-card-mini-" + metric.key : "ui-metric-card-mini"
    }, loading ? loadingTile : React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
      className: "flex justify-between",
      "data-cy": "ui-metric-card-header-" + (metric == null ? void 0 : metric.key)
    }, React__default.createElement(UIText$1, {
      className: "self-center leading-3",
      typography: UITypography.label,
      color: disabled ? UIColor.grayLight3 : UIColor.grayLight2,
      "data-cy": "ui-metric-card-title-" + (metric == null ? void 0 : metric.key)
    }, metric.title), React__default.createElement(UITooltip$1, {
      title: metric.description,
      side: UIPopoverSide.bottom
    }, React__default.createElement(UIIcon$1, {
      iconType: UIIconType.info,
      iconColor: UIColor.grayLight2
    }))), React__default.createElement("div", {
      className: classNames("text-lg font-semibold text-gray-1", {
        "text-gray-3-dark dark:text-gray-3": selected,
        "text-gray-3 dark:text-gray-3-dark": disabled
      })
    }, value)));
  }
  return React__default.createElement(UIContainer$1, {
    className: classNames("group relative w-full cursor-pointer overflow-hidden", "shadow hover:border-gray-2-light active:border-gray-1 active:bg-gray-6-light", "graydark:active:bg-gray-4-dark dark:hover:border-gray-2-dark", {
      "pointer-events-none": loading || disabled
    }),
    border: true,
    rounded: true,
    "data-cy": "ui-metric-card-" + (metric == null ? void 0 : metric.key)
  }, React__default.createElement("div", {
    className: classNames("absolute h-1 w-full", color)
  }), split && React__default.createElement("div", {
    className: classNames("absolute right-0 h-1 w-[50%]", UIMetricCardColor$1.teal)
  }), React__default.createElement("div", {
    "data-cy": "ui-metric-card-body-" + (metric == null ? void 0 : metric.key),
    className: classNames("flex h-[126px] w-full flex-col justify-between p-4", className),
    onClick: function onClick() {
      onMetricSelected && onMetricSelected(metric);
    }
  }, loading ? loadingTile : React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
    className: "flex items-center justify-between"
  }, React__default.createElement(UIText$1, {
    typography: UITypography.label2,
    color: UIColor.grayLight2,
    "data-cy": "ui-metric-card-title-" + (metric == null ? void 0 : metric.key)
  }, metric.title), React__default.createElement(UIButton$1, {
    "data-cy": "ui-metric-card-close-" + (metric == null ? void 0 : metric.key),
    variant: UIButtonVariant.quaternary,
    iconType: UIIconType.close,
    size: UIButtonSize.large,
    onClick: function onClick(e) {
      e.stopPropagation();
      onClose && onClose(metric);
    }
  })), React__default.createElement("div", {
    className: "text-[24px] font-semibold dark:text-gray-3",
    "data-cy": "ui-metric-card-value-" + (metric == null ? void 0 : metric.key)
  }, value), React__default.createElement("div", {
    className: "flex justify-between"
  }, showDeltaValue && React__default.createElement(UIDeltaPercentage, {
    deltaPercent: deltaPercent,
    invertDeltaColors: (_metric$metricMetadat = metric.metricMetadata) == null ? void 0 : _metric$metricMetadat.invertDeltaColors
  }), metric.description && React__default.createElement(UITooltip$1, {
    popoverClassName: "ml-auto",
    title: metric.description,
    side: UIPopoverSide.bottom
  }, React__default.createElement(UIIcon$1, {
    iconType: UIIconType.info,
    iconColor: UIColor.grayLight2,
    className: "hover:text-gray-1 dark:hover:text-gray-3-light"
  }))))));
};
var UIPLGraphMetricButton = function UIPLGraphMetricButton(props) {
  var _props$variant = props.variant,
    variant = _props$variant === void 0 ? UIMetricCardVariant.fullSize : _props$variant;
  return React__default.createElement("div", {
    className: classNames("flex w-full items-center justify-center bg-transparent", "rounded-lg border border-dashed border-gray-3-light dark:border-gray-3-dark", "text-gray-2-light dark:text-gray-2-dark", {
      "h-[80px]": variant === UIMetricCardVariant.mini,
      "h=[126px]": variant === UIMetricCardVariant.fullSize
    }),
    "data-cy": "ui-empty-metric-selector"
  }, "");
};
var UIEmptyMetricSelector = /*#__PURE__*/React__default.memo(UIPLGraphMetricButton);
var UIPLCustomizeMetricsModal = function UIPLCustomizeMetricsModal(props) {
  var sources = props.sources,
    selectedSources = props.selectedSources,
    data = props.data,
    open = props.open,
    onClose = props.onClose,
    onSubmit = props.onSubmit;
  var _React$useState = React__default.useState(selectedSources),
    shownMetrics = _React$useState[0],
    setShownMetrics = _React$useState[1];
  var _React$useState2 = React__default.useState([]),
    hiddenMetrics = _React$useState2[0],
    setHiddenMetrics = _React$useState2[1];
  // Updates the shown metrics when the selected sources are updated in metric selector
  React__default.useEffect(function () {
    setShownMetrics(selectedSources);
  }, [selectedSources]);
  // When shownMetrics changes, the hiddenMetrics should also update
  React__default.useEffect(function () {
    setHiddenMetrics(sources.filter(function (source) {
      return !shownMetrics.some(function (s) {
        return s.key == source.key;
      });
    }));
  }, [shownMetrics, setShownMetrics, sources]);
  /** onClick to remove metric from shownMetric row */
  var hideMetric = React__default.useCallback(function (source) {
    setShownMetrics(shownMetrics.filter(function (metric) {
      return metric.key !== source.key;
    }));
  }, [shownMetrics]);
  /** onClick adds metric to shownMetric row if row is not already full */
  var selectMetric = function selectMetric(source) {
    if (shownMetrics.length < 4) {
      var newSource = _extends({}, source, {
        graphed: true
      });
      setShownMetrics(function (shownMetrics) {
        return [].concat(shownMetrics, [newSource]);
      });
    }
  };
  var shownSources = React__default.useMemo(function () {
    var sourceRow = shownMetrics.map(function (metric) {
      var _data$metric$key;
      return React__default.createElement(UIMetricCard, {
        key: metric.key,
        metric: metric,
        value: data == null ? void 0 : (_data$metric$key = data[metric.key]) == null ? void 0 : _data$metric$key.periodValue,
        variant: UIMetricCardVariant.mini,
        onMetricSelected: hideMetric,
        selected: true
      });
    });
    while (sourceRow.length < 4) {
      sourceRow.push(React__default.createElement(UIEmptyMetricSelector, {
        key: "empty-metric-" + sourceRow.length,
        variant: UIMetricCardVariant.mini
      }));
    }
    return sourceRow;
  }, [shownMetrics, data, hideMetric]);
  var noChangesMade = shownMetrics.length === selectedSources.length && shownMetrics.every(function (s, i) {
    return s.key === selectedSources[i].key;
  });
  return React__default.createElement(UIModal, {
    onClose: onClose,
    open: open,
    headerText: "Customize Metrics",
    footerButtons: [{
      variant: UIButtonVariant.primary,
      text: "Apply",
      onClick: function onClick() {
        return onSubmit(shownMetrics);
      },
      disabled: noChangesMade
    }],
    height: 587,
    width: 800
  }, React__default.createElement("div", {
    className: "flex flex-col overflow-y-auto p-6"
  }, React__default.createElement("div", {
    className: "mb-4 flex flex-row items-center"
  }, React__default.createElement(UIIcon$1, {
    iconType: UIIconType.view,
    className: "mr-3"
  }), React__default.createElement(UIText$1, {
    typography: UITypography.label3
  }, "Showing"), shownMetrics.length === 4 && React__default.createElement(UIText$1, {
    className: "ml-auto",
    color: UIColor.orange1
  }, "Maximum number of metrics selected")), React__default.createElement("div", {
    className: "mb-5 grid grid-cols-4 gap-4"
  }, shownSources), React__default.createElement("div", {
    className: "mb-4 flex flex-row items-center"
  }, React__default.createElement(UIIcon$1, {
    iconType: UIIconType.hide,
    className: "mr-3"
  }), React__default.createElement(UIText$1, {
    typography: UITypography.label3
  }, "Hidden")), React__default.createElement("div", {
    className: "grid-rows-auto grid grid-flow-row grid-cols-4 gap-4"
  }, hiddenMetrics.map(function (metric) {
    var _data$metric$key2;
    return React__default.createElement(UIMetricCard, {
      key: metric.key,
      metric: metric,
      value: data == null ? void 0 : (_data$metric$key2 = data[metric.key]) == null ? void 0 : _data$metric$key2.periodValue,
      variant: UIMetricCardVariant.mini,
      onMetricSelected: selectMetric,
      disabled: shownMetrics.length === 4
    });
  }))));
};
var UICustomizeMetricsModal = /*#__PURE__*/React__default.memo(UIPLCustomizeMetricsModal);
var UIPLGraphMetricButton$1 = function UIPLGraphMetricButton(props) {
  var onClick = props.onClick,
    text = props.text;
  return React__default.createElement("div", {
    className: classNames("flex h-[126px] w-full cursor-pointer flex-row items-center justify-center rounded-lg border border-dashed bg-transparent", "border-gray-3-light hover:border-gray-2-light active:border-gray-1 dark:border-gray-3-dark dark:hover:border-gray-2-dark dark:active:border-gray-1", "text-gray-2-light hover:text-gray-1 active:text-gray-2-dark dark:text-gray-2-dark dark:hover:text-gray-1 dark:active:text-gray-2-light"),
    onClick: onClick,
    "data-cy": text ? "ui-graph-metric-button-" + text.toLocaleLowerCase().replace(" ", "-") : "ui-graph-metric-button"
  }, React__default.createElement(UIIcon$1, {
    iconType: UIIconType.add,
    className: "mr-2"
  }), text ? text : "Add Metric");
};
var UIGraphMetricButton = /*#__PURE__*/React__default.memo(UIPLGraphMetricButton$1);

// Total number of metrics that can be shown in metric selector
var SELECTION_LIMIT = 4;
var UIGraphMetricSelector = function UIGraphMetricSelector(props) {
  var sources = props.sources,
    selectedSources = props.selectedSources,
    onMetricChange = props.onMetricChange,
    toggleSource = props.toggleSource,
    data = props.data,
    loading = props.loading,
    splitMetric = props.splitMetric,
    _props$graphedLimit = props.graphedLimit,
    graphedLimit = _props$graphedLimit === void 0 ? SELECTION_LIMIT : _props$graphedLimit,
    _props$showDeltaValue = props.showDeltaValue,
    showDeltaValue = _props$showDeltaValue === void 0 ? true : _props$showDeltaValue,
    addButtonText = props.addButtonText;
  var _React$useState = React__default.useState(false),
    showModal = _React$useState[0],
    setShowModal = _React$useState[1];
  React__default.useEffect(function () {
    // If the number of graphed sources is higher than the limit, then we want to deselect them until there's only the limit graphed
    if (selectedSources.reduce(function (count, source) {
      return source.graphed ? count + 1 : count;
    }, 0) > graphedLimit) {
      selectedSources.filter(function (source) {
        return source.graphed;
      }).forEach(function (graphedSource, index) {
        if (index >= graphedLimit) {
          toggleSource(graphedSource);
        }
      });
    }
  }, [graphedLimit]);
  var deselectMetric = function deselectMetric(source) {
    onMetricChange(selectedSources.filter(function (s) {
      return s.key !== source.key;
    }), source);
  };
  /** Open CustomizeMetrics Modal */
  var openModal = function openModal() {
    setShowModal(true);
  };
  /** Close CustomizeMetrics Modal */
  var closeModal = function closeModal() {
    setShowModal(false);
  };
  /** on apply button click, pass new metrics up to PLLineGraph */
  var onSubmit = function onSubmit(sources) {
    onMetricChange(sources);
    setShowModal(false);
  };
  if (loading) {
    return React__default.createElement("div", {
      className: "mb-5 flex flex-row justify-between space-x-4"
    }, Array(4).fill(null).map(function (_v, index) {
      return React__default.createElement(UIMetricCard, {
        key: index,
        //@ts-ignore
        metric: null,
        //@ts-ignore
        value: null,
        loading: true,
        onMetricSelected: function onMetricSelected() {
          return null;
        }
      });
    }));
  }
  var addMetricBoxes = [];
  for (var i = selectedSources.length; i < SELECTION_LIMIT; i++) {
    // first empty box should be the add metric button, any more should be empty
    addMetricBoxes.push(i == selectedSources.length ? React__default.createElement(UIGraphMetricButton, {
      key: "metricButton",
      onClick: openModal,
      text: addButtonText
    }) : React__default.createElement(UIEmptyMetricSelector, {
      key: "empty-metric-" + i
    }));
  }
  return React__default.createElement("div", {
    className: "mb-5 flex flex-row justify-between space-x-4"
  }, selectedSources.map(function (source) {
    var _data$source$key, _data$source$key2, _source$color;
    return React__default.createElement(UIMetricCard, {
      key: source.key,
      metric: source,
      value: data == null ? void 0 : (_data$source$key = data[source.key]) == null ? void 0 : _data$source$key.periodValue,
      deltaPercent: data == null ? void 0 : (_data$source$key2 = data[source.key]) == null ? void 0 : _data$source$key2.deltaPercent,
      color: (_source$color = source.color) == null ? void 0 : _source$color["class"],
      onMetricSelected: toggleSource,
      onClose: deselectMetric,
      split: splitMetric && source.graphed,
      showDeltaValue: showDeltaValue
    });
  }), addMetricBoxes, showModal && React__default.createElement(UICustomizeMetricsModal, {
    sources: sources,
    selectedSources: selectedSources,
    data: data,
    onSubmit: onSubmit,
    open: true,
    onClose: closeModal
  }));
};
var index$1 = /*#__PURE__*/React__default.memo(UIGraphMetricSelector);
var UIBreadcrumbs = function UIBreadcrumbs(props) {
  var crumbs = props.crumbs;
  return React__default.createElement("div", {
    className: "mx-8 my-3 flex h-[20px] flex-row items-center space-x-2"
  }, crumbs.map(function (breadcrumb, index) {
    return React__default.createElement(React__default.Fragment, {
      key: index
    }, breadcrumb.product && React__default.createElement(UIThumbnail$1, {
      variant: UIThumbnailType.single,
      image: breadcrumb.product.image
    }, breadcrumb.product.child_count ? React__default.createElement(UIBadge$1, {
      variant: UIBadgeVariant.black,
      size: UIBadgeSize.small,
      text: breadcrumb.product.child_count.toString(),
      className: "absolute right-[-8px] top-[-8px]"
    }) : null)
    // </div>
    , React__default.createElement(UIText$1, {
      typography: breadcrumb.path ? UITypography.label : UITypography.caption,
      color: breadcrumb.path ? UIColor.indigo1 : UIColor.gray2,
      className: "inline-block h-5 max-w-xs overflow-hidden text-ellipsis whitespace-nowrap leading-5"
    }, breadcrumb.path ? React__default.createElement(Link, {
      to: breadcrumb.path
    }, breadcrumb.text) : breadcrumb.text), index + 1 < crumbs.length && React__default.createElement(UIIcon$1, {
      iconType: UIIconType.chevron,
      iconColor: UIColor.gray2,
      iconSize: "w-3 h-3"
    }));
  }));
};
var UIProductWidgetSize;
(function (UIProductWidgetSize) {
  UIProductWidgetSize["default"] = "default";
  UIProductWidgetSize["large"] = "large";
})(UIProductWidgetSize || (UIProductWidgetSize = {}));
var UIProductWidget = function UIProductWidget(_ref) {
  var _ref$product = _ref.product,
    asin = _ref$product.asin,
    title = _ref$product.title,
    childAsinsCount = _ref$product.childAsinsCount,
    image = _ref$product.image,
    subcategory = _ref$product.subcategory,
    isLoading = _ref.isLoading,
    titleColor = _ref.titleColor,
    multiLineTitle = _ref.multiLineTitle,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? UIProductWidgetSize["default"] : _ref$size;
  return React__default.createElement("div", {
    className: "flex items-center"
  }, React__default.createElement("div", {
    className: classNames("relative ml-2 mr-6 flex items-center justify-center border rounded border-gray-4-light", {
      "min-w-[2.5rem] w-10 h-10": size === UIProductWidgetSize["default"],
      "min-w-[70px] w-[70px] h-[70px]": size === UIProductWidgetSize.large
    })
  }, !!childAsinsCount && React__default.createElement(UIBadge$1, {
    className: "absolute -top-2 -right-2 z-10",
    variant: UIBadgeVariant.black,
    text: "" + childAsinsCount,
    size: UIBadgeSize.small
  }), isLoading && React__default.createElement("div", {
    className: "absolute w-full h-full bg-indigo-3-light flex items-center justify-center"
  }, React__default.createElement(UILoadingIndicator$1, {
    iconColor: UIColor.indigo1
  })), image ? React__default.createElement("img", {
    className: "w-full h-full object-contain rounded",
    src: image,
    alt: ""
  }) : React__default.createElement(UIIcon$1, {
    iconType: UIIconType.noImage,
    iconColor: UIColor.grayLight2,
    iconSize: "w-5 h-5"
  })), React__default.createElement("div", {
    className: "flex flex-col items-start justify-between gap-2"
  }, React__default.createElement(UIText$1, {
    typography: UITypography.label2,
    color: titleColor,
    className: classNames({
      "line-clamp-2": multiLineTitle,
      "line-clamp-1": !multiLineTitle
    })
  }, title || "Product title not found"), React__default.createElement("div", {
    className: "flex gap-2"
  }, React__default.createElement(UIBadge$1, {
    variant: UIBadgeVariant.gray,
    text: asin,
    size: UIBadgeSize.small
  }), subcategory && React__default.createElement(UIBadge$1, {
    variant: UIBadgeVariant.gray,
    text: subcategory,
    size: UIBadgeSize.small
  }))));
};
var UIProductCard = function UIProductCard(_ref) {
  var product = _ref.product,
    countryStore = _ref.countryStore,
    isLoading = _ref.isLoading,
    className = _ref.className;
  return React__default.createElement(UIContainer$1, {
    rounded: true,
    border: true,
    shadow: true,
    className: classNames("flex items-center justify-between p-6", className)
  }, isLoading ? React__default.createElement("div", {
    className: "flex items-center justify-center w-full h-14"
  }, React__default.createElement(UILoadingIndicator$1, {
    iconColor: UIColor.indigo1
  })) : React__default.createElement(React__default.Fragment, null, React__default.createElement(UIProductWidget, {
    product: product,
    titleColor: UIColor.grayDark3,
    multiLineTitle: true,
    size: UIProductWidgetSize.large
  }), React__default.createElement(UIButton$1, {
    variant: UIButtonVariant.secondary,
    text: "View on Amazon",
    onClick: function onClick() {
      return window.open("https://" + countryStore + "/dp/" + product.asin, "_newtab");
    }
  })));
};
var UILineGraphDropdown = function UILineGraphDropdown(props) {
  var dropdownPopoutClassName = props.dropdownPopoutClassName,
    items = props.items,
    selectedItem = props.selectedItem;
  var _React$useState = React__default.useState(false),
    open = _React$useState[0],
    setOpen = _React$useState[1];
  var openDropdown = React__default.useCallback(function () {
    setOpen(true);
  }, []);
  var closeDropdown = React__default.useCallback(function () {
    setOpen(false);
  }, []);
  var trigger = React__default.createElement("div", {
    className: classNames("flex flex-row items-center justify-center text-gray-2-dark hover:text-gray-3-dark", "cursor-pointer active:text-gray-4-dark dark:text-gray-2-light"),
    onClick: openDropdown
  }, React__default.createElement(UIText$1, {
    color: UIColor.inherit,
    typography: UITypography.label2
  }, selectedItem == null ? void 0 : selectedItem.title), React__default.createElement(UIIcon$1, {
    iconSize: "w-2 h-2",
    iconType: UIIconType.down,
    iconColor: UIColor.grayLight2,
    className: "ml-2"
  }));
  return React__default.createElement(UIDropdownPopout, {
    className: dropdownPopoutClassName,
    closePopout: closeDropdown,
    items: items,
    onClick: function onClick() {
      closeDropdown();
    },
    open: open,
    side: UIPopoverSide.bottom,
    trigger: trigger
  });
};
var index$2 = /*#__PURE__*/React__default.memo(UILineGraphDropdown);
var UIOutsideClickHandler = function UIOutsideClickHandler(props) {
  var ref = React__default.useRef();
  useOnClickOutside([ref], props.onOutsideClick);
  return React__default.createElement("div", {
    ref: ref
  }, props.children);
};
export { CurrencyMetric, DefaultMetricUnitNumDecimals, FilterOperations, LineGraphAxis, MetricUnit, MultiplierMetric, NumberMetric, PercentageMetric, TableRowType, TableSortDirection, ThemeContextMode, ToastType, UIActionBar, UIAutoCompleteInput, UIBadge$1 as UIBadge, UIBadgeSize, UIBadgeVariant, UIBanner, UIBreadcrumbs, UIButton$1 as UIButton, UIButtonSize, UIButtonVariant, UICheckbox, UIColor, UIContainer$1 as UIContainer, UIContainerVariant, UIDeltaPercentage, UIDropdown, UIDropdownPopout, UIDropdownTrigger, UIDropdownTriggerVariant, UIEmptyMetricSelector, FilterInfoBar as UIFilterInfoBar, index as UIFlag, UIFlagSize, UIFunnel, UIFunnelSliceVariant, UIGraphMetricButton, index$1 as UIGraphMetricSelector, UIGraphTooltipType, UIIcon$1 as UIIcon, UIIconGradientColor, UIIconType, UIInput, UILineGraph, index$2 as UILineGraphDropdown, UILineGraphTooltip, UILoadingIndicator$1 as UILoadingIndicator, UIMetricCard, UIMetricCardColor$1 as UIMetricCardColor, UIMetricCardVariant, UIModal, UIModalOverlay, UIMultiselectDropdown, UIMultiselectDropdownPopout, UIMultiselectDropdownTrigger, UINestedRowDropdown, UIOutsideClickHandler, UICustomizeMetricsModal as UIPLCustomizeMetricsModal, UIPagination, UIPieChart, UIPill, UIPopover, UIPopoverAlign, UIPopoverSide, UIProductCard, UIProductWidget, UIRadioButton, UIRangeInput, UISelectableContainer, UISelectablePill, UISelector, UISidebar, UISidebarItem, UISidebarSubheader, UITabBar, UITable, UITableDataMoreMenuTrigger, UITableFilter, UITableRowContext, UIText$1 as UIText, UITextArea, UIThumbnail$1 as UIThumbnail, UIThumbnailType, UIToast, UIToastWithoutRedux, UIToggle, UITooltip$1 as UITooltip, UITypography, nestedRowSelect, useOnClickOutside };